/**
 * Orbituary — Orbital Artillery
 * Entry point: initializes renderer, game state, and game loop.
 */

import './game/urlPreset';   // the Lab's ?preset= expands before anything reads the URL
import './game/gpuCheck';   // dev: ?gpucheck, the owner's GPU check (the features off and on, a table to copy)
import { toggleIngameScoreboard } from './ui/scoreboardView';
import { fireCheckSubject } from './core/seats';
import { shellExplosion } from './core/explosions';
import { structureFullHealth } from './core/body';
import * as THREE from 'three';

/** A live (in-flight) trail segment with a growable GPU buffer. */
/** A round flight's playback objects; observed trail segments (gaps where the viewer lost sight) persist at its end. */
interface RoundFlightVisual {
  mesh: THREE.Group;
  flame: EngineFlame;
  trail: THREE.Line;
  preview: ShotPreview | null;
  ownTeam: boolean;
  observed: Vec3[][];
  lastVisible: boolean;
  teamColor: string;
}
interface LiveTrail {
  line: THREE.Line;
  count: number;            // points written
  capacity: number;         // buffer capacity in points
  chevrons: THREE.Group;
  nextChevronIdx: number;   // next point index to place a chevron at
  coneGeo: THREE.ConeGeometry;
  coneMat: THREE.MeshBasicMaterial;
}
import type { GameState, Vec3, ProjectileState, FireParams, StructureType, TurnInput, JetpackParams, ShopParams, BuildParams, MountParams, HealParams, MoveParams, DetonateParams, MeleeParams, DropParams, CollectActionParams, CommandParams, PlantFlagParams, RemoveStakeParams, PickupParams, FogState, PlayerState, PlanetState, WinResult, ItemKey, CampaignState, WeaponKey, PlayerLoadout } from './types';
import { WEAPONS, WEAPON_KEYS, TEAMS, PLANET_LAYERS } from './constants';
import { PHYS_DT, MAX_PROJECTILE_TIME, OUT_OF_BOUNDS, PROBE_REVEAL_RADIUS, EXPOSURE_MARKER_MAX_AGE, SPOTTED_NOTIFICATION_COOLDOWN } from './constants';
import { vecLength, vecClone, vecSub, vecSlerp, vecNormalize, vecAdd, vecScale, vecDot, surfaceTangentFrame } from './math';

// Core
import {
  createGameConfig,
  createInitialGameState,
  setPhase,
  startAimingPhase,
  startFiringPhase,
  
  checkWinCondition,
  isGameOver,
  createProjectile,
  getPlayerWorldPosition,
  getPlayerSurfaceNormal,
  computeLaunchDirection,
  popPendingCluster,
  
  getStructureWorldPosition,
  getHazardWorldPosition,
  getRuinWorldPosition,
  getFloraWorldPosition,
  getRockWorldPosition,
  getCarryableWorldPosition,
  getGroundItemWorldPosition,
  getCollectibleWorldPosition,
  getDrillSiteWorldPosition,
} from './core/gameState';
import { generateMap } from './core/mapgen';
import { chooseHealItem } from './core/damage';
import { initWasm, setPlanets, isWasmReady, isWasmLoaded, setWasmEnabled } from './ai/wasmPhysics';
import type { ProjectileSimResult, TerrainEdit } from './types';
import type { StructureState } from './types';
import { PERSON_BODY_HEIGHT, personSetOf } from './core/scaleProfile';
import { raycastTerrain, nearestSurfaceVertex } from './core/surface';
import { losEyePoint, hasLineOfSight } from './core/los';
import { describeShot, analyzeFlight } from './core/shotRecord';
import { classifyShot, describeDud, nextPace, VISIBLE_FRACTION_WATCHABLE, type ShotPreview } from './core/resolutionDirector';
import { planRoundTimeline, segmentAt, summarizeRound, type RoundTimeline, type RoundFlightInterest } from './core/roundDirector';
import type { ResolutionPace } from './types';
import { takeNextOrder, queueLabel } from './core/turnFlow';
import { createDeploySpawnMarker, removeDeployVisual } from './rendering/deployMarkers';
import { type RoundRecord, type RoundOrder, type RoundFlight } from './core/roundSim';
import { ReplayRecorder, ReplayDriver, saveLastReplay, loadLastReplay, parseReplay, replayVersionWarning, serializeReplay, replayFileName, type ReplayRecord } from './replay/replay';
import { resolveUnitModifiers, kitFromCampaign, previewWindow, previewPoints, CAMPAIGN_BASE_PREVIEW_QUALITY } from './core/unitModifiers';
import { stabilityFactor, maxFirePower, stabilizersOn } from './core/stabilizers';
import { createGravityField, updateGravityField, disposeGravityField, setGravityFieldPreview, type GravityField } from './rendering/gravityField';
import { createThreatCue, updateThreatCues, disposeThreatCues, arrivalBearing, type ThreatCue } from './rendering/threatCue';
import type { ShotRecord } from './types';
import { getEffectWorldPosition } from './core/gameState';
import { disposeObjectTree, bakeStaticParts } from './rendering/meshBake';
import { layCraterDecals } from './rendering/groundAfterHit';
import { resolveImpact, type ImpactResolution } from './core/resolveImpact';
import { type TurnResolution } from './core/resolveTurn';
import { checkFire, jumpLaunchVector, type JumpLaunch } from './core/actions';
import { getBotTeammates } from './core/botCommands';
import { composeModeConfig } from './core/modes';
import { botGuardTeams } from './core/perception';
import { getModeRules } from './core/modeRules';
import { setupModeLayers, finalizeModeDeploy } from './core/modeSetup';
import { canCollect } from './core/collectibles';
import { initFloraDestructionState } from './core/floraTracking';
import { initMatchEventState, isTrajectoryPreviewDisabled, isBearingAlertDisabled } from './core/matchEvents';
import { getPickupableObjects, canCarrierJump, getPlayerCarrying, getCarrierMovementPenalty } from './core/carryables';
import { checkGroundItemPickup } from './core/groundItems';
import { removeStake, getRemovableStakes } from './core/stakes';
import { hasAirbornePlayers, simulateJumpTrajectory, getSurfaceRadius } from './core/physics';
import { PHYS_DT as AIRBORNE_DT, WORMHOLE_COLORS } from './constants';

// Rendering
import {
  createScene,
  createPlanetMesh,
  createStarMesh,
  createWormholeMesh,
  updateStarCorona,
  updatePlanetClouds,
  disposePlanetClouds,
  updateWormholeEffect,
  updatePlanetMesh,
  updatePlanetColors,
  createPlayerMesh,
  updatePlayerMesh,
  disposePlayerMesh,
  createProjectileMesh,
  orientProjectileMesh,
  disposeProjectileMesh,
  createTrailLine,
  updateTrailLine,
  createPreviewLine,
  removeFromScene,
  handleResize,
  render,
  createAimIndicator,
  updateAimIndicator,
  hideAimIndicator,
  createMovementRing,
  updateMovementRing,
  updateMovementCostLabel,
  hideMovementRing,
  createStructureMesh,
  updateStructureMesh,
  disposeStructureMesh,
  setStructureRuined,
  applyFogColors,
  getPlanetMeshColors,
  createGhostMesh,
  disposeGhostMeshes,
  createStructureGhostMesh,
  disposeStructureGhostMeshes,
  createExposureMesh,
  disposeExposureMeshes,
  updateMarkerScreenScale,
  createHazardMesh,
  updateHazardMeshes,
  disposeHazardMesh,
  createRuinMesh,
  updateRuinClaimMarker,
  disposeRuinMesh,
  createPlantedFlagMesh,
  disposePlantedFlagMesh,
  updatePlantedFlags,
  createFloraMesh,
  updateFloraMeshes,
  disposeFloraMesh,
  createRockMesh,
  reseatPropMesh,
  disposeRockMesh,
  createEnvironmentalEffectMesh,
  updateEnvironmentalEffectMeshes,
  disposeEnvironmentalEffectMesh,
  scatterDriftwings,
  type SceneObjects,
  type GhostMesh,
  type StructureGhostMesh,
  type ExposureMesh,
  type HazardMesh,
  type RuinMesh,
  type FloraMesh,
  type RockMesh,
  type EnvironmentalEffectMesh,
  createBotThinkingSprite,
  showBotThinking,
  hideBotThinking,
  type BotThinkingSprite,
} from './rendering/renderer';
import { sweepStructureHeads, startStructureRise, riseStructures, syncStructureMeshes, lightWeb, stepStructureSmoke, aimStructures } from './rendering/renderer';
import { SkyHaze } from './rendering/skyHaze';
import { WeatherView } from './rendering/weatherView';
import { Layer0Views } from './rendering/layer0Views';
import { weatherCues } from './audio/weatherAhead';
import { CellVolumes } from './rendering/cellVolumes';
import { cellsAt, weatherTime } from './core/windField';
import { groundMaterialAt } from './core/groundMaterial';
import { TopoMap, contourInterval } from './ui/topoMap';
import { setTopoOverlay, overlayStep, topoDensity } from './rendering/topoOverlay';
import { HUD_MAP_SIZE } from './ui/groundHud';
import { DroneTablet } from './ui/droneTablet';
import { DroneFeed } from './ui/droneFeed';
import { GroundScatter } from './rendering/groundScatter';
import { LitterField, litterScale, litterWarmPart } from './rendering/groundLitter';
/** 67.61: the ground litter hides above this many people of the camera's height over its ground (the fine ground's fade is from 60). */
const LITTER_HIDE_PEOPLE = 40;
import { planetLitterGround } from './game/litterGround';
import { forestDrawn } from './rendering/forestDraw';
import { NearFieldStreamer, NEAR_FIELD } from './rendering/nearField';
import { MissileCam } from './ui/missileCam';
import { FineTerrainPatch, fineGroundDrawn, fineHeightFade } from './rendering/fineTerrainPatch';
import { riverbedSource, type RiverbedSource } from './rendering/riverbedLook';
import { attachRiverbedFar } from './rendering/riverbedFar';
import { flatDiscs, FINE_FLATTEN } from './rendering/fineFlatten';
import { setGroundLift, setGroundSink, farLift, BOWL_SINK_PEOPLE } from './rendering/groundLift';
import { attachSurfaceLook, lookPlanetIndex, surfaceLookOf, disposeSurfaceLook, refreshSurfaceLookColours } from './rendering/surfaceLook';   // 71.8
import { attachMountainStyle, mountainStylePlanetIndex, mountainStyleOf, applyMountainStyleToPatch, disposeMountainStyle, refreshMountainStyleColours } from './rendering/mountainStyle';   // 388
import { battlePlanetIndex } from './core/actionSetup';
import { HitShapesView, hitShapesShown } from './rendering/hitShapesView';
import { TreeStands, stressTrees, treeStressParam, treeDrawMode } from './rendering/treeStands';
import { StandSkirts } from './rendering/standSkirts';
import { FarFieldPlay, farFieldDrawn } from './rendering/farFieldPlay';
import { warmPrograms, type ProgramWarm } from './rendering/programWarm';
import { SystemSkyPlay } from './rendering/systemSkyPlay';
import { PlanetAuroras } from './rendering/aurora';
import { PlanetRockMerge } from './rendering/rockMerge';
import { fitCameraDepth } from './rendering/cameraDepth';
import {
  createImpactDust,
  updateImpactDust,
  disposeImpactDust,
  type ImpactDustEffect,
} from './rendering/environmentModels';
import {
  createCameraState,
  setupCameraControls,
  updateCamera,
  setCameraTarget,
  lerpCameraTarget,
  setCameraZoom,
  setMissileCam,
  setManualPivot,
  glideCameraTo,
  setCameraFrame,
  clearCameraFrame,
  frameWouldFit,
  type CameraState,
} from './rendering/camera';
import {
  createExplosion,
  fireballRadius,
  updateExplosion,
  disposeExplosion,
  createEngineFlame,
  updateEngineFlame,
  disposeEngineFlame,
  createEruptionEffect,
  updateEruptionEffect,
  disposeEruptionEffect,
  createShatterEffect,
  updateShatterEffect,
  disposeShatterEffect,
  createRippleEffect,
  updateRippleEffect,
  disposeRippleEffect,
  type Explosion,
  type EruptionEffect,
  type ShatterEffect,
  type RippleEffect,
  createImpactFade,
  updateImpactFade,
  disposeImpactFade,
  type ImpactFade,
  type EngineFlame,
} from './rendering/effects';
import { updateAnimation, setHeldTool, resetAnimContext } from './rendering/playerModel';
import { createDroneDelivery, updateDroneDelivery, disposeDroneDelivery, type DroneDeliveryEffect } from './rendering/droneDelivery';
import { createDropCanEffect, updateDropCanEffect, disposeDropCanEffect, type DropCanEffect } from './rendering/dropCan';
import { createFerryPickupEffect, updateFerryPickupEffect, disposeFerryPickupEffect, type FerryPickupEffect } from './rendering/ferry';
import { buildingsByFunction } from './core/buildingScale';
import { modularBuildingsOn } from './core/modules';
import { buildTeamStructure } from './rendering/buildingModels';
import { ActionHost } from './game/actionHost';
import { ShootingStars, skyCharacter } from './rendering/shootingStars';
import { loadPlayerHand, savePlayerHand } from './rendering/handedness';
import { actionDuelSetup } from './game/smokeSetup';
import { actionScale } from './ui/actionScale';
import { setupLab } from './ui/lab';
import { hostActionBotOptions, hostActionDriver, type DuelSetup } from './ai/actionDuel';
import { reseatPlinth } from './rendering/groundSeat';
import type { PropScaleOpts } from './rendering/renderer';
import { createActionBot } from './ai/actionBot';
import { createCommander, hookAnswer, orderTo, type Commander, type IssueOrder, type DropCalls, type Squad } from './ai/squadOrders';
import type { SkirmishSetup } from './ai/actionSkirmish';
import { squadOf } from './core/squads';
import { wantsCallIn, callInText } from './core/callIn';
import { markBodies, markNearestSeen } from './core/marks';
import { arrangeRange, rangeDriver, RANGE_LAYOUT } from './ai/actionRange';
import { callsReserves, sidePlays, skirmishMatchSetup } from './ai/actionSkirmish';
import { surfacePropScale } from './core/propScale';
import {
  createCarryableMesh, updateCarryableMeshes, disposeCarryableMesh,
  createCollectibleMesh, updateCollectibleMeshes, disposeCollectibleMesh,
  createDrillSiteMesh, updateDrillSiteMesh, disposeDrillSiteMesh,
  createCarrierIndicator,
  createGroundItemMesh, updateGroundItemMeshes, disposeGroundItemMesh,
  type CarryableMesh, type CollectibleMesh, type DrillSiteMesh, type GroundItemMesh,
} from './rendering/carryableModels';
import { initMinimap, updateMinimap, setMinimapVisible, type MinimapState } from './ui/minimap';

// UI
import {
  buildPlayerBars,
  updatePlayerBars,
  updateTurnInfo,
  showControlsBar,
  updateAimDisplay,
  updateAimSphere,
  logMessage,
  clearLog,
  clearScoreboard,
  showGameOver,
  hideGameOver,
  showMenu,
  updateCameraMode,
  updateChipVisibility,
  updateThreatEdgeMarkers,
  showErrorScreen,
  hideErrorScreen,
  showInGameHelp,
  isInGameHelpVisible,
  showInGameSettings,
  isInGameSettingsVisible,
  updateSettingsToggles, updatePaceToggle,
  updateVolumeSliders,
} from './ui/hud';
import {
  createAimState,
  createKeyState,
  setupKeyboardInput,
  processAimInput,
  setupAimPad,
  buildWeaponButtons,
  updateWeaponButtons,
  getSelectedWeaponKey,
  setupPowerSlider,
  setupFireButton,
  updateFireButtonText,
  setUnqueueButton,
  setupMoveButton,
  updateMoveButton,
  updateMovementDisplay,
  setupJumpButton,
  updateJumpButton,
  setupMeleeButton,
  updateMeleeButton,
  setupHealButton,
  updateHealButton,
  setupShopButton,
  showInGameShop,
  hideInGameShop,
  isInGameShopOpen,
  getInGameShopPurchases,
  getInGameShopFuelQty,
  getInGameShopItemPurchases,
  showBuildMenu,
  hideBuildMenu,
  isBuildMenuOpen,
  setupBuildButton,
  updateBuildButton,
  setupFlagButton,
  updateFlagButton,
  setupYieldButton,
  setFireButtonEnabled,
  type AimState,
  type KeyState,
} from './ui/controls';
import { arcDistance, computeTurnOrder, canPlantFlag, plantFlag } from './core/gameState';
import { canBuildEcotourism } from './core/economy';
import { JUMP_MIN_COST, MELEE_RANGE, MELEE_DAMAGE, MELEE_MOVEMENT_COST, BUILD_MOVEMENT_COST, BUILD_PLACEMENT_RADIUS, WALK_SPEED, HEAL_MOVEMENT_COST, HEAL_ITEMS } from './constants';
import { setupMenuHandlers, type MenuOptions, updateP2PStatus, showP2PHostConfig } from './ui/menu';
import { PeerManager } from './network/peer';
import { NetworkSync, canApplyNetAction, type TurnSync } from './network/sync';
import type { NetAction } from './network/protocol';

// Fog
import {
  initializeFog,
  updateFogForPlayer,
  refreshFogColors,
  revealAtPoint,
  revealAlongPath,
  getVisibleEnemies,
  getTeamVisibleEnemies,
  getTeamFog,
  getEnemiesAtPoint,
  getEnemiesAlongPath,
  updateGhostMarkers,
  getStructuresAtPoint,
  getStructuresAlongPath,
  updateStructureGhosts,
  checkIfPlayerSpotted,
} from './core/fog';

// AI
import {
  computeBotLoadout,
} from './ai/bot';
import { BotWorkerClient } from './ai/botWorkerClient';
import { BotTurnRunner, botConfigFor, type BotHost } from './ai/botTurnRunner';
import { traceTurnChecksum, resetChecksumTrace } from './game/checksumTrace';
import { describeChange } from './core/perception';
import { listenForSectorBattle, postSectorBattleDone } from './game/sectorBridge';
import { SectorSession, resolveBattleInWorker, sectorOptions } from './game/sectorSession';
import { rulesOf } from './ui/sectorRules';
import { showSectorScreen, hideSectorScreen, renderSectorScreen, loadSectorRules } from './ui/sectorScreen';
import { autoplayOptions, autoplaySetup, resolveForComparison, markAutoplayDone, exposeForAutoplay, AUTOPLAY_TIME_SCALE, actionLaunch, LEAN_RENDER_EVERY, resolveDuelForComparison, resolveSkirmishForComparison, markActionDone } from './game/autoplay';
import { DeployPhaseController, type DeployHost } from './game/deployPhase';
import { SeededRandom } from './core/random';
import { completeMatchState, createMatch, type MatchSetup } from './match/setup';
import { terraceReadout } from './core/terraces';
import { Match, type ScanResult, type AirborneStep, type SimPresenter, type HitscanOutcome, type PlanResult, type TurnPass, type ProximityOutcome, type RoundClose, type ShotSettled } from './match/match';
import { seatActor } from './match/input';
import type { MatchInputBody, TurnAction } from './types';
import type { QueuedOrder } from './core/turnFlow';
import { BOT_PRESETS } from './constants';

// Leadership UI
import {
  showLeadershipPanel,
  isLeadershipPanelVisible,
  updateLeadershipPanel,
  setupOrdersButton,
  showOrdersButton,
  type LeadershipCallbacks,
} from './ui/leadership';

// Inventory UI
import {
  showInventoryPanel,
  isInventoryPanelVisible,
  updateInventoryPanel,
  setupInventoryButton,
  type InventoryCallbacks,
} from './ui/inventory';

// Notifications
import { pushNotification, toggleCommsLog, isCommsLogOpen, setToastsEnabled, areToastsEnabled, clearCommsLog } from './ui/notifications';
import { FrameProfiler } from './ui/frameProfiler';
import { FRAME_CLAMP_MS } from './ui/frameStats';
import { initAudio, setBusVolume, getBusVolume, rocketLaunch, explosionSmall, explosionLarge, craterImpact, uiClick, turnStart, turnEnd, victory, defeat, footstepDefault, jumpLaunch, jumpLand, playSpatial, updateListenerPosition } from './audio/sfx';
import type { AudioBus } from './audio/sfx';
import { HazardEmitterManager } from './audio/spatial';
import { AmbientManager } from './audio/ambient';
import { WeatherSound } from './audio/weatherSound';
import { windHere, windAt } from './core/wind';
import { WindProps, findWindMounts, windFelt, smokeDrift, type WindFelt } from './rendering/windView';
import { PERSON_UNITS } from './rendering/personModel';
import { G } from './constants';
import { MusicManager, loadMusicSetId, saveMusicSetId, type MusicSetId } from './audio/music';
import { MUSIC_SETS, musicSetFor } from './audio/musicMix';
import { updateMusicMix, resetConductor } from './audio/conductor';
import { deriveMatchPhase } from './audio/musicMix';
import type { ConductorState, ConductorTurnPhase, ConductorMatchPhase } from './audio/conductor';
import { fx, cur, itemDisplayName, matchMessage, matchSender, alertSender, isFlavorEnabled, setFlavorEnabled } from './ui/flavor';
import { isDevMode, setDevMode } from './ui/devMode';

const SHOT_CAM_KEY = 'orbituary-shot-cam';
type ShotCam = 'connect' | 'follow' | 'ride';
function loadShotCam(): ShotCam {
  try { const v = localStorage.getItem(SHOT_CAM_KEY); return v === 'follow' || v === 'ride' ? v : 'connect'; } catch { return 'connect'; }
}
function saveShotCam(v: ShotCam): void {
  try { localStorage.setItem(SHOT_CAM_KEY, v); } catch { /* storage unavailable */ }
}
import { generateCampaignGraph, completeBattle, loseBattle, advanceWave, battleConfigToBridge, getCampaignResult, getCampaignSummary, calculateSystemHealth } from './core/campaign';
import { showCampaignMap, updateCampaignMap, hideCampaignMap, saveCampaignToStorage, loadCampaignFromStorage, clearCampaignStorage, showCampaignEndScreen, hideCampaignEndScreen } from './ui/campaignMap';
import { buildCampaignLoadout, purchaseWeapon, purchaseAmmo, purchaseItem, purchaseApUpgrade, purchaseSkillUpgrade, purchaseFitnessUpgrade, purchaseJumpUpgrade, returnUnusedInventory } from './core/campaignStore';
import { showCampaignStore, updateCampaignStore, hideCampaignStore } from './ui/campaignStore';
import { showCampaignGear, updateCampaignGear, hideCampaignGear } from './ui/campaignGear';
import { purchaseGearUpgrade } from './core/campaignGear';
import { getAliveUnits, markUnitsDead, renameUnit } from './core/campaignRoster';
import { showCampaignTeam, updateCampaignTeam, hideCampaignTeam } from './ui/campaignTeam';
import { setupCheatConsole } from './ui/cheatConsole';
import { inputKey, orderLogLines } from './core/orderLog';
import { injectThemeVars } from './ui/theme';
import { groundMaterialsOn } from './core/groundMaterial';
import { countOf, defaultOn } from './core/defaultOn';

// Inject OKLCH theme CSS custom properties before any UI renders
injectThemeVars();

// =============================================================================
// GAME CLASS
// =============================================================================

class Game {

  // ---- Match runtime (src/match/match.ts, Phase 51) ----------------------------------
  // The match-state fields live on `this.match`; these accessors keep the rest of
  // Game unchanged while logic moves over step by step.
  private match = new Match();
  /** Phase 59.3: the action clock's host while a real-time match runs (null in the turn game). */
  private actionHost: ActionHost | null = null;
  /** 68.21: the inputs the page's bot commanders gave (`inputKey`), so the order log can tell them from yours. */
  private botOrderKeys = new Set<string>();
  /** Ruling 22: inside an atmosphere the sky is sky and distance hazes (off above it — the orbit view is unchanged). */
  private skyHaze: SkyHaze;
  /** Weather, seen (rendering/weatherView.ts): the sky haze's fog and tint, and the fall round the camera. */
  private weatherView!: WeatherView;
  /** 62.54 step 7: the weather cells seen from outside (with the weather cells on) */
  private weatherCellsView!: CellVolumes;
  private lastFrameDt = 1 / 60;
  /** I5: drones in flight and the viewer's sightings, drawn from the action match each frame. */
  private layer0Views: Layer0Views | null = null;
  /** I7: the layer-0 map — a fixed topographic view of the area with what the viewer's team knows. */
  private topoMap: TopoMap | null = null;
  private actionViewer: { team: number | null; id: number | null } = { team: null, id: null };
  /** I8: the drone tablet, open while the operator sets a course. */
  private droneTablet: DroneTablet | null = null;
  private droneFeed: DroneFeed | null = null;
  /** Decision 11 drawn: the person-scale ground around the camera on the action clock (off until the sim stands people on it). */
  private fineGround: FineTerrainPatch | null = null;
  /** 74.20: where the drainage's beds are drawn (rendering/riverbedLook.ts), or none */
  private riverbeds: RiverbedSource | null = null;
  /** 63.3 step 2: the Lab's hit-shape view (`?hitshapes=1`), on the action clock. */
  private hitShapes: HitShapesView | null = null;
  /** Ground scatter (grass, scrub, stones) round a person-scale battle: presentation only, four draw calls. */
  private groundScatter: GroundScatter | null = null;
  /** 67.61: the ground litter under the camera (the Lab's Ground litter dial; null when off or not on a planet's ground) */
  private litter: { field: LitterField; planetIndex: number } | null = null;
  private get gameState(): GameState { return this.match.state; }
  private set gameState(v: GameState) { this.match.state = v; }
  private get clock(): Match['clock'] { return this.match.clock; }
  private set clock(v: Match['clock']) { this.match.clock = v; }
  private get turnFlow(): Match['turnFlow'] { return this.match.turnFlow; }
  private set turnFlow(v: Match['turnFlow']) { this.match.turnFlow = v; }
  private get batchQueue(): Match['batchQueue'] { return this.match.batchQueue; }
  private set batchQueue(v: Match['batchQueue']) { this.match.batchQueue = v; }
  private get batchActive(): Match['batchActive'] { return this.match.batchActive; }
  private set batchActive(v: Match['batchActive']) { this.match.batchActive = v; }
  private get plannedMoves(): Match['plannedMoves'] { return this.match.plannedMoves; }
  private get turnAdvancing(): Match['turnAdvancing'] { return this.match.turnAdvancing; }
  private set turnAdvancing(v: Match['turnAdvancing']) { this.match.turnAdvancing = v; }
  private get hasFiredThisTurn(): Match['hasFiredThisTurn'] { return this.match.hasFiredThisTurn; }
  private set hasFiredThisTurn(v: Match['hasFiredThisTurn']) { this.match.hasFiredThisTurn = v; }
  /**
   * R11 step 2: an action enters the Match as an input through Match.apply — a human's stamped for its seat,
   * a bot's for itself — and the Match logs it. The result is the action method's own.
   */
  private turn(player: PlayerState, action: TurnAction, params: TurnInput['params']): PlanResult<object> {
    const actor = player.isBot ? player.id : seatActor(player.teamIndex);
    return this.match.apply(this.match.stamp(actor, { kind: 'turn', input: { playerId: player.id, action, params } }), this.simPresenter);
  }
  private input(player: PlayerState, body: MatchInputBody): PlanResult<object> {
    const actor = player.isBot ? player.id : seatActor(player.teamIndex);
    return this.match.apply(this.match.stamp(actor, body), this.simPresenter);
  }
  private get currentShot(): Match['currentShot'] { return this.match.currentShot; }
  private set currentShot(v: Match['currentShot']) { this.match.currentShot = v; }
  private get precomputedSim(): Match['precomputedSim'] { return this.match.precomputedSim; }
  private set precomputedSim(v: Match['precomputedSim']) { this.match.precomputedSim = v; }
  private get projCursor(): Match['projCursor'] { return this.match.projCursor; }
  private set projCursor(v: Match['projCursor']) { this.match.projCursor = v; }
  private get precomputedGeyserIdx(): Match['precomputedGeyserIdx'] { return this.match.precomputedGeyserIdx; }
  private set precomputedGeyserIdx(v: Match['precomputedGeyserIdx']) { this.match.precomputedGeyserIdx = v; }
  private get pendingClusterLaunch(): Match['pendingClusterLaunch'] { return this.match.pendingClusterLaunch; }
  private set pendingClusterLaunch(v: Match['pendingClusterLaunch']) { this.match.pendingClusterLaunch = v; }
  private get simAccumulator(): Match['simAccumulator'] { return this.match.simAccumulator; }
  private set simAccumulator(v: Match['simAccumulator']) { this.match.simAccumulator = v; }
  private get probeTrailPoints(): Match['probeTrailPoints'] { return this.match.probeTrailPoints; }
  private set probeTrailPoints(v: Match['probeTrailPoints']) { this.match.probeTrailPoints = v; }
  private get probeRevealCounter(): Match['probeRevealCounter'] { return this.match.probeRevealCounter; }
  private set probeRevealCounter(v: Match['probeRevealCounter']) { this.match.probeRevealCounter = v; }
  private get boostedGeyserIds(): Match['boostedGeyserIds'] { return this.match.boostedGeyserIds; }
  private set boostedGeyserIds(v: Match['boostedGeyserIds']) { this.match.boostedGeyserIds = v; }
  private get playerBoostedGeysers(): Match['playerBoostedGeysers'] { return this.match.playerBoostedGeysers; }
  private set playerBoostedGeysers(v: Match['playerBoostedGeysers']) { this.match.playerBoostedGeysers = v; }
  private get airborneRevealCounter(): Match['airborneRevealCounter'] { return this.match.airborneRevealCounter; }
  private set airborneRevealCounter(v: Match['airborneRevealCounter']) { this.match.airborneRevealCounter = v; }
  private get wasmReady(): Match['wasmReady'] { return this.match.wasmReady; }
  private set wasmReady(v: Match['wasmReady']) { this.match.wasmReady = v; }
  private get wasmAllowed(): Match['wasmAllowed'] { return this.match.wasmAllowed; }
  private set wasmAllowed(v: Match['wasmAllowed']) { this.match.wasmAllowed = v; }
  private get truePlanetColors(): Match['truePlanetColors'] { return this.match.truePlanetColors; }
  private set truePlanetColors(v: Match['truePlanetColors']) { this.match.truePlanetColors = v; }
  private get botFlightWaiter(): Match['botFlightWaiter'] { return this.match.botFlightWaiter; }
  private set botFlightWaiter(v: Match['botFlightWaiter']) { this.match.botFlightWaiter = v; }
  private get botFlightSettled(): Match['botFlightSettled'] { return this.match.botFlightSettled; }
  private set botFlightSettled(v: Match['botFlightSettled']) { this.match.botFlightSettled = v; }
  private get gameWasm(): boolean { return this.match.gameWasm; }
  private get roundFlow(): boolean { return this.match.roundFlow; }

  private sceneObjs: SceneObjects;
  private cameraState: CameraState;
  private aimState: AimState;
  private keyState: KeyState;
  private effects: Explosion[] = [];
  private impactFades: ImpactFade[] = [];   // projectile bodies/flames fading into their impact (37.2d)
  private remoteFlightEndConfirmed = false;  // P2P: remote shooter confirmed no (further) detonation
  private lastTime: number | null = null;
  private previewDirty = false;
  private previewThrottleTime = 0;
  private profiler = new FrameProfiler();

  // Movement mode
  private raycaster = new THREE.Raycaster();
  private mouseNDC = new THREE.Vector2();
  private moveTargetDir: Vec3 | null = null;
  private jumpTargetPlanet: number | null = null;
  private meleeTarget: number | null = null; // Player ID of melee target
  private jumpPreviewLine: THREE.Line | null = null;
  private jumpPreviewDirty = true;

  // Walk animation (smooth interpolation)
  private walkAnim: {
    playerId: number;
    startDir: Vec3;
    endDir: Vec3;
    progress: number; // 0..1
    duration: number; // seconds
  } | null = null;
  private walkStepCount = 0; // footsteps played in current walk

  // Build mode
  private buildStructureType: StructureType | null = null;
  private buildTargetDir: Vec3 | null = null;

  // Waypoint mode (leadership panel: click planet surface to set holdPosition target)
  private waypointMode: { botId: number; planetIndex: number; holdTurns: number; prevCameraTarget: Vec3 } | null = null;
  private waypointTargetDir: Vec3 | null = null;
  private waypointMarker: THREE.Mesh | null = null;

  // Fog of war
  private battShown = false;   // BATT countdown currently on screen
  private visibleEnemyIds: Set<number> = new Set();
  private spottedCooldowns: Map<number, number> = new Map();

  // What the viewer can see: a match setting (fog of war or everyone). Dev mode can flip it.
  private visibility: 'fog' | 'free' = 'fog';
  // Resolution director (Phase 40.1): the current flight's tier, and the missile-cam state it may have overridden
  private resolutionPace: ResolutionPace = 'full';
  private shotPreview: ShotPreview | null = null;
  private missileCamBeforeShot: boolean | null = null;
  /** Shot camera: 'connect' frames the projectile and its predicted impact together; 'follow' tracks the projectile. */
  private shotCam: ShotCam = loadShotCam();
  /** I9: the missile cam — the shot seen from the round (Settings → Shot camera: ride). */
  private missileCam: MissileCam | null = null;
  private rideThisFrame: { projectile: ProjectileState; impact: Vec3 | null } | null = null;
  private lastShotEnd: 'impact' | 'lost' | null = null;
  // Plan & resolve UI: the UNQUEUE button, planned-move markers and planned-path ghosts
  private unqueueWired = false;
  private plannedMoveMarkers = new Map<number, THREE.Object3D>();
  /** Round flows: the planned flight of each queued human shot (as much as its preview showed), drawn as a ghost for the planner's team until the round resolves. */
  private plannedPaths = new Map<number, { teamIndex: number; points: Vec3[] }>();
  private plannedPathGhosts = new Map<number, THREE.Line>();
  // Plan round playback (40.5; 64.6 step 3): the Match steps the round live, one round step a tick; the director
  // planned the whole playback from the round's preview (the same record), which this presents step by step
  private roundPlay: {
    record: RoundRecord;
    step: number;
    /** The director's playback is over (its last step shown, or nothing to show); the Match runs the round out. */
    ended: boolean;
    /** The Match closed the round while the playback's tail (its rest on the last impact) still plays: shown when it ends. */
    close: RoundClose | null;
    lastStep: number;
    visuals: Map<number, RoundFlightVisual>;
    orders: RoundOrder[];
    /** The director's plan for the playback: speed and camera focus per step (core/roundDirector.ts). */
    timeline: RoundTimeline;
    round: number;
  } | null = null;
  // Dev reveal (SISSI): true planet colors, every unit and marker visible
  private devReveal = false;
  private get fogCam(): boolean { return this.visibility === 'fog' && !this.devReveal; }
  // Planet the camera was last cycled to with Tab (index into planets), or null
  private focusPlanetIndex: number | null = null;

  /** True when the current player is a remote human (P2P) — local input should be blocked. */
  private isRemoteTurn(): boolean {
    if (this.applyingRemoteInput) return false; // applying remote peer's action
    if (!this.networkSync) return false;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    return this.networkSync.isRemoteHuman(player);
  }

  /** Whether log messages about this player's actions should be shown to the viewer. */
  private shouldLogForPlayer(playerId: number): boolean {
    if (!this.fogCam) return true;
    const player = this.gameState.players.find(p => p.id === playerId);
    if (!player) return false;
    const viewer = this.gameState.players[this.viewerIndex];
    if (player.teamIndex === viewer.teamIndex) return true;
    return this.visibleEnemyIds.has(playerId);
  }

  private trailPersist = true; // show previous round trails
  private trailHistoryRounds = 1; // how many past rounds of trails to keep

  // Persisted projectile trails (visible portions from this round)
  private observedTrailSegments: Vec3[][] = [];  // segments of visible trail points (gaps on LOS loss)
  private lastProjVisible = false;               // track visibility transitions for gap detection
  private persistedTrails: THREE.Line[] = [];
  private historicalTrails: THREE.Line[][] = []; // older round trails, index 0 = most recent
  private lastTrailPromotionTurn = -1;           // turnNumber when trails were last promoted
  private liveTrailLines: LiveTrail[] = [];      // live trail segments during flight, updated incrementally
  private currentTrailColor = '#ffffff';
  private currentTrailTeamColor = '#ffffff';     // owner's team primary color
  private isOwnTeamProjectile = false;

  // Hazard meshes
  private hazardMeshes: HazardMesh[] = [];
  private ruinMeshes: RuinMesh[] = [];

  // Flora & environmental effect meshes
  private floraMeshes: FloraMesh[] = [];
  /** E5.3: the normal-size biome trees, instanced in chunks with tiers of detail (null when `?trees=each` or none stand). */
  private treeStands: TreeStands | null = null;
  /** 62.61: the near field — what is made round the camera, and the frame's budget for building it (rendering/nearField.ts) */
  private nearField: NearFieldStreamer | null = null;
  /** 209's follow-up: the skirts round the walkable tops (the pad's deck), drawn from the sim's stands. */
  private standSkirts: StandSkirts | null = null;
  /** 67.73: the structures past their full models' range — far looks, silhouettes, the lights at night, a struck base's smoke. */
  private farField: FarFieldPlay | null = null;
  /** 74.1 (DECISIONS 340): the battle world's system in the sky — the sun and the other bodies from the ground; presentation only. */
  private systemSky: SystemSkyPlay | null = null;
  /** 74.16 (DECISIONS 385): every world's aurora, by its air and its star's wind (rendering/aurora.ts) */
  private auroras: PlanetAuroras | null = null;
  /** 63.5 step 5: each crater's decals by the ground it was dug in (the action clock's ground: 63.5 step 6, no switch), kept for the battle. */
  private craterDecals: THREE.Group[] = [];
  private rockMeshes: RockMesh[] = [];
  /** The planet-scale rocks merged per planet, rebuilt when a planet's rocks change (null when `?trees=each` or none). */
  private rockMerge: PlanetRockMerge | null = null;
  private effectMeshes: EnvironmentalEffectMesh[] = [];
  private impactDustEffects: ImpactDustEffect[] = [];

  // Hazard effects
  private eruptionEffects: EruptionEffect[] = [];
  private shatterEffects: ShatterEffect[] = [];
  private rippleEffects: RippleEffect[] = [];

  // Drone delivery animation
  private activeDroneDelivery: DroneDeliveryEffect | null = null;

  // Respawn drop cans (mid-game redeployment)
  private respawnDropCans: Map<number, DropCanEffect> = new Map();  // playerId → active effect

  // Deploy phase (src/game/deployPhase.ts) — crashed cans outlive it as scenery the game owns
  private deploy = new DeployPhaseController(this.makeDeployHost());
  private crashedDropCanMeshes: Map<number, THREE.Group> = new Map();  // state ID → scenery mesh

  // Campaign
  private campaignState: CampaignState | null = null;
  private activeCampaignNodeId: number | null = null;
  private activeCampaignUnitIds: number[] | null = null;
  private preBattleLoadout: PlayerLoadout | null = null;

  // Carryable / collectible / drill site meshes
  private carryableMeshes: Map<number, CarryableMesh> = new Map();
  private collectibleMeshes: Map<number, CollectibleMesh> = new Map();
  private drillSiteMeshes: Map<number, DrillSiteMesh> = new Map();
  private groundItemMeshes: Map<number, GroundItemMesh> = new Map();
  private carrierIndicators: Map<number, THREE.Mesh> = new Map();

  // Mode-specific visual markers
  private artifactSiteMarker: THREE.Group | null = null;
  private signalSourceMarker: THREE.Group | null = null;  // Signal Trace: revealed signal location
  private depotMeshes: Map<number, THREE.Group> = new Map();  // teamIndex → depot visual
  private homeWaypointMarker: THREE.Group | null = null;  // CTC home planet beacon when carrying

  // Ferry pickup (end-of-game animation)
  private ferryPickupEffects: FerryPickupEffect[] = [];
  private ferryPickupWinner: WinResult | null = null;

  // Airborne units: the follow cam during a jump
  private jumpFollowPlayerId: number | null = null;  // player ID to follow cam during jump

  // Fast-forward: multiplies physics accumulator rate during flight
  private timeScale = 1;

  // Audio — ambient biome stems + music stems + conductor
  private ambientManager: AmbientManager | null = null;
  /** Weather, heard (audio/weatherSound.ts): rain, wind and grit on the ambient bus, following the weather view's planet. */
  private weatherSound: WeatherSound | null = null;
  private musicManager: MusicManager | null = null;
  /** The sector campaign on screen (BUILDLIST 56.13); null outside it. */
  private sectorSession: SectorSession | null = null;
  private hazardEmitterManager: HazardEmitterManager | null = null;
  private combatIntensity = 0;
  private timeSinceLastAction = 0;
  private conductorAccum = 0; // accumulates dt, fires conductor every 100ms

  private cleanupFns: Array<() => void> = [];
  private minimapState: MinimapState | null = null;
  private botWorker = new BotWorkerClient();
  private botThinking: BotThinkingSprite | null = null;
  /** Set by a touch long-press so the synthetic click that follows does not also fire a tap action. */
  private ignoreNextCanvasClick = false;
  /** Gravity field flow map (dev mode for now; rendering/gravityField.ts). */
  private gravityField: GravityField | null = null;
  /** Incoming-fire bearing cues on the viewer's threatened units (rendering/threatCue.ts). */
  private threatCues: ThreatCue[] = [];
  /** True while applying a remote peer's input — bypasses isRemoteTurn() guards */
  private applyingRemoteInput = false;
  /** Bot turns run here, against the perform* actions below (src/ai/botTurnRunner.ts) */
  private botRunner = new BotTurnRunner(this.makeBotHost());

  // P2P multiplayer
  private peerManager: PeerManager | null = null;
  private networkSync: TurnSync | null = null;
  /** Who called in the drone delivery in flight (its end ends their turn). */
  private droneBuyerId = -1;
  // Replays (40.4): every match is recorded silently; playback drives the game through a ReplayDriver
  private recorder: ReplayRecorder | null = null;
  private get replaying(): boolean { return this.networkSync?.kind === 'replay'; }

  constructor() {
    // Initialize rendering
    this.sceneObjs = createScene();
    this.skyHaze = new SkyHaze(this.sceneObjs.scene);
    this.weatherView = new WeatherView(this.sceneObjs.scene);
    this.weatherCellsView = new CellVolumes(this.sceneObjs.scene);
    this.cameraState = createCameraState();

    // Initialize empty game state
    const config = createGameConfig(2, 1, 5, String(Date.now()));
    this.gameState = createInitialGameState(config);

    // Initialize input
    this.aimState = createAimState();
    this.keyState = createKeyState();

    this.minimapState = initMinimap(180);

    this.setupEventHandlers();
    this.setupCheatConsole();
    // Load the WASM kernel now so its availability is known before any P2P handshake
    initWasm().then(ok => { this.wasmReady = ok; });
    this.animate();
  }

  private setupCheatConsole(): void {
    setupCheatConsole((cmd) => this.executeCheat(cmd));
  }

  private executeCheat(cmd: string): string | null {
    const parts = cmd.toLowerCase().split(/\s+/);
    const command = parts[0];

    switch (command) {
      case 'win': {
        if (this.gameState.phase !== 'aiming' && this.gameState.phase !== 'deploying') {
          return 'Can only use during a battle.';
        }
        // Kill all enemy players
        const humanTeam = this.gameState.players.find(p => !p.isBot)?.teamIndex ?? 0;
        this.gameState = {
          ...this.gameState,
          players: this.gameState.players.map(p =>
            p.teamIndex !== humanTeam ? { ...p, alive: false, health: 0 } : p
          ),
        };
        this.updatePlayerMeshes();
        this.clock.after(0.1, () => this.handleGameOver());
        return 'Instant win triggered.';
      }

      case 'money': {
        const amount = parseInt(parts[1]) || 500;
        if (!this.campaignState) return 'Not in campaign.';
        this.campaignState = {
          ...this.campaignState,
          playerMoney: this.campaignState.playerMoney + amount,
        };
        return `Added ${amount} CBP. Total: ${this.campaignState.playerMoney}`;
      }

      case 'ap': {
        const amount = parseInt(parts[1]) || 1;
        if (!this.campaignState) return 'Not in campaign.';
        // Set all units' AP and global AP
        this.campaignState = {
          ...this.campaignState,
          actionPoints: amount,
          roster: this.campaignState.roster.map(u => ({ ...u, ap: amount })),
        };
        return `Set AP to ${amount} for all units.`;
      }

      case 'heal': {
        const player = this.gameState.players[this.gameState.currentPlayerIndex];
        if (player) {
          this.gameState = {
            ...this.gameState,
            players: this.gameState.players.map(p =>
              p.id === player.id ? { ...p, health: 100 } : p
            ),
          };
          this.updatePlayerMeshes();
          return `Healed ${player.name} to 100 HP.`;
        }
        return 'No current player.';
      }

      case 'cam': {
        const mode = parts[1];
        if (mode === 'fog' || mode === 'free') {
          this.setVisibility(mode);
          return `Visibility: ${mode === 'fog' ? 'fog of war' : 'everyone visible'}.`;
        } else if (mode === 'sissi') {
          this.toggleDevReveal();
          return `SISSI ${this.devReveal ? 'on' : 'off'}.`;
        }
        return 'Usage: cam fog | cam free | cam sissi';
      }

      case 'orders': {
        // 68.21: what became of every order to your squads (and the reserve): who gave it, refused / replaced / expired / in force
        if (!this.match?.state.squads?.length) return 'No squads in this match.';
        const team = parts[1] !== undefined ? Number(parts[1]) : (this.actionViewer.team ?? 0);
        const lines = orderLogLines(this.match.inputLog, this.match.state, team, this.match.actionTick, this.botOrderKeys);
        console.log(`[orders] team ${team}\n` + lines.join('\n'));
        return lines.slice(-12).join(' | ') + (lines.length > 12 ? ' … (all of it in the browser console)' : '');
      }

      case 'help':
        return 'Commands: win, money [amount], ap [amount], heal, cam [fog|free|sissi], orders [team], help';

      default:
        return `Unknown command: ${command}. Type "help" for commands.`;
    }
  }

  private setupEventHandlers(): void {
    // Camera controls
    const cleanupCamera = setupCameraControls(
      this.sceneObjs.renderer.domElement,
      this.cameraState,
      {
        // A long press is the touch right-click: look at a planet
        onLongPress: (x, y) => {
          this.ignoreNextCanvasClick = true;
          this.handleRightClickPivot({ clientX: x, clientY: y, button: 2 } as MouseEvent);
        },
      },
    );
    this.cleanupFns.push(cleanupCamera);

    // Drag the aim sphere to aim (touch stand-in for WASD)
    const aimPad = document.getElementById('aim-sphere') as HTMLCanvasElement | null;
    if (aimPad) {
      this.cleanupFns.push(setupAimPad(aimPad, this.aimState, () => this.onAimChanged()));
    }

    // Keyboard input
    const cleanupKeyboard = setupKeyboardInput(
      this.keyState,
      (reverse) => this.cyclePlanetFocus(reverse),
      () => this.tryFire(),
      () => this.toggleMoveMode(),
      () => this.toggleJumpMode(),
      () => this.tryMeleeAttack(),
      () => { uiClick(); this.toggleInGameShop(); },
      () => { uiClick(); this.toggleBuildMode(); },
      () => { uiClick(); this.toggleDevReveal(); },
      () => this.yieldTurn(),
      () => { uiClick(); this.toggleInGameHelp(); },
      () => { uiClick(); this.toggleInGameSettings(); },
      () => { uiClick(); toggleCommsLog(); },
      () => { uiClick(); this.plantFlagAtFeet(); },
      () => { uiClick(); this.resetCameraToPlayer(); },
      () => { uiClick(); this.toggleLeadershipPanel(); },
      () => { uiClick(); this.tryRemoveStake(); },
      () => { uiClick(); this.tryCollectOrPickup(); },
      () => { uiClick(); this.tryInteract(); },
      () => { uiClick(); this.tryDeliver(); },
      () => { uiClick(); this.tryDropObject(); },
      () => { uiClick(); this.tryHeal(); },
      () => { uiClick(); this.toggleInventoryPanel(); },
      () => this.cycleWeapon(),
      (slot) => this.selectWeaponSlot(slot),
    );
    this.cleanupFns.push(cleanupKeyboard);

    // Comms log button click
    const btnCommsLog = document.getElementById('btn-comms-log');
    if (btnCommsLog) {
      const commsHandler = () => toggleCommsLog();
      btnCommsLog.addEventListener('click', commsHandler);
      this.cleanupFns.push(() => btnCommsLog.removeEventListener('click', commsHandler));
    }

    // Visibility chip: in dev mode, click toggles the reveal
    const camModeEl = document.getElementById('camera-mode');
    if (camModeEl) {
      const camHandler = () => this.toggleDevReveal();
      camModeEl.addEventListener('click', camHandler);
      this.cleanupFns.push(() => camModeEl.removeEventListener('click', camHandler));
    }

    // Camera buttons: next planet (Tab) and back to your unit (C) — the touch stand-ins
    const btnFocusPlanet = document.getElementById('btn-focus-planet');
    if (btnFocusPlanet) {
      const h = () => { uiClick(); this.cyclePlanetFocus(false); };
      btnFocusPlanet.addEventListener('click', h);
      this.cleanupFns.push(() => btnFocusPlanet.removeEventListener('click', h));
    }
    const btnCameraReset = document.getElementById('btn-camera-reset');
    if (btnCameraReset) {
      const h = () => { uiClick(); this.resetCameraToPlayer(); };
      btnCameraReset.addEventListener('click', h);
      this.cleanupFns.push(() => btnCameraReset.removeEventListener('click', h));
    }

    // Config button click
    const btnConfig = document.getElementById('btn-config');
    if (btnConfig) {
      const configHandler = () => { uiClick(); this.toggleInGameSettings(); };
      btnConfig.addEventListener('click', configHandler);
      this.cleanupFns.push(() => btnConfig.removeEventListener('click', configHandler));
    }

    // Mode action buttons (interact, collect, deliver)
    const interactBtn = document.getElementById('interact-btn');
    if (interactBtn) {
      const ih = () => { uiClick(); this.tryInteract(); };
      interactBtn.addEventListener('click', ih);
      this.cleanupFns.push(() => interactBtn.removeEventListener('click', ih));
    }
    const collectBtn = document.getElementById('collect-btn');
    if (collectBtn) {
      const ch = () => { uiClick(); this.tryCollectOrPickup(); };
      collectBtn.addEventListener('click', ch);
      this.cleanupFns.push(() => collectBtn.removeEventListener('click', ch));
    }
    const deliverBtn = document.getElementById('deliver-btn');
    if (deliverBtn) {
      const dh = () => { uiClick(); this.tryDeliver(); };
      deliverBtn.addEventListener('click', dh);
      this.cleanupFns.push(() => deliverBtn.removeEventListener('click', dh));
    }

    // Help button click
    const btnHelpIngame = document.getElementById('btn-help-ingame');
    if (btnHelpIngame) {
      const helpHandler = () => { uiClick(); this.toggleInGameHelp(); };
      btnHelpIngame.addEventListener('click', helpHandler);
      this.cleanupFns.push(() => btnHelpIngame.removeEventListener('click', helpHandler));
    }

    // Shop button
    const cleanupShop = setupShopButton(() => { uiClick(); this.toggleInGameShop(); });
    this.cleanupFns.push(cleanupShop);

    // Build button
    const cleanupBuild = setupBuildButton(() => { uiClick(); this.toggleBuildMode(); });
    this.cleanupFns.push(cleanupBuild);

    // Flag button
    const cleanupFlag = setupFlagButton(() => { uiClick(); this.plantFlagAtFeet(); });
    this.cleanupFns.push(cleanupFlag);

    // Orders (leadership) button
    const cleanupOrders = setupOrdersButton(() => { uiClick(); this.toggleLeadershipPanel(); });
    this.cleanupFns.push(cleanupOrders);

    // Inventory button
    const cleanupInventory = setupInventoryButton(() => { uiClick(); this.toggleInventoryPanel(); });
    this.cleanupFns.push(cleanupInventory);

    // Cancel build button
    const btnCancelBuild = document.getElementById('btn-cancel-build');
    if (btnCancelBuild) {
      const handler = () => this.cancelBuild();
      btnCancelBuild.addEventListener('click', handler);
      this.cleanupFns.push(() => btnCancelBuild.removeEventListener('click', handler));
    }

    // In-game shop delivery/cancel buttons
    const btnCallDelivery = document.getElementById('btn-call-delivery');
    const btnCancelDelivery = document.getElementById('btn-cancel-delivery');
    if (btnCallDelivery) {
      const handler = () => this.confirmDelivery();
      btnCallDelivery.addEventListener('click', handler);
      this.cleanupFns.push(() => btnCallDelivery.removeEventListener('click', handler));
    }
    if (btnCancelDelivery) {
      const handler = () => this.cancelDelivery();
      btnCancelDelivery.addEventListener('click', handler);
      this.cleanupFns.push(() => btnCancelDelivery.removeEventListener('click', handler));
    }

    // Move button
    const cleanupMove = setupMoveButton(() => this.toggleMoveMode());
    this.cleanupFns.push(cleanupMove);

    // Jump button
    const cleanupJump = setupJumpButton(() => this.toggleJumpMode());
    this.cleanupFns.push(cleanupJump);

    // Melee button
    const cleanupMelee = setupMeleeButton(() => this.tryMeleeAttack());
    this.cleanupFns.push(cleanupMelee);

    // Heal button
    const cleanupHeal = setupHealButton(() => this.tryHeal());
    this.cleanupFns.push(cleanupHeal);

    // Click handler for movement
    const onClick = (e: MouseEvent) => {
      if (this.ignoreNextCanvasClick) { this.ignoreNextCanvasClick = false; return; }
      this.handleClick(e);
    };
    this.sceneObjs.renderer.domElement.addEventListener('click', onClick);
    this.cleanupFns.push(() => {
      this.sceneObjs.renderer.domElement.removeEventListener('click', onClick);
    });

    // Right-click handler for camera pivot
    const onRightClick = (e: MouseEvent) => {
      if (e.button !== 2) return;
      this.handleRightClickPivot(e);
    };
    this.sceneObjs.renderer.domElement.addEventListener('mousedown', onRightClick);
    this.cleanupFns.push(() => {
      this.sceneObjs.renderer.domElement.removeEventListener('mousedown', onRightClick);
    });
    // No browser context menu anywhere over the game: right-click is the camera pivot (the canvas already suppresses it)
    const hud = document.getElementById('hud');
    const onHudContextMenu = (e: MouseEvent) => e.preventDefault();
    hud?.addEventListener('contextmenu', onHudContextMenu);
    this.cleanupFns.push(() => hud?.removeEventListener('contextmenu', onHudContextMenu));

    // Mouse move for movement preview
    const onMouseMove = (e: MouseEvent) => this.handleMouseMove(e);
    this.sceneObjs.renderer.domElement.addEventListener('mousemove', onMouseMove);
    this.cleanupFns.push(() => {
      this.sceneObjs.renderer.domElement.removeEventListener('mousemove', onMouseMove);
    });

    // Power slider
    const cleanupSlider = setupPowerSlider((power) => {
      const currentPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
      if (!currentPlayer.alive) return;
      // Power slider controls the same value in both modes
      this.aimState.power = power;
      if (this.aimState.mode === 'jump') {
        // In jump mode, update jump preview
        this.jumpPreviewDirty = true;
      } else {
        // In aim mode, update weapon preview
        updateAimDisplay(this.aimState.azimuth, this.aimState.elevation, this.aimState.power);
        updateAimSphere(this.aimState.azimuth, this.aimState.elevation, this.aimState.power);
        this.updatePreview();
      }
    });
    this.cleanupFns.push(cleanupSlider);

    // Fire button
    const cleanupFire = setupFireButton(() => this.tryFire());
    this.cleanupFns.push(cleanupFire);

    // Yield button
    const cleanupYield = setupYieldButton(() => this.yieldTurn());
    this.cleanupFns.push(cleanupYield);

    // Menu handlers
    const cleanupMenu = setupMenuHandlers(
      (options) => this.startGame(options),
      () => this.restart(),
      (roomCode) => this.hostGame(roomCode),
      (roomCode) => this.joinGame(roomCode),
      () => this.showCampaignScreen(),
      () => this.showSectorScreen(),
    );
    this.cleanupFns.push(cleanupMenu);
    // ?sector=first opens a curated scenario (sector/scenario.ts); ?sector alone, the sector screen
    { const sp = new URLSearchParams(location.search).get('sector'); if (sp !== null) this.showSectorScreen(sp && sp !== '1' ? sp : undefined); }
    // Dev: ?autoplay starts a fixed all-bot match (scripts/browserSmoke.ts)
    const auto = autoplayOptions();
    const autoSetup = autoplaySetup();
    const action = actionLaunch();
    if (action) {
      exposeForAutoplay(this);
      showMenu(false);
      // Autoplay resolves the same duel first; the smoke compares the two digests
      const compare = action.humanTeam !== null || action.range ? Promise.resolve()
        : action.skirmish ? resolveSkirmishForComparison(action.setup) : resolveDuelForComparison(action.setup);
      void compare.then(() =>
        this.startActionFromSetup(action.setup, {
          humanTeam: action.humanTeam, timeScale: action.timeScale, tickLimit: action.tickLimit, range: action.range,
          onGameOver: winner => { void markActionDone(this.gameState, this.match.actionTick, winner); },
        }));
    }
    else if (autoSetup) {
      exposeForAutoplay(this);
      showMenu(false);
      // Auto-resolve the same setup in this page first (same engine), then watch it: the smoke compares the traces
      void resolveForComparison(autoSetup).then(() => this.startFromSetup(autoSetup));
    }
    else if (auto) { exposeForAutoplay(this); showMenu(false); this.startGame(auto); }
    // Phase 59.3: the real-time duel from the main menu (you, team 0, against a bot)
    // The duel and the skirmish launch at the menu's real-time scale (human 1/20 by default — decisions 49, 87 — so
    // biome stands dress the ground; classic behind the toggle). The range keeps classic: its targets stand at 6, 9 and 12 units.
    document.getElementById('btn-action')?.addEventListener('click', () => {
      showMenu(false);
      this.startActionFromSetup({ ...actionDuelSetup({ seed: `duel-${Date.now() % 100000}`, units: 1, difficulty: ['medium'], timeLimit: 600, scale: actionScale() }), scale: actionScale() }, { humanTeam: 0, tickLimit: Math.round(600 / PHYS_DT) });
    });
    document.getElementById('btn-action-skirmish')?.addEventListener('click', () => {
      showMenu(false);
      this.startActionFromSetup(skirmishMatchSetup({ seed: `skirmish-${Date.now() % 100000}`, teams: 2, unitsPerTeam: 6, difficulty: ['medium'], timeLimit: 600, scale: actionScale(), commanders: ['adaptive', 'adaptive'] }), { humanTeam: 0, tickLimit: Math.round(605 / PHYS_DT) });   // 68.23 (decision 153): the enemy's commander present by default; yours orders only the squads you have not
    });
    // The Lab (decision 123): every experimental switch in one dev-only panel, launched through this same path
    setupLab(l => { showMenu(false); this.startActionFromSetup(l.setup, { humanTeam: l.humanTeam, tickLimit: l.tickLimit, range: l.range }); });
    document.getElementById('btn-action-range')?.addEventListener('click', () => {
      showMenu(false);
      this.startActionFromSetup({ ...actionDuelSetup({ seed: `range-${Date.now() % 100000}`, units: 1, unitsByTeam: [1, RANGE_LAYOUT.length], difficulty: ['medium'], timeLimit: 600 }), weather: false, planetPick: 'stars' }, { humanTeam: 0, range: true, tickLimit: Math.round(600 / PHYS_DT) });   // the range stays calm (decision 147)
    });
    // Sector: a page opened with ?sectorBattle plays the battle its opener sends (watch or take command)
    listenForSectorBattle((setup, humanTeam) => { showMenu(false); this.startFromSetup(setup, { humanTeam }); });

    // In-game help dismiss
    const btnHelpClose = document.getElementById('btn-ingame-help-close');
    if (btnHelpClose) {
      const handler = () => showInGameHelp(false);
      btnHelpClose.addEventListener('click', handler);
      this.cleanupFns.push(() => btnHelpClose.removeEventListener('click', handler));
    }

    // Leave a sector battle part-way (decision 111)
    // X2: mark — the commander marks the nearest enemy the held unit sees, for the whole team (a task on every squad)
    document.getElementById('btn-ingame-scoreboard')?.addEventListener('click', () => toggleIngameScoreboard());   // 67.14
    document.getElementById('btn-ingame-mark')?.addEventListener('click', () => {
      const me = this.gameState.players.find(p => !p.isBot && p.alive) ?? null;
      if (!me || !this.match.state.squads?.length) return;
      const fog = this.match.botContext(me);
      const mark = markNearestSeen(this.gameState, me, fog?.visibleEnemyIds ?? []);
      showInGameSettings(false);
      if (!mark) { logMessage(`${me.name}: nothing in sight to mark`); return; }
      let n = 0;
      for (const body of markBodies(this.match.state, me.teamIndex, mark)) if (this.match.apply(this.match.stamp(seatActor(me.teamIndex), body)).ok) n++;
      this.gameState = this.match.state;
      logMessage(n ? `${me.name} marks ${this.gameState.players.find(p => p.id === mark.unitId)?.name ?? 'a point'} for the team` : `${me.name}: the mark was refused`);
    });
    // X11: call in fire — the held unit asks for the guns on the nearest enemy it sees on its planet (a battle with call-ins on)
    document.getElementById('btn-ingame-callin')?.addEventListener('click', () => {
      const me = this.gameState.players.find(p => !p.isBot && p.alive) ?? null;
      if (!me || !this.gameState.config.callIns) return;
      const fog = this.match.botContext(me);
      const ask = wantsCallIn(this.gameState, me, fog?.visibleEnemyIds ?? []);
      showInGameSettings(false);
      if (!ask) { logMessage(`${me.name}: nothing to call in on — no enemy in sight on this planet, or no gun free`); return; }
      this.callInFire(me, ask.planetIndex, ask.surfaceDir);
    });
    // X22: withdraw — the human team's squads head for their exit (shown only in a battle with exits)
    document.getElementById('btn-ingame-withdraw')?.addEventListener('click', () => {
      const team = this.actionViewer?.team ?? null;
      if (team === null || !this.match.state.exits) return;
      const n = this.withdrawTeam(team, 'human');
      showInGameSettings(false);
      if (n > 0) logMessage(`Withdraw: ${n} squad${n === 1 ? '' : 's'} ordered to the exit`);
    });
    const btnLeaveSector = document.getElementById('btn-ingame-leave-sector');
    if (btnLeaveSector) {
      const handler = () => this.leaveSectorBattle();
      btnLeaveSector.addEventListener('click', handler);
      this.cleanupFns.push(() => btnLeaveSector.removeEventListener('click', handler));
    }

    // In-game settings dismiss
    const btnSettingsClose = document.getElementById('btn-ingame-settings-close');
    if (btnSettingsClose) {
      const handler = () => showInGameSettings(false);
      btnSettingsClose.addEventListener('click', handler);
      this.cleanupFns.push(() => btnSettingsClose.removeEventListener('click', handler));
    }

    // Settings toggle buttons
    const settingVis = document.getElementById('setting-visibility');
    if (settingVis) {
      const handler = () => {
        if (!isDevMode()) return; // a match setting; only dev mode flips it mid-match
        uiClick();
        this.setVisibility(this.visibility === 'fog' ? 'free' : 'fog');
      };
      settingVis.addEventListener('click', handler);
      this.cleanupFns.push(() => settingVis.removeEventListener('click', handler));
    }

    // Replays: main-menu playback of the last match or a file; game-over save
    const replayLastBtn = document.getElementById('btn-replay-last');
    if (replayLastBtn) {
      const handler = () => { const rec = this.lastReplay ?? loadLastReplay(); if (rec) { uiClick(); this.startReplay(rec); } };
      replayLastBtn.addEventListener('click', handler);
      this.cleanupFns.push(() => replayLastBtn.removeEventListener('click', handler));
    }
    const replayFile = document.getElementById('replay-file') as HTMLInputElement | null;
    const replayLoadBtn = document.getElementById('btn-replay-load');
    if (replayFile && replayLoadBtn) {
      const open = () => replayFile.click();
      const onFile = () => {
        const f = replayFile.files?.[0];
        if (!f) return;
        void f.text().then(text => {
          const rec = parseReplay(text);
          if (!rec) { logMessage('That file is not an Orbituary replay.'); return; }
          this.startReplay(rec);
        });
        replayFile.value = '';
      };
      replayLoadBtn.addEventListener('click', open);
      replayFile.addEventListener('change', onFile);
      this.cleanupFns.push(() => { replayLoadBtn.removeEventListener('click', open); replayFile.removeEventListener('change', onFile); });
    }
    const saveReplayBtn = document.getElementById('btn-save-replay');
    if (saveReplayBtn) {
      const handler = () => { if (this.lastReplay) { uiClick(); this.downloadReplay(this.lastReplay); } };
      saveReplayBtn.addEventListener('click', handler);
      this.cleanupFns.push(() => saveReplayBtn.removeEventListener('click', handler));
    }
    this.updateReplayMenuButton();
    const settingRes = document.getElementById('setting-resolution');
    if (settingRes) {
      const handler = () => {
        uiClick();
        this.resolutionPace = nextPace(this.resolutionPace);
        updatePaceToggle(this.resolutionPace);
      };
      settingRes.addEventListener('click', handler);
      this.cleanupFns.push(() => settingRes.removeEventListener('click', handler));
    }
    const settingSissi = document.getElementById('setting-sissi');
    if (settingSissi) {
      const handler = () => { uiClick(); this.toggleDevReveal(); };
      settingSissi.addEventListener('click', handler);
      this.cleanupFns.push(() => settingSissi.removeEventListener('click', handler));
    }

    const settingShotCam = document.getElementById('setting-shot-cam');
    if (settingShotCam) {
      const handler = () => { uiClick(); this.toggleShotCam(); };
      settingShotCam.addEventListener('click', handler);
      this.cleanupFns.push(() => settingShotCam.removeEventListener('click', handler));
    }
    this.updateShotCamToggle();

    const settingHand = document.getElementById('setting-hand');
    if (settingHand) {
      const handler = () => { uiClick(); this.toggleHand(); };
      settingHand.addEventListener('click', handler);
      this.cleanupFns.push(() => settingHand.removeEventListener('click', handler));
    }
    this.updateHandToggle();

    const settingMusicSet = document.getElementById('setting-music-set');
    if (settingMusicSet) {
      const handler = () => { uiClick(); this.toggleMusicSet(); };
      settingMusicSet.addEventListener('click', handler);
      this.cleanupFns.push(() => settingMusicSet.removeEventListener('click', handler));
    }
    this.updateMusicSetToggle();

    const settingField = document.getElementById('setting-gravity-field');
    if (settingField) {
      const handler = () => { uiClick(); this.toggleGravityField(); };
      settingField.addEventListener('click', handler);
      this.cleanupFns.push(() => settingField.removeEventListener('click', handler));
    }
    const btnField = document.getElementById('btn-gravity-field');
    if (btnField) {
      const handler = () => { uiClick(); this.toggleGravityField(); };
      btnField.addEventListener('click', handler);
      this.cleanupFns.push(() => btnField.removeEventListener('click', handler));
    }

    const settingDev = document.getElementById('setting-dev');
    if (settingDev) {
      const handler = () => {
        uiClick();
        setDevMode(!isDevMode());
        if (!isDevMode()) {
          // Leaving dev mode puts the match back on its own rules
          if (this.devReveal) this.toggleDevReveal();
          const configured = this.gameState.config.visibility ?? 'fog';
          if (this.visibility !== configured) this.setVisibility(configured);
        }
        updateSettingsToggles(this.visibility, this.devReveal, this.trailPersist, this.trailHistoryRounds);
      };
      settingDev.addEventListener('click', handler);
      this.cleanupFns.push(() => settingDev.removeEventListener('click', handler));
    }

    const settingTrail = document.getElementById('setting-trail-persist');
    if (settingTrail) {
      const handler = () => {
        uiClick();
        this.trailPersist = !this.trailPersist;
        // Hide/show all historical round trails and their chevrons
        for (const round of this.historicalTrails) {
          for (const trail of round) {
            trail.visible = this.trailPersist;
            const chevrons = trail.userData.chevrons as THREE.Group | undefined;
            if (chevrons) chevrons.visible = this.trailPersist;
          }
        }
        updateSettingsToggles(this.visibility, this.devReveal, this.trailPersist, this.trailHistoryRounds);
        logMessage(this.trailPersist ? 'Old trails kept' : 'Old trails hidden');
      };
      settingTrail.addEventListener('click', handler);
      this.cleanupFns.push(() => settingTrail.removeEventListener('click', handler));
    }

    const settingTrailRounds = document.getElementById('setting-trail-rounds') as HTMLInputElement | null;
    if (settingTrailRounds) {
      const handler = () => {
        uiClick();
        this.trailHistoryRounds = parseInt(settingTrailRounds.value, 10);
        // Prune excess historical rounds
        while (this.historicalTrails.length > this.trailHistoryRounds) {
          const old = this.historicalTrails.pop()!;
          for (const trail of old) this.removeTrailLine(trail);
        }
        updateSettingsToggles(this.visibility, this.devReveal, this.trailPersist, this.trailHistoryRounds);
        logMessage(`Trail history: ${this.trailHistoryRounds} round${this.trailHistoryRounds !== 1 ? 's' : ''}`);
      };
      settingTrailRounds.addEventListener('input', handler);
      this.cleanupFns.push(() => settingTrailRounds.removeEventListener('input', handler));
    }

    const settingToasts = document.getElementById('setting-toasts');
    if (settingToasts) {
      const handler = () => {
        uiClick();
        setToastsEnabled(!areToastsEnabled());
      };
      settingToasts.addEventListener('click', handler);
      this.cleanupFns.push(() => settingToasts.removeEventListener('click', handler));
    }

    const settingFlavor = document.getElementById('setting-flavor');
    if (settingFlavor) {
      const handler = () => { uiClick(); setFlavorEnabled(!isFlavorEnabled()); };
      settingFlavor.addEventListener('click', handler);
      this.cleanupFns.push(() => settingFlavor.removeEventListener('click', handler));
    }

    // Audio bus volume sliders
    initAudio();
    this.ensureMusic();
    this.musicManager?.solo('calm', 2.0);
    const busIds: AudioBus[] = ['master', 'sfx', 'music', 'ambient', 'ui'];
    for (const bus of busIds) {
      const slider = document.getElementById(`setting-vol-${bus}`) as HTMLInputElement | null;
      if (!slider) continue;
      slider.value = String(Math.round(getBusVolume(bus) * 100));
      const label = document.getElementById(`setting-vol-${bus}-label`);
      if (label) label.textContent = `${Math.round(getBusVolume(bus) * 100)}%`;
      const handler = () => {
        const v = parseInt(slider.value) / 100;
        setBusVolume(bus, v);
        const lbl = document.getElementById(`setting-vol-${bus}-label`);
        if (lbl) lbl.textContent = `${Math.round(v * 100)}%`;
      };
      slider.addEventListener('input', handler);
      this.cleanupFns.push(() => slider.removeEventListener('input', handler));
    }

    // Error screen dismiss → return to main menu
    const btnErrorDismiss = document.getElementById('btn-error-dismiss');
    if (btnErrorDismiss) {
      const handler = () => {
        hideErrorScreen();
        this.restart();
      };
      btnErrorDismiss.addEventListener('click', handler);
      this.cleanupFns.push(() => btnErrorDismiss.removeEventListener('click', handler));
    }

    // Global error handlers
    const onError = (event: ErrorEvent) => {
      showErrorScreen(event.error || event.message, 'generic');
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const msg = event.reason instanceof Error ? event.reason : String(event.reason);
      showErrorScreen(msg, 'generic');
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    this.cleanupFns.push(() => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    });

    // Window resize
    const onResize = () => handleResize(this.sceneObjs);
    window.addEventListener('resize', onResize);
    this.cleanupFns.push(() => window.removeEventListener('resize', onResize));

    // Build weapon buttons
    const currentPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
    buildWeaponButtons((index) => {
      const player = this.gameState.players[this.gameState.currentPlayerIndex];
      this.aimState.weaponIndex = index;
      updateWeaponButtons(index, player?.loadout);
      this.updatePreview();
    }, currentPlayer?.loadout, this.gameState.config.allowedWeapons);
  }

  // ===========================================================================
  // GAME LIFECYCLE
  // ===========================================================================

  // ===========================================================================
  // P2P MULTIPLAYER
  // ===========================================================================

  private hostGame(roomCode: string): void {
    updateP2PStatus('Creating room...', 'net-waiting');
    this.peerManager = new PeerManager();

    this.peerManager.onStateChange = (state) => {
      if (state === 'waiting') {
        updateP2PStatus(`Room: ${roomCode} — Waiting for opponent...`, 'net-waiting');
      }
    };

    this.peerManager.onConnected = () => {
      updateP2PStatus('Opponent connected!', 'net-connected');
      this.networkSync = new NetworkSync(this.peerManager!, true);
      this.setupNetworkSyncCallbacks();
      this.networkSync.sendHello(isWasmLoaded());
      // Host proceeds to config screen to set up the game
      setTimeout(() => showP2PHostConfig(), 500);
    };

    this.peerManager.onError = (err) => {
      updateP2PStatus(`Error: ${err}`);
    };

    this.peerManager.onDisconnected = () => {
      this.handleNetworkDisconnect();
    };

    this.peerManager.host(roomCode);
  }

  private joinGame(roomCode: string): void {
    updateP2PStatus('Connecting...', 'net-waiting');
    this.peerManager = new PeerManager();

    this.peerManager.onConnected = () => {
      updateP2PStatus('Connected! Waiting for host to configure...', 'net-connected');
      this.peerManager!.send({ type: 'hello', wasm: isWasmLoaded() });
    };

    this.peerManager.onError = (err) => {
      updateP2PStatus(`Error: ${err}`);
    };

    this.peerManager.onDisconnected = () => {
      this.handleNetworkDisconnect();
    };

    // Client receives gameConfig message → creates sync, starts game
    this.peerManager.onMessage = (msg) => {
      if (msg.type === 'gameConfig') {
        // Now create NetworkSync (takes over onMessage from here)
        this.networkSync = new NetworkSync(this.peerManager!, false);
        this.setupNetworkSyncCallbacks();
        // Host decided whether both peers use the WASM kernel
        this.wasmAllowed = msg.useWasm;
        setWasmEnabled(msg.useWasm);
        this.startGame(msg.options, true);
      }
    };

    this.peerManager.join(roomCode);
  }

  private setupNetworkSyncCallbacks(): void {
    if (!this.networkSync) return;

    this.networkSync.onActionQueued = () => this.pumpRemoteActions();
    this.networkSync.onDesync = (turnNumber, playerIndex, localHash, remoteHash) => {
      console.warn(`[DESYNC] Turn ${turnNumber} player ${playerIndex}: local=${localHash}, remote=${remoteHash}`);
      const el = document.getElementById('network-status');
      if (el) {
        el.textContent = `DESYNC @ turn ${turnNumber}`;
        el.className = 'net-desync';
        el.classList.remove('hidden');
      }
    };
  }

  private updateNetworkStatus(): void {
    const el = document.getElementById('network-status');
    if (!el) return;
    if (this.replaying) {
      const left = (this.networkSync as ReplayDriver).remaining;
      el.textContent = left > 0 ? `REPLAY · ${left} inputs left` : 'REPLAY · bots finishing';
      el.className = 'net-connected';
      el.classList.remove('hidden');
      return;
    }
    if (!this.peerManager || !this.peerManager.connected) {
      el.classList.add('hidden');
      return;
    }
    el.textContent = `P2P ${this.peerManager.latency}ms`;
    el.className = 'net-connected';
    el.classList.remove('hidden');
  }

  /** Broadcast a turn input to the remote peer. Only for local human actions. */
  /**
   * Send a local human action to the remote peer. Every in-turn human action
   * goes through here so both peers replay the same input stream. Never sends
   * while applying the remote peer's own action.
   */
  private broadcastTurnInput(input: TurnInput): void {
    if (this.applyingRemoteInput) return;
    const player = this.gameState.players.find(p => p.id === input.playerId);
    if (!player || player.isBot) return;
    if (!this.networkSync) return;
    if (!this.networkSync.isLocalHuman(player)) return;
    this.networkSync.sendAction(this.gameState.turnNumber, this.gameState.currentPlayerIndex, input);
  }

  /**
   * Apply queued remote actions, in order, while the game is ready for them.
   * Called every frame and whenever an action arrives. An action is applied
   * only when it is for the current turn/player and nothing is animating, so
   * inputs are never dropped (they wait) and never run mid-animation.
   */
  private pumpRemoteActions(): void {
    if (!this.networkSync) return;
    for (;;) {
      const msg = this.networkSync.peekAction();
      if (!msg) return;
      if (msg.turnNumber < this.gameState.turnNumber) {
        console.warn(`[P2P] dropping stale action seq=${msg.seq} turn=${msg.turnNumber} (now ${this.gameState.turnNumber})`);
        this.networkSync.takeAction();
        continue;
      }
      if (!this.canApplyRemoteAction(msg)) return;
      this.networkSync.takeAction();
      this.applyRemoteAction(msg);
    }
  }

  private canApplyRemoteAction(msg: NetAction): boolean {
    const sync = this.networkSync;
    if (!sync) return false;
    return canApplyNetAction(msg, {
      turnNumber: this.gameState.turnNumber,
      currentPlayerIndex: this.gameState.currentPlayerIndex,
      phase: this.gameState.phase,
      players: this.gameState.players,
      projectileOwnerId: this.gameState.projectile?.ownerId ?? null,
      flightReady: !!this.precomputedSim && !this.pendingClusterLaunch,
      busy: !!this.turnAdvancing || !!this.walkAnim || !!this.activeDroneDelivery || hasAirbornePlayers(this.gameState.players) || this.respawnDropCans.size > 0,
      isRemoteHuman: p => sync.isRemoteHuman(p),
    });
  }

  /** Apply one remote human action through the same perform* path local input uses. */
  private applyRemoteAction(msg: NetAction): void {
    const input = msg.input;
    const player = this.gameState.players[msg.playerIndex];
    if (!player) return;

    const waitEl = document.getElementById('net-waiting-turn');
    if (waitEl) waitEl.style.display = 'none';

    // isRemoteTurn() guards let the action through while this flag is set
    this.applyingRemoteInput = true;
    try {
      switch (input.action) {
        case 'fire': this.performFire(input.params as FireParams); break;
        case 'queue': this.queueOrder(player, input.params as FireParams); break;
        case 'unqueue': this.performUnqueue(); break;
        case 'jump':
        case 'jetpack': this.performJump(input.params as JetpackParams); break;
        case 'move': this.performMove((input.params as MoveParams).surfaceDir); break;
        case 'skip': this.performYield(); break;
        case 'shop': this.performShop(input.params as ShopParams); break;
        case 'build': { const p = input.params as BuildParams; this.performBuild(p.structureType, p.surfaceDir); break; }
        case 'mount': this.performMount((input.params as MountParams).structureId); break;
        case 'dismount': this.performDismount(); break;
        case 'heal': this.performHeal((input.params as HealParams).itemKey); break;
        case 'detonate': this.performDetonate((input.params as DetonateParams).step); break;
        case 'flightEnd': this.remoteFlightEndConfirmed = true; break; // sync marker, not a game action
        case 'melee': this.performMelee((input.params as MeleeParams).targetId); break;
        case 'plantFlag': this.performPlantFlag((input.params as PlantFlagParams).surfaceDir); break;
        case 'removeStake': this.performRemoveStake((input.params as RemoveStakeParams).stakeId); break;
        case 'pickup': this.performPickup((input.params as PickupParams).objectId); break;
        case 'drop': this.performDrop((input.params as DropParams).carryableId); break;
        case 'interact': this.performInteract(); break;
        case 'collect': this.performCollect(input.params as CollectActionParams); break;
        case 'deliver': this.performDeliver(); break;
        case 'command': this.performCommand(input.params as CommandParams); break;
        case 'skip': this.endTurnFor(player); break;   // 64.6 2b-ii: the end of a turn is an input the Match applies
        default:
          this.turn(player, input.action, input.params);   // logged (host-driven: the Match refuses it with code 'host'); the flow moves on
          this.nextTurn();
          break;
      }
    } finally {
      this.applyingRemoteInput = false;
    }
  }

  /** Apply a remote human's turn input — mirrors what local actions do. */
  /** Apply remote post-fire action (move/jump/skip after firing). */
  private handleNetworkDisconnect(): void {
    if (!this.networkSync) return;
    const remoteTeam = this.networkSync.remoteTeamIndex;

    // Convert remote humans to bots
    const newPlayers = this.gameState.players.map(p => {
      if (p.teamIndex === remoteTeam && !p.isBot) {
        return {
          ...p,
          isBot: true,
          botConfig: { skill: 0.5, personality: { aggression: 0.5, mobility: 0.5, riskTolerance: 0.5 } },
        };
      }
      return p;
    });
    this.gameState = { ...this.gameState, players: newPlayers };

    // Clear network state
    this.peerManager = null;
    this.networkSync = null;

    // Hide waiting overlay if visible
    const waitEl = document.getElementById('net-waiting-turn');
    if (waitEl) waitEl.style.display = 'none';

    // Update HUD
    const el = document.getElementById('network-status');
    if (el) {
      el.textContent = 'DISCONNECTED — opponents are now bots';
      el.className = 'net-desync';
      el.classList.remove('hidden');
    }

    // If we were waiting for remote input, resume with the now-bot player
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (this.gameState.phase === 'aiming' && player.isBot) {
      this.startAimPhase();
    }
  }

  private startGame(options: MenuOptions, isClient = false): void {
    resetChecksumTrace();
    this.match.gameOverHandled = false;
    // A finished replay's driver must not leak into the next match
    if (this.networkSync?.kind === 'replay' && !isClient) this.networkSync = null;
    if (!this.networkSync) {
      this.wasmAllowed = true;
      setWasmEnabled(true);
    }
    // Record this match (silently; a replay is the options + the human inputs). Not while replaying one.
    this.recorder = this.replaying ? null : new ReplayRecorder(options, isWasmLoaded() && this.wasmAllowed);
    // Clear old state
    this.clearScene();
    clearLog();
    clearCommsLog();
    clearScoreboard();
    hideGameOver();

    // Generate map
    const numStars = options.numStars ?? 0;
    const numWormholePairs = options.numWormholePairs ?? 0;
    const map = generateMap(
      options.numPlanets,
      options.numPlayers,
      options.numBots,
      options.seed,
      options.playerNames,
      options.playerLoadouts,
      numStars,
      numWormholePairs,
      options.botDifficulty ?? 'medium',
      options.botPersonality ?? 'balanced',
      options.botOverrides,
      options.teamIndices,
      options.unitsPerTeam ?? 1,
      options.botUnitsPerTeam ?? 1,
      options.numTeams,
      options.humanTeams,
      options.botTeams,
      options.unitSlots?.length ? options.unitSlots : undefined,
      options.hazardDensity ?? 'some',
      options.ruinDensity ?? 'some',
      undefined, // ruinOverrides
      options.planetSize ?? 'mixed',
      options.mapLayout ?? 'random',
      options.starType && options.starType !== 'random' ? options.starType : undefined,
      options.cbpBudget ?? 12,
      options.botCbpBudget,
      options.systemPreset
    );

    // Create game state
    const config = createGameConfig(
      options.numPlayers,
      options.numBots,
      options.numPlanets,
      options.seed,
      numStars,
      numWormholePairs,
      options.numTeams,
      options.humanTeams,
      options.botTeams,
      options.unitsPerTeam,
      options.botUnitsPerTeam,
      options.turnOrderMode ?? 'sequential',
      options.friendlyFire ?? 0,
      options.datalinkedLOS ?? false,
      options.botFogEnabled ?? true,
      options.adaptiveBotPersonality ?? false,
      options.hideEnemyDeploy ?? false,
      options.hazardDensity ?? 'some',
      options.ruinDensity ?? 'some',
      options.floraDensity ?? 'some',
      options.planetSize ?? 'mixed',
      options.mapLayout ?? 'random',
      options.starType ?? 'random',
      options.systemPreset ?? 'custom',
      composeModeConfig(options.gameMode ?? 'deathmatch', { turnLimit: options.turnLimit, modifiers: options.modifiers }),
      options.allowedWeapons,
      options.visibility ?? 'fog',
      options.fallDamageMultiplier ?? 1.0,
      options.cbpBudget ?? 12,
      options.previewQuality ?? 1,
      options.stabilizers ?? false,
    );
    config.resolutionPace = options.resolutionPace ?? 'full';
    config.turnFlow = options.turnFlow ?? 'perUnit';
    // 57.4: the stealth modifier turns the alert layer on; the guard teams default to the bot teams (completeMatchState)
    if (options.stealth) config.modeConfig = { ...config.modeConfig, startingAlert: 'unaware' };

    // P2P network: set network fields on config, send to client if host
    if (this.networkSync && this.networkSync.kind === 'p2p') {
      config.networkMode = true;
      config.networkRole = this.peerManager?.isHost ? 'host' : 'client';
      if (this.peerManager?.isHost && !isClient) {
        const useWasm = isWasmLoaded() && (this.networkSync.remoteWasm ?? false);
        this.wasmAllowed = useWasm;
        setWasmEnabled(useWasm);
        this.peerManager.send({ type: 'gameConfig', options, useWasm });
      }
    }

    // Apply stakesPerUnit from modeConfig if set
    const stakesPerUnit = config.modeConfig.stakesPerUnit;
    const playersWithStakes = stakesPerUnit
      ? map.players.map(p => ({ ...p, flagsRemaining: stakesPerUnit }))
      : map.players;

    this.gameState = {
      ...createInitialGameState(config),
      planets: map.planets,
      players: playersWithStakes,
      hazards: map.hazards,
      ruins: map.ruins,
      flora: map.flora,
      rocks: map.rocks,
      environmentalEffects: map.environmentalEffects,
      plantedFlags: [],
      carryables: [],
    };

    // Visibility is a match setting; the dev reveal never carries over
    this.visibility = config.visibility ?? 'fog';
    this.resolutionPace = config.resolutionPace ?? 'full';
    updatePaceToggle(this.resolutionPace);
    this.turnFlow = config.turnFlow ?? 'perUnit';
    this.batchQueue = [];
    this.batchActive = false;
    this.roundPlay = null;
    this.clearPlannedMoves();
    this.clearPlannedPaths();
    this.devReveal = false;
    this.focusPlanetIndex = null;
    updateCameraMode(this.visibility, this.devReveal);

    // P2P: update NetworkSync with actual team indices from generated players
    if (this.networkSync) {
      const humanTeamIndices = [...new Set(
        this.gameState.players.filter(p => !p.isBot).map(p => p.teamIndex)
      )];
      if (humanTeamIndices.length >= 2) {
        this.networkSync.setTeamIndices(humanTeamIndices[0], humanTeamIndices[1]);
      }
    }

    // Mode layers: objective placements, environment schedules, respawn pools
    this.gameState = setupModeLayers(this.gameState);

    // Apply pre-game fuel purchases
    if (options.playerFuel) {
      this.gameState = {
        ...this.gameState,
        players: this.gameState.players.map(p => ({
          ...p,
          jumppackFuel: options.playerFuel![p.teamIndex] ?? 0,
        })),
      };
    }

    // The in-match pool is what the team left unspent in the pre-match shop
    if (options.teamStartingCBP) {
      this.gameState = {
        ...this.gameState,
        players: this.gameState.players.map(p => ({
          ...p,
          cbp: options.teamStartingCBP![p.teamIndex] ?? p.cbp,
        })),
      };
    }

    // Unit kit → modifiers: each unit's traits (from generation), the human team's gear on top
    // (campaign), resolved once. With no gear and no traits this is exactly today's numbers.
    {
      const base = { previewQuality: config.previewQuality ?? 1 };
      this.gameState = {
        ...this.gameState,
        players: this.gameState.players.map(p => {
          // A dev KIT row for this team replaces the kit outright (quick play); otherwise campaign gear + the unit's traits
          const override = options.teamKits?.[p.teamIndex];
          const kit = override ?? kitFromCampaign(p.isBot ? undefined : options.gearUpgrades, p.kit?.traits);
          const modifiers = resolveUnitModifiers(kit, base);
          return {
            ...p, kit, modifiers,
            movementRange: modifiers.movementRange,
            movementRemaining: modifiers.movementRange,
            jumpBonus: modifiers.jumpVelocityBonus,
          };
        }),
      };
    }

    // Flora destruction tracking, match events, turn order — shared with a setup (src/match/setup.ts)
    this.gameState = completeMatchState(this.gameState);

    this.buildMatchScene();

    // Sync WASM planet data before fog (second+ game may have stale context)
    if (isWasmReady()) {
      setPlanets(this.gameState.planets);
    }

    // Initialize fog of war
    this.gameState = {
      ...this.gameState,
      fog: initializeFog(this.gameState.players.length, this.gameState.planets),
    };

    // No reveal yet: the deploy phase starts fogged and the drop cans reveal what they fly over (37.2a).
    // (Players' pre-deploy spawn positions are placeholders; revealing from them leaked terrain.)


    this.buildMatchHud();

    // Initialize main-thread WASM (non-blocking, falls back to JS if unavailable)
    initWasm().then(ok => {
      this.wasmReady = ok;
      if (ok) {
        setPlanets(this.gameState.planets);
        console.log('[Game] Main-thread WASM: ACTIVE');
      }
    });

    // Start deploy phase (players select planets before combat begins)
    this.startDeployPhase();
  }

  /**
   * Start a match from its setup (Phase 51.9b) — what the sector's battles, auto-resolved
   * by runBattle, are made of. The setup arrives deployed and ready to aim, so there is
   * no deploy phase. `humanTeam` hands that team to the player (take command); without
   * it every unit is a bot (watch — the same match auto-resolve plays).
   */
  startFromSetup(setup: MatchSetup, opts: { humanTeam?: number | null; visibility?: 'fog' | 'free' } = {}): void {
    // Phase 59.5: a battle on the action clock is played in real time (watch or take command alike)
    if (setup.clock === 'action') {
      this.startActionFromSetup(setup, { humanTeam: opts.humanTeam ?? null, battle: true, onGameOver: () => this.actionBattleOver() });
      return;
    }
    this.prepareFromSetup(setup, opts);
    traceTurnChecksum(this.gameState);   // ?trace: the setup's state as the match begins (runBattle traces the same line)
    this.startAimPhase();
  }

  /**
   * Phase 59.3: a real-time match from a setup (`clock: 'action'`) — the same world build as
   * startFromSetup, then the action host instead of the turn flow. `humanTeam` null = watch.
   */
  startActionFromSetup(setup: MatchSetup, opts: { humanTeam?: number | null; timeScale?: number; tickLimit?: number; range?: boolean; battle?: boolean; onGameOver?: (winnerTeam: number | null) => void } = {}): void {
    const humanTeam = opts.humanTeam ?? null;
    this.prepareFromSetup({ ...setup, clock: 'action' }, { humanTeam, visibility: 'free' });
    // At human scale the action host draws people with the E2 person (rendering/personView.ts) and hides these
    // classic models; the turn match's units keep theirs (the giant class, ruling 24).
    let commanders: Commander[] = [];
    // X22: the WITHDRAW button lives in the in-game settings while this battle has exits and the viewer commands a team
    const wdBtn = document.getElementById('btn-ingame-withdraw');
    if (wdBtn) wdBtn.style.display = this.match.state.exits && humanTeam !== null ? '' : 'none';
    const ciBtn = document.getElementById('btn-ingame-callin');
    if (ciBtn) ciBtn.style.display = this.match.state.config.callIns && humanTeam !== null ? '' : 'none';
    const mkBtn = document.getElementById('btn-ingame-mark');
    if (mkBtn) mkBtn.style.display = this.match.state.squads?.length && humanTeam !== null ? '' : 'none';
    if (opts.range) {
      // Phase 59.4: the practice range — team 1 are scripted targets laid out round the shooter
      const r = arrangeRange(this.gameState, setup.seed);
      this.gameState = r.state;
      this.match.actionDriver = rangeDriver(setup.seed, r.motions);
    } else {
      // The brain switch (the harness's --brain; the Lab's row) and the knowledge feeds and shared picture (K2): the
      // same options runSkirmish / runDuel build from the setup (actionDuel hostActionBotOptions)
      this.match.actionDriver = hostActionDriver(setup as DuelSetup & SkirmishSetup);   // 67.24: each team its own brain when the sides differ
      // 66.4: the stand-in commanders (the harness's, through the same `order` inputs) command the bot teams in the page too
      const styles = (setup as SkirmishSetup).commanders;
      this.botOrderKeys = new Set();   // 68.21: a new match numbers its inputs afresh
      if (styles?.some(c => c !== 'none')) {
        const match = this.match;
        const issueFor = (t: number): IssueOrder => (squad, order, tier, expires, units, claim) => {
          const to = orderTo(match.state, squad, units);   // (69.72 step 5's follow-up: a units task needs no squad; a squad by its first living member in one; 69.87: a Match squad by its id)
          if ('refused' in to) return to;
          const input = match.stamp(seatActor(t), { kind: 'order', team: t, to, tier, order, by: 'commander', ...(expires !== undefined ? { expires } : {}), ...claim });   // (69.72 step 5: what it claims the squad for, DECISIONS 304)   // 69.2e: a build's one planter
          this.botOrderKeys.add(inputKey(input));   // 68.21: the order log tells the bot commander's from yours
          return hookAnswer(match.apply(input));   // (69.72 step 5: the refusal's code to the commander)
        };
        const teams = new Set(this.gameState.players.map(p => p.teamIndex));
        const playFor = (t: number) => (squads: Squad[], objective: number | { planetIndex: number; surfaceDir: Vec3 }, opts?: { support?: Squad[]; here?: true; flank?: true; mounted?: true }) => {   // 68.22 step 4: the same input as the harness (66.15: a place, the support named)
          const idOf = (q: Squad) => { const to = orderTo(match.state, q); return 'squad' in to ? to.squad : undefined; };   // (69.72 step 5's follow-up: by its first living member in a squad; 69.87: a Match squad by its id)
          const ids = [...new Set(squads.map(idOf).filter((x): x is number => x !== undefined))];
          const support = opts?.support?.map(idOf).filter((x): x is number => x !== undefined);
          return hookAnswer(match.apply(match.stamp(seatActor(t), { kind: 'coordinate', team: t, squads: ids, objective: typeof objective === 'number' ? { pointId: objective } : objective, by: 'commander', ...(support?.length ? { support } : {}), ...(opts?.here ? { here: true as const } : {}), ...(opts?.flank ? { flank: true as const } : {}), ...(opts?.mounted ? { mounted: true as const } : {}) })));
        };
        const reservesFor = (t: number) => (call: 'hold' | 'call') => { const input = match.stamp(seatActor(t), { kind: 'reserves', team: t, call }); this.botOrderKeys.add(inputKey(input)); match.apply(input); };   // 68.4: the same input as the harness
        // 68.39: a side's airstrikes are its commander's — the bot commander's, not the human's side (the human calls their own)
        const airstrikeFor = (t: number) => (at: { planetIndex: number; surfaceDir: Vec3 }) => { const input = match.stamp(seatActor(t), { kind: 'airstrike', team: t, at }); this.botOrderKeys.add(inputKey(input)); match.apply(input); };
        // 69.3b step 3: the side's drop calls and its order on where its waves land — the bot commander's, not the human's side
        const botInput = (t: number, body: Parameters<typeof match.stamp>[1]) => { const input = match.stamp(seatActor(t), body); this.botOrderKeys.add(inputKey(input)); match.apply(input); };
        const dropsFor = (t: number): DropCalls | undefined => (t === humanTeam ? undefined : {
          ...(countOf(match.state.config, 'helicopters') ? { heli: (at, order) => botInput(t, { kind: 'dropship', team: t, at, payload: 'troops', order, by: 'commander', craft: 'heli' }) } : {}),   // 69.59 step 1: the same input as the harness
          ...(match.state.dropships ? { dropship: (at, order, hot) => botInput(t, { kind: 'dropship', team: t, at, payload: 'troops', order, by: 'commander', ...(hot ? { hot: true as const } : {}) }) } : {}),   // (69.52 step 4: hot)
          ...(match.state.pods ? { pods: (at, order) => botInput(t, { kind: 'podDrop', team: t, at, order, by: 'commander' }) } : {}),
          ...(defaultOn(match.state.config, 'shipPickup') || match.state.config.storyCarry ? { pickup: (squad, at, priority, craft) => botInput(t, { kind: 'pickup', team: t, squad, at, by: 'commander', ...(priority ? { priority } : {}), ...(craft ? { craft } : {}) }) } : {}),   // 69.18: the same input as the harness
          ...(countOf(match.state.config, 'gunships') ? { gunship: at => botInput(t, { kind: 'dropship', team: t, at, payload: 'gunship', by: 'commander' }) } : {}),   // 63.13
          ...(match.state.config.hotRescue ? { rescue: (squad, at, threat, cause) => botInput(t, { kind: 'rescue', team: t, squad, at, threat, by: 'commander', ...(cause ? { cause } : {}) }) } : {}),   // 69.62: the same input as the harness
          ...(match.state.supplyDrones ? { supplyDrop: (at, load) => botInput(t, { kind: 'supplyDrop', team: t, at, load, by: 'commander' }) } : {}),   // 69.32 step 2: the same input as the harness
          spawnAt: at => botInput(t, { kind: 'spawnAt', team: t, at }),
        });
        commanders = [...teams].sort((a, b) => a - b).map(t => createCommander(match.state, t, styles[t] ?? 'none', (setup as SkirmishSetup).squadSize ?? 3, (setup as SkirmishSetup).replanSeconds, issueFor(t), !!(setup as SkirmishSetup).tactics, callsReserves(setup as SkirmishSetup) ? reservesFor(t) : undefined, sidePlays((setup as SkirmishSetup).commanderPlays, t) ? playFor(t) : undefined, match.state.airstrikes && t !== humanTeam ? airstrikeFor(t) : undefined, dropsFor(t), match.state.config.efforts ? efforts => botInput(t, { kind: 'efforts', team: t, efforts, by: 'commander' }) : undefined));   // (§0.11 step D)
      }
    }
    const human = humanTeam === null ? null : this.gameState.players.find(p => p.teamIndex === humanTeam && p.alive) ?? null;
    this.hitShapes?.dispose();
    this.hitShapes = hitShapesShown() ? new HitShapesView(this.sceneObjs.scene) : null;
    this.fineGround?.dispose();
    this.fineGround = fineGroundDrawn() && personSetOf(this.match.state).fineAmplitude > 0 ? new FineTerrainPatch(this.sceneObjs.scene, { halfSizeWavelengths: 12, perWavelength: 6 }) : null;
    // 67.50 (1): the drawn relief flattened under every prop (the props stand on the coarse ground, as in the sim)
    this.fineGround?.setFlatDiscs(planet => flatDiscs(this.match.state, this.match.state.planets.findIndex(p => p.id === planet.id)), FINE_FLATTEN.marginPeople * PERSON_BODY_HEIGHT * personSetOf(this.match.state).k);
    { const ms = mountainStyleOf(this.match.state.planets[battlePlanetIndex(this.match.state)]?.id ?? -1); if (ms && this.fineGround) applyMountainStyleToPatch(this.fineGround.mesh.material as THREE.Material, ms); }   // 388 (chained before the look's: it reads the look's colour)
    { const look = surfaceLookOf(this.match.state.planets[battlePlanetIndex(this.match.state)]?.id ?? -1); if (look) this.fineGround?.setSurfaceLook(look); }   // 71.8: the patch in its planet's look
    // 74.20 (DECISIONS 439): the drainage drawn on the patch's ground — the beds' tint (rendering/riverbedLook.ts; the source set in buildMatchScene)
    if (this.fineGround && this.riverbeds) this.fineGround.setRiverbeds(this.riverbeds.linesOf, this.riverbeds.person);
    this.actionHost = new ActionHost({
      match: this.match,
      scene: this.sceneObjs.scene,
      // S3: people and the camera stand on the ground as drawn (the fine patch near the camera, the mesh beyond)
      ...(this.fineGround ? { drawnGround: (planet: PlanetState, dir: Vec3) => this.fineGround!.drawnGround(planet, dir, personSetOf(this.match.state)) } : {}),
      // 208's follow-up: a structure's base as drawn (the wave ship stands on its pad's deck)
      structureShown: (id: number) => this.sceneObjs.structureMeshes.get(id)?.fogShown !== false,   // 69.74 step 2b
      drawnBase: (id: number) => { const sm = this.sceneObjs.structureMeshes.get(id), s = this.match.state.structures.find(x => x.id === id), pl = s && this.match.state.planets[s.planetIndex]; return sm && pl ? sm.group.position.distanceTo(new THREE.Vector3(pl.position.x, pl.position.y, pl.position.z)) : null; },
      camera: this.sceneObjs.camera,
      canvas: this.sceneObjs.renderer.domElement,
      playerMeshes: this.sceneObjs.playerMeshes,
      structureGroup: (id: number) => this.sceneObjs.structureMeshes.get(id)?.group,   // 63.3's follow-up: where a pit's gun-layer sits, as drawn
      humanId: human?.id ?? null,
      timeScale: opts.timeScale,
      tickLimit: opts.tickLimit,
      ...(commanders.length || (setup as SkirmishSetup).withdraw ? { beforeTick: () => {
        for (const c of commanders) c.step(this.match.state, this.match.actionTick, PHYS_DT);
        // X22: the scripted withdrawal, as the harness runs it (the smoke compares the two)
        const wd = (setup as SkirmishSetup).withdraw;
        if (wd && this.match.actionTick === Math.round(wd.atSeconds / PHYS_DT)) this.withdrawTeam(wd.team, 'setup');
      } } : {}),
      onGameOver: winner => opts.onGameOver?.(winner),
      onDroneTablet: () => this.toggleDroneTablet(),
      onMapZoom: dir => this.topoMap?.zoomStep(dir),
      onDroneFeed: () => this.toggleDroneFeed(),
      // 64.11: a seat's shell landed — the turn game's own impact presentation (effects, the crater on the mesh, the props re-seated)
      onShellImpact: e => { this.playImpactFx(e.shell.weaponKey, e.point, e.planetIndex); this.applyImpactOutcome(e.resolution, e.point); this.updatePlayerMeshes(); },
      onCrashGround: r => { this.applyImpactGround(r); this.updatePlayerMeshes(); },   // 69.24: a crash's craters, drawn
      // A battle handed over by a setup (sector) ends into its own screen; no replay, no menu hop
      onRestart: opts.battle ? undefined : () => this.startActionFromSetup({ ...setup, seed: `${setup.seed}+` }, opts),
      onExit: opts.battle ? undefined : () => { this.clearScene(); showMenu(true); },
    });
    // whose knowledge the markers show: the player's team, or team 0's when watching
    this.layer0Views?.dispose();
    this.layer0Views = new Layer0Views(this.sceneObjs.scene, this.sceneObjs.camera, humanTeam ?? 0);
    this.warmImpactPrograms();   // a shell's landing compiled its effects' programs every time: once, here
    this.warmBattlePrograms();
    this.topoMap?.dispose();
    this.topoMap = new TopoMap(HUD_MAP_SIZE, { parent: this.actionHost.mapSlot });   // 67.35: the corner map in the ground HUD's corner
    this.actionViewer = { team: humanTeam ?? 0, id: human?.id ?? null };
  }

  /** Everything startFromSetup does before the first turn: the Match, the world, fog and HUD. */
  private prepareFromSetup(setup: MatchSetup, opts: { humanTeam?: number | null; visibility?: 'fog' | 'free' } = {}): void {
    resetChecksumTrace();
    // A setup is local: no peer, no replay record
    this.networkSync = null;
    this.recorder = null;
    this.clearScene();
    clearLog();
    clearCommsLog();
    clearScoreboard();
    hideGameOver();

    // The Match from the setup (src/match/setup.ts), on this peer's kernel
    const wasmReady = this.match.wasmReady;
    this.match.dispose();
    const { match } = createMatch(setup);
    // 63.14 2b's readout (the owner: "haven't found any terracing yet"): which structures the setup terraced, and the rise it read under each
    if (setup.terraces) console.info(`[terraces] ${terraceReadout(match.state)}`);
    match.wasmReady = wasmReady;
    match.wasmAllowed = true;
    setWasmEnabled(true);
    this.match = match;
    const humanTeam = opts.humanTeam ?? null;
    if (humanTeam !== null) {
      this.gameState = { ...this.gameState, players: this.gameState.players.map(p => p.teamIndex === humanTeam ? { ...p, isBot: false } : p) };
      // 57.4: the human side is the infiltrator, never a guard — unless the setup named the guard teams itself
      if (this.gameState.config.modeConfig.startingAlert && !setup.guardTeams) {
        const modeConfig = { ...this.gameState.config.modeConfig, guardTeams: botGuardTeams(this.gameState.players) };
        this.gameState = { ...this.gameState, config: { ...this.gameState.config, modeConfig } };
      }
    }

    this.visibility = opts.visibility ?? (humanTeam !== null ? 'fog' : 'free');
    this.resolutionPace = this.gameState.config.resolutionPace ?? 'full';
    updatePaceToggle(this.resolutionPace);
    this.roundPlay = null;
    this.clearPlannedMoves();
    this.clearPlannedPaths();
    this.devReveal = false;
    this.focusPlanetIndex = null;
    updateCameraMode(this.visibility, this.devReveal);

    this.buildMatchScene();
    if (isWasmReady()) setPlanets(this.gameState.planets);
    // The setup's fog was revealed against blank colours (a headless match draws nothing):
    // reveal again from the same positions with the real ones — same vertices, real colour memory
    const colors = this.gameState.planets.map(p => this.truePlanetColors.get(p.id)!);
    const fresh = initializeFog(this.gameState.players.length, this.gameState.planets);
    this.gameState = {
      ...this.gameState,
      fog: fresh.map((f, i) => updateFogForPlayer(f, this.gameState.players[i], this.gameState.planets, colors, this.gameState.turnNumber, false)),
    };
    this.buildMatchHud();
    // The kernel may still be loading (a page that starts straight into a setup): upload the world when it is
    initWasm().then(ok => {
      this.wasmReady = ok;
      if (ok) setPlanets(this.gameState.planets);
    });
    if (this.gameState.modeState.salvage) { this.disposeDepotMeshes(); this.createDepotMeshes(); }
    document.getElementById('players-row')?.classList.remove('hidden');
    buildPlayerBars(this.gameState.players);
  }

  /** Every mesh for the match's world and units, then the true planet colours from the meshes (fog snapshots from them). */
  private buildMatchScene(): void {
    // 74.20 (DECISIONS 439): where the drainage's beds are drawn — the fine patch's tint, the reeds, the far strips (rendering/
    // riverbedLook.ts); an action battle's only (classic stays as it is). The battle planet's lines built here (20–60 ms a
    // megaplanet, cached on its sheet: the woods' creek cut reads the same ones), not at the first patch build
    this.riverbeds = this.gameState.config.clock === 'action' ? riverbedSource(this.gameState, battlePlanetIndex(this.gameState)) : null;
    // 67.50 (3): an action battle with the fine ground drawn draws the far ground at the relief's middle (rendering/
    // groundLift.ts) — set before anything is seated on it; the fine patch is made under the same condition
    setGroundLift(this.gameState.config.clock === 'action' && fineGroundDrawn() ? farLift(personSetOf(this.gameState)) : 0);
    // 69.24: and the mesh sunk under the patch below the deepest bowl in the fine ground (a craterlet's, a dig's), so the coarse
    // ground never shows through one (with no fine relief, none)
    setGroundSink(this.gameState.config.clock === 'action' && fineGroundDrawn() && personSetOf(this.gameState).fineAmplitude > 0 ? BOWL_SINK_PEOPLE * PERSON_BODY_HEIGHT * personSetOf(this.gameState).k : 0);
    // Create planet/star/wormhole meshes
    for (const planet of this.gameState.planets) {
      if (planet.type === 'star') {
        const starData = createStarMesh(planet, this.sceneObjs.scene);
        this.sceneObjs.planetMeshes.set(planet.id, starData);
      } else if (planet.type === 'wormhole') {
        const whData = createWormholeMesh(planet, this.sceneObjs.scene);
        this.sceneObjs.planetMeshes.set(planet.id, whData);
      } else {
        const meshData = createPlanetMesh(planet, this.sceneObjs.scene);
        this.sceneObjs.planetMeshes.set(planet.id, meshData);
      }
    }
    // 74.20 (439 (4)): the battle planet's beds as strips on its mesh, seen past the fine patch (rendering/riverbedFar.ts)
    {
      const bi = battlePlanetIndex(this.gameState), bp = this.gameState.planets[bi], md = bp && this.sceneObjs.planetMeshes.get(bp.id);
      if (this.riverbeds && bp && md && !md.isStar && !md.isWormhole && this.riverbeds.planets.includes(bi)) { const dl = this.riverbeds.linesOf(bp); if (dl) attachRiverbedFar(md.mesh, bp, dl, this.riverbeds.person); }
    }
    // 388: the mountain style — rock by the slope, strata, snow by the snowline (rendering/mountainStyle.ts); before the look's chain
    {
      const ps = personSetOf(this.gameState), si = mountainStylePlanetIndex(this.gameState, battlePlanetIndex(this.gameState), ps.k);
      const sp = si === null ? undefined : this.gameState.planets[si], md = sp && this.sceneObjs.planetMeshes.get(sp.id);
      if (sp && md && !md.isStar && !md.isWormhole) attachMountainStyle(md, sp, { person: PERSON_BODY_HEIGHT * ps.k, sun: this.skyHaze.sun });
    }
    // 71.8 (DECISIONS 372, 375): the Lab's Ground look — the battle planet drawn in its world's pick (rendering/surfaceLook.ts)
    {
      const ps = personSetOf(this.gameState), li = lookPlanetIndex(this.gameState, battlePlanetIndex(this.gameState), ps.k);
      const lp = li === null ? undefined : this.gameState.planets[li], md = lp && this.sceneObjs.planetMeshes.get(lp.id);
      if (lp && md && !md.isStar && !md.isWormhole) attachSurfaceLook(md, lp, { person: PERSON_BODY_HEIGHT * ps.k, sun: this.skyHaze.sun });
    }

    // Create player meshes
    for (const player of this.gameState.players) {
      const mesh = createPlayerMesh(player, this.sceneObjs.scene);
      this.sceneObjs.playerMeshes.set(player.id, mesh);
    }

    // Structures a setup placed before the match began (sector preparation, harness emplacements)
    for (const structure of this.gameState.structures) this.ensureStructureMesh(structure);

    // Create hazard meshes
    this.hazardMeshes = [];
    for (const hazard of this.gameState.hazards) {
      const pos = getHazardWorldPosition(hazard, this.gameState.planets);
      const normal = new THREE.Vector3(
        hazard.surfaceDir.x, hazard.surfaceDir.y, hazard.surfaceDir.z
      ).normalize();
      const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
      // Seated on the ground's frame (rendering/groundSeat.ts) — the old per-hazard lift scan is gone with it
      const hm = createHazardMesh(hazard, worldPos, normal, this.sceneObjs.scene, this.gameState.planets[hazard.planetIndex].radius, this.propOptsFor(hazard.planetIndex));
      this.hazardMeshes.push(hm);
    }

    // Create ruin meshes
    this.ruinMeshes = [];
    for (const ruin of this.gameState.ruins) {
      const pos = getRuinWorldPosition(ruin, this.gameState.planets);
      const normal = new THREE.Vector3(
        ruin.surfaceDir.x, ruin.surfaceDir.y, ruin.surfaceDir.z
      ).normalize();
      const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
      const rm = createRuinMesh(ruin, worldPos, normal, this.sceneObjs.scene, this.gameState.planets[ruin.planetIndex].radius, this.propOptsFor(ruin.planetIndex));
      this.ruinMeshes.push(rm);
    }

    // Create flora meshes (normal-size biome props are sized from the person and coloured by the planet — decision 87)
    this.floraMeshes = [];
    this.treeStands?.dispose();
    this.treeStands = null;
    this.standSkirts?.dispose();
    this.standSkirts = new StandSkirts(this.sceneObjs.scene);
    this.farField?.dispose();
    this.farField = personSetOf(this.gameState).k < 1 && farFieldDrawn() ? new FarFieldPlay(this.sceneObjs.scene, PERSON_BODY_HEIGHT * personSetOf(this.gameState).k) : null;
    this.systemSky?.dispose();
    this.systemSky = personSetOf(this.gameState).k < 1 ? SystemSkyPlay.forMatch(this.sceneObjs.scene, this.gameState, this.skyHaze.sun) : null;   // 74.1
    for (const g of this.craterDecals) { this.sceneObjs.scene.remove(g); disposeObjectTree(g); }   // 63.5 step 5
    this.craterDecals = [];
    const stress = stressTrees(this.gameState, treeStressParam());   // E5.3: ?treeStress=N draws extra trees (dev, presentation only)
    const drawMode = treeDrawMode();
    const instanced = drawMode === 'instanced' && (this.gameState.flora.some(f => f.size === 'normal') || this.gameState.rocks.some(r => r.size === 'normal'));
    // E5.4 step 2: the planet's forest drawn past the battle's dressing (the Lab's Planet forest row; each planet's own seed, 158)
    this.nearField = new NearFieldStreamer();   // 62.61: one near field for the scene (the forest's cells; the patch's and the stands' builds claim its ledger)
    // (E5.4: the forest the sim holds is drawn — on by default at a person's scale, v104 — so the held trees are the drawn ones)
    if (instanced) this.treeStands = new TreeStands(this.sceneObjs.scene, this.gameState, stress, pi => this.propOptsFor(pi).rockColor ?? '#7a7468', { forest: !!this.gameState.forest || forestDrawn(), streamer: this.nearField });
    for (const flora of [...this.gameState.flora, ...stress]) {
      if (instanced && flora.size === 'normal') continue;   // drawn by the stands
      const pos = getFloraWorldPosition(flora, this.gameState.planets);
      const normal = new THREE.Vector3(
        flora.surfaceDir.x, flora.surfaceDir.y, flora.surfaceDir.z
      ).normalize();
      const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
      this.floraMeshes.push(createFloraMesh(flora, worldPos, normal, this.sceneObjs.scene, this.gameState.planets[flora.planetIndex].radius, this.propOptsFor(flora.planetIndex)));
    }

    // Ground scatter under and between the biome stands (human scale only; one instanced mesh per kind)
    this.groundScatter?.dispose();
    this.groundScatter = this.gameState.flora.some(f => f.size === 'normal') ? new GroundScatter(this.sceneObjs.scene, this.gameState, this.gameState.config.seed ?? 'scatter') : null;
    // 62.61's follow-up: past the dressing, the forest cells' own clutter, streamed round the eye with the planet's forest
    if (this.groundScatter && this.treeStands && this.nearField) this.groundScatter.streamForest(this.nearField, this.treeStands);
    // 74.20: and the reed belts on the drainage's damp reaches and basin shores, streamed the same way
    if (this.groundScatter && this.nearField && this.riverbeds) this.groundScatter.streamReeds(this.nearField, this.riverbeds.linesOf, this.riverbeds.planets);

    // Create rock meshes: the planet-scale ones merged per planet, the person-scale ones in the stands (E5.3);
    // `?trees=each` draws every rock the old way, one mesh each
    this.rockMeshes = [];
    this.rockMerge?.dispose();
    const giants = drawMode === 'instanced' ? this.gameState.rocks.filter(r => r.size !== 'normal') : [];
    this.rockMerge = giants.length ? new PlanetRockMerge(this.sceneObjs.scene, giants, this.gameState.planets, pi => this.propOptsFor(pi)) : null;
    for (const rock of this.gameState.rocks) {
      if (drawMode === 'instanced' && rock.size !== 'normal') continue;   // merged per planet
      if (instanced && rock.size === 'normal') continue;   // drawn by the stands (E5.3)
      const pos = getRockWorldPosition(rock, this.gameState.planets);
      const normal = new THREE.Vector3(
        rock.surfaceDir.x, rock.surfaceDir.y, rock.surfaceDir.z
      ).normalize();
      const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
      this.rockMeshes.push(createRockMesh(rock, worldPos, normal, this.sceneObjs.scene, this.gameState.planets[rock.planetIndex].radius, this.propOptsFor(rock.planetIndex)));
    }

    // Create environmental effect meshes
    this.effectMeshes = [];
    for (const effect of this.gameState.environmentalEffects) {
      if (effect.effectType === 'aurora') continue;   // 74.16: rolled still (mapgen's draws), drawn by the planet below
      const planet = this.gameState.planets[effect.planetIndex];
      this.effectMeshes.push(
        createEnvironmentalEffectMesh(effect, planet, this.sceneObjs.scene)
      );
    }
    // 74.16 (DECISIONS 385): the aurora is the planet's — a world shows one by its air and its star's wind (the battle world's star is the sky's)
    this.auroras?.dispose();
    this.auroras = new PlanetAuroras(this.sceneObjs.scene, this.gameState.planets, this.systemSky);

    // Create carryable meshes (CTF nav cores, carried data fragments, etc.)
    this.carryableMeshes.clear();
    for (const carryable of this.gameState.carryables) {
      if (!carryable.alive || carryable.carriedBy !== null) continue;
      const pos = getCarryableWorldPosition(carryable, this.gameState.planets);
      const normal = new THREE.Vector3(
        carryable.surfaceDir.x, carryable.surfaceDir.y, carryable.surfaceDir.z
      ).normalize();
      const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
      const cm = createCarryableMesh(carryable, worldPos, normal, this.sceneObjs.scene);
      this.carryableMeshes.set(carryable.id, cm);
    }

    // Create collectible meshes (data fragments in vaults, salvage nodes)
    this.collectibleMeshes.clear();
    if (this.gameState.collectibles) {
      for (const collectible of this.gameState.collectibles) {
        if (collectible.collected) continue;
        const pos = getCollectibleWorldPosition(collectible, this.gameState.planets);
        const normal = new THREE.Vector3(
          collectible.surfaceDir.x, collectible.surfaceDir.y, collectible.surfaceDir.z
        ).normalize();
        const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
        const cm = createCollectibleMesh(collectible, worldPos, normal, this.sceneObjs.scene);
        this.collectibleMeshes.set(collectible.id, cm);
      }
    }

    // Create drill site meshes (excavation mode)
    this.drillSiteMeshes.clear();
    if (this.gameState.modeState.excavation) {
      for (const drill of this.gameState.modeState.excavation.drillSites) {
        const pos = getDrillSiteWorldPosition(drill, this.gameState.planets);
        const normal = new THREE.Vector3(
          drill.surfaceDir.x, drill.surfaceDir.y, drill.surfaceDir.z
        ).normalize();
        const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
        const dm = createDrillSiteMesh(drill, drill.teamIndex, worldPos, normal, this.sceneObjs.scene);
        this.drillSiteMeshes.set(drill.id, dm);
      }
    }

    // Create artifact site marker (excavation mode) — pulsing ring on artifact planet surface
    this.disposeArtifactSiteMarker();
    if (this.gameState.modeState.excavation) {
      this.createArtifactSiteMarker();
    }

    // Depot visuals (salvage run mode) — created in finishDeployPhase after planet assignment
    this.disposeDepotMeshes();

    // Update player positions
    this.updatePlayerMeshes();

    // Snapshot true planet colors for fog system (before fog is applied)
    this.truePlanetColors.clear();
    for (const planet of this.gameState.planets) {
      const meshData = this.sceneObjs.planetMeshes.get(planet.id);
      if (meshData) {
        this.truePlanetColors.set(planet.id, getPlanetMeshColors(meshData));
      }
    }
  }

  /** The match HUD and its companions: player bars, aim indicator, movement ring, thinking sprite, ambient sound, music, hazard emitters. */
  private buildMatchHud(): void {
    // Build UI
    buildPlayerBars(this.gameState.players);
    showMenu(false);

    // Create aim indicator
    this.sceneObjs.aimIndicator = createAimIndicator(this.sceneObjs.scene);

    // Create movement ring
    this.sceneObjs.movementRing = createMovementRing(this.sceneObjs.scene);

    // Create bot thinking indicator
    this.botThinking = createBotThinkingSprite(this.sceneObjs.scene);

    // Start ambient audio — create stems and set initial biome
    try {
      this.ambientManager = new AmbientManager();
      this.weatherSound = new WeatherSound();
      const firstPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
      const firstPlanet = this.gameState.planets[firstPlayer.planetIndex];
      this.ambientManager.setBiome(firstPlanet.archetype);
    } catch {
      this.ambientManager = null;
    }

    // Start music — conductor drives the mix from here
    this.combatIntensity = 0;
    this.timeSinceLastAction = 0;
    this.conductorAccum = 0;
    resetConductor();
    this.ensureMusic();
    this.musicManager?.solo('calm', 2.0);

    // Start spatial hazard emitters
    try {
      this.hazardEmitterManager = new HazardEmitterManager(
        this.gameState.hazards,
        this.gameState.planets
      );
    } catch {
      this.hazardEmitterManager = null;
    }
  }

  // ===========================================================================
  // DEPLOY PHASE
  // ===========================================================================

  /** Hide the match HUD and hand over to the deploy controller (planet picks → drop → fan-out → startMatchAfterDeploy). */
  private startDeployPhase(): void {
    document.getElementById('players-row')?.classList.add('hidden');
    const turnInfoEl = document.getElementById('turn-info');
    if (turnInfoEl) turnInfoEl.textContent = '';
    showControlsBar(false);
    this.deploy.start();
  }

  private makeDeployHost(): DeployHost {
    const game = this;
    return {
      get state() { return game.gameState; },
      setState: next => { game.gameState = next; },
      get scene() { return game.sceneObjs; },
      get cameraState() { return game.cameraState; },
      get network() { return game.networkSync; },
      warmupBots: () => game.botWorker.warmup(),
      registerCrashedCan: (canId, group) => { game.crashedDropCanMeshes.set(canId, group); },
      recordDeploySelection: (teamIndex, planetIndex, surfaceDir) => game.recorder?.deploy(teamIndex, planetIndex, surfaceDir),
      get fogActive() { return game.fogCam; },
      get viewerTeamIndex() { return game.gameState.players[game.viewerIndex]?.teamIndex ?? -1; },
      scanFromCan: (teamIndex, pos) => game.scanFromDeployCan(teamIndex, pos),
      repaintFog: () => game.applyFogToScene(),
      refreshFogView: () => game.updateFogForCurrentPlayer(),
      isUnitVisibleToViewer: id => {
        const viewer = game.gameState.players[game.viewerIndex];
        const p = game.gameState.players.find(x => x.id === id);
        return !!viewer && !!p && (p.teamIndex === viewer.teamIndex || game.visibleEnemyIds.has(id));
      },
      onComplete: () => { void game.startMatchAfterDeploy(); },
    };
  }

  /**
   * A deploy can scans the terrain it passes over for every unit of its team, at
   * probe radius. Terrain only — unit positions are placeholders until the cans
   * land, so enemy spotting waits for the fan-out's refreshFogView().
   */
  private scanFromDeployCan(teamIndex: number, pos: Vec3): void {
    const planetColorsArray = this.gameState.planets.map(p => this.truePlanetColors.get(p.id)!);
    for (let i = 0; i < this.gameState.players.length; i++) {
      const p = this.gameState.players[i];
      if (p.teamIndex !== teamIndex || !this.gameState.fog[i]) continue;
      revealAtPoint(this.gameState.fog[i], pos, this.gameState.planets, PROBE_REVEAL_RADIUS, planetColorsArray, this.gameState.turnNumber);
    }
    this.viewerFogCache = null; // revealAtPoint mutates fog arrays in place
  }
  /** The remote human still has movement after their shot: show the wait overlay; the action pump does the rest. */
  private waitForRemotePostFire(): void {
    this.match.resumeAiming();
    showControlsBar(false);
    const waitEl = document.getElementById('net-waiting-turn');
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (waitEl) {
      waitEl.textContent = this.replaying ? `REPLAY — ${player.name.toUpperCase()}` : `AWAITING ${player.name.toUpperCase()}...`;
      waitEl.style.display = '';
    }
    this.pumpRemoteActions();
  }

  private disposeCrashedDropCan(canId: number): void {
    const group = this.crashedDropCanMeshes.get(canId);
    if (group) {
      disposeObjectTree(group);
      group.parent?.remove(group);
      this.crashedDropCanMeshes.delete(canId);
    }
  }

  /** Play a drop can descent animation for a mid-game respawn. Player mesh stays hidden until landing. */
  startRespawnDropCan(playerId: number): void {
    const player = this.gameState.players.find(p => p.id === playerId);
    if (!player) return;
    const planet = this.gameState.planets[player.planetIndex];
    const team = TEAMS[player.teamIndex % TEAMS.length];

    // Hide player mesh until drop can lands
    const meshData = this.sceneObjs.playerMeshes.get(playerId);
    if (meshData) meshData.group.visible = false;

    const effect = createDropCanEffect(
      this.sceneObjs.scene,
      planet.position,
      player.surfaceDir,
      getSurfaceRadius(planet, player.surfaceDir),
      team,
      this.gameState.planets,
      0,
    );
    this.respawnDropCans.set(playerId, effect);
  }

  /** Everyone has landed: fog, mode objectives and HUD for the match, spread over a few frames, then the first aim phase. */
  private async startMatchAfterDeploy(): Promise<void> {
    const nextFrame = (): Promise<void> => this.clock.nextFrame();

    // Fog carries over from the deploy phase: what the cans revealed on the way down stays known (37.2a).

    // Initial LOS reveal from new positions — yield every 2 players
    const planetColorsArray = this.gameState.planets.map(p => this.truePlanetColors.get(p.id)!);
    for (let i = 0; i < this.gameState.players.length; i++) {
      const p = this.gameState.players[i];
      const darkPlanets2 = this.gameState.modeState.blackout?.darkenedPlanets;
      const darkVis2 = this.gameState.config.modeConfig.visibilityOnDark;
      this.gameState.fog[i] = updateFogForPlayer(
        this.gameState.fog[i],
        p,
        this.gameState.planets,
        planetColorsArray,
        this.gameState.turnNumber,
        this.gameWasm,
        darkPlanets2,
        darkVis2,
      );
      if (i % 2 === 1) await nextFrame();
    }

    // Mode-specific setup — next frame
    await nextFrame();

    // Mode layers: home planets, cores and depots follow the deployed positions
    for (const [, cm] of this.carryableMeshes) disposeCarryableMesh(this.sceneObjs.scene, cm);
    this.carryableMeshes.clear();
    this.gameState = finalizeModeDeploy(this.gameState);
    for (const carryable of this.gameState.carryables) {
      if (!carryable.alive || carryable.carriedBy !== null) continue;
      const pos = getCarryableWorldPosition(carryable, this.gameState.planets);
      const normal = new THREE.Vector3(carryable.surfaceDir.x, carryable.surfaceDir.y, carryable.surfaceDir.z).normalize();
      const cm = createCarryableMesh(carryable, new THREE.Vector3(pos.x, pos.y, pos.z), normal, this.sceneObjs.scene);
      this.carryableMeshes.set(carryable.id, cm);
    }
    if (this.gameState.modeState.salvage) {
      // Depot visuals now that planets are assigned
      this.disposeDepotMeshes();
      this.createDepotMeshes();
    }

    // Final sync + transition to aiming — next frame
    await nextFrame();

    // Sync meshes with post-deploy positions before starting aim phase
    this.updatePlayerMeshes();

    // Restore HUD for aiming phase
    document.getElementById('players-row')?.classList.remove('hidden');
    buildPlayerBars(this.gameState.players);

    // Transition to aiming phase
    this.gameState = startAimingPhase(this.gameState);
    this.startAimPhase();
  }

  private updateFerryPickup(dt: number, time: number): void {
    let allDone = true;

    for (const effect of this.ferryPickupEffects) {
      if (effect.state === 'done') continue;

      const prevState = effect.state;
      const result = updateFerryPickupEffect(effect, dt, time);

      // Hide boarded player meshes
      for (const pid of result.boardedPlayerIds) {
        const meshData = this.sceneObjs.playerMeshes.get(pid);
        if (meshData) {
          meshData.group.visible = false;
        }
      }

      // Boarding walk: slerp survivors toward ferry landing point
      if (effect.state === 'boarding') {
        const stop = effect.stops[effect.currentStopIndex];
        const planet = this.gameState.planets[stop.planetIndex];
        for (const anim of stop.walkAnims) {
          if (anim.progress >= 1) continue;
          const player = this.gameState.players.find(p => p.id === anim.playerId);
          if (!player) continue;

          // Initialize startDir on first frame
          if (anim.startDir.x === 0 && anim.startDir.y === 0 && anim.startDir.z === 0) {
            anim.startDir = { ...player.surfaceDir };
          }

          const t = anim.progress;
          const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          const interpDir = vecSlerp(anim.startDir, anim.endDir, eased);
          const normalDir = vecNormalize(interpDir);

          // Find surface height
          const bp = planet.basePositions;
          const hm = planet.heightmap;
          let bestDot = -2, bestIdx = 0;
          for (let i = 0; i < hm.length; i++) {
            const vx = bp[i * 3], vy = bp[i * 3 + 1], vz = bp[i * 3 + 2];
            const d = vx * normalDir.x + vy * normalDir.y + vz * normalDir.z;
            if (d > bestDot) { bestDot = d; bestIdx = i; }
          }
          const surfaceRadius = planet.radius + hm[bestIdx];
          const worldPos: Vec3 = {
            x: planet.position.x + normalDir.x * surfaceRadius,
            y: planet.position.y + normalDir.y * surfaceRadius,
            z: planet.position.z + normalDir.z * surfaceRadius,
          };

          const meshData = this.sceneObjs.playerMeshes.get(anim.playerId);
          if (meshData) {
            updatePlayerMesh(player, meshData, worldPos, normalDir);
            meshData.group.visible = true;
            meshData.animCtx.state = 'waddle';
          }
        }
      }

      // Sound effects on state transitions
      if (result.stateChanged) {
        const pos = effect.rootGroup.position;
        const posVec: Vec3 = { x: pos.x, y: pos.y, z: pos.z };
        if (effect.state === 'approach' && prevState !== 'approach') {
          playSpatial(posVec, rocketLaunch, 4);
        }
        if (effect.state === 'landed') {
          playSpatial(posVec, craterImpact, 3);
        }
        if (effect.state === 'ascend') {
          playSpatial(posVec, rocketLaunch, 3);
        }
      }

      if (result.running) {
        allDone = false;

        // Camera follow: lerp toward first ferry
        if (effect === this.ferryPickupEffects[0]) {
          const ferryPos = effect.rootGroup.position;
          lerpCameraTarget(this.cameraState, { x: ferryPos.x, y: ferryPos.y, z: ferryPos.z }, 0.05, dt);
        }
      }
    }

    if (allDone) {
      // All ferries complete — show game over screen
      for (const effect of this.ferryPickupEffects) {
        disposeFerryPickupEffect(this.sceneObjs.scene, effect);
      }
      this.ferryPickupEffects = [];
      this.showGameOverScreen(this.ferryPickupWinner);
      this.ferryPickupWinner = null;
    }
  }

  /** Game over: close the record, keep it as "last match", and let the game-over screen save it as a file. */
  private finishReplayRecord(winner: string | null): void {
    const rec = this.recorder;
    this.recorder = null;
    const saveBtn = document.getElementById('btn-save-replay') as HTMLButtonElement | null;
    if (!rec) { if (saveBtn) saveBtn.classList.add('hidden'); return; }
    const record = rec.finish(this.match.inputLog, { winner, turns: this.gameState.turnNumber });   // R11 step 7: the human seats' part of the Match's input log
    saveLastReplay(record);
    this.lastReplay = record;
    if (saveBtn) saveBtn.classList.remove('hidden');
    this.updateReplayMenuButton();
    if (this.networkSync?.kind === 'p2p') this.updateNetworkStatus();
  }
  private lastReplay: ReplayRecord | null = null;
  /** Main menu: show "Replay last match" only when there is one. */
  private updateReplayMenuButton(): void {
    const btn = document.getElementById('btn-replay-last');
    if (!btn) return;
    const rec = this.lastReplay ?? loadLastReplay();
    btn.classList.toggle('hidden', !rec);
    if (rec) btn.title = `${rec.result?.winner ? rec.result.winner + ' won' : 'unfinished'} · ${rec.result?.turns ?? '?'} turns · seed ${rec.options.seed}${replayVersionWarning(rec) ? ' · older rules' : ''}`;
  }
  /** Play a record: the game runs with every human team as a "remote" whose inputs come from the record. */
  private startReplay(record: ReplayRecord): void {
    if (record.wasm && !isWasmLoaded()) logMessage('Replay was recorded with the WASM kernel, which is not loaded — bots may diverge.');
    const versionWarning = replayVersionWarning(record);
    if (versionWarning) logMessage(versionWarning);
    this.peerManager = null;
    const driver = new ReplayDriver(record, id => this.gameState.players.findIndex(p => p.id === id));
    this.networkSync = driver;
    driver.onActionQueued = () => this.pumpRemoteActions();
    this.wasmAllowed = record.wasm && isWasmLoaded();
    setWasmEnabled(this.wasmAllowed);
    showMenu(false);
    hideGameOver();
    this.startGame(record.options, true);
    this.updateNetworkStatus();
  }
  private downloadReplay(record: ReplayRecord): void {
    const blob = new Blob([serializeReplay(record)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = replayFileName(record);
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private restart(): void {
    if (this.minimapState) setMinimapVisible(this.minimapState, false);
    showMenu(true);
    hideGameOver();
  }

  // ===========================================================================
  // CAMPAIGN
  // ===========================================================================

  /** The sector campaign (BUILDLIST 56.13): a saved one if there is one, else a fresh sector; or a curated scenario (`?sector=first`, the FIRST SCENARIO button). */
  private showSectorScreen(scenario?: string, restart = false): void {
    const host = {
      startBattle: (setup: MatchSetup, humanTeam: number | null) => {
        hideSectorScreen();
        hideGameOver();
        this.startFromSetup(setup, { humanTeam, visibility: humanTeam === null ? 'free' : 'fog' });
        // decision 111: a sector battle can be left part-way (the in-game settings overlay, ESC)
        const leave = document.getElementById('btn-ingame-leave-sector');
        if (leave) leave.style.display = '';
      },
      changed: () => renderSectorScreen(),
    };
    const saved = scenario ? null : SectorSession.loadSaved(host, resolveBattleInWorker);
    if (scenario) this.sectorSession = SectorSession.forScenario(scenario, host, resolveBattleInWorker, restart);
    else if (saved instanceof SectorSession) this.sectorSession = saved;
    else {
      this.sectorSession = SectorSession.fresh(`sector-${Date.now()}`, host, resolveBattleInWorker, sectorOptions(rulesOf(loadSectorRules())));
      // R6: say why the saved sector is gone before the new one's first save replaces it
      if (saved) this.sectorSession.status = `Your saved sector could not be loaded — ${saved.error} Saving this new sector will replace it.`;
    }
    showMenu(false);
    hideGameOver();
    showSectorScreen(this.sectorSession, this.sectorCallbacks());
  }

  /** An action battle's host holds the keyboard and mouse, and the layer-0 views hold the scene: release them before the sector screen. */
  private releaseBattleViews(): void {
    this.actionHost?.dispose();
    this.actionHost = null;
    this.layer0Views?.dispose();
    this.layer0Views = null;
    this.topoMap?.dispose();
    this.topoMap = null;
    this.droneTablet?.cancel();
    this.droneFeed?.dispose(); this.droneFeed = null;
    this.droneTablet = null;
    this.fineGround?.dispose();
    this.fineGround = null;
    setGroundLift(0);
    this.hitShapes?.dispose();
    this.hitShapes = null;
    this.groundScatter?.dispose();
    this.groundScatter = null;
    this.disposeLitter();   // 67.61
    const leave = document.getElementById('btn-ingame-leave-sector');
    if (leave) leave.style.display = 'none';
    const wd = document.getElementById('btn-ingame-withdraw');
    if (wd) wd.style.display = 'none';
  }

  /** Decision 111: leave a sector battle part-way — it goes on in the sector's model from where the match stands. */
  /** X11: a unit asks the command layer for fire on a point; the answer (a mission for a gun, or the reason it cannot) goes to the log. */
  callInFire(unit: PlayerState, planetIndex: number, surfaceDir: Vec3): boolean {
    const sq = squadOf(this.match.state, unit.id);
    if (!sq) { logMessage(`${unit.name} has no squad to call in through`); return false; }
    const r = this.match.apply(this.match.stamp(unit.id, { kind: 'order', team: unit.teamIndex, to: { squad: sq.id }, tier: 'task', order: { kind: 'callIn', planetIndex, surfaceDir }, by: 'human' }));   // (69.72 step 5, DECISIONS 304: the player's, tagged)
    const last = this.match.state.callIns?.at(-1);
    if (this.shouldLogForPlayer(unit.id)) logMessage(last ? callInText(this.match.state, last) : r.ok ? `${unit.name} calls in fire` : `${unit.name} calls in fire — refused: ${r.reason}`);
    this.gameState = this.match.state;
    return r.ok;
  }

  /** X22: the team's seat orders every one of its squads to withdraw — head for the exit and leave (a task through apply). */
  withdrawTeam(team: number, by: 'human' | 'setup'): number {   // (69.72 step 5, DECISIONS 304: the player's button, or the setup's scripted withdrawal)
    let n = 0;
    for (const sq of (this.match.state.squads ?? []).filter(q => q.team === team)) {
      if (this.match.apply(this.match.stamp(seatActor(team), { kind: 'order', team, to: { squad: sq.id }, tier: 'task', order: { kind: 'withdraw' }, by })).ok) n++;
    }
    return n;
  }

  private leaveSectorBattle(): void {
    const session = this.sectorSession;
    if (!session?.busy) return;
    showInGameSettings(false);
    hideGameOver();
    this.releaseBattleViews();
    const seconds = this.gameState.config.clock === 'action' ? this.match.actionTick * PHYS_DT : this.gameState.turnNumber * 15;   // a turn-clock battle: ~15 s a round (decision 110's placeholder)
    session.leaveBattle(this.gameState, seconds);
    showSectorScreen(session, this.sectorCallbacks());
  }

  private sectorCallbacks(): { onBack: () => void; onNew: () => void; onScenario: (id: string, restart: boolean) => void } {
    return {
      onScenario: (id, restart) => this.showSectorScreen(id, restart),
      onBack: () => { hideSectorScreen(); this.sectorSession = null; showMenu(true); },
      onNew: () => {
        if (!this.sectorSession) return;
        const host = this.sectorSession['host'];
        this.sectorSession = SectorSession.fresh(`sector-${Date.now()}`, host, resolveBattleInWorker, sectorOptions(rulesOf(loadSectorRules())));
        showSectorScreen(this.sectorSession, this.sectorCallbacks());
      },
    };
  }

  private showCampaignScreen(): void {
    // Load from storage or generate new
    this.campaignState = loadCampaignFromStorage()
      ?? generateCampaignGraph(`campaign-${Date.now()}`);
    saveCampaignToStorage(this.campaignState);

    showMenu(false);
    hideGameOver();
    hideCampaignEndScreen();
    this.openCampaignMap();
  }

  private startCampaignBattle(nodeId: number, selectedUnitIds?: number[]): void {
    if (!this.campaignState) return;
    const node = this.campaignState.nodes.find(n => n.id === nodeId);
    if (!node || node.status !== 'available') return;
    if (!this.campaignState.roster.some(u => u.alive && u.ap > 0)) return;

    this.activeCampaignNodeId = nodeId;
    this.activeCampaignUnitIds = selectedUnitIds ?? null;

    // Build campaign loadout from inventory
    const loadout = buildCampaignLoadout(this.campaignState.inventory);
    this.preBattleLoadout = loadout;

    // Drain ammo/items from inventory (sent to battle)
    // Stash pre-battle ammo so a reload mid-battle can restore it
    const stashAmmo = { ...this.campaignState.inventory.ammoStock };
    const stashItems = { ...this.campaignState.inventory.items };
    this.campaignState = {
      ...this.campaignState,
      inventory: {
        ...this.campaignState.inventory,
        ammoStock: {},
        items: {},
      },
    };
    saveCampaignToStorage(this.campaignState, {
      battleStashAmmo: stashAmmo,
      battleStashItems: stashItems,
      activeBattleNodeId: nodeId,
    });

    const bridge = battleConfigToBridge(node.battleConfig, this.campaignState.playerMoney);
    // Set allowed weapons to owned weapons only
    bridge.allowedWeapons = [...this.campaignState.inventory.ownedWeapons];

    // Build MenuOptions from bridge
    const options = this.buildCampaignMenuOptions(bridge, selectedUnitIds);
    // Apply campaign loadout for human team, generate bot loadouts for enemy teams
    const playerLoadouts: PlayerLoadout[] = [loadout];
    for (let t = 0; t < bridge.botTeams; t++) {
      const personality = BOT_PRESETS[bridge.botPersonality] ?? BOT_PRESETS.balanced;
      const rng = new SeededRandom(`${bridge.seed}-botshop-${t + 1}`);
      const botResult = computeBotLoadout(bridge.botCbpBudget, personality, rng, bridge.gameMode, bridge.allowedWeapons as WeaponKey[] | undefined);
      playerLoadouts.push({
        ammo: {
          standard: null,
          heavy: botResult.purchases.heavy ?? 0,
          cluster: botResult.purchases.cluster ?? 0,
          nuke: botResult.purchases.nuke ?? 0,
          probe: botResult.purchases.probe ?? 0,
          punch: botResult.purchases.punch ?? 0,
          antigrav: botResult.purchases.antigrav ?? 0,
          gravBomb: botResult.purchases.gravBomb ?? 0,
          tether: botResult.purchases.tether ?? 0,
          terraformer: botResult.purchases.terraformer ?? 0,
          emp: botResult.purchases.emp ?? 0,
          drill: botResult.purchases.drill ?? 0,
          fire: botResult.purchases.fire ?? 0,
        },
        upgrades: {},
        items: {},
      });
    }
    options.playerLoadouts = playerLoadouts;

    hideCampaignMap();
    this.startGame(options);
  }

  private buildCampaignMenuOptions(bridge: ReturnType<typeof battleConfigToBridge>, selectedUnitIds?: number[]): MenuOptions {
    // Use roster for human team (team 0)
    const roster = this.campaignState?.roster ?? [];
    // Filter to selected units if provided, otherwise fall back to all alive
    const selectedUnits = selectedUnitIds
      ? roster.filter(u => selectedUnitIds.includes(u.id) && u.alive)
      : getAliveUnits(roster);

    // Human team unit count = selected units
    const humanUnitCount = selectedUnits.length;

    // Per-team names and indices (indexed by team slot, not per-player)
    const playerNames: string[] = [selectedUnits[0]?.name ?? 'Commander'];
    const teamIndices: number[] = [0];

    for (let t = 0; t < bridge.botTeams; t++) {
      playerNames.push(''); // use default team name
      teamIndices.push(t + 1);
    }

    // Build unitSlots for team 0 from selected units (so bots get correct skill/personality)
    const team0Slots: import('./ui/menu').UnitSlotConfig[] = selectedUnits.map((unit, idx) => ({
      type: idx === 0 ? 'player' as const : 'bot' as const,
      name: unit.name,
      difficulty: unit.skillKey,
      personality: unit.personalityKey,
      fitnessLevel: unit.fitnessLevel ?? 0,
      jumpLevel: unit.jumpLevel ?? 0,
    }));
    const unitSlots: import('./ui/menu').UnitSlotConfig[][] = [team0Slots];

    // Add enemy bot team slots
    for (let t = 0; t < bridge.botTeams; t++) {
      const enemySlots: import('./ui/menu').UnitSlotConfig[] = [];
      for (let u = 0; u < bridge.botUnitsPerTeam; u++) {
        enemySlots.push({
          type: 'bot' as const,
          name: '',
          difficulty: bridge.botDifficulty,
          personality: bridge.botPersonality,
        });
      }
      unitSlots.push(enemySlots);
    }

    return {
      numPlayers: 1, // always 1 human player
      numBots: (humanUnitCount - 1) + bridge.numBots, // roster bots + enemy bots
      numPlanets: bridge.numPlanets,
      numStars: bridge.numStars,
      numWormholePairs: bridge.numWormholePairs,
      seed: bridge.seed,
      playerNames,
      teamIndices,
      cbpBudget: bridge.cbpBudget,
      botCbpBudget: bridge.botCbpBudget,
      playerLoadouts: [],
      botDifficulty: bridge.botDifficulty,
      botPersonality: bridge.botPersonality,
      botOverrides: [],
      unitSlots,
      numTeams: bridge.numTeams,
      humanTeams: bridge.humanTeams,
      botTeams: bridge.botTeams,
      unitsPerTeam: humanUnitCount,
      botUnitsPerTeam: bridge.botUnitsPerTeam,
      turnOrderMode: 'alternating',
      friendlyFire: 50,
      datalinkedLOS: true,
      playerFuel: undefined,
      botFogEnabled: true,
      adaptiveBotPersonality: true,
      hideEnemyDeploy: true,
      hazardDensity: bridge.hazardDensity,
      ruinDensity: bridge.ruinDensity,
      floraDensity: 'some',
      planetSize: bridge.planetSize,
      mapLayout: bridge.mapLayout,
      starType: bridge.starType,
      systemPreset: 'custom',
      gameMode: bridge.gameMode,
      turnLimit: undefined,
      allowedWeapons: bridge.allowedWeapons as WeaponKey[] | undefined,
      fallDamageMultiplier: 1,
      visibility: 'fog',
      previewQuality: CAMPAIGN_BASE_PREVIEW_QUALITY,
      gearUpgrades: this.campaignState?.inventory.gearUpgrades,
    };
  }

  private returnToCampaignMap(won: boolean): void {
    if (!this.campaignState || this.activeCampaignNodeId === null) return;

    // Return unused ammo/items to inventory
    if (this.preBattleLoadout && this.gameState) {
      const humanPlayer = this.gameState.players.find(p => !p.isBot && p.teamIndex === 0);
      if (humanPlayer) {
        this.campaignState = {
          ...this.campaignState,
          inventory: returnUnusedInventory(
            this.campaignState.inventory,
            this.preBattleLoadout,
            humanPlayer,
          ),
        };
      }
    }
    this.preBattleLoadout = null;

    // Permadeath: identify dead team-0 players and mark roster units dead
    if (this.campaignState.permadeath && this.gameState) {
      const deployedIds = this.activeCampaignUnitIds ?? [0];
      const deadTeam0 = this.gameState.players
        .filter(p => p.teamIndex === 0 && !p.alive)
        .map(p => deployedIds[p.unitIndex] ?? p.unitIndex);
      if (deadTeam0.length > 0) {
        this.campaignState = {
          ...this.campaignState,
          roster: markUnitsDead(this.campaignState.roster, deadTeam0, true),
        };
      }
    }

    if (won) {
      const systemHealth = calculateSystemHealth(
        this.gameState.planets,
        this.gameState.hazards,
        this.gameState.floraDestruction ?? undefined,
      );
      this.campaignState = completeBattle(this.campaignState, this.activeCampaignNodeId, this.activeCampaignUnitIds ?? undefined, systemHealth);
    } else {
      this.campaignState = loseBattle(this.campaignState, this.activeCampaignNodeId, this.activeCampaignUnitIds ?? undefined);
    }

    this.activeCampaignNodeId = null;
    this.activeCampaignUnitIds = null;
    saveCampaignToStorage(this.campaignState);

    hideGameOver();
    this.clearScene();
    this.checkCampaignEnd() || this.openCampaignMap();
  }

  private advanceCampaignWave(): void {
    if (!this.campaignState) return;
    this.campaignState = advanceWave(this.campaignState);
    saveCampaignToStorage(this.campaignState);

    if (this.checkCampaignEnd()) return;
    updateCampaignMap(this.campaignState);
  }

  /** Returns true if the campaign ended (shows end screen). */
  private checkCampaignEnd(): boolean {
    if (!this.campaignState) return false;
    const result = getCampaignResult(this.campaignState);
    if (result.outcome === 'ongoing') return false;

    const summary = getCampaignSummary(this.campaignState);
    clearCampaignStorage();
    this.campaignState = null;

    showCampaignEndScreen(result, summary, {
      onMenu: () => {
        showMenu(true);
      },
      onRestart: () => {
        this.campaignState = generateCampaignGraph(`campaign-${Date.now()}`);
        saveCampaignToStorage(this.campaignState);
        this.openCampaignMap();
      },
    });
    return true;
  }

  private openCampaignMap(): void {
    if (!this.campaignState) return;
    showCampaignMap(this.campaignState, {
      onDeploy: (nodeId, selectedUnitIds) => this.startCampaignBattle(nodeId, selectedUnitIds),
      onAdvanceWave: () => this.advanceCampaignWave(),
      onBack: () => this.exitCampaign(),
      onRestart: () => this.restartCampaign(),
      onStore: () => this.openCampaignStore(),
      onTeam: () => this.openCampaignTeam(),
      onGear: () => this.openCampaignGear(),
    });
  }

  private openCampaignStore(): void {
    if (!this.campaignState) return;
    showCampaignStore(this.campaignState, {
      onPurchaseWeapon: (weapon) => {
        if (!this.campaignState) return;
        const result = purchaseWeapon(this.campaignState, weapon);
        if (result) {
          this.campaignState = result;
          saveCampaignToStorage(this.campaignState);
          updateCampaignStore(this.campaignState);
          updateCampaignMap(this.campaignState);
        }
      },
      onPurchaseAmmo: (weapon, qty) => {
        if (!this.campaignState) return;
        const result = purchaseAmmo(this.campaignState, weapon, qty);
        if (result) {
          this.campaignState = result;
          saveCampaignToStorage(this.campaignState);
          updateCampaignStore(this.campaignState);
          updateCampaignMap(this.campaignState);
        }
      },
      onPurchaseItem: (item, qty) => {
        if (!this.campaignState) return;
        const result = purchaseItem(this.campaignState, item, qty);
        if (result) {
          this.campaignState = result;
          saveCampaignToStorage(this.campaignState);
          updateCampaignStore(this.campaignState);
          updateCampaignMap(this.campaignState);
        }
      },
      onClose: () => {
        hideCampaignStore();
      },
    });
  }

  private openCampaignGear(): void {
    if (!this.campaignState) return;
    showCampaignGear(this.campaignState, {
      onPurchaseGear: (gearKey) => {
        if (!this.campaignState) return;
        const result = purchaseGearUpgrade(this.campaignState, gearKey);
        if (result) {
          this.campaignState = result;
          saveCampaignToStorage(this.campaignState);
          updateCampaignGear(this.campaignState);
          updateCampaignMap(this.campaignState);
        }
      },
      onClose: () => {
        hideCampaignGear();
      },
    });
  }

  private openCampaignTeam(): void {
    if (!this.campaignState) return;
    showCampaignTeam(this.campaignState, {
      onRename: (unitId, newName) => {
        if (!this.campaignState) return;
        this.campaignState = {
          ...this.campaignState,
          roster: renameUnit(this.campaignState.roster, unitId, newName),
        };
        saveCampaignToStorage(this.campaignState);
        updateCampaignTeam(this.campaignState);
      },
      onUpgradeAp: (unitId) => {
        if (!this.campaignState) return;
        const result = purchaseApUpgrade(this.campaignState, unitId);
        if (!result) return;
        this.campaignState = result;
        saveCampaignToStorage(this.campaignState);
        updateCampaignTeam(this.campaignState);
        updateCampaignMap(this.campaignState);
      },
      onUpgradeSkill: (unitId) => {
        if (!this.campaignState) return;
        const result = purchaseSkillUpgrade(this.campaignState, unitId);
        if (!result) return;
        this.campaignState = result;
        saveCampaignToStorage(this.campaignState);
        updateCampaignTeam(this.campaignState);
        updateCampaignMap(this.campaignState);
      },
      onUpgradeFitness: (unitId) => {
        if (!this.campaignState) return;
        const result = purchaseFitnessUpgrade(this.campaignState, unitId);
        if (!result) return;
        this.campaignState = result;
        saveCampaignToStorage(this.campaignState);
        updateCampaignTeam(this.campaignState);
        updateCampaignMap(this.campaignState);
      },
      onUpgradeJump: (unitId) => {
        if (!this.campaignState) return;
        const result = purchaseJumpUpgrade(this.campaignState, unitId);
        if (!result) return;
        this.campaignState = result;
        saveCampaignToStorage(this.campaignState);
        updateCampaignTeam(this.campaignState);
        updateCampaignMap(this.campaignState);
      },
      onClose: () => {
        hideCampaignTeam();
      },
    });
  }

  private exitCampaign(): void {
    hideCampaignMap();
    this.campaignState = null;
    this.activeCampaignNodeId = null;
    this.activeCampaignUnitIds = null;
    showMenu(true);
  }

  private restartCampaign(): void {
    clearCampaignStorage();
    hideCampaignMap();
    this.campaignState = generateCampaignGraph(`campaign-${Date.now()}`);
    saveCampaignToStorage(this.campaignState);
    this.openCampaignMap();
  }

  private clearScene(): void {
    this.actionHost?.dispose();
    this.actionHost = null;
    this.layer0Views?.dispose();
    this.layer0Views = null;
    this.topoMap?.dispose();
    this.topoMap = null;
    this.droneTablet?.cancel();
    this.droneFeed?.dispose(); this.droneFeed = null;
    this.missileCam?.release();
    this.droneTablet = null;
    this.fineGround?.dispose();
    this.fineGround = null;
    setGroundLift(0);
    this.hitShapes?.dispose();
    this.hitShapes = null;
    this.groundScatter?.dispose();
    this.groundScatter = null;
    this.disposeLitter();   // 67.61
    // Remove planet/star/wormhole meshes
    for (const [, meshData] of this.sceneObjs.planetMeshes) {
      removeFromScene(this.sceneObjs.scene, meshData.mesh);
      if (meshData.glow) removeFromScene(this.sceneObjs.scene, meshData.glow);
      if (meshData.corona) removeFromScene(this.sceneObjs.scene, meshData.corona);
      if (meshData.star) removeFromScene(this.sceneObjs.scene, meshData.star.group);
      disposePlanetClouds(this.sceneObjs.scene, meshData);
      disposeSurfaceLook(meshData);   // 71.8
      disposeMountainStyle(meshData);   // 388
      if (meshData.ring) removeFromScene(this.sceneObjs.scene, meshData.ring);
      if (meshData.light) this.sceneObjs.scene.remove(meshData.light);
      if (meshData.clickSphere) removeFromScene(this.sceneObjs.scene, meshData.clickSphere);
    }
    this.sceneObjs.planetMeshes.clear();

    // Remove player meshes
    for (const [, playerMesh] of this.sceneObjs.playerMeshes) {
      disposePlayerMesh(this.sceneObjs.scene, playerMesh);
    }
    this.sceneObjs.playerMeshes.clear();

    this.disposeWindProps();   // 67.36
    this.wispDrifts.clear();
    // Remove structure meshes
    for (const [, structMesh] of this.sceneObjs.structureMeshes) {
      disposeStructureMesh(this.sceneObjs.scene, structMesh);
    }
    this.sceneObjs.structureMeshes.clear();

    // Remove projectile
    if (this.sceneObjs.projectileMesh) {
      disposeProjectileMesh(this.sceneObjs.scene, this.sceneObjs.projectileMesh);
      this.sceneObjs.projectileMesh = null;
    }

    // Remove trail
    if (this.sceneObjs.trailLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.trailLine);
      this.sceneObjs.trailLine = null;
    }

    // Remove preview
    if (this.sceneObjs.previewLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.previewLine);
      if (this.gravityField) setGravityFieldPreview(this.gravityField, null);
      this.sceneObjs.previewLine = null;
    }

    // Clear effects
    for (const e of this.effects) {
      disposeExplosion(this.sceneObjs.scene, e);
    }
    for (const f of this.impactFades) disposeImpactFade(this.sceneObjs.scene, f);
    this.impactFades = [];
    if (this.roundPlay) { for (const [, v] of this.roundPlay.visuals) { removeFromScene(this.sceneObjs.scene, v.trail); disposeProjectileMesh(this.sceneObjs.scene, v.mesh); disposeEngineFlame(this.sceneObjs.scene, v.flame); } this.roundPlay = null; }
    this.clearPlannedPaths();
    this.effects = [];

    // Clear hazard effects
    for (const e of this.eruptionEffects) disposeEruptionEffect(this.sceneObjs.scene, e);
    this.eruptionEffects = [];
    for (const e of this.shatterEffects) disposeShatterEffect(this.sceneObjs.scene, e);
    this.shatterEffects = [];
    for (const e of this.rippleEffects) disposeRippleEffect(this.sceneObjs.scene, e);
    this.rippleEffects = [];
    this.boostedGeyserIds.clear();
    this.playerBoostedGeysers.clear();

    // Clear hazard meshes
    for (const hm of this.hazardMeshes) {
      disposeHazardMesh(this.sceneObjs.scene, hm);
    }
    this.hazardMeshes = [];

    // Clear ruin meshes
    for (const rm of this.ruinMeshes) {
      disposeRuinMesh(this.sceneObjs.scene, rm);
    }
    this.ruinMeshes = [];

    // Clear flora meshes
    for (const fm of this.floraMeshes) disposeFloraMesh(this.sceneObjs.scene, fm);
    this.floraMeshes = [];
    this.treeStands?.dispose();
    this.treeStands = null;
    this.standSkirts?.dispose();
    this.standSkirts = null;
    this.farField?.dispose();
    this.farField = null;
    this.systemSky?.dispose();
    this.systemSky = null;
    this.auroras?.dispose();
    this.auroras = null;
    this.nearField = null;
    for (const g of this.craterDecals) { this.sceneObjs.scene.remove(g); disposeObjectTree(g); }   // 63.5 step 5
    this.craterDecals = [];

    // Clear rock meshes
    for (const rm of this.rockMeshes) disposeRockMesh(this.sceneObjs.scene, rm);
    this.rockMeshes = [];
    this.rockMerge?.dispose();
    this.rockMerge = null;

    // Clear environmental effect meshes
    for (const em of this.effectMeshes) disposeEnvironmentalEffectMesh(this.sceneObjs.scene, em);
    this.effectMeshes = [];

    // Clear impact dust
    for (const d of this.impactDustEffects) disposeImpactDust(this.sceneObjs.scene, d);
    this.impactDustEffects = [];

    // Clear drone delivery
    if (this.activeDroneDelivery) {
      disposeDroneDelivery(this.sceneObjs.scene, this.activeDroneDelivery);
      this.activeDroneDelivery = null;
    }

    // Clear respawn drop cans
    for (const [, effect] of this.respawnDropCans) {
      disposeDropCanEffect(effect, false);
    }
    this.respawnDropCans.clear();

    // Clear ferry pickup effects
    for (const effect of this.ferryPickupEffects) {
      disposeFerryPickupEffect(this.sceneObjs.scene, effect);
    }
    this.ferryPickupEffects = [];
    this.ferryPickupWinner = null;

    // Nothing from the old game may fire into the new one
    this.clock.clear();

    // Clear deploy phase effects and crashed drop can scenery
    this.deploy.reset();
    for (const [, group] of this.crashedDropCanMeshes) {
      disposeObjectTree(group);
      group.parent?.remove(group);
    }
    this.crashedDropCanMeshes.clear();

    // Clear carryable / collectible / drill site meshes
    for (const [, cm] of this.carryableMeshes) disposeCarryableMesh(this.sceneObjs.scene, cm);
    this.carryableMeshes.clear();
    for (const [, cm] of this.collectibleMeshes) disposeCollectibleMesh(this.sceneObjs.scene, cm);
    this.collectibleMeshes.clear();
    for (const [, dm] of this.drillSiteMeshes) disposeDrillSiteMesh(this.sceneObjs.scene, dm);
    this.drillSiteMeshes.clear();
    for (const [, ind] of this.carrierIndicators) {
      ind.geometry.dispose();
      (ind.material as THREE.Material).dispose();
      ind.parent?.remove(ind);
    }
    this.carrierIndicators.clear();

    // Clear mode-specific markers
    this.disposeArtifactSiteMarker();
    this.disposeSignalSourceMarker();
    this.disposeDepotMeshes();
    this.disposeHomeWaypointMarker();

    // Clear planted flag meshes
    for (const [, pfm] of this.sceneObjs.plantedFlagMeshes) {
      disposePlantedFlagMesh(this.sceneObjs.scene, pfm);
    }
    this.sceneObjs.plantedFlagMeshes.clear();

    // Clear ghost meshes
    disposeGhostMeshes(this.sceneObjs.scene, this.sceneObjs.ghostMeshes);
    this.sceneObjs.ghostMeshes = [];
    if (this.gravityField) { disposeGravityField(this.sceneObjs.scene, this.gravityField); this.gravityField = null; this.updateGravityFieldToggles(); }
    this.clearThreatCues();
    disposeStructureGhostMeshes(this.sceneObjs.scene, this.sceneObjs.structureGhostMeshes);
    this.sceneObjs.structureGhostMeshes = [];
    disposeExposureMeshes(this.sceneObjs.scene, this.sceneObjs.exposureMeshes);
    this.sceneObjs.exposureMeshes = [];
    this.spottedCooldowns.clear();
    this.aimMemory.clear();

    // Remove jump preview line
    if (this.jumpPreviewLine) {
      removeFromScene(this.sceneObjs.scene, this.jumpPreviewLine);
      this.jumpPreviewLine = null;
    }

    // Hide aim indicator
    if (this.sceneObjs.aimIndicator) {
      hideAimIndicator(this.sceneObjs.aimIndicator);
    }

    // Reset jump follow state
    this.jumpFollowPlayerId = null;

    // Terminate bot worker (will be lazily recreated on next bot turn)
    this.botWorker.terminate();

    // Stop ambient and music audio
    if (this.weatherSound) { this.weatherSound.cleanup(); this.weatherSound = null; }
    if (this.ambientManager) {
      this.ambientManager.cleanup();
      this.ambientManager = null;
    }
    if (this.musicManager) {
      resetConductor();
      this.musicManager.solo('calm', 2.5);
    }
    if (this.hazardEmitterManager) {
      this.hazardEmitterManager.cleanup();
      this.hazardEmitterManager = null;
    }

    // Clear all trails (current + all historical rounds)
    for (const trail of this.persistedTrails) this.removeTrailLine(trail);
    this.persistedTrails = [];
    for (const round of this.historicalTrails) {
      for (const trail of round) this.removeTrailLine(trail);
    }
    this.historicalTrails = [];
    this.lastTrailPromotionTurn = -1;
    this.clearLiveTrailLines();
  }

  // ===========================================================================
  // TURN PHASES
  // ===========================================================================

  /**
   * 64.6 step 2a: the current unit's window opens without an advance (the first turn, after a round or a batch) — the
   * Match begins its turn and hops on past a dead or hazard-killed unit as at every boundary; this host presents each step.
   */
  private startAimPhase(): void {
    this.followPass(this.match.openWindow(this.simPresenter));
  }

  /** A boundary's answer, walked (as the harness walks it): game over, the pickup prompt, a round or a batch — or a window. */
  private followPass(pass: TurnPass): void {
    if (pass.next === 'gameOver') { this.handleGameOver(); return; }
    if (pass.next === 'pickup' || pass.next === 'stalled') return;
    if (this.networkSync && pass.hops.some(h => h.advance)) this.updateNetworkStatus();
    if (pass.next === 'round') { this.startRound(); return; }       // everyone plans: the order wrapped
    if (pass.next === 'batch') { this.resolveBatch(); return; }     // control leaves the planning team
    if (pass.player) this.windowOpened(pass.player);
  }



  /** A unit's turn begins: its aim, the controls, the camera's ground, the ambience (presentation; 64.6 step 2a's `begun`). */
  private presentTurnStart(player: PlayerState): void {
    this.syncRuinClaimMarkers();   // claims ticked / broke during resolution

    // Create aim state with persisted values from player (the last shot), then
    // anything a local human dialled in last turn without firing
    this.aimState = createAimState();
    this.aimState.azimuth = player.lastAimAzimuth;
    this.aimState.elevation = player.lastAimElevation;
    this.aimState.power = player.lastAimPower;
    this.aimState.weaponIndex = WEAPON_KEYS.indexOf(player.selectedWeapon);
    if (this.aimState.weaponIndex < 0) this.aimState.weaponIndex = 0;
    const remembered = this.aimMemory.get(player.id);
    if (remembered && !player.isBot) {
      this.aimState.azimuth = remembered.azimuth;
      this.aimState.elevation = remembered.elevation;
      this.aimState.power = remembered.power;
      this.aimState.weaponIndex = remembered.weaponIndex;
    }

    // If current weapon is out of ammo or banned, switch to one with ammo
    const currentKey = WEAPON_KEYS[this.aimState.weaponIndex];
    const currentAmmo = player.loadout.ammo[currentKey];
    const allowedWpns = this.gameState.config.allowedWeapons;
    const isBanned = allowedWpns && currentKey !== 'standard' && !allowedWpns.includes(currentKey);
    if (isBanned || (currentAmmo !== null && currentAmmo !== undefined && currentAmmo <= 0)) {
      for (let i = 0; i < WEAPON_KEYS.length; i++) {
        const key = WEAPON_KEYS[i];
        if (allowedWpns && key !== 'standard' && !allowedWpns.includes(key)) continue;
        const ammo = player.loadout.ammo[key];
        if (ammo === null || ammo === undefined || ammo > 0) {
          this.aimState.weaponIndex = i;
          break;
        }
      }
    }

    // Update power slider to match persisted value, then the stabilizer cap for this unit
    const slider = document.getElementById('power-slider') as HTMLInputElement | null;
    if (slider) {
      slider.value = String(player.lastAimPower);
    }
    this.applyPowerCap(player);

    updateTurnInfo(this.gameState);
    updatePlayerBars(this.gameState);
    // Rebuild weapon buttons each turn (respects allowedWeapons for campaign)
    buildWeaponButtons((index) => {
      const p = this.gameState.players[this.gameState.currentPlayerIndex];
      this.aimState.weaponIndex = index;
      updateWeaponButtons(index, p?.loadout);
      this.updatePreview();
    }, player.loadout, this.gameState.config.allowedWeapons);
    updateWeaponButtons(this.aimState.weaponIndex, player.loadout);
    updateMovementDisplay(player.movementRemaining);
    updateMoveButton(false);
    updateJumpButton(false, player.movementRemaining >= JUMP_MIN_COST, player.jumppackFuel);
    updateFlagButton(canPlantFlag(player));
    updateFireButtonText(this.turnFlow !== 'perUnit' && !player.isBot ? queueLabel(this.batchQueue, player.teamIndex, this.gameState.players) : 'FIRE');
    setFireButtonEnabled(true);
    setUnqueueButton(false);
    this.refreshPlannedGhosts();
    if (!this.unqueueWired) {
      this.unqueueWired = true;
      document.getElementById('unqueue-btn')?.addEventListener('click', () => this.unqueueShot());
    }
    const fireBtn = document.getElementById('fire-btn');
    if (fireBtn) fireBtn.classList.remove('yield-mode');
    const yieldBtn = document.getElementById('yield-btn');
    if (yieldBtn) yieldBtn.classList.remove('yield-active');
    const isLocalHumanTurn = !player.isBot && !this.isRemoteTurn();
    showControlsBar(isLocalHumanTurn);
    if (this.minimapState) setMinimapVisible(this.minimapState, true);
    if (isLocalHumanTurn) turnStart();

    // Update aim display with persisted values
    updateAimDisplay(this.aimState.azimuth, this.aimState.elevation, this.aimState.power);
    updateAimSphere(this.aimState.azimuth, this.aimState.elevation, this.aimState.power);

    // Hide movement ring when starting aim phase
    if (this.sceneObjs.movementRing) {
      hideMovementRing(this.sceneObjs.movementRing);
    }
    this.moveTargetDir = null;
    this.walkAnim = null;
    this.jumpFollowPlayerId = null;
    this.buildStructureType = null;
    this.buildTargetDir = null;
    hideBuildMenu();
    showLeadershipPanel(false);
    showInventoryPanel(false);
    this.updateOrdersButtonVisibility();
    this.updateHomeWaypointMarker();
    this.updateModeActionButtons();

    // A new turn releases any pinned pivot and any shot framing; the actor is focused after the fog update below
    setMissileCam(this.cameraState, false);
    setManualPivot(this.cameraState, false);
    clearCameraFrame(this.cameraState);
    this.focusPlanetIndex = null;

    // Set megaplanet surface camera params
    const aimPlanet = this.gameState.planets[player.planetIndex];
    this.cameraState.surfacePlanetRadius = aimPlanet.radius;
    const norm = player.surfaceDir;
    const nLen = Math.sqrt(norm.x * norm.x + norm.y * norm.y + norm.z * norm.z);
    if (nLen > 0.001) {
      this.cameraState.surfaceNormal.set(norm.x / nLen, norm.y / nLen, norm.z / nLen);
    }

    // Update ambient biome stem for focused player's planet
    this.updateAmbientBiome();

    // Clear persisted trails at the start of the human player's turn
    if (!player.isBot) {
      this.clearPersistedTrails();
    }

  }

  /** The hazards near the unit whose turn began, presented (the Match applied them; 64.6 step 2a). */
  private presentProximity(proxResult: ProximityOutcome, player: PlayerState): void {
    if (proxResult.damage > 0) {
      logMessage(`${player.name} takes ${proxResult.damage} environmental damage`);
    }
    if (proxResult.launched) {
      logMessage(`${player.name} launched by geyser!`);
      const geyserMesh = this.sceneObjs.playerMeshes.get(player.id);
      if (geyserMesh) {
        geyserMesh.animCtx.state = 'airborne_knockback';
      }
    }
    updatePlayerBars(this.gameState);
    this.updatePlayerMeshes();
    // A unit the hazard killed passes the turn on (the Match hops to the next)
    if (proxResult.died) logMessage(`${player.name} destroyed by hazard!`);
  }

  /** The window is open for `player`: the melee and heal state, then the bot's thinking delay, a remote seat, or the controls. */
  private windowOpened(player: PlayerState): void {
    // Check for melee targets and heal availability
    this.checkMeleeTargets();
    this.updateHealButtonState();

    if (player.isBot) {
      // Bot takes its turn after a skill-dependent "thinking" delay
      const config = botConfigFor(player, this.gameState);
      const delay = 400 + (1 - config.skill) * 800; // fast=400ms, slow=1200ms
      this.showBotThinkingFor(player);
      this.clock.after(delay / 1000, () => {
        if (this.gameState.phase !== 'aiming') return;
        void this.botRunner.runTurn(player.id);
      });
    } else if (this.networkSync?.isRemoteHuman(player)) {
      // Remote human — disable controls, wait for their input
      showControlsBar(false);
      const waitEl = document.getElementById('net-waiting-turn');
      if (waitEl) {
        waitEl.textContent = this.replaying ? `REPLAY — ${player.name.toUpperCase()}` : `AWAITING ${player.name.toUpperCase()}...`;
        waitEl.style.display = '';
      }

      // Their actions arrive on the ordered queue; apply anything already waiting
      this.pumpRemoteActions();
    } else {
      this.updatePreview();
    }
  }

  private checkMeleeTargets(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];

    // Need enough movement to melee
    if (player.movementRemaining < MELEE_MOVEMENT_COST) {
      this.meleeTarget = null;
      updateMeleeButton(false);
      return;
    }

    const playerPos = getPlayerWorldPosition(player, this.gameState.planets);

    // Find closest enemy player on same planet within melee range
    let closestTarget: number | null = null;
    let closestDist = MELEE_RANGE;

    for (const other of this.gameState.players) {
      if (other.id === player.id) continue;
      if (!other.alive) continue;
      if (other.planetIndex !== player.planetIndex) continue;
      // Skip teammates when friendly fire is off
      if (this.gameState.config.friendlyFire === 0 && other.teamIndex === player.teamIndex) continue;

      const otherPos = getPlayerWorldPosition(other, this.gameState.planets);
      const dx = otherPos.x - playerPos.x;
      const dy = otherPos.y - playerPos.y;
      const dz = otherPos.z - playerPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < closestDist) {
        closestDist = dist;
        closestTarget = other.id;
      }
    }

    this.meleeTarget = closestTarget;

    // Update melee button visibility
    if (closestTarget !== null) {
      const target = this.gameState.players.find(p => p.id === closestTarget);
      updateMeleeButton(true, target?.name);
    } else {
      updateMeleeButton(false);
    }
  }

  /** Local melee button. */
  private tryMeleeAttack(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    hideInGameShop();
    hideBuildMenu();
    if (this.meleeTarget === null) return;
    this.performMelee(this.meleeTarget);
  }

  /** Punch a target — the one code path for local and remote melee. */
  private performMelee(targetId: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.movementRemaining < MELEE_MOVEMENT_COST) return;

    const target = this.gameState.players.find(p => p.id === targetId);
    if (!target || !target.alive) return;

    this.broadcastTurnInput({ playerId: player.id, action: 'melee', params: { targetId } });

    const visibleToViewer = this.shouldLogForPlayer(player.id);
    if (visibleToViewer) logMessage(`${player.name} punches ${target.name}!`);

    // Match: the punch
    const hit = this.turn(player, 'melee', { targetId }) as ReturnType<Match['melee']>;
    if (!hit.ok) return;
    const killed = hit.killed;

    if (visibleToViewer) logMessage(`${target.name} takes ${MELEE_DAMAGE} dmg`);
    this.spikeCombatIntensity(0.3);
    if (killed) {
      logMessage(`${target.name} destroyed!`);
      pushNotification({
        type: 'gcppa',
        message: matchMessage('death', { name: target.name }),
        sender: matchSender(),
        color: '#c8a84c',
      });
    }

    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
    updatePlayerBars(this.gameState);
    updateMovementDisplay(updatedPlayer.movementRemaining);
    this.updatePlayerMeshes();

    this.checkMeleeTargets();

    if (isGameOver(this.gameState)) {
      this.handleGameOver();
      return;
    }
  }

  // ===========================================================================
  // HEAL
  // ===========================================================================

  private hasAnyHealItem(player: PlayerState): boolean {
    const items = player.loadout.items;
    if (!items) return false;
    return ((items.healSmall ?? 0) > 0) || ((items.healLarge ?? 0) > 0);
  }

  private updateHealButtonState(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player || player.isBot || !player.alive) {
      updateHealButton(false, false);
      return;
    }
    const hasItems = this.hasAnyHealItem(player);
    const movementOk = player.movementRemaining >= HEAL_MOVEMENT_COST;
    const atFullHealth = player.health >= 100;

    const totalSmall = player.loadout.items?.healSmall ?? 0;
    const totalLarge = player.loadout.items?.healLarge ?? 0;
    const totalCount = totalSmall + totalLarge;

    if (hasItems) {
      const canUse = movementOk && !atFullHealth;
      const label = atFullHealth
        ? `HEAL x${totalCount} (K)`
        : `${chooseHealItem(player) === 'healLarge' ? 'Lg' : 'Sm'} Heal x${(player.loadout.items?.[chooseHealItem(player)!] ?? 0)} (K)`;
      updateHealButton(true, canUse, label);
    } else {
      updateHealButton(true, false, `HEAL (0) (K)`);
    }
  }

  /** Local heal button: use the best available heal item. */
  private tryHeal(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    if (!player.alive) return;
    if (player.health >= 100) return;

    hideInGameShop();
    hideBuildMenu();

    const itemKey = chooseHealItem(player);
    if (!itemKey) return;
    this.performHeal(itemKey);
  }

  // ===========================================================================
  // IN-GAME SHOP
  // ===========================================================================

  private toggleInGameShop(): void {
    if (this.activeDroneDelivery || this.respawnDropCans.size > 0) return;
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    if (isInGameShopOpen()) {
      hideInGameShop();
    } else {
      showInventoryPanel(false);
      showInGameShop(player, this.gameState.config.allowedWeapons);
    }
  }

  /** Local in-game shop confirm. */
  private confirmDelivery(): void {
    if (this.isRemoteTurn()) return;
    const purchases = getInGameShopPurchases();
    const fuelQty = getInGameShopFuelQty();
    const items = getInGameShopItemPurchases();
    this.performShop({ purchases, fuelQty, items });
  }

  /** Buy ammo, fuel and heal items — one code path for local and remote. */
  private performShop(params: ShopParams): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const purchases = params.purchases;
    const fuelQty = params.fuelQty ?? 0;
    const itemPurchases = params.items ?? {};

    // Match: ammo, fuel and heal items (items at 3× the shop price)
    const bought = this.turn(player, 'shop', params) as ReturnType<Match['shop']>;
    if (!bought.ok) {
      if (bought.reason === 'empty') hideInGameShop();
      else logMessage('Cannot afford delivery!');
      return;
    }
    const total = bought.total;
    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];

    const shopInput: TurnInput = { playerId: player.id, action: 'shop', params: { purchases, fuelQty, items: itemPurchases } };
    this.broadcastTurnInput(shopInput);

    logMessage(`${player.name} calls in a delivery (${total} ${cur()})`);

    hideInGameShop();
    updatePlayerBars(this.gameState);
    updateWeaponButtons(this.aimState.weaponIndex, updatedPlayer.loadout);
    this.updateHealButtonState();

    // Play drone delivery animation, then advance turn
    this.startDroneDelivery(updatedPlayer);
  }

  private cancelDelivery(): void {
    hideInGameShop();
  }

  private startDroneDelivery(player: PlayerState): void {
    this.droneBuyerId = player.id;
    const planet = this.gameState.planets[player.planetIndex];
    const worldPos = getPlayerWorldPosition(player, this.gameState.planets);
    const normal = getPlayerSurfaceNormal(player);

    const playerPos3 = new THREE.Vector3(worldPos.x, worldPos.y, worldPos.z);
    const planetPos3 = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z);
    const normal3 = new THREE.Vector3(normal.x, normal.y, normal.z);

    this.activeDroneDelivery = createDroneDelivery(
      this.sceneObjs.scene,
      playerPos3,
      planetPos3,
      normal3,
      planet.radius,
      this.gameState.planets,
    );
  }

  // ===========================================================================
  // LEADERSHIP PANEL
  // ===========================================================================

  private toggleLeadershipPanel(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;

    if (isLeadershipPanelVisible()) {
      showLeadershipPanel(false);
    } else {
      showInventoryPanel(false);
      this.refreshLeadershipPanel();
      showLeadershipPanel(true);
    }
  }

  private refreshLeadershipPanel(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player || player.isBot) return;

    // Build known enemies from fog (indexed by player array position, not id)
    const fogIndex = this.gameState.players.findIndex(p => p.id === player.id);
    const fog = fogIndex >= 0 ? this.gameState.fog[fogIndex] : undefined;
    const visibleIds = fog
      ? getTeamVisibleEnemies(player, this.gameState.players, this.gameState.planets, this.gameWasm)
      : [];
    const knownEnemies = this.gameState.players.filter(
      p => p.alive && p.teamIndex !== player.teamIndex &&
      (visibleIds.includes(p.id) || (fog?.ghosts.some(g => g.playerId === p.id)))
    );
    const ghosts = fog?.ghosts ?? [];

    const callbacks: LeadershipCallbacks = {
      onCommand: (botId, cmdType, params) => this.handleLeadershipCommand(botId, cmdType, params),
      onCancel: (botId) => this.handleLeadershipCancel(botId),
      onDismiss: () => showLeadershipPanel(false),
      onToggleHoldFire: (botId) => this.handleToggleHoldFire(botId),
    };

    updateLeadershipPanel(this.gameState, player.id, callbacks, knownEnemies, ghosts);
  }

  /** Leadership panel: build the directive and issue it. */
  private handleLeadershipCommand(
    botId: number,
    commandType: import('./types').BotCommandType,
    params?: {
      targetPlayerId?: number;
      targetPlanetIndex?: number;
      weaponKey?: import('./types').WeaponKey;
      holdPreset?: import('./ui/leadership').HoldPreset;
      holdTurns?: number;
    }
  ): void {
    if (this.isRemoteTurn()) return;

    // For holdPosition with waypoint: enter waypoint mode instead of issuing command
    if (commandType === 'holdPosition' && params?.holdPreset === 'waypoint') {
      const bot = this.gameState.players.find(p => p.id === botId);
      if (bot) {
        this.enterWaypointMode(botId, bot.planetIndex, params.holdTurns ?? 2);
      }
      return;
    }

    const cmd: CommandParams = { botId, commandType, ...params };
    if (commandType === 'holdPosition' && params?.holdPreset) {
      const bot = this.gameState.players.find(p => p.id === botId);
      if (bot) {
        cmd.surfaceDir = this.computeHoldSurfaceDir(bot, params.holdPreset);
        cmd.holdTurns = params.holdTurns ?? 2;
      }
    }
    this.performCommand(cmd);
  }

  /** Issue a team-leader directive to a bot — one code path for local and remote. */
  private performCommand(cmd: CommandParams): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const { botId, commandType, ...rest } = cmd;

    // Match: the order (influence, not override)
    const ordered = this.turn(player, 'command', cmd) as ReturnType<Match['command']>;
    if (!ordered.ok) {
      logMessage(ordered.reason);
      return;
    }
    this.broadcastTurnInput({ playerId: player.id, action: 'command', params: cmd });
    void rest;

    const bot = this.gameState.players.find(p => p.id === botId);
    const botName = bot?.name ?? 'Unit';
    let desc: string = commandType;
    if (commandType === 'fireAtTarget' && cmd.targetPlayerId !== undefined) {
      const t = this.gameState.players.find(p => p.id === cmd.targetPlayerId);
      desc = `engage hostile ${t?.name ?? 'unknown'}`;
    } else if (commandType === 'moveToPlanet' && cmd.targetPlanetIndex !== undefined) {
      desc = `relocate to Planet ${cmd.targetPlanetIndex}`;
    } else if (commandType === 'fireProbe') {
      desc = 'deploy recon probe';
    } else if (commandType === 'prioritizeEnemy' && cmd.targetPlayerId !== undefined) {
      const t = this.gameState.players.find(p => p.id === cmd.targetPlayerId);
      desc = `prioritize hostile ${t?.name ?? 'unknown'}`;
    } else if (commandType === 'holdPosition') {
      const presetLabel = cmd.holdPreset === 'waypoint' ? 'hold waypoint' :
        cmd.holdPreset === 'cover' ? 'take cover' :
        cmd.holdPreset === 'advance' ? 'advance forward' : 'dig in';
      desc = `${presetLabel} for ${cmd.holdTurns ?? 2} turns`;
    }
    logMessage(`ORDER QUEUED: ${botName} — ${desc}`);

    updatePlayerBars(this.gameState);
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
    this.refreshLeadershipPanel();
  }

  /**
   * Compute target surfaceDir for a holdPosition command based on preset.
   * - 'cover': antipodal from known enemies (hide behind planet)
   * - 'advance': toward known enemies
   * - 'hold': current position (stay put)
   */
  private computeHoldSurfaceDir(bot: PlayerState, preset: import('./ui/leadership').HoldPreset): import('./types').Vec3 {
    if (preset === 'hold') {
      return vecClone(bot.surfaceDir);
    }

    // Gather known enemy positions for this bot's team
    const enemies = this.gameState.players.filter(
      p => p.alive && p.teamIndex !== bot.teamIndex
    );

    if (enemies.length === 0) {
      return vecClone(bot.surfaceDir);
    }

    const planet = this.gameState.planets[bot.planetIndex];
    // Average enemy direction from planet center
    let avgX = 0, avgY = 0, avgZ = 0;
    for (const e of enemies) {
      const ePos = getPlayerWorldPosition(e, this.gameState.planets);
      const toEnemy = vecSub(ePos, planet.position);
      const dir = vecNormalize(toEnemy);
      avgX += dir.x; avgY += dir.y; avgZ += dir.z;
    }
    const avgDir = vecNormalize({ x: avgX / enemies.length, y: avgY / enemies.length, z: avgZ / enemies.length });

    if (preset === 'cover') {
      // Opposite of average enemy direction (hide behind planet)
      return vecNormalize({ x: -avgDir.x, y: -avgDir.y, z: -avgDir.z });
    } else {
      // 'advance': toward average enemy direction
      return avgDir;
    }
  }

  private handleLeadershipCancel(botId: number): void {
    const bot = this.gameState.players.find(p => p.id === botId);
    if (!bot || !this.input(bot, { kind: 'toggle', unitId: botId, toggle: 'cancelOrder' }).ok) return;
    logMessage(`Order cancelled for ${bot?.name ?? 'unit'}`);
    this.refreshLeadershipPanel();
  }

  private handleToggleHoldFire(botId: number): void {
    const bot = this.gameState.players.find(p => p.id === botId);
    if (!bot) return;
    const toggled = this.input(bot, { kind: 'toggle', unitId: botId, toggle: 'holdFire' }) as PlanResult<{ holdFire: boolean }>;
    if (!toggled.ok) return;
    const status = toggled.holdFire ? 'HOLD FIRE' : 'OPEN FIRE';
    const desc = toggled.holdFire ? 'weapons hold' : 'weapons free';
    logMessage(`${status} — ${bot.name} ${desc}`);
    this.refreshLeadershipPanel();
  }

  private enterWaypointMode(botId: number, planetIndex: number, holdTurns: number): void {
    // Save current camera target for return
    const prevTarget: Vec3 = {
      x: this.cameraState.target.x,
      y: this.cameraState.target.y,
      z: this.cameraState.target.z,
    };
    this.waypointMode = { botId, planetIndex, holdTurns, prevCameraTarget: prevTarget };
    this.waypointTargetDir = null;

    // Close leadership panel, focus camera on bot's planet
    showLeadershipPanel(false);
    const planet = this.gameState.planets[planetIndex];
    setCameraTarget(this.cameraState, planet.position);

    logMessage('Click to set waypoint on planet surface. Right-click or Escape to cancel.');
  }

  private exitWaypointMode(cancelled: boolean): void {
    if (!this.waypointMode) return;

    // Return camera to previous target
    setCameraTarget(this.cameraState, this.waypointMode.prevCameraTarget);

    // Clean up waypoint marker
    if (this.waypointMarker) {
      this.sceneObjs.scene.remove(this.waypointMarker);
      this.waypointMarker.geometry.dispose();
      (this.waypointMarker.material as THREE.Material).dispose();
      this.waypointMarker = null;
    }

    this.waypointMode = null;
    this.waypointTargetDir = null;

    if (cancelled) {
      logMessage('Waypoint selection cancelled.');
    }

    // Reopen leadership panel
    showLeadershipPanel(true);
    this.refreshLeadershipPanel();
  }

  private commitWaypoint(): void {
    if (!this.waypointMode || !this.waypointTargetDir) return;
    const { botId, holdTurns } = this.waypointMode;
    const before = this.gameState;
    this.performCommand({ botId, commandType: 'holdPosition', surfaceDir: this.waypointTargetDir, holdTurns, holdPreset: 'waypoint' });
    this.exitWaypointMode(this.gameState === before);
  }

  private updateOrdersButtonVisibility(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const hasBotTeammates = !player.isBot && player.alive &&
      getBotTeammates(player, this.gameState).length > 0;
    showOrdersButton(hasBotTeammates);
  }

  // ===========================================================================
  // INVENTORY PANEL
  // ===========================================================================

  private toggleInventoryPanel(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;

    if (isInventoryPanelVisible()) {
      showInventoryPanel(false);
    } else {
      // Mutual exclusion: close other panels
      hideInGameShop();
      hideBuildMenu();
      showLeadershipPanel(false);
      this.refreshInventoryPanel();
      showInventoryPanel(true);
    }
  }

  private refreshInventoryPanel(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player || player.isBot) return;

    const callbacks: InventoryCallbacks = {
      onDropCarryable: (carryableId) => this.tryDropSpecificObject(carryableId),
      onUseHeal: (itemKey) => this.tryHealWithItem(itemKey),
      onDismiss: () => showInventoryPanel(false),
    };

    updateInventoryPanel(this.gameState, player.id, callbacks);
  }

  /** Inventory panel: drop a specific carried object. */
  private tryDropSpecificObject(carryableId: number): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    this.performDrop(carryableId);
  }

  /** Inventory panel: use a specific heal item. */
  private tryHealWithItem(itemKey: ItemKey): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    this.performHeal(itemKey);
  }

  /** Use a heal item — one code path for local (button + inventory) and remote. */
  private performHeal(itemKey: ItemKey): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player.alive) return;
    if (player.health >= 100) return;

    // Match: one item used, healing scaled by the kit, movement spent
    const healedOut = this.turn(player, 'heal', { itemKey }) as ReturnType<Match['heal']>;
    if (!healedOut.ok) return;
    const actualHeal = healedOut.healed;
    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];

    const healInput: TurnInput = { playerId: player.id, action: 'heal', params: { itemKey } };
    this.broadcastTurnInput(healInput);

    logMessage(`${player.name} uses ${itemDisplayName(itemKey)} (+${actualHeal} HP)`);

    updatePlayerBars(this.gameState);
    updateMovementDisplay(updatedPlayer.movementRemaining);
    this.updateHealButtonState();
    this.checkMeleeTargets();
    this.refreshInventoryPanel();
  }

  /** Show/hide mode-specific action buttons (interact, collect, deliver) based on game mode. */
  private updateModeActionButtons(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const isHumanAlive = !player.isBot && player.alive;

    const interactBtn = document.getElementById('interact-btn');
    const collectBtn = document.getElementById('collect-btn');
    const deliverBtn = document.getElementById('deliver-btn');

    const ms = this.gameState.modeState;
    const hasInteract = isHumanAlive && !!(ms.signalTrace || ms.uplink || ms.excavation);
    const hasCollect = isHumanAlive && !!(ms.salvage || ms.uplink);
    const hasDeliver = isHumanAlive && !!(ms.salvage || ms.uplink);

    if (interactBtn) {
      interactBtn.classList.toggle('hidden', !hasInteract);
      if (ms.excavation) {
        interactBtn.textContent = 'BUILD DRILL (I)';
      } else {
        interactBtn.textContent = 'INTERACT (I)';
      }
    }
    if (collectBtn) collectBtn.classList.toggle('hidden', !hasCollect);
    if (deliverBtn) deliverBtn.classList.toggle('hidden', !hasDeliver);
  }

  // ===========================================================================
  // BUILD MODE
  // ===========================================================================

  private toggleBuildMode(): void {
    if (this.activeDroneDelivery || this.respawnDropCans.size > 0) return;
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    if (isBuildMenuOpen()) {
      this.cancelBuild();
    } else {
      hideInGameShop();
      showInventoryPanel(false);
      showBuildMenu(player, this.gameState.structures, (type) => {
        this.selectStructureForPlacement(type as StructureType);
      }, getModeRules(this.gameState.config.modeConfig).objective);
    }
  }

  private selectStructureForPlacement(type: StructureType): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];

    if (type === 'antenna') {
      // Antenna: costs 1.0 movement, no CBP, no per-player limit
      if (player.movementRemaining < 1.0) {
        logMessage('Not enough movement! (need 1.0)');
        hideBuildMenu();
        return;
      }
    } else {
      if (!canBuildEcotourism(player.id, this.gameState.structures)) {
        logMessage('Build limit reached!');
        hideBuildMenu();
        return;
      }
      if (player.movementRemaining < BUILD_MOVEMENT_COST) {
        logMessage(`Not enough movement! (need ${BUILD_MOVEMENT_COST}, have ${player.movementRemaining.toFixed(1)})`);
        hideBuildMenu();
        return;
      }
    }

    this.buildStructureType = type;
    this.buildTargetDir = null;
    hideBuildMenu();
    this.setControlMode('build');
    logMessage('Click near your position to place structure');
  }

  private handleBuildMouseMove(player: import('./types').PlayerState): void {
    const planet = this.gameState.planets[player.planetIndex];
    const meshData = this.sceneObjs.planetMeshes.get(planet.id);
    if (!meshData) return;

    const intersects = this.raycaster.intersectObject(meshData.mesh);
    if (intersects.length > 0) {
      const point = intersects[0].point;
      const dir: Vec3 = {
        x: point.x - planet.position.x,
        y: point.y - planet.position.y,
        z: point.z - planet.position.z,
      };
      const len = Math.sqrt(dir.x ** 2 + dir.y ** 2 + dir.z ** 2);
      const candidateDir = { x: dir.x / len, y: dir.y / len, z: dir.z / len };

      // Only allow placement within BUILD_PLACEMENT_RADIUS of the player
      const distance = arcDistance(player.surfaceDir, candidateDir, planet.radius);
      if (distance <= BUILD_PLACEMENT_RADIUS) {
        this.buildTargetDir = candidateDir;
        if (this.sceneObjs.movementRing) {
          this.sceneObjs.movementRing.dot.visible = true;
          const surfaceOffset = planet.radius + 0.2;
          this.sceneObjs.movementRing.dot.position.set(
            planet.position.x + candidateDir.x * surfaceOffset,
            planet.position.y + candidateDir.y * surfaceOffset,
            planet.position.z + candidateDir.z * surfaceOffset
          );
        }
      } else {
        this.buildTargetDir = null;
        if (this.sceneObjs.movementRing) {
          this.sceneObjs.movementRing.dot.visible = false;
        }
      }
    } else {
      this.buildTargetDir = null;
      if (this.sceneObjs.movementRing) {
        this.sceneObjs.movementRing.dot.visible = false;
      }
    }
  }

  /** Local click to place the selected structure. */
  private handleBuildClick(): void {
    if (this.isRemoteTurn()) return;
    if (!this.buildTargetDir || !this.buildStructureType) return;
    this.performBuild(this.buildStructureType, this.buildTargetDir);
  }

  /** Build a structure at a surface direction — the one code path for local and remote builds. */
  /** S6: take an emplacement's seat (no UI yet — the action exists for replays, peers and the hosts to come). */
  private performMount(structureId: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const r = this.turn(player, 'mount', { structureId }) as ReturnType<Match['mount']>;
    if (!r.ok) { logMessage(r.reason); return; }
    const input: TurnInput = { playerId: player.id, action: 'mount', params: { structureId } };
    this.broadcastTurnInput(input);
    logMessage(`${player.name} mans the emplacement${r.captured ? ' — captured' : ''}`);
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
  }

  private performDismount(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const r = this.turn(player, 'dismount', {}) as ReturnType<Match['dismount']>;
    if (!r.ok) { logMessage(r.reason); return; }
    const input: TurnInput = { playerId: player.id, action: 'dismount', params: {} };
    this.broadcastTurnInput(input);
    logMessage(`${player.name} climbs down`);
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
  }

  private performBuild(structureType: StructureType, surfaceDir: Vec3): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];

    const isAntenna = structureType === 'antenna';
    // Match: the movement cost, the structure, and in Signal Trace an antenna's listening post
    const built = this.turn(player, 'build', { structureType, surfaceDir }) as ReturnType<Match['build']>;
    if (!built.ok) {
      logMessage(built.reason);
      this.cancelBuild();
      return;
    }

    // Create structure mesh
    const newStructure = this.gameState.structures.find(st => st.id === built.structureId)!;
    const worldPos = getStructureWorldPosition(newStructure, this.gameState.planets);
    const normal = getPlayerSurfaceNormal({ surfaceDir: newStructure.surfaceDir } as any);
    this.ensureStructureMesh(newStructure);
    const builtMesh = this.sceneObjs.structureMeshes.get(newStructure.id);
    if (builtMesh) startStructureRise(builtMesh);   // 28: it rises over the hammer swing below

    // Hammer swing (humans, bots and remote peers alike)
    const builderMesh = this.sceneObjs.playerMeshes.get(player.id);
    if (builderMesh) {
      builderMesh.animCtx.state = 'build';
      builderMesh.animCtx.buildStart = 0;
    }

    // Record turn input
    const buildInput: TurnInput = { playerId: player.id, action: 'build', params: { structureType, surfaceDir } };
    this.broadcastTurnInput(buildInput);

    if (isAntenna) {
      logMessage(`${player.name} deploys a listening post antenna!`);
    } else {
      logMessage(`${player.name} deploys ecotourism center (+1 ${cur()}/turn)`);
    }

    // Reset build mode and return to aim mode (building doesn't end the turn)
    this.buildStructureType = null;
    this.buildTargetDir = null;
    hideBuildMenu();

    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
    updateMovementDisplay(updatedPlayer.movementRemaining);
    updateJumpButton(false, updatedPlayer.movementRemaining >= JUMP_MIN_COST, updatedPlayer.jumppackFuel);

    this.setControlMode('aim');
  }

  private cancelBuild(): void {
    hideBuildMenu();
    this.buildStructureType = null;
    this.buildTargetDir = null;
    if (this.aimState.mode === 'build') {
      this.setControlMode('aim');
    }
  }

  /** The top-bar chips mirror the map: SEEN / last-seen age / unknown per enemy. */
  private updateChipVisibility(): void {
    updateChipVisibility(this.gameState, this.viewerIndex, this.visibleEnemyIds, this.getViewerFog(), this.fogCam);
  }

  /**
   * Stabilizers: the power slider's ceiling follows how settled the unit is this turn, and
   * the requested power is pulled down to it (no-op with the setting off).
   */
  private applyPowerCap(player: PlayerState): void {
    const cap = maxFirePower(player, this.gameState.config);
    const slider = document.getElementById('power-slider') as HTMLInputElement | null;
    if (slider) slider.max = String(cap);
    const hint = document.getElementById('power-cap-hint');
    if (hint) {
      const show = stabilizersOn(this.gameState.config) && cap < 100;
      hint.style.display = show ? '' : 'none';
      hint.textContent = show ? `· max ${cap}% (unsettled)` : '';
    }
    if (this.aimState.power > cap) {
      this.aimState.power = cap;
      if (slider) slider.value = String(cap);
    }
  }

  /** After any aim change (keys or the aim pad): HUD readouts, aim sphere, and a fresh preview. */
  private onAimChanged(): void {
    const current = this.gameState.players[this.gameState.currentPlayerIndex];
    if (current) this.applyPowerCap(current);
    updateAimDisplay(this.aimState.azimuth, this.aimState.elevation, this.aimState.power);
    updateAimSphere(this.aimState.azimuth, this.aimState.elevation, this.aimState.power);
    if (this.aimState.mode === 'jump') this.jumpPreviewDirty = true;
    else this.markPreviewDirty();
  }

  /** Re-apply what the viewer may see after visibility or the dev reveal changed. */
  private refreshVisibility(): void {
    updateCameraMode(this.visibility, this.devReveal);
    this.updateChipVisibility();
    if (this.devReveal) this.restoreTrueColors();
    else this.applyFogToScene();
    this.updatePlayerMeshes();
    this.updateGhostMeshes();
    updateSettingsToggles(this.visibility, this.devReveal, this.trailPersist, this.trailHistoryRounds);
  }

  /** Bodies the connecting cam must not look through (live planets and stars). */
  private frameOccluders(): Array<{ x: number; y: number; z: number; r: number }> {
    return this.gameState.planets
      .filter(p => p.alive && p.type !== 'wormhole')
      .map(p => ({ x: p.position.x, y: p.position.y, z: p.position.z, r: p.radius }));
  }

  private toggleShotCam(): void {
    this.shotCam = this.shotCam === 'connect' ? 'follow' : this.shotCam === 'follow' ? 'ride' : 'connect';
    saveShotCam(this.shotCam);
    if (this.shotCam !== 'connect') clearCameraFrame(this.cameraState);
    if (this.shotCam !== 'ride') this.missileCam?.release();
    this.updateShotCamToggle();
    logMessage(this.shotCam === 'connect' ? 'Shot camera: connect (shot and target in one view)' : this.shotCam === 'follow' ? 'Shot camera: follow the projectile' : 'Shot camera: ride the shot (the missile cam)');
  }

  private updateShotCamToggle(): void {
    const el = document.getElementById('setting-shot-cam');
    if (el) { el.textContent = this.shotCam.toUpperCase(); el.classList.toggle('off', this.shotCam === 'follow'); }
  }

  /** Dev mode only (for now): the gravity flow map over the system. */
  private toggleGravityField(): void {
    if (!isDevMode()) return;
    if (this.gravityField) {
      disposeGravityField(this.sceneObjs.scene, this.gravityField);
      this.gravityField = null;
      logMessage('Gravity field off');
    } else {
      this.gravityField = createGravityField(this.sceneObjs.scene, this.gameState.planets, 1);
      this.markPreviewDirty();
      logMessage('Gravity field on — green is calm, orange is steep; contours show the wells, arrows the break');
    }
    this.updateGravityFieldToggles();
  }

  private updateGravityFieldToggles(): void {
    const on = this.gravityField !== null;
    const row = document.getElementById('setting-gravity-field');
    if (row) { row.textContent = on ? 'ON' : 'OFF'; row.classList.toggle('off', !on); }
    const btn = document.getElementById('btn-gravity-field');
    if (btn) btn.classList.toggle('active', on);
  }

  /** Dev mode only: SISSI — see everything, true colors. */
  private toggleDevReveal(): void {
    if (!isDevMode()) return;
    this.devReveal = !this.devReveal;
    logMessage(this.devReveal ? 'SISSI on — everything visible' : 'SISSI off');
    this.refreshVisibility();
  }

  /** Fog of war vs everyone visible. Set by the match; dev mode can switch it live. */
  private setVisibility(v: 'fog' | 'free'): void {
    if (this.visibility === v) return;
    this.visibility = v;
    logMessage(v === 'fog' ? 'Visibility: fog of war' : 'Visibility: everyone visible');
    this.refreshVisibility();
  }

  /** Can the viewer see this unit right now (teammate, spotted enemy, or no fog)? */
  private isUnitVisibleToViewer(player: PlayerState): boolean {
    if (!this.fogCam) return true;
    const viewer = this.gameState.players[this.viewerIndex];
    if (!viewer) return true;
    return player.teamIndex === viewer.teamIndex || this.visibleEnemyIds.has(player.id);
  }

  /** The local human the camera returns to when the actor is hidden. */
  private homePlayer(): PlayerState | undefined {
    if (this.networkSync) return this.gameState.players.find(p => p.alive && this.networkSync!.isLocalHuman(p));
    return this.gameState.players.find(p => p.alive && !p.isBot) ?? this.gameState.players[this.gameState.currentPlayerIndex];
  }

  /** Follow whoever is acting if the viewer can see them; otherwise stay on the viewer's own unit. */
  private focusOnActor(actor: PlayerState): void {
    if (this.cameraState.manualPivot) return;
    const subject = this.isUnitVisibleToViewer(actor) ? actor : (this.homePlayer() ?? actor);
    const pos = subject.airborne && subject.airbornePos
      ? subject.airbornePos
      : getPlayerWorldPosition(subject, this.gameState.planets);
    glideCameraTo(this.cameraState, pos, 35);
  }

  /** Tab: look at the next planet (Shift+Tab: previous), starting from the acting unit's planet. Pins the camera. */
  private cyclePlanetFocus(reverse: boolean): void {
    if (this.gameState.phase !== 'aiming' && this.gameState.phase !== 'deploying') return;
    const planets = this.gameState.planets;
    const candidates = planets.map((p, i) => ({ p, i })).filter(x => x.p.alive && x.p.type === 'planet');
    if (candidates.length === 0) return;
    const current = this.gameState.players[this.gameState.currentPlayerIndex];
    const from = this.focusPlanetIndex ?? current?.planetIndex ?? candidates[0].i;
    let pos = candidates.findIndex(x => x.i === from);
    if (pos < 0) pos = 0;
    else pos = (pos + (reverse ? -1 : 1) + candidates.length) % candidates.length;
    const next = candidates[pos];
    this.focusPlanetIndex = next.i;
    glideCameraTo(this.cameraState, next.p.position, Math.max(20, next.p.radius * 4));
    setManualPivot(this.cameraState, true);
  }

  /** Number keys: pick the n-th weapon in the weapon bar. */
  private selectWeaponSlot(slot: number): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player || player.isBot || this.isRemoteTurn()) return;
    const allowed = this.gameState.config.allowedWeapons;
    const available = WEAPON_KEYS.filter(key => {
      if (allowed && key !== 'standard' && !allowed.includes(key)) return false;
      const ammo = player.loadout.ammo[key];
      return ammo === null || ammo === undefined || ammo > 0;
    });
    const key = available[slot];
    if (!key) return;
    this.aimState.weaponIndex = WEAPON_KEYS.indexOf(key);
    updateWeaponButtons(this.aimState.weaponIndex, player.loadout);
    this.updatePreview();
  }

  private handleRightClickPivot(e: MouseEvent): void {
    // Cancel waypoint mode on right-click
    if (this.waypointMode) {
      this.exitWaypointMode(true);
      return;
    }
    // Raycast against all planet/star/wormhole meshes
    const ndc = new THREE.Vector2(
      (e.clientX / window.innerWidth) * 2 - 1,
      -(e.clientY / window.innerHeight) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, this.sceneObjs.camera);

    // Use click spheres for reliable planet detection
    const clickTargets: THREE.Mesh[] = [];
    const clickToId = new Map<THREE.Mesh, number>();
    for (const [planetId, data] of this.sceneObjs.planetMeshes) {
      const target = data.clickSphere ?? data.mesh;
      clickTargets.push(target);
      clickToId.set(target, planetId);
    }

    const intersects = this.raycaster.intersectObjects(clickTargets);
    if (intersects.length > 0) {
      const hitMesh = intersects[0].object as THREE.Mesh;
      const planetId = clickToId.get(hitMesh);
      if (planetId !== undefined) {
        const planet = this.gameState.planets.find(p => p.id === planetId);
        if (planet) {
          const idx = this.gameState.planets.indexOf(planet);
          this.focusPlanetIndex = idx >= 0 ? idx : null;
          glideCameraTo(this.cameraState, planet.position, Math.max(20, planet.radius * 4));
          setManualPivot(this.cameraState, true);
        }
      }
    } else {
      // Clicked empty space — pivot to the 3D point on a plane at the current target distance
      const dir = new THREE.Vector3();
      this.raycaster.ray.at(this.cameraState.distance, dir);
      setCameraTarget(this.cameraState, { x: dir.x, y: dir.y, z: dir.z });
      setManualPivot(this.cameraState, true);
    }
  }

  /** C / Esc: unpin and glide back to the acting unit (or home when it is hidden). */
  private resetCameraToPlayer(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player) return;
    setManualPivot(this.cameraState, false);
    this.focusPlanetIndex = null;
    this.focusOnActor(player.alive ? player : (this.homePlayer() ?? player));
  }

  private updateAmbientBiome(): void {
    if (!this.ambientManager) return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    // In fog cam, ambient follows the local human player's planet (stays on their view)
    const focusPlayer = (this.fogCam && player.isBot)
      ? this.gameState.players[this.viewerIndex] ?? player
      : player;
    if (focusPlayer.airborne || !focusPlayer.alive) {
      this.ambientManager.setBiome('space');
    } else {
      const planet = this.gameState.planets[focusPlayer.planetIndex];
      this.ambientManager.setBiome(planet.archetype);
    }
  }

  private spikeCombatIntensity(amount: number): void {
    this.combatIntensity = Math.min(1, this.combatIntensity + amount);
    this.timeSinceLastAction = 0;
  }

  /** The music manager lives for the whole app: menu, matches, game over. */
  /** 67.19: the conductor at ~10 Hz from the action host's reading of the fight (the turn game's is tickConductor). */
  private tickActionMusic(dt: number): void {
    if (!this.musicManager || !this.actionHost) return;
    this.conductorAccum += dt;
    if (this.conductorAccum < 0.1) return;
    this.conductorAccum = 0;
    updateMusicMix(this.actionHost.musicState(), this.musicManager);
  }

  /** The music manager for `set` (default: the one this match wants — 67.19: the action clock plays its own); swapping sets restarts the loops. */
  private ensureMusic(set: MusicSetId = musicSetFor(loadMusicSetId(), this.gameState?.config?.clock === 'action')): MusicManager | null {
    if (this.musicManager && this.musicManager.set.id === set) return this.musicManager;
    if (this.musicManager) { this.musicManager.cleanup(); this.musicManager = null; resetConductor(); }
    try { this.musicManager = new MusicManager(MUSIC_SETS[set]); } catch { this.musicManager = null; }
    return this.musicManager;
  }

  /** Settings: which loop set the music bus plays (composed layers or the original track). Restarts the loops. */
  private toggleMusicSet(): void {
    const next: 'composed' | 'original' = loadMusicSetId() === 'composed' ? 'original' : 'composed';
    saveMusicSetId(next);
    if (this.musicManager) {
      this.musicManager.cleanup();
      this.musicManager = null;
    }
    resetConductor();
    this.ensureMusic()?.solo('calm', 1.0);
    this.updateMusicSetToggle();
    logMessage(`Music: ${MUSIC_SETS[next].label.toLowerCase()} loop set`);
  }

  /** 67.21: the player's strong hand — the unit they control draws with it and the camera rides over that shoulder. Presentation only. */
  private toggleHand(): void {
    const next = loadPlayerHand() === 'right' ? 'left' : 'right';
    savePlayerHand(next);
    this.actionHost?.setPlayerHand(next);
    this.updateHandToggle();
  }

  private updateHandToggle(): void {
    const el = document.getElementById('setting-hand');
    if (el) el.textContent = loadPlayerHand() === 'right' ? 'RIGHT' : 'LEFT';
  }

  private updateMusicSetToggle(): void {
    const el = document.getElementById('setting-music-set');
    if (el) el.textContent = MUSIC_SETS[loadMusicSetId()].label;
  }

  private tickConductor(dt: number): void {
    if (!this.musicManager) return;

    // Decay combat intensity per frame
    this.combatIntensity *= Math.pow(0.995, dt * 60); // ~0.995 per 1/60s frame
    if (this.combatIntensity < 0.01) this.combatIntensity = 0;

    // Track idle time
    this.timeSinceLastAction += dt;

    // Run conductor at ~10Hz
    this.conductorAccum += dt;
    if (this.conductorAccum < 0.1) return;
    this.conductorAccum = 0;

    // Derive turn phase
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    let turnPhase: ConductorTurnPhase = 'idle';
    if (this.gameState.phase === 'aiming') {
      if (this.buildStructureType !== null) {
        turnPhase = 'building';
      } else if (!player.isBot) {
        turnPhase = 'aiming';
      }
    } else if (this.gameState.phase === 'firing') {
      turnPhase = this.gameState.projectile ? 'projectileInFlight' : 'firing';
    } else if (this.gameState.phase === 'resolving') {
      turnPhase = 'resolving';
    }

    // Derive match phase: never "endgame" from turn one in a 1v1 (audio/musicMix.ts)
    const viewerTeam = this.gameState.players[this.viewerIndex]?.teamIndex;
    const aliveTeams = new Set(this.gameState.players.filter(p => p.alive).map(p => p.teamIndex));
    const enemies = this.gameState.players.filter(p => p.teamIndex !== viewerTeam);
    const matchPhase: ConductorMatchPhase = deriveMatchPhase(
      aliveTeams.size, this.gameState.config.numTeams,
      enemies.filter(p => p.alive).length, enemies.length,
    );

    // Local player health (for low-health overlay)
    const human = this.gameState.players[this.viewerIndex];
    const playerHealth = human?.alive ? human.health / 100 : 1;

    const state: ConductorState = {
      turnPhase,
      combatIntensity: this.combatIntensity,
      playerHealth,
      buildMode: this.buildStructureType !== null,
      matchPhase,
      timeSinceLastAction: this.timeSinceLastAction,
    };

    updateMusicMix(state, this.musicManager);
  }

  private toggleInGameHelp(): void {
    // Close settings if open
    if (isInGameSettingsVisible()) showInGameSettings(false);
    const mode = this.gameState.config.modeConfig?.mode ?? 'deathmatch';
    showInGameHelp(!isInGameHelpVisible(), mode);
  }

  private toggleInGameSettings(): void {
    // Deselect planet during deploy phase
    if (this.gameState.phase === 'deploying' && this.deploy.cancelSelection()) return;
    // Cancel waypoint mode if active
    if (this.waypointMode) {
      this.exitWaypointMode(true);
      return;
    }
    // Close comms log if open
    if (isCommsLogOpen()) {
      toggleCommsLog();
      return;
    }
    // Close help if open
    if (isInGameHelpVisible()) {
      showInGameHelp(false);
      return; // Escape closes help first
    }
    // A pinned camera snaps back before settings open
    if (!isInGameSettingsVisible() && this.cameraState.manualPivot) {
      this.resetCameraToPlayer();
      return;
    }
    const opening = !isInGameSettingsVisible();
    if (opening) {
      updateSettingsToggles(this.visibility, this.devReveal, this.trailPersist, this.trailHistoryRounds);
      updateVolumeSliders((bus: string) => getBusVolume(bus as AudioBus));
    }
    showInGameSettings(opening);
  }

  private handleDeathRespawnCooldown(killId: number): void {
    this.match.deathCooldown(killId);
  }

  /**
   * Phase 59.5: an action battle from a setup has ended — the same hand-back as a turn battle:
   * the sector bridge hears it, and an in-place sector battle gets RETURN TO SECTOR.
   */
  private actionBattleOver(): void {
    if (this.match.gameOverHandled) return;
    this.match.gameOverHandled = true;
    postSectorBattleDone(this.gameState);
    const win = isGameOver(this.gameState) ? checkWinCondition(this.gameState) : null;
    void markActionDone(this.gameState, this.match.actionTick, win?.winningTeamIndex ?? null);
    if (this.sectorSession?.busy) this.showGameOverScreen(win);
  }

  private handleGameOver(): void {
    // Guard: run once per match (several code paths can trigger game over). Not the phase: advanceTurn sets 'gameover' itself on a
    // turn-limit ending, which used to make this return and freeze the match on its last turn.
    if (this.match.gameOverHandled) return;
    this.match.gameOverHandled = true;
    // Pending turn-flow pauses (post-shot continue, bot pacing) must not run after the game ends
    this.clock.clear();

    const result = checkWinCondition(this.gameState);
    const winner = result?.winningPlayer ?? null;
    postSectorBattleDone(this.gameState);
    markAutoplayDone(result?.winningTeamIndex !== undefined && result?.winningTeamIndex !== null ? `team ${result.winningTeamIndex}` : null, this.gameState.turnNumber, result?.reason ?? null);
    // Use companyName (team name) for display when multi-unit
    const winnerDisplayName = winner
      ? (this.gameState.config.unitsPerTeam > 1 || this.gameState.config.botUnitsPerTeam > 1
        ? winner.companyName : winner.name)
      : null;
    if (winner) {
      pushNotification({
        type: 'gcppa',
        message: matchMessage('gameOver', { name: winnerDisplayName! }),
        sender: matchSender(),
        color: '#c8a84c',
      });
    } else {
      pushNotification({
        type: 'gcppa',
        message: matchMessage('draw', {}),
        sender: matchSender(),
        color: '#c8a84c',
      });
    }

    // Find alive, non-airborne survivors grouped by team
    const survivors = this.gameState.players.filter(p => p.alive && !p.airborne);

    if (survivors.length === 0) {
      // Draw or all dead — skip ferry, show game over directly
      this.match.setPhase('gameover');
      this.showGameOverScreen(result);
      return;
    }

    // Start pickup phase
    this.match.setPhase('pickup');
    this.ferryPickupWinner = result;

    // Create one ferry per surviving team
    const teamSet = new Set(survivors.map(s => s.teamIndex));
    for (const teamIdx of teamSet) {
      const teamSurvivors = survivors
        .filter(p => p.teamIndex === teamIdx)
        .map(p => ({ id: p.id, planetIndex: p.planetIndex, surfaceDir: p.surfaceDir }));
      const teamPalette = TEAMS[teamIdx];
      const effect = createFerryPickupEffect(
        this.sceneObjs.scene,
        teamIdx,
        teamPalette,
        teamSurvivors,
        this.gameState.planets,
      );
      this.ferryPickupEffects.push(effect);
    }

    // Victory/defeat SFX
    const humanPlayer = this.gameState.players.find(p => !p.isBot);
    if (winner && humanPlayer && winner.teamIndex === humanPlayer.teamIndex) {
      victory();
    } else {
      defeat();
    }
  }

  private showGameOverScreen(result: WinResult | null): void {
    this.match.setPhase('gameover');
    if (this.minimapState) setMinimapVisible(this.minimapState, false);
    showGameOver(result, this.gameState.turnNumber, this.gameState.players, this.gameState.config.modeConfig, this.gameState.shotRecords);
    this.finishReplayRecord(result?.winningPlayer?.companyName ?? result?.winningPlayer?.name ?? null);

    // Sector: the battle's final state goes back to the sector and the turn continues
    if (this.sectorSession?.busy) {
      const restartBtn = document.getElementById('btn-restart');
      if (restartBtn) {
        const newBtn = restartBtn.cloneNode(true) as HTMLElement;
        newBtn.textContent = 'RETURN TO SECTOR';
        restartBtn.parentNode?.replaceChild(newBtn, restartBtn);
        newBtn.addEventListener('click', () => {
          newBtn.textContent = 'PLAY AGAIN';
          hideGameOver();
          this.releaseBattleViews();
          const session = this.sectorSession;
          if (!session) return;
          // R7: an action battle hands back its length in seconds (its turn number means nothing)
          session.battleDone(this.gameState, this.gameState.config.clock === 'action' ? this.match.actionTick * PHYS_DT : undefined);
          showSectorScreen(session, this.sectorCallbacks());
        });
      }
      return;
    }
    // Campaign: replace restart button behavior
    if (this.campaignState) {
      const restartBtn = document.getElementById('btn-restart');
      if (restartBtn) {
        restartBtn.textContent = 'RETURN TO CAMPAIGN';
        const won = result ? result.winningTeamIndex === 0 : false;
        const handler = () => {
          restartBtn.removeEventListener('click', handler);
          restartBtn.textContent = 'PLAY AGAIN';
          this.returnToCampaignMap(won);
        };
        // Remove old listeners by cloning
        const newBtn = restartBtn.cloneNode(true) as HTMLElement;
        newBtn.textContent = 'RETURN TO CAMPAIGN';
        restartBtn.parentNode?.replaceChild(newBtn, restartBtn);
        newBtn.addEventListener('click', handler);
      }
    }
  }

  private cycleWeapon(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    // Cycle to next weapon with ammo
    const loadout = player.loadout;
    const allowed = this.gameState.config.allowedWeapons;
    let nextIndex = this.aimState.weaponIndex;
    for (let i = 0; i < WEAPON_KEYS.length; i++) {
      nextIndex = (nextIndex + 1) % WEAPON_KEYS.length;
      const key = WEAPON_KEYS[nextIndex];
      // Skip banned weapons (standard always allowed)
      if (allowed && key !== 'standard' && !allowed.includes(key)) continue;
      const ammo = loadout.ammo[key];
      // Skip if weapon has 0 ammo (null/undefined = unlimited)
      if (ammo !== null && ammo !== undefined && ammo <= 0) continue;
      break;
    }
    this.aimState.weaponIndex = nextIndex;
    updateWeaponButtons(this.aimState.weaponIndex, player.loadout);
    this.updatePreview();
  }

  private tryFire(): void {
    if (this.activeDroneDelivery || this.respawnDropCans.size > 0) return;
    // Detonate early: if projectile is in flight and belongs to local human, air-burst it
    if (this.gameState.phase === 'firing' && this.gameState.projectile) {
      const owner = this.gameState.players.find(p => p.id === this.gameState.projectile!.ownerId);
      if (owner && !owner.isBot && (!this.networkSync || this.networkSync.isLocalHuman(owner))) {
        this.detonateEarly();
      }
      return;
    }

    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot || !player.alive) return;
    if (this.isRemoteTurn()) return;

    // After firing, fire button becomes END TURN
    if (this.hasFiredThisTurn && this.aimState.mode !== 'jump') {
      this.yieldTurn();
      return;
    }

    if (this.aimState.mode === 'jump') {
      this.executeJump();
    } else if (this.turnFlow !== 'perUnit') {
      this.queueShot();
    } else {
      this.fire();
    }
  }

  /**
   * Plan, then resolve (40.2): the unit's aim becomes an order instead of a shot and
   * its turn ends. The orders fly, in queue order, when control would leave the team
   * (nextTurn → resolveBatch). Ammo and movement are spent when the shot fires.
   */
  private queueShot(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const weaponKey = getSelectedWeaponKey(this.aimState.weaponIndex);
    // A hitscan weapon has no flight to plan: it resolves now, as in unit-by-unit play
    if (WEAPONS[weaponKey].hitscan) { this.fire(); return; }
    if (this.roundFlow && this.batchQueue.some(o => o.playerId === player.id)) { logMessage('Shot already queued — UNQUEUE to change it'); return; }
    const check = checkFire(player, weaponKey);
    if (!check.ok) { logMessage(check.reason); return; }
    const params: FireParams = { azimuth: this.aimState.azimuth, elevation: this.aimState.elevation, power: this.aimState.power, weaponKey };
    this.broadcastTurnInput({ playerId: player.id, action: 'queue', params });
    this.queueOrder(player, params);
  }

  /** Hold fire with these params; humans end the turn here, bots end theirs through the runner. */
  private queueOrder(player: PlayerState, params: FireParams, endTurn = true): void {
    if (this.roundFlow) {
      // Match: the shot leaves from where the unit stands now; ammo and the firing cost are spent now
      const queued = this.turn(player, 'queue', params) as PlanResult<{ projectile: ProjectileState }>;
      if (!queued.ok) { logMessage(queued.reason); return; }
      const proj = queued.projectile;
      if (!player.isBot && !this.networkSync?.isRemoteHuman(player)) this.recordPlannedPath(player, proj);
      logMessage(`${player.name} holds fire — ${WEAPONS[params.weaponKey].name} queued`);
      // The turn stays open: the unit may still plan a move, or take the shot back; END TURN finishes it
      if (!player.isBot) {
        this.hasFiredThisTurn = true;
        // isRemoteTurn() is false while a remote action is being applied — ask the sync directly
        if (this.networkSync?.isRemoteHuman(player)) { this.waitForRemotePostFire(); return; }
        this.resumeAimPhase();
        setUnqueueButton(true);
      }
      return;
    }
    this.turn(player, 'queue', params);
    logMessage(`${player.name} holds fire — ${WEAPONS[params.weaponKey].name} queued`);
    if (!endTurn) return;
    turnEnd();
    this.endTurnFor(player);
  }

  /** Local UNQUEUE: take back the current unit's queued shot while its turn is still open. */
  private unqueueShot(): void {
    if (this.gameState.phase !== 'aiming' || !this.roundFlow) return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot || this.isRemoteTurn() || this.walkAnim) return;
    if (!this.batchQueue.some(o => o.playerId === player.id)) return;
    this.broadcastTurnInput({ playerId: player.id, action: 'unqueue', params: {} });
    this.performUnqueue();
  }

  /** Remove the current unit's queued shot and refund it — one code path for local, remote and replayed un-queues. */
  private performUnqueue(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const taken = this.turn(player, 'unqueue', {}) as PlanResult<{ order: QueuedOrder }>;
    if (!taken.ok) return;
    const order = taken.order;
    this.plannedPaths.delete(player.id);
    this.refreshPlannedGhosts();
    logMessage(`${player.name} takes back the ${WEAPONS[order.params.weaponKey].name}`);
    if (player.isBot || this.networkSync?.isRemoteHuman(player)) return;
    const fresh = this.gameState.players[this.gameState.currentPlayerIndex];
    setUnqueueButton(false);
    updateFireButtonText(queueLabel(this.batchQueue, fresh.teamIndex, this.gameState.players));
    document.getElementById('fire-btn')?.classList.remove('yield-mode');
    document.getElementById('yield-btn')?.classList.remove('yield-active');
    updateMovementDisplay(fresh.movementRemaining);
    updateWeaponButtons(this.aimState.weaponIndex, fresh.loadout);
    updateJumpButton(false, fresh.movementRemaining >= JUMP_MIN_COST, fresh.jumppackFuel);
    this.updateMovementRing();
    this.setControlMode('aim');
  }

  /** Round flows: plan a walk — charged now, walked during the round at walking speed (the unit can still be hit on the way). */
  private planMove(player: PlayerState, targetDir: Vec3): boolean {
    const plan = this.turn(player, 'move', { surfaceDir: targetDir }) as ReturnType<Match['move']>;   // round flows: Match plans the walk
    if (!plan.ok) { logMessage(plan.reason); return false; }
    const { distance } = plan;
    const planet = this.gameState.planets[player.planetIndex];
    this.broadcastTurnInput({ playerId: player.id, action: 'move', params: { surfaceDir: vecClone(targetDir) } });
    this.showPlannedMoveMarker(player, planet, targetDir);
    const fresh = this.gameState.players.find(p => p.id === player.id)!;
    if (!fresh.isBot) { updateMovementDisplay(fresh.movementRemaining); updateJumpButton(false, fresh.movementRemaining >= JUMP_MIN_COST, fresh.jumppackFuel); this.updateMovementRing(); }
    if (this.shouldLogForPlayer(player.id)) logMessage(`${player.name} plans a move (${distance.toFixed(1)} units)`);
    return true;
  }

  /** Round flows: plan a jump — charged now, flown during the round by the simulator. */
  /** A jump the Match planned (round flows): the input, the predicted landing marker, the HUD. */
  private presentPlannedJump(player: PlayerState, aim: JetpackParams, launch: JumpLaunch): void {
    this.broadcastTurnInput({ playerId: player.id, action: 'jetpack', params: aim });
    // Where the jump is expected to come down (the jump preview's own prediction against the world as it stands)
    this.hidePlannedMoveMarker(player.id);
    const traj = simulateJumpTrajectory(launch.launchPos, launch.launchVel, this.gameState.planets);
    if (traj.landingPlanetIndex !== null && traj.landingPoint) {
      const lp = this.gameState.planets[traj.landingPlanetIndex];
      this.showPlannedMoveMarker(player, lp, vecNormalize(vecSub(traj.landingPoint, lp.position)), '#88c4a0');
    }
    const fresh = this.gameState.players.find(p => p.id === player.id)!;
    if (!fresh.isBot) { updateMovementDisplay(fresh.movementRemaining); updateJumpButton(false, false); this.updateMovementRing(); }
    if (this.shouldLogForPlayer(player.id)) logMessage(`${player.name} plans a jump${launch.burnFuel ? ' [FUEL]' : ''} (${launch.movementToUse.toFixed(1)} movement)`);
    this.clearJumpPreview();
    this.aimState.mode = 'aim';
    updateFireButtonText(this.hasFiredThisTurn ? 'END TURN (Y)' : this.roundFlow || this.turnFlow === 'batched' ? 'QUEUE SHOT' : 'FIRE');
  }

  private showPlannedMoveMarker(player: PlayerState, planet: PlanetState, surfaceDir: Vec3, color = '#9fd8ff'): void {
    this.hidePlannedMoveMarker(player.id);
    if (!this.isUnitVisibleToViewer(player)) return;
    // Over the network the other side's plan is hidden until the round resolves (a replay shows everyone's)
    if (this.networkSync?.kind === 'p2p' && this.networkSync.isRemoteHuman(player)) return;
    this.plannedMoveMarkers.set(player.id, createDeploySpawnMarker(this.sceneObjs.scene, planet, surfaceDir, color));
  }
  private hidePlannedMoveMarker(playerId: number): void {
    const m = this.plannedMoveMarkers.get(playerId);
    if (m) { removeDeployVisual(this.sceneObjs.scene, m); this.plannedMoveMarkers.delete(playerId); }
  }
  private clearPlannedMoves(): void {
    for (const id of [...this.plannedMoveMarkers.keys()]) this.hidePlannedMoveMarker(id);
    this.match.clearPlannedMoves();
  }

  /** Remember a queued shot's planned flight — windowed by the unit's preview quality, exactly what its aim preview showed. */
  private recordPlannedPath(player: PlayerState, proj: ProjectileState): void {
    const sim = this.simulateFullFlight(proj);
    const quality = (player.modifiers?.previewQuality ?? this.gameState.config.previewQuality ?? 1) * stabilityFactor(player, this.gameState.config);
    const pastImpacts = (this.gameState.shotRecords ?? []).filter(r => r.shooterId === player.id && r.impactPoint && !r.submunition).map(r => r.impactPoint!);
    const points = isTrajectoryPreviewDisabled(this.gameState) ? [] : previewPoints(sim.points, previewWindow(quality, sim.points, pastImpacts, PHYS_DT), PHYS_DT);   // R14 (b): the flare
    this.plannedPaths.set(player.id, { teamIndex: player.teamIndex, points });
    this.refreshPlannedGhosts();
  }
  /** Ghosts of the viewer's team's planned paths — never another team's plan (hot-seat or network). */
  private refreshPlannedGhosts(): void {
    const viewerTeam = this.gameState.players[this.viewerIndex]?.teamIndex ?? -1;
    for (const [id, line] of this.plannedPathGhosts) {
      const p = this.plannedPaths.get(id);
      if (!p || p.teamIndex !== viewerTeam) { removeFromScene(this.sceneObjs.scene, line); this.plannedPathGhosts.delete(id); }
    }
    for (const [id, p] of this.plannedPaths) {
      if (p.teamIndex !== viewerTeam || this.plannedPathGhosts.has(id) || p.points.length < 2) continue;
      this.plannedPathGhosts.set(id, createPreviewLine(this.sceneObjs.scene, p.points));
    }
  }
  private clearPlannedPaths(): void {
    for (const [, line] of this.plannedPathGhosts) removeFromScene(this.sceneObjs.scene, line);
    this.plannedPathGhosts.clear();
    this.plannedPaths.clear();
  }

  /** A round flight ended (or the playback stopped): keep what the viewer saw of its trail, like a single shot's. */
  private persistRoundTrail(f: RoundFlight, v: RoundFlightVisual): void {
    removeFromScene(this.sceneObjs.scene, v.trail);
    for (const seg of v.observed) {
      if (seg.length < 2) continue;
      const line = this.createLineFromPoints(seg, WEAPONS[f.weaponKey].color, 0.25);
      line.userData.teamColor = v.teamColor;
      this.persistedTrails.push(line);
    }
    v.observed = [];
  }

  /** Fire the next queued order, or finish the batch and start the waiting unit's turn. */
  private resolveBatch(): void {
    const next = takeNextOrder(this.batchQueue, this.gameState.players);
    if (!next) {
      this.batchQueue = [];
      this.batchActive = false;
      this.match.resumeAiming();
      if (isGameOver(this.gameState)) { this.handleGameOver(); return; }
      this.startAimPhase();
      return;
    }
    this.batchQueue = next.rest;
    this.batchActive = true;
    const fired = this.performFire(next.order.params, next.order.playerId);
    // No flight to wait for (refused, or hitscan): straight on to the next order
    if (!fired || WEAPONS[next.order.params.weaponKey].hitscan) this.clock.after(0.5, () => this.resolveBatch());
  }

  /** Local DETONATE button: air-burst the shot at its current trajectory step. */
  private detonateEarly(): void {
    if (!this.gameState.projectile || !this.precomputedSim) return;
    const player = this.gameState.players.find(p => p.id === this.gameState.projectile!.ownerId);
    if (player) this.broadcastTurnInput({ playerId: player.id, action: 'detonate', params: { step: this.projCursor } });
    this.performDetonate(this.projCursor);
  }

  /**
   * Air-burst at a trajectory step. Both peers resolve the blast at the SAME
   * precomputed point, regardless of how far their playback had progressed.
   */
  private performDetonate(step: number): void {
    const projectile = this.gameState.projectile;
    const sim = this.precomputedSim;
    if (!projectile || !sim || this.pendingClusterLaunch) return;

    const weapon = WEAPONS[projectile.weaponKey];
    const idx = Math.max(0, Math.min(step, sim.points.length - 1));
    const detonationPoint = sim.points[idx];

    logMessage(`${weapon.name} detonated early!`);
    showControlsBar(false);

    this.effects.push(
      createExplosion(this.sceneObjs.scene, detonationPoint, fireballRadius(weapon.blastRadius, this.effectScale()), weapon.color, this.effectScale())
    );
    const wk = projectile.weaponKey;
    if (wk === 'nuke' || wk === 'heavy' || wk === 'cluster') {
      playSpatial(detonationPoint, explosionLarge);
      this.spikeCombatIntensity(0.5);
    } else {
      playSpatial(detonationPoint, explosionSmall);
      this.spikeCombatIntensity(0.4);
    }

    // Match: blast only — no crater, terrain drop, specials or flora (core/resolveImpact.ts, airBurst)
    const owner = this.gameState.players.find(p => p.id === projectile.ownerId)!;
    const burst = this.turn(owner, 'detonate', { step: idx }) as ReturnType<Match['detonateAtStep']>;   // the Match moves the cursor and bursts at that point
    if (!burst.ok) return;
    const { r, record } = burst;
    this.applyImpactOutcome(r, detonationPoint);
    this.logShotRecord(record);

    updatePlayerBars(this.gameState);
    this.updatePlayerMeshes();

    // Persist trail and clean up projectile
    this.persistCurrentTrail();
    if (this.sceneObjs.projectileMesh) {
      disposeProjectileMesh(this.sceneObjs.scene, this.sceneObjs.projectileMesh);
      this.sceneObjs.projectileMesh = null;
    }
    if (this.sceneObjs.trailLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.trailLine);
      this.sceneObjs.trailLine = null;
    }
    if (this.sceneObjs.engineFlame) {
      disposeEngineFlame(this.sceneObjs.scene, this.sceneObjs.engineFlame);
      this.sceneObjs.engineFlame = null;
    }

    this.endProjectile();
  }

  /** Fire with the current aim state (local UI and bots). Returns false if the shot was refused. */
  private fire(): boolean {
    const weaponKey = getSelectedWeaponKey(this.aimState.weaponIndex);
    return this.performFire({
      azimuth: this.aimState.azimuth,
      elevation: this.aimState.elevation,
      power: this.aimState.power,
      weaponKey,
    });
  }

  /**
   * Fire with explicit params — the one code path for local, bot and remote shots.
   * `shooterId` fires a queued order for a unit whose turn has already passed
   * (batched turn flow); per-turn guards and the network broadcast do not apply.
   */
  private performFire(params: FireParams, shooterId?: number): boolean {
    const batched = shooterId !== undefined;
    if (!batched && this.walkAnim) return false; // can't fire while walking
    if (!batched && this.hasFiredThisTurn) return false; // 1 shot per turn
    hideInGameShop();
    hideBuildMenu();
    const player = batched ? this.gameState.players.find(p => p.id === shooterId) : this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player) return false;
    const weapon = WEAPONS[params.weaponKey];

    // R2: a seated shot uses the gun's ammunition — the same check the match makes (core/seats.ts fireCheckSubject)
    const check = checkFire(fireCheckSubject(player, params.weaponKey, this.gameState), params.weaponKey);
    if (!check.ok) {
      logMessage(check.reason);
      return false;
    }

    logMessage(`${player.name} fires ${weapon.name}!`);
    const firePos = getPlayerWorldPosition(player, this.gameState.planets);
    playSpatial(firePos, rocketLaunch);
    this.spikeCombatIntensity(0.15);

    // Recoil animation
    const recoilMesh = this.sceneObjs.playerMeshes.get(player.id);
    if (recoilMesh) {
      recoilMesh.animCtx.state = 'recoil';
      recoilMesh.animCtx.recoilStart = 0;
    }

    if (!batched) {
      this.hasFiredThisTurn = true;
      this.broadcastTurnInput({ playerId: player.id, action: 'fire', params });
    }

    // Match: cost (ammo, movement, the aim remembered), facing, the shot tracked — then a beam
    // resolves now or the projectile launches (its flight plays through Match.advance)
    const fired = this.turn(player, 'fire', params) as ReturnType<Match['fire']>;
    if (fired.ok && fired.hitscan) this.presentHitscan(player, params, fired.hitscan);
    return true;
  }

  /** A beam the Match resolved: the line, the flash, the log; then the turn carries on after a beat. */
  private presentHitscan(player: PlayerState, params: FireParams, h: HitscanOutcome): void {
    const weapon = WEAPONS[params.weaponKey];
    const { startPos, hitPoint } = h;

    // Visual beam from start to hit
    const beamGeom = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(startPos.x, startPos.y, startPos.z),
      new THREE.Vector3(hitPoint.x, hitPoint.y, hitPoint.z),
    ]);
    const beamMat = new THREE.LineBasicMaterial({ color: weapon.color, linewidth: 2, transparent: true, opacity: 1.0 });
    const beamLine = new THREE.Line(beamGeom, beamMat);
    this.sceneObjs.scene.add(beamLine);

    // Small impact flash
    this.effects.push(createExplosion(this.sceneObjs.scene, hitPoint, 0.3 * this.effectScale(), weapon.color, this.effectScale()));
    playSpatial(startPos, craterImpact);
    this.spikeCombatIntensity(0.2);

    if (h.hitPlayerId !== null) {
      const target = this.gameState.players.find(p => p.id === h.hitPlayerId)!;
      logMessage(`${target.name} takes ${weapon.damage} railgun dmg`);
      if (h.killed) {
        logMessage(`${target.name} destroyed!`);
        pushNotification({
          type: 'gcppa',
          message: matchMessage('death', { name: target.name }),
          sender: matchSender(),
          color: '#c8a84c',
        });
      }
      updatePlayerBars(this.gameState);
      this.updatePlayerMeshes();
    } else {
      logMessage('Railgun shot misses');
    }
    this.logShotRecord(h.record);
    for (const c of h.alerts) logMessage(describeChange(c, this.gameState.planets));
    void player;

    // Fade beam over 0.4s then clean up
    const fadeStart = performance.now();
    const fadeDuration = 400;
    const fadeBeam = () => {
      const elapsed = performance.now() - fadeStart;
      const t = Math.min(1, elapsed / fadeDuration);
      beamMat.opacity = 1 - t;
      if (t < 1) {
        requestAnimationFrame(fadeBeam);
      } else {
        this.sceneObjs.scene.remove(beamLine);
        beamGeom.dispose();
        beamMat.dispose();
      }
    };
    requestAnimationFrame(fadeBeam);

    // A network peer's match continues the turn here (the host's path); otherwise the turn policy settles the beam on
    // the next tick (presentShotSettled)
    if (!this.match.hostSettlesShots) return;
    if (isGameOver(this.gameState)) {
      this.clock.after(0.5, () => this.handleGameOver());
      return;
    }
    this.clock.after(0.5, () => this.continueAfterShot());
  }

  private launchProjectile(projectile: ProjectileState): void {
    this.match.launchShot(projectile, this.simPresenter);
  }

  /**
   * Record what the shot in flight did (core/shotRecord.ts) and, for the viewer's
   * own side or a visible enemy, put the one-line readout in the log.
   */
  /** The current flight as flown so far (for its shot record). */
  private flownPoints(): Vec3[] {
    return this.match.flownPoints();
  }

  /** The one-line shot report, for shots the viewer may know about. */
  private logShotRecord(record: ShotRecord | null): void {
    if (record && !record.submunition && this.shouldLogForPlayer(record.shooterId)) logMessage(describeShot(record, PHYS_DT));
  }

  /**
   * Hitscan weapon fire: instant raycast, direct damage, no projectile arc.
   * Used by railgun — flat trajectory, no area effect.
   */

  /** Visuals, camera and director for a shot or bomblet the Match just launched (its flight is precomputed). */
  private presentLaunch(projectile: ProjectileState, sim: ProjectileSimResult): void {
    // A bomblet replaces the previous body and trail
    if (this.sceneObjs.projectileMesh) {
      disposeProjectileMesh(this.sceneObjs.scene, this.sceneObjs.projectileMesh);
      this.sceneObjs.projectileMesh = null;
    }
    if (this.sceneObjs.trailLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.trailLine);
      this.sceneObjs.trailLine = null;
    }

    // Check if this projectile belongs to the local viewer or their team
    const viewer = this.gameState.players[this.viewerIndex];
    const projOwner = this.gameState.players.find(p => p.id === projectile.ownerId);
    this.isOwnTeamProjectile = !!(viewer && projOwner && (
      projOwner.id === viewer.id || projOwner.teamIndex === viewer.teamIndex
    ));

    // Show DETONATE button for local player's own shots (not probes — 0 blast radius)
    const isOwnShot = viewer && projectile.ownerId === viewer.id;
    const controlsBar = document.getElementById('controls-bar');
    if (isOwnShot && WEAPONS[projectile.weaponKey].blastRadius > 0) {
      updateFireButtonText('DETONATE');
      if (controlsBar) controlsBar.classList.add('detonate-mode');
      showControlsBar(true);
    } else {
      if (controlsBar) controlsBar.classList.remove('detonate-mode');
      showControlsBar(false);
    }

    // Missile cam: always in free cam mode, own/team only in fog cam mode
    if (!this.fogCam || this.isOwnTeamProjectile) {
      setMissileCam(this.cameraState, true);
      setManualPivot(this.cameraState, false); // clear manual pivot to follow projectile
      setCameraZoom(this.cameraState, 55);
    }

    // Hide aim indicator
    if (this.sceneObjs.aimIndicator) {
      hideAimIndicator(this.sceneObjs.aimIndicator);
    }

    // Remove preview line
    if (this.sceneObjs.previewLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.previewLine);
      if (this.gravityField) setGravityFieldPreview(this.gravityField, null);
      this.sceneObjs.previewLine = null;
    }

    // Create projectile mesh
    const weapon = WEAPONS[projectile.weaponKey];
    this.sceneObjs.projectileMesh = createProjectileMesh(this.sceneObjs.scene, projectile.weaponKey);
    this.sceneObjs.projectileMesh.position.set(
      projectile.pos.x,
      projectile.pos.y,
      projectile.pos.z
    );
    orientProjectileMesh(this.sceneObjs.projectileMesh, projectile.vel);

    // Create engine flame (only if has fuel)
    if (this.sceneObjs.engineFlame) {
      disposeEngineFlame(this.sceneObjs.scene, this.sceneObjs.engineFlame);
    }
    if (projectile.fuelRemaining > 0) {
      this.sceneObjs.engineFlame = createEngineFlame(this.sceneObjs.scene, projectile.weaponKey);
    } else {
      this.sceneObjs.engineFlame = null;
    }

    // Create trail
    this.sceneObjs.trailLine = createTrailLine(this.sceneObjs.scene, weapon.color, 500);
    this.remoteFlightEndConfirmed = false;

    // Director: score the precomputed flight before it plays, and pick the pace and camera
    this.shotPreview = this.previewShot(projectile, sim);
    if (this.shotPreview?.missileCam && this.missileCamBeforeShot === null) {
      this.missileCamBeforeShot = this.cameraState.missileCam;
      setMissileCam(this.cameraState, true);
    }

    // Initialize observed trail for persistence
    this.currentTrailColor = weapon.color;
    this.currentTrailTeamColor = projOwner
      ? TEAMS[projOwner.teamIndex % TEAMS.length].primary
      : '#ffffff';
    const initiallyVisible = !this.fogCam || this.isOwnTeamProjectile
      || this.isProjectileVisibleToViewer(projectile.pos);
    this.observedTrailSegments = initiallyVisible ? [[vecClone(projectile.pos)]] : [];
    this.lastProjVisible = initiallyVisible;
    this.clearLiveTrailLines();

    // Bearing cue: an enemy shot the viewer cannot see, landing near one of the viewer's units
    this.startThreatCues(projectile, initiallyVisible);
  }

  /**
   * Incoming-fire cue (37.16). Direction only: a ring on each threatened unit with a notch
   * toward where the shot arrives from. Never for shots the viewer can already see, never
   * for the viewer's own team's shots, never showing distance or the shooter.
   */
  private startThreatCues(projectile: ProjectileState, shotVisible: boolean): void {
    this.clearThreatCues();
    if (!this.fogCam || this.isOwnTeamProjectile || shotVisible) return;
    const sim = this.precomputedSim;
    if (!sim || !sim.hitPoint || sim.hitPlanetIndex < 0) return;
    if (this.gameState.planets[sim.hitPlanetIndex]?.type !== 'planet') return;
    if (isBearingAlertDisabled(this.gameState)) return;   // R14 (b): a comms blackout cuts the bearing cue (bots lose the incoming-fire intel alike)
    const viewer = this.gameState.players[this.viewerIndex];
    if (!viewer) return;
    const weapon = WEAPONS[projectile.weaponKey];
    const threatRadius = Math.max(4, weapon.blastRadius * 3); // the knockback reach
    let any = false;
    for (const unit of this.gameState.players) {
      if (!unit.alive || unit.airborne || unit.teamIndex !== viewer.teamIndex) continue;
      if (unit.planetIndex !== sim.hitPlanetIndex) continue;
      const pos = getPlayerWorldPosition(unit, this.gameState.planets);
      const d = Math.hypot(pos.x - sim.hitPoint.x, pos.y - sim.hitPoint.y, pos.z - sim.hitPoint.z);
      if (d > threatRadius) continue;
      const up = getPlayerSurfaceNormal(unit);
      this.threatCues.push(createThreatCue(this.sceneObjs.scene, unit.id, pos, up, arrivalBearing(sim.finalVel, up)));
      any = true;
    }
    if (any) logMessage(fx('Incoming fire!', 'TACCOM: inbound ordnance detected'));
  }

  private clearThreatCues(): void {
    if (this.threatCues.length === 0) return;
    disposeThreatCues(this.sceneObjs.scene, this.threatCues);
    this.threatCues = [];
    updateThreatEdgeMarkers([]);
  }

  /** Per frame: pulse the rings; mark threatened units that are off-screen on the screen edge. */
  private updateThreatCueVisuals(cam: THREE.PerspectiveCamera): void {
    if (this.threatCues.length === 0) return;
    updateThreatCues(this.threatCues, performance.now() / 1000);
    const entries = this.threatCues.map(c => {
      const v = c.group.position.clone().project(cam);
      return { id: c.unitId, ndcX: v.x, ndcY: v.y, behind: v.z > 1 };
    });
    updateThreatEdgeMarkers(entries);
  }

  /**
   * Simulate the complete flight of a projectile — the SAME simulation for the
   * aim preview and the real shot (playback replays these points), so the two
   * can never disagree. WASM when available, otherwise the pure JS mirror in
   * core/flight.ts; both produce identical results.
   *
   * Wormhole transits are handled here as multi-segment flights.
   */
  private simulateFullFlight(projectile: ProjectileState): ProjectileSimResult {
    return this.match.simulateFlight(projectile);
  }

  /**
   * The simulation clock. Everything that changes game state over time during
   * a resolution — projectile playback, airborne players, the delay before the
   * next cluster bomblet — advances here in PHYS_DT steps, in a fixed order.
   * Frame rate, fast-forward and wall-clock timers never affect the outcome.
   */
  // ===========================================================================
  // SIMULTANEOUS ROUND (40.5) — simulate once, play the record out
  // ===========================================================================

  /** Plan, resolve together: the whole queue flies through one round simulation; the result is final now and plays out over the next seconds. */
  private startRound(): void {
    // Match: the turn boundary began the round (the orders into the round simulator); its clock steps it live and it
    // closes itself (roundClosed). The director plans the playback from its preview, the record the live steps make.
    const preview = this.match.roundPreview();
    if (!preview) return;
    const { record, orders, before } = preview;
    if (this.turnFlow === 'allPlan') logMessage(`Round ${preview.round}: ${orders.filter(o => o.fire).length} shots, ${orders.filter(o => o.walk || o.jump).length} moves resolve together`);
    this.clearPlannedMoves();      // markers (the plans themselves were consumed by the round)
    this.clearPersistedTrails();   // last round's trails become history; this round's stay bright while the next plans are made
    for (const f of record.flights) if (f.outcome === 'skipped' && f.skipReason && f.submunitionOf === null) logMessage(`${WEAPONS[f.weaponKey].name} not fired: ${f.skipReason}`);
    // Probes scan what they flew over when the round closes (the Match reveals; roundClosed presents it)
    this.viewerFogCache = null;
    const viewer = this.gameState.players[this.viewerIndex];
    const visuals = new Map<number, RoundFlightVisual>();
    const interests: RoundFlightInterest[] = [];
    for (const f of record.flights) {
      if (f.outcome === 'skipped' || f.points.length === 0) continue;
      const owner = before.players.find(p => p.id === f.ownerId);
      const ownTeam = !!owner && !!viewer && owner.teamIndex === viewer.teamIndex;
      const ev = record.impacts.find(i => i.flightIndex === f.index);
      let preview: ShotPreview | null = null;
      // How much of the path the viewer can see decides whether the camera may follow it and how the director rates it
      let visibleFraction = 1;
      if (this.fogCam && !ownTeam) { const n = Math.min(24, f.points.length); let seen = 0; for (let k = 0; k < n; k++) if (this.isProjectileVisibleToViewer(f.points[Math.floor(k * (f.points.length - 1) / Math.max(1, n - 1))])) seen++; visibleFraction = n ? seen / n : 0; }
      if (this.resolutionPace !== 'full') {
        const dmg = ev ? ev.damages.reduce((s, d) => s + d.damage, 0) : 0;
        const viewerTeamDamaged = !!ev && !!viewer && ev.damages.some(d => d.damage > 0 && before.players.find(p => p.id === d.playerId)?.teamIndex === viewer.teamIndex);
        const { assists } = analyzeFlight(f.points, before.planets, owner?.planetIndex ?? -1, f.hitPlanetIndex);
        preview = classifyShot({ hitPlayerId: f.hitPlayerId, predictedDamage: dmg, predictedKills: ev?.kills.length ?? 0, assists, flightSeconds: f.points.length * PHYS_DT, ownTeam, visibleFraction, nearMiss: false, reveals: !!WEAPONS[f.weaponKey].reveals, viewerTeamDamaged }, this.resolutionPace);
      }
      const mesh = createProjectileMesh(this.sceneObjs.scene, f.weaponKey);
      mesh.visible = false;
      const flame = createEngineFlame(this.sceneObjs.scene, f.weaponKey);
      flame.group.visible = false;
      const trail = createTrailLine(this.sceneObjs.scene, WEAPONS[f.weaponKey].color, Math.max(2, f.points.length));
      trail.visible = false;
      visuals.set(f.index, { mesh, flame, trail, preview, ownTeam, observed: [], lastVisible: false, teamColor: owner ? TEAMS[owner.teamIndex % TEAMS.length].primary : '#ffffff' });
      interests.push({ flightIndex: f.index, preview, ownTeam, watchable: visibleFraction >= VISIBLE_FRACTION_WATCHABLE });
    }
    for (const line of record.logs) if (/ fires /.test(line)) logMessage(line);
    // The director plans the whole playback now: pace per step, camera focus, and where to stop
    const timeline = planRoundTimeline(record, interests, this.resolutionPace);
    this.roundPlay = { record, step: 0, ended: false, close: null, lastStep: timeline.lastStep, visuals, orders, timeline, round: preview.round };
    this.simAccumulator = 0;
    showControlsBar(false);
    this.setBattIndicator(null);
    if (!this.fogCam || visuals.size > 0) { setMissileCam(this.cameraState, false); setManualPivot(this.cameraState, false); }
    if (record.flights.every(f => f.outcome === 'skipped')) this.endRoundPlayback();
  }

  /**
   * One step of round playback (the Match just stepped the live round to `step`): launches, positions, impacts and
   * knocked-back units at their recorded steps. The impacts' world is the Match's, kept in step as they land.
   */
  private stepRound(step: number): void {
    const rp = this.roundPlay;
    if (!rp || rp.ended) return;
    rp.step = step;
    const { record } = rp;
    const now = performance.now() / 1000;
    for (const f of record.flights) {
      const v = rp.visuals.get(f.index);
      if (!v) continue;
      if (step === f.launchStep) {
        const p0 = f.points[0];
        playSpatial(p0, rocketLaunch);
        const shooter = this.gameState.players.find(p => p.id === f.ownerId);
        const recoil = shooter ? this.sceneObjs.playerMeshes.get(shooter.id) : undefined;
        if (recoil && f.submunitionOf === null) { recoil.animCtx.state = 'recoil'; recoil.animCtx.recoilStart = 0; }
      }
      if (step < f.launchStep || step > f.endStep) continue;
      const i = step - f.launchStep;
      const pos = f.points[Math.min(i, f.points.length - 1)];
      const nxt = f.points[Math.min(i + 1, f.points.length - 1)];
      const vel = { x: (nxt.x - pos.x) / PHYS_DT, y: (nxt.y - pos.y) / PHYS_DT, z: (nxt.z - pos.z) / PHYS_DT };
      const visible = !this.fogCam || v.ownTeam || this.isProjectileVisibleToViewer(pos);
      if (visible) { if (!v.lastVisible || v.observed.length === 0) v.observed.push([vecClone(pos)]); else v.observed[v.observed.length - 1].push(vecClone(pos)); }
      v.lastVisible = visible;
      v.mesh.position.set(pos.x, pos.y, pos.z);
      if (Math.hypot(vel.x, vel.y, vel.z) > 1e-6) orientProjectileMesh(v.mesh, vel);
      v.mesh.visible = visible;
      updateEngineFlame(v.flame, pos, vel, i * PHYS_DT < 2.0, PHYS_DT, now);
      if (!visible) v.flame.group.visible = false;
      v.trail.visible = visible;
      if (step === f.endStep) {
        const kind = f.outcome === 'lost' ? 'lost' : 'impact';
        rp.visuals.delete(f.index);
        this.persistRoundTrail(f, v);
        this.impactFades.push(createImpactFade(this.sceneObjs.scene, v.mesh, v.flame, f.hitPoint ?? pos, kind));
        if (kind === 'lost') logMessage(`${WEAPONS[f.weaponKey].name} lost in space`);
      }
    }
    for (const ev of record.impacts) {
      if (ev.step !== step) continue;
      const f = record.flights[ev.flightIndex];
      this.playImpactFx(f.weaponKey, ev.point, ev.planetIndex);
      this.applyImpactOutcome(ev.resolution, ev.point);
      updatePlayerBars(this.gameState);   // the state is live: the bars follow the impacts as they land
    }
    for (const w of record.walks) {
      if (step < w.fromStep || step > w.fromStep + w.dirs.length) continue;
      const i = step - w.fromStep;
      const mesh = this.sceneObjs.playerMeshes.get(w.playerId);
      const player = this.gameState.players.find(pl => pl.id === w.playerId);
      if (!mesh || !player) continue;
      if (i < w.dirs.length) {
        if (i === 0) mesh.animCtx.state = 'waddle';
        const at = { ...player, surfaceDir: w.dirs[i], airborne: false };
        if (this.isUnitVisibleToViewer(player)) updatePlayerMesh(at, mesh, getPlayerWorldPosition(at, this.gameState.planets), getPlayerSurfaceNormal(at));
      } else if (player.alive && !player.airborne) {
        mesh.animCtx.state = 'idle';
        updatePlayerMesh(player, mesh, getPlayerWorldPosition(player, this.gameState.planets), getPlayerSurfaceNormal(player));
      }
    }
    for (const a of record.airborne) {
      if (step < a.fromStep) continue;
      const i = step - a.fromStep;
      const mesh = this.sceneObjs.playerMeshes.get(a.playerId);
      if (!mesh) continue;
      if (i === 0 && a.cause === 'jump') {
        mesh.animCtx.state = 'airborne_jump';
        const p0 = a.points[0] ?? mesh.group.position;
        playSpatial({ x: p0.x, y: p0.y, z: p0.z }, jumpLaunch);
        const jp = this.gameState.players.find(pl => pl.id === a.playerId);
        if (jp && this.shouldLogForPlayer(a.playerId)) logMessage(`${jp.name} launches!`);
      }
      if (i < a.points.length) {
        const p = a.points[i];
        mesh.group.position.set(p.x, p.y, p.z);
      } else if (a.landedStep !== null && step === a.landedStep + 1) {
        const player = this.gameState.players.find(pl => pl.id === a.playerId);
        if (player && player.alive) {
          updatePlayerMesh(player, mesh, getPlayerWorldPosition(player, this.gameState.planets), getPlayerSurfaceNormal(player));
          mesh.animCtx.state = 'landing_knockback'; mesh.animCtx.landingStart = 0;
          if (a.landingDamage > 0) logMessage(`${player.name} crash lands (${Math.round(a.landingDamage)} dmg)`);
          updatePlayerBars(this.gameState);
        } else if (player && !player.alive) {
          mesh.group.visible = false;
        }
      }
    }
    rp.step++;
    if (rp.step > rp.lastStep + 1) this.endRoundPlayback();
  }

  /** Per frame while a round plays: trails, then the timeline's pace and camera focus for this step. */
  private updateRoundVisuals(dt: number): void {
    const rp = this.roundPlay;
    if (!rp) return;
    for (const f of rp.record.flights) {
      const v = rp.visuals.get(f.index);
      if (!v || rp.step < f.launchStep || rp.step > f.endStep) continue;
      updateTrailLine(v.trail, f.points.slice(0, Math.min(rp.step - f.launchStep, f.points.length - 1) + 1));
    }
    const seg = segmentAt(rp.timeline, rp.step);
    this.shotPreview = seg && this.resolutionPace !== 'full' ? { tier: 'routine', speed: seg.speed, missileCam: false, reason: seg.reason } : null;
    let follow: Vec3 | null = null;
    if (seg && seg.focus !== null) {
      const f = rp.record.flights[seg.focus];
      if (rp.step <= f.endStep) {
        const v = rp.visuals.get(f.index);
        if (v && v.mesh.visible) follow = f.points[Math.min(rp.step - f.launchStep, f.points.length - 1)];
      } else if (f.hitPoint) {
        follow = f.hitPoint;   // rest on the impact
      }
    }
    if (follow && !this.cameraState.manualPivot) lerpCameraTarget(this.cameraState, follow, 0.06, dt);
  }

  /** The director's playback is over: shots still drifting when it stopped are shown lost. The Match runs the round out. */
  private endRoundPlayback(): void {
    const rp = this.roundPlay;
    if (!rp || rp.ended) return;
    rp.ended = true;
    for (const [idx, v] of rp.visuals) {
      const f = rp.record.flights[idx];
      this.persistRoundTrail(f, v);
      this.impactFades.push(createImpactFade(this.sceneObjs.scene, v.mesh, v.flame, { x: v.mesh.position.x, y: v.mesh.position.y, z: v.mesh.position.z }, 'lost'));
      if (rp.timeline.cutFlights.includes(idx)) logMessage(`${WEAPONS[rp.record.flights[idx].weaponKey].name} lost in space`);
    }
    rp.visuals.clear();
    this.shotPreview = null;
    this.clearPlannedPaths();
    if (rp.close) this.presentRoundEnd(rp.close);
  }

  /**
   * The Match closed the round (64.6 step 3). The director's playback rests on the last impact a little past the round's
   * end: that tail plays on, a step a tick (afterTick), and the round's end shows when it is over.
   */
  private roundClosed(close: RoundClose): void {
    const rp = this.roundPlay;
    if (rp && !rp.ended) { rp.close = close; return; }
    this.presentRoundEnd(close);
  }

  /** The round's end: the round card, the probes' reveals, then the next window once whoever the round left airborne lands. */
  private presentRoundEnd(close: RoundClose): void {
    this.roundPlay = null;
    // The round card: what the viewer could know of the round
    const viewer = this.gameState.players[this.viewerIndex];
    const summary = summarizeRound(close.record, {
      round: close.round, players: this.gameState.players, viewerTeam: viewer?.teamIndex ?? -1,
      teamName: t => TEAMS[t % TEAMS.length].name, canSee: p => !this.fogCam || this.isProjectileVisibleToViewer(p),
    });
    logMessage(summary.headline);
    pushNotification({ type: 'broadcast', message: [summary.headline, ...summary.lines].join(' — '), sender: 'Director', color: '#9fd8ff' });
    for (const sp of close.spotted) this.presentProbeReveal(sp.ownerId, sp.unitIds);
    this.updatePlayerMeshes();
    updatePlayerBars(this.gameState);
    this.updateFogForCurrentPlayer();
    if (isGameOver(this.gameState)) { this.handleGameOver(); return; }
    // 64.6 step 4: the policy opens the next window itself once whoever the round left airborne lands; if it already did
    // while the playback ran, that answer was held for this moment
    const held = this.pendingWindow;
    this.pendingWindow = null;
    if (held) this.followPass(held);
  }

  /**
   * 64.6 step 4: the turn policy opened the window after a round (SimPresenter.windowOpened). While the round still plays
   * on screen the answer waits (the state is ahead of the picture, as it is for the round's own close); otherwise it is
   * walked at once, as a pass of this host's own.
   */
  private pendingWindow: TurnPass | null = null;
  private windowOpenedByPolicy(pass: TurnPass): void {
    if (this.roundPlay) { this.pendingWindow = pass; return; }
    this.followPass(pass);
  }

  /**
   * Resolution director (core/resolutionDirector.ts): score the precomputed flight
   * before it plays. Damage and kills come from a dry run of the pure impact
   * resolver (JS crater, so WASM terrain is untouched); visibility is sampled along
   * the path from the viewer's fog, so an unseen enemy shot is never lingered on.
   */
  private previewShot(projectile: ProjectileState, sim: ProjectileSimResult | null): ShotPreview | null {
    if (this.resolutionPace === 'full' || !sim) return null;
    const state = this.gameState;
    const planets = state.planets;
    const weapon = WEAPONS[projectile.weaponKey];
    const shooter = state.players.find(p => p.id === projectile.ownerId);
    const viewer = state.players[this.viewerIndex];
    const ownTeam = !!shooter && !!viewer && shooter.teamIndex === viewer.teamIndex;
    let predictedDamage = 0, predictedKills = 0, viewerTeamDamaged = false, nearMiss = false;
    const hitPlanet = sim.hitPlanetIndex >= 0 ? planets[sim.hitPlanetIndex] : null;
    if (hitPlanet && sim.hitPoint && hitPlanet.type === 'planet') {
      try {
        const r = resolveImpact(state, { impactPoint: sim.hitPoint, planetIndex: sim.hitPlanetIndex, projectile: { ...projectile, pos: sim.hitPoint, vel: sim.finalVel } });
        for (const d of r.damages) {
          predictedDamage += d.damage;
          const p = state.players.find(x => x.id === d.playerId);
          if (d.damage > 0 && p && viewer && p.teamIndex === viewer.teamIndex) viewerTeamDamaged = true;
        }
        predictedKills = r.kills.length;
      } catch { /* preview only — the real resolution happens at impact */ }
      const reach = Math.max(1, weapon.blastRadius * 1.5);
      for (const p of state.players) {
        if (!p.alive || p.id === projectile.ownerId) continue;
        if (vecLength(vecSub(getPlayerWorldPosition(p, planets), sim.hitPoint)) <= reach) { nearMiss = true; break; }
      }
    }
    const { assists } = analyzeFlight(sim.points, planets, shooter?.planetIndex ?? -1, sim.hitPlanetIndex);
    let visibleFraction = 1;
    if (this.fogCam && !ownTeam) {
      const n = Math.min(24, sim.points.length);
      let seen = 0;
      for (let k = 0; k < n; k++) {
        const idx = Math.floor(k * (sim.points.length - 1) / Math.max(1, n - 1));
        if (this.isProjectileVisibleToViewer(sim.points[idx])) seen++;
      }
      visibleFraction = n > 0 ? seen / n : 0;
    }
    return classifyShot({
      hitPlayerId: sim.hitPlayerId, predictedDamage, predictedKills, assists,
      flightSeconds: sim.points.length * PHYS_DT, ownTeam, visibleFraction, nearMiss,
      reveals: !!weapon.reveals, viewerTeamDamaged,
    }, this.resolutionPace);
  }

  /** Flight over: drop the director's camera override and card the dud. */
  private finishDirectedShot(kind: 'impact' | 'lost'): void {
    if (this.missileCamBeforeShot !== null) {
      setMissileCam(this.cameraState, this.missileCamBeforeShot);
      this.missileCamBeforeShot = null;
    }
    const preview = this.shotPreview;
    this.shotPreview = null;
    if (!preview || preview.tier !== 'dud' || this.resolutionPace === 'full') return;
    const proj = this.gameState.projectile;
    if (!proj) return;
    const shooter = this.gameState.players.find(p => p.id === proj.ownerId);
    const team = shooter ? TEAMS[shooter.teamIndex % TEAMS.length] : null;
    pushNotification({ type: 'broadcast', message: describeDud(shooter?.name ?? 'Unknown', WEAPONS[proj.weaponKey].name, kind === 'lost'), sender: team?.name ?? 'Director', color: team?.primary ?? '#888888' });
  }

  private stepSimulation(dt: number): void {
    // 64.6 2b-ii: a live network peer's match settles its shots on the host's path (frozen P2P, decisions 28/41)
    this.match.hostSettlesShots = this.networkSync?.kind === 'p2p';
    this.match.advance(dt, this.simPresenter);
    // The director's playback is over and the round still runs (shots it cut, drifting): the Match runs it out now
    if (this.roundPlay?.ended && this.match.roundRunning) this.match.runRoundOut(this.simPresenter);
  }

  /** The game's side of the simulation clock (Match.advance): animate, log, sound, and the turn flow after a shot. */
  private readonly simPresenter: SimPresenter = {
    // 64.6 step 2: this host's view of a boundary's hops, at the points it always presented them
    turnAdvancing: () => {
      this.rememberAim();
      // P2P: compare state at the end of every player's turn
      if (this.networkSync) this.networkSync.sendChecksum(this.gameState);
      traceTurnChecksum(this.gameState);
    },
    turnAdvanced: a => this.applyTurnOutcome(a.r),
    turnBegun: player => this.presentTurnStart(player),
    fogRefreshed: () => {
      // the Match refreshed the unit's fog; this is the view of it (the spotted alerts), and the camera
      this.updateFogForCurrentPlayer(false);
      this.focusOnActor(this.gameState.players[this.gameState.currentPlayerIndex]);
    },
    proximityApplied: (proxResult, player) => { if (proxResult) this.presentProximity(proxResult, player); },
    shotSettled: s => this.presentShotSettled(s),
    roundStepped: step => this.stepRound(step),
    roundClosed: close => this.roundClosed(close),
    windowOpened: pass => this.windowOpenedByPolicy(pass),   // 64.6 step 4: the policy opened the window after a round
    // the playback's tail after the round closed: one shown step a tick, as the round's own steps were
    afterTick: () => { const rp = this.roundPlay; if (rp?.close && !rp.ended) this.stepRound(rp.step); },
    holdFlightEnd: ownerId => {
      if (!this.networkSync || this.remoteFlightEndConfirmed) return false;
      const owner = this.gameState.players.find(p => p.id === ownerId);
      return !!owner && this.networkSync.isRemoteHuman(owner);
    },
    // Local human shooter: tell the peer the flight ended without a detonation
    flightEndReached: ownerId => this.broadcastTurnInput({ playerId: ownerId, action: 'flightEnd', params: {} }),
    launched: (projectile, sim) => this.presentLaunch(projectile, sim),
    probeScan: scan => this.presentScan(scan, 'probe'),
    geyserDeflect: pos => {
      logMessage('Shot deflected by geyser!');
      this.effects.push(createExplosion(this.sceneObjs.scene, pos, 0.5 * this.effectScale(), '#a0c8d0', this.effectScale()));
    },
    impacted: e => this.presentImpact(e),
    lost: e => this.presentLost(e),
    flightOver: () => this.endProjectile(),
    airborne: step => this.presentAirborneStep(step),
  };

  /**
   * BATT countdown over the projectile for its last seconds of flight. Hidden the
   * frame the projectile is gone, whoever fired it — it used to be hidden only on
   * the human's resume-aim and on a lost projectile, so an enemy probe that hit
   * at BATT 5 left the warning on screen through the turn change.
   */
  private setBattIndicator(remaining: number | null): void {
    const show = remaining !== null;
    if (!show && !this.battShown) return;
    const el = document.getElementById('batt-indicator');
    if (!el) return;
    if (show) { el.textContent = `BATT ${Math.ceil(remaining)}`; el.classList.remove('hidden'); }
    else el.classList.add('hidden');
    this.battShown = show;
  }

  /** Per-frame projectile visuals: mesh, flame, trail, camera follow, BATT indicator. */
  private updateProjectileVisuals(dt: number): void {
    if (this.gameState.phase !== 'firing' || !this.gameState.projectile) { this.setBattIndicator(null); return; }
    const projectile = this.gameState.projectile;

    // Free cam: everything visible. Fog cam: own/team always visible, enemies check LOS.
    const projVisible = !this.fogCam || this.isOwnTeamProjectile || this.isProjectileVisibleToViewer(projectile.pos);

    // Collect observed trail segments (with gap detection on visibility transitions)
    if (projVisible) {
      if (!this.lastProjVisible) {
        // Visibility regained — start a new segment
        this.observedTrailSegments.push([vecClone(projectile.pos)]);
      } else {
        // Continue current segment
        const currentSeg = this.observedTrailSegments[this.observedTrailSegments.length - 1];
        if (currentSeg) currentSeg.push(vecClone(projectile.pos));
      }
    }
    this.lastProjVisible = projVisible;

    // Update visuals (hidden when outside human player's LOS)
    if (this.sceneObjs.projectileMesh) {
      this.sceneObjs.projectileMesh.position.set(
        projectile.pos.x,
        projectile.pos.y,
        projectile.pos.z
      );
      orientProjectileMesh(this.sceneObjs.projectileMesh, projectile.vel);
      this.sceneObjs.projectileMesh.visible = projVisible;
    }

    // Update engine flame (visual only - flame during "burn" phase)
    const animTime = performance.now() / 1000;
    if (this.sceneObjs.engineFlame) {
      const burning = projectile.fuelRemaining > 0;
      updateEngineFlame(
        this.sceneObjs.engineFlame,
        projectile.pos,
        projectile.vel,
        burning,
        dt,
        animTime
      );
      // Override visibility when projectile is outside viewer's LOS
      if (!projVisible) {
        this.sceneObjs.engineFlame.group.visible = false;
      }
    }

    // Update trail: use observed segments for both modes (trailPoints caps at 500,
    // but observedTrailSegments is uncapped so the trail stays visible for long flights)
    if (this.sceneObjs.trailLine) {
      this.sceneObjs.trailLine.visible = false;
    }
    this.updateLiveTrailLines();

    // Camera: any projectile the viewer can see. The connecting cam frames the shot and its predicted
    // impact in one view once they are close enough to read; until then (or when the shot is lost /
    // ends in a star or wormhole, or the player has pinned the view) it follows the projectile.
    this.rideThisFrame = null;
    if (projVisible) {
      const sim = this.precomputedSim;
      const predicted = sim && sim.hitPoint && sim.hitPlanetIndex >= 0 && this.gameState.planets[sim.hitPlanetIndex]?.type === 'planet' ? sim.hitPoint : null;
      // I9: the missile cam rides the shot (the orbit camera keeps following underneath, so the hand-back is at the impact)
      if (this.shotCam === 'ride' && !this.cameraState.manualPivot) this.rideThisFrame = { projectile, impact: predicted };
      const impact = this.shotCam === 'connect' && !this.cameraState.manualPivot ? predicted : null;
      if (impact && frameWouldFit(this.sceneObjs.camera, projectile.pos, impact)) {
        setCameraFrame(this.cameraState, projectile.pos, impact, this.frameOccluders());
      } else {
        clearCameraFrame(this.cameraState);
        const followSpeed = this.cameraState.missileCam ? 0.12 : 0.06;
        lerpCameraTarget(this.cameraState, projectile.pos, followSpeed, dt);
      }
    }

    // BATT indicator: blink for the last 5 seconds of a flight the viewer can see
    const remaining = MAX_PROJECTILE_TIME - projectile.time;
    this.setBattIndicator(projVisible && remaining <= 5 ? remaining : null);
  }

  /** Explosion (or probe ping), dust, startled wildlife and the weapon's impact sound. */
  private playImpactFx(weaponKey: WeaponKey, impactPoint: Vec3, planetIndex: number): void {
    const weapon = WEAPONS[weaponKey];
    // Explosion — or, for a zero-blast probe, a scanner ping ring on the surface (37.2d)
    const s = this.effectScale();
    const pc = this.gameState.planets[planetIndex]?.position ?? { x: 0, y: 0, z: 0 };
    const nx = impactPoint.x - pc.x, ny = impactPoint.y - pc.y, nz = impactPoint.z - pc.z;
    const nl = Math.hypot(nx, ny, nz) || 1;
    // 67.54: on the action clock a standard or heavy shell's burst is the host's (its class's look: fire, smoke, debris, dust, scorch)
    const drawnByHost = !!this.actionHost && shellExplosion(weaponKey) !== null;
    if (drawnByHost) { /* the host drew it */ }
    else if (weapon.blastRadius > 0) {
      this.effects.push(createExplosion(this.sceneObjs.scene, impactPoint, fireballRadius(weapon.blastRadius, s), weapon.color, s));   // a person-scale burst under the person set
    } else {
      this.rippleEffects.push(createRippleEffect(this.sceneObjs.scene, impactPoint, { x: nx / nl, y: ny / nl, z: nz / nl }, { color: weapon.color, size: 2.5 * s, maxLife: 0.9 }));
    }

    // Impact dust effect, rising along the ground's up (67.54: the host's burst has its own)
    if (!drawnByHost) this.impactDustEffects.push(
      createImpactDust(this.sceneObjs.scene, new THREE.Vector3(impactPoint.x, impactPoint.y, impactPoint.z), new THREE.Vector3(nx / nl, ny / nl, nz / nl), s, this.smokeDriftAt(impactPoint))   // 67.36: it blows downwind as it settles
    );

    // Scatter driftwings on impacted planet
    scatterDriftwings(this.effectMeshes, planetIndex, performance.now() / 1000);

    // Impact SFX — weapon-branched. 67.25a: on the action clock the host's ActionSound voices a shell's landing by its
    // distance in people (hearBlast); the turn game's panner (full loudness to 50 world units) is the turn game's
    if (this.actionHost) return;
    const wk = weaponKey;
    if (wk === 'nuke' || wk === 'heavy' || wk === 'cluster' || wk === 'antigrav' || wk === 'gravBomb') {
      playSpatial(impactPoint, explosionLarge);
      this.spikeCombatIntensity(0.5);
    } else if (wk === 'probe' || wk === 'punch' || wk === 'emp' || wk === 'tether') {
      playSpatial(impactPoint, craterImpact);
      this.spikeCombatIntensity(0.2);
    } else {
      playSpatial(impactPoint, explosionSmall);
      this.spikeCombatIntensity(0.4);
    }
  }

  /** The profile's scale for every fixed-size effect (rendering/effects.ts): 1 at classic (today's sizes), the person's k below it. */
  private effectScale(): number { return personSetOf(this.gameState).k; }

  /** A flight ended on a planet (the Match resolved it): effects, outcome, shot report, probe reveal, trail, hand-off. */
  private presentImpact(e: Parameters<SimPresenter['impacted']>[0]): void {
    this.lastShotEnd = 'impact';   // I9: the missile cam says how the shot ended
    if (e.ending === 'directHit') logMessage(`Direct hit on ${this.gameState.players.find(p => p.id === e.hitPlayerId)?.name ?? 'enemy'}!`);
    this.playImpactFx(e.projectile.weaponKey, e.point, e.planetIndex);
    this.applyImpactOutcome(e.r, e.point);
    this.logShotRecord(e.record);
    for (const c of e.alerts) logMessage(describeChange(c, this.gameState.planets));
    updatePlayerBars(this.gameState);
    this.updatePlayerMeshes();
    if (WEAPONS[e.projectile.weaponKey].reveals) this.presentProbeReveal(e.projectile.ownerId, e.probeSpotted);
    this.persistCurrentTrail();
    // The body and flame fade into the impact instead of vanishing on this frame (37.2d)
    this.handOffProjectileVisuals(e.point, 'impact');
  }

  /**
   * An impact's ground, drawn: the planet's mesh and its colours where the crater moved them, the fog, everything seated on the
   * ground re-seated (props, trees, plinths, the effects that live on it), the crater's decals. 69.24 (DECISIONS 242, the owner:
   * "Bug is with dropship crashes, they cause the terrain issue"): a crash's pieces dig their craters through the Match as a
   * shell's do, but only a shell's impact reached this — the crashes' craters were in the sim's ground (the fine patch, the walk)
   * and not in the drawn mesh, which stood above them over the craters' facets, and the trees and plants hung there. The crash
   * calls this alone (its look is its own: actionHost.crashDown).
   */
  private applyImpactGround(r: ImpactResolution): void {
    const planets = this.gameState.planets;
    const scene = this.sceneObjs.scene;
    if (r.terrain) {
      // (Match.applyImpactWorld has already synced the kernel, true colours and fog colour memory)
      const p = planets[r.terrain.planetIndex];
      const meshData = this.sceneObjs.planetMeshes.get(r.terrain.planetId);
      if (meshData) {
        updatePlanetMesh(p, meshData, r.terrain.modifiedVertices);
        updatePlanetColors(p, meshData, r.terrain.newColors);
      }
      if (this.fogCam) this.applyFogToScene(r.terrain.planetIndex);
      // Seated props on this planet sit on the new ground (a crater tilts the facets around it, not only the ones it moved)
      for (const rm of this.rockMeshes) if (rm.seat?.planetIndex === r.terrain.planetIndex) reseatPropMesh(rm, planets);
      this.rockMerge?.groundChanged(planets, r.terrain.planetIndex);
      for (const fm of this.floraMeshes) if (fm.seat?.planetIndex === r.terrain.planetIndex) reseatPropMesh(fm, planets);
      this.treeStands?.groundChanged(planets, r.terrain.planetIndex, r.terrain.modifiedVertices);
      for (const hm of this.hazardMeshes) if (hm.seat?.planetIndex === r.terrain.planetIndex) reseatPropMesh(hm, planets);
      for (const rm of this.ruinMeshes) if (rm.plinth?.planetIndex === r.terrain.planetIndex) reseatPlinth(rm, planets);
      for (const sm of this.sceneObjs.structureMeshes.values()) if (sm.plinth?.planetIndex === r.terrain.planetIndex) reseatPlinth(sm, planets);
      // Ground-dwelling effects (crawler swarms, driftwing roosts) follow the terrain
      for (const em of this.effectMeshes) {
        if (em.planetIndex !== r.terrain.planetIndex) continue;
        if (em.effectType === 'planetRings') continue;
        const effect = this.gameState.environmentalEffects.find(e => e.id === em.effectId);
        if (!effect) continue;
        const pos = getEffectWorldPosition(effect, planets);
        em.group.position.set(pos.x, pos.y, pos.z);
      }
    }
    // 69.41 (DECISIONS 254 (2)): the ground litter laid before the edits, re-laid on the ground as it is now (it hung where the
    // ground had been round a crash's pan)
    if (r.terrain && this.litter) for (const e of r.edits) if ((e.kind === 'crater' || e.kind === 'mound') && e.planetIndex === this.litter.planetIndex) this.litter.field.regroundNear(e.point, e.radius);
    // 63.5 step 5: each crater's decals by the ground it was dug in, on the ground as dug (the clock's ground — step 6: no
    // switch; presentation only — the sim's edits say where, how big and in what)
    if (r.terrain && groundMaterialsOn(this.gameState.config)) {
      for (const e of r.edits) {
        if (e.kind !== 'crater' || !planets[e.planetIndex]) continue;
        // 69.41: an earlier crater's decals the new one reached, laid again on the ground as it is now (the same pieces: its
        // edit and seed; they were laid once and floated where a later crater, a crash's pan, lowered the ground under them)
        this.craterDecals = this.craterDecals.map(old => {
          const was = old.userData.decal as { edit: TerrainEdit; seed: number } | undefined;
          if (!was || was.edit.planetIndex !== e.planetIndex) return old;
          const dx = was.edit.point.x - e.point.x, dy = was.edit.point.y - e.point.y, dz = was.edit.point.z - e.point.z, reach = was.edit.radius + e.radius;
          if (dx * dx + dy * dy + dz * dz > reach * reach) return old;
          scene.remove(old); disposeObjectTree(old);
          const g = layCraterDecals(planets[e.planetIndex], was.edit, was.seed, PERSON_BODY_HEIGHT * personSetOf(this.gameState).k);
          g.userData.decal = was;
          bakeStaticParts(g);
          scene.add(g);
          return g;
        });
        const at = this.gameState.terrainEdits?.indexOf(e) ?? -1, seed = at >= 0 ? at + 1 : this.craterDecals.length + 1;
        const g = layCraterDecals(planets[e.planetIndex], e, seed, PERSON_BODY_HEIGHT * personSetOf(this.gameState).k);   // (69.71: its pieces at a person's scale)
        g.userData.decal = { edit: e, seed };
        bakeStaticParts(g);   // one draw a crater (4–24 pieces before)
        scene.add(g);
        this.craterDecals.push(g);
      }
    }
  }

  /**
   * Play out an impact resolution: meshes, effects, log, notifications. Everything
   * here reads `r` and the already-updated game state (Match.applyImpactWorld has
   * run); no game logic lives in this method.
   */
  private applyImpactOutcome(r: ImpactResolution, impactPoint: Vec3): void {
    const planets = this.gameState.planets;
    const scene = this.sceneObjs.scene;

    this.applyImpactGround(r);
    if (r.ripple) {
      this.rippleEffects.push(createRippleEffect(scene, r.ripple.point, r.ripple.normal, { size: this.effectScale() }));
    }

    // Objects that slid with the terrain
    for (const sId of r.dropped.structures) {
      const s = this.gameState.structures.find(st => st.id === sId);
      const sm = this.sceneObjs.structureMeshes.get(sId);
      if (s && sm && !sm.plinth) updateStructureMesh(sm, getStructureWorldPosition(s, planets), vecNormalize(s.surfaceDir), undefined, s.yaw);   // seated ones were re-seated above
    }
    for (const hId of r.dropped.hazards) {
      const hm = this.hazardMeshes.find(m => m.hazardId === hId);
      const h = this.gameState.hazards.find(hz => hz.id === hId);
      if (hm && h && !hm.seat) { const pos = getHazardWorldPosition(h, planets); hm.group.position.set(pos.x, pos.y, pos.z); }
    }
    for (const rId of r.dropped.ruins) {
      const rm = this.ruinMeshes.find(m => m.ruinId === rId);
      const ru = this.gameState.ruins.find(x => x.id === rId);
      if (rm && ru && !rm.plinth) { const pos = getRuinWorldPosition(ru, planets); rm.group.position.set(pos.x, pos.y, pos.z); }
    }
    for (const canId of r.dropped.dropCans) {
      const can = this.gameState.crashedDropCans.find(c => c.id === canId);
      const canMesh = this.crashedDropCanMeshes.get(canId);
      if (can && canMesh) {
        const p = planets[can.planetIndex];
        const n = vecNormalize(can.surfaceDir);
        const sr = getSurfaceRadius(p, can.surfaceDir);
        canMesh.position.set(p.position.x + n.x * sr, p.position.y + n.y * sr, p.position.z + n.z * sr);
      }
    }
    for (const fId of r.dropped.flora) {
      const fm = this.floraMeshes.find(m => m.floraId === fId);
      const f = this.gameState.flora.find(fl => fl.id === fId);
      if (fm && f && !fm.seat) { const pos = getFloraWorldPosition(f, planets); fm.group.position.set(pos.x, pos.y, pos.z); }   // seated flora was re-seated above
    }
    for (const cId of r.dropped.collectibles) {
      const cm = this.collectibleMeshes.get(cId);
      const c = (this.gameState.collectibles ?? []).find(co => co.id === cId);
      if (cm && c) { const pos = getCollectibleWorldPosition(c, planets); cm.group.position.set(pos.x, pos.y, pos.z); }
    }
    for (const caId of r.dropped.carryables) {
      const cam = this.carryableMeshes.get(caId);
      const ca = this.gameState.carryables.find(cr => cr.id === caId);
      if (cam && ca) { const pos = getCarryableWorldPosition(ca, planets); cam.group.position.set(pos.x, pos.y, pos.z); }
    }
    for (const giId of r.dropped.groundItems) {
      const gm = this.groundItemMeshes.get(giId);
      const gi = this.gameState.groundItems.find(g => g.id === giId);
      if (gm && gi) { const pos = getGroundItemWorldPosition(gi, planets); gm.group.position.set(pos.x, pos.y, pos.z); }
    }

    // Destroyed things
    for (const sId of r.ruinedStructures) {
      const sm = this.sceneObjs.structureMeshes.get(sId);
      if (sm) setStructureRuined(sm);
    }
    for (const hId of r.destroyedHazards) {
      this.hazardEmitterManager?.muteHazard(hId);
      const idx = this.hazardMeshes.findIndex(hm => hm.hazardId === hId);
      if (idx >= 0) { disposeHazardMesh(scene, this.hazardMeshes[idx]); this.hazardMeshes.splice(idx, 1); }
    }
    for (const pos of r.shatterPositions) this.shatterEffects.push(createShatterEffect(scene, pos, this.effectScale()));
    for (const fId of r.destroyedFlags) {
      const pfm = this.sceneObjs.plantedFlagMeshes.get(fId);
      if (pfm) { disposePlantedFlagMesh(scene, pfm); this.sceneObjs.plantedFlagMeshes.delete(fId); }
    }
    for (const canId of r.destroyedDropCans) this.disposeCrashedDropCan(canId);

    // Log, notifications, combat cues
    for (const line of r.logs) logMessage(line);
    for (const killId of r.kills) {
      const p = this.gameState.players.find(pl => pl.id === killId);
      if (p) {
        pushNotification({ type: 'gcppa', message: matchMessage('death', { name: p.name }), sender: matchSender(), color: '#c8a84c' });
      }
    }
    for (const d of r.damages) if (!d.isBot) this.spikeCombatIntensity(0.3);
    for (const pid of r.launched) {
      const m = this.sceneObjs.playerMeshes.get(pid);
      if (m) m.animCtx.state = 'airborne_knockback';
    }
    if (r.kills.length > 0) {
      this.syncCarryableMeshPositions();
      this.updateCarrierIndicators();
      this.syncGroundItemMeshes();
    }
    void impactPoint;
  }

  /**
   * Probe reveal along the recorded flight path (terrain + enemies + enemy
   * structures), then refresh fog visuals if the probe's owner is the viewer.
   * Shared by impact and lost-in-space endings, and by round flights (recorded path).
   */
  private applyProbeReveal(ownerId: number, points: Vec3[] = this.probeTrailPoints): void {
    if (points.length === 0) return;
    this.probeTrailPoints = [];
    if (this.gameState.players.findIndex(p => p.id === ownerId) < 0) return;
    // Match: terrain, ghost markers and structure ghosts along the path, into the owner's fog
    this.presentProbeReveal(ownerId, this.match.revealProbePath(ownerId, points));
  }

  /** The screen half of a probe's path reveal: only what the viewer's own side scanned may appear. */
  private presentProbeReveal(ownerId: number, probeSpotted: number[]): void {
    const playerIdx = this.gameState.players.findIndex(p => p.id === ownerId);
    if (playerIdx < 0) return;
    const onScreen = this.feedsViewerFog(playerIdx);
    if (probeSpotted.length > 0 && onScreen) {
      for (const id of probeSpotted) this.visibleEnemyIds.add(id);
      this.updateChipVisibility();
    }
    if (onScreen) {
      this.applyFogToScene();
      this.updateGhostMeshes();
      this.updateExposureMeshes();
      this.updatePlayerMeshes();
      this.updateStructureVisibility();
      this.updateHazardVisibility();
      this.updateFloraVisibility();
      this.updateDropCanVisibility();
    }
  }

  /**
   * End-of-flight hand-off: the trail is dropped (its observed segments were just
   * persisted), the body and engine flame become an ImpactFade that finishes them
   * over the next frames, and the scene fields are cleared so a cluster submunition
   * can create fresh visuals immediately.
   */
  private handOffProjectileVisuals(point: Vec3, kind: 'impact' | 'lost'): void {
    this.finishDirectedShot(kind);
    if (this.sceneObjs.trailLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.trailLine);
      this.sceneObjs.trailLine = null;
    }
    const mesh = this.sceneObjs.projectileMesh, flame = this.sceneObjs.engineFlame;
    this.sceneObjs.projectileMesh = null;
    this.sceneObjs.engineFlame = null;
    if (mesh || flame) this.impactFades.push(createImpactFade(this.sceneObjs.scene, mesh, flame, point, kind));
  }

  /** A flight ended without an impact (star, wormhole, lost, battery dead): the Match recorded it; this shows it. */
  private presentLost(e: Parameters<SimPresenter['lost']>[0]): void {
    this.lastShotEnd = 'lost';   // I9: the missile cam says how the shot ended
    if (e.ending === 'star' && e.point) {
      logMessage('Shot consumed by stellar fire...');
      this.effects.push(createExplosion(this.sceneObjs.scene, e.point, 1.0, this.gameState.planets[e.planetIndex].palette.b));
    } else if (e.ending === 'wormhole' && e.point) {
      logMessage('Shot torn apart by wormhole instability!');
      this.effects.push(createExplosion(this.sceneObjs.scene, e.point, 0.5, WORMHOLE_COLORS[0].ring));
    } else {
      logMessage('Shot lost in space...');
    }
    clearCameraFrame(this.cameraState);
    this.clearThreatCues();
    this.setBattIndicator(null);
    if (WEAPONS[e.projectile.weaponKey].reveals) this.presentProbeReveal(e.projectile.ownerId, e.probeSpotted);
    this.logShotRecord(e.record);
    this.persistCurrentTrail();
    // Battery dead: the flame sputters out and the body tumbles away (37.2d)
    const lostAt = this.gameState.projectile?.pos
      ?? (this.sceneObjs.projectileMesh ? { x: this.sceneObjs.projectileMesh.position.x, y: this.sceneObjs.projectileMesh.position.y, z: this.sceneObjs.projectileMesh.position.z } : { x: 0, y: 0, z: 0 });
    this.handOffProjectileVisuals(lostAt, 'lost');
  }

  private endProjectile(): void {
    if (this.gameState.phase === 'resolving') return; // Guard against multiple calls
    clearCameraFrame(this.cameraState);
    this.clearThreatCues();

    const gameOver = this.match.endShot();   // the resolving phase
    showControlsBar(false);
    const controlsBar = document.getElementById('controls-bar');
    if (controlsBar) controlsBar.classList.remove('detonate-mode');

    // Clean up
    if (this.sceneObjs.projectileMesh) {
      disposeProjectileMesh(this.sceneObjs.scene, this.sceneObjs.projectileMesh);
      this.sceneObjs.projectileMesh = null;
    }
    if (this.sceneObjs.trailLine) {
      removeFromScene(this.sceneObjs.scene, this.sceneObjs.trailLine);
      this.sceneObjs.trailLine = null;
    }
    if (this.sceneObjs.engineFlame) {
      disposeEngineFlame(this.sceneObjs.scene, this.sceneObjs.engineFlame);
      this.sceneObjs.engineFlame = null;
    }

    // Check win condition
    if (gameOver) {
      this.handleGameOver();
      return;
    }

    // A network peer's match settles the shot here (the host's path); otherwise the turn policy does, on the tick the
    // last airborne unit lands (presentShotSettled)
    if (this.match.hostSettlesShots) this.waitForLandingsThenNextTurn();
  }

  /** Once every knocked-back player is down, check the win condition again (landings can kill) and continue the turn. */
  private waitForLandingsThenNextTurn(): void {
    this.match.afterLandings(gameOver => {
      if (gameOver) {
        this.handleGameOver();
        return;
      }
      this.clock.after(0.5, () => this.continueAfterShot());
    });
  }

  /**
   * The current player's shot has fully resolved. Bots: hand control back to the
   * turn runner, which decides what happens next. Humans: resume aiming (or wait
   * for the remote peer) while movement remains, else advance the turn.
   */
  private continueAfterShot(): void {
    if (this.batchActive) { this.resolveBatch(); return; }   // plan-then-resolve: next queued order
    const current = this.gameState.players[this.gameState.currentPlayerIndex];
    if (current.isBot) { this.settleBotFlight(); return; }
    if (current.alive && current.movementRemaining > 0) {
      if (this.networkSync?.isRemoteHuman(current)) this.waitForRemotePostFire();
      else this.resumeAimPhase();
      return;
    }
    this.nextTurn();
  }

  /** A bot's shot resolved: phase back to 'aiming' and wake the runner awaiting it. */
  private settleBotFlight(): void {
    // Match: back to aiming, wake the runner; nothing awaiting (should not happen) — never stall the game
    if (!this.match.settleBotFlight()) this.nextTurn();
  }

  /** Resume aiming for the current player after their shot resolves (no turn advance). */
  private resumeAimPhase(): void {
    this.setBattIndicator(null);

    this.match.resumeAiming();
    const player = this.gameState.players[this.gameState.currentPlayerIndex];

    updatePlayerBars(this.gameState);
    updateMovementDisplay(player.movementRemaining);
    updateWeaponButtons(this.aimState.weaponIndex, player.loadout);
    updateFlagButton(canPlantFlag(player));
    showControlsBar(!this.isRemoteTurn());
    showLeadershipPanel(false);
    showInventoryPanel(false);

    // Repurpose fire button as END TURN — prominent yield reminder
    updateFireButtonText('END TURN (Y)');
    setFireButtonEnabled(true);
    const fireBtn = document.getElementById('fire-btn');
    if (fireBtn) fireBtn.classList.add('yield-mode');
    const yieldBtn = document.getElementById('yield-btn');
    if (yieldBtn) yieldBtn.classList.add('yield-active');

    // Auto-switch to move mode since firing is spent
    this.setControlMode('move');

    // Re-focus camera on player
    if (!this.cameraState.manualPivot) {
      const playerPos = getPlayerWorldPosition(player, this.gameState.planets);
      glideCameraTo(this.cameraState, playerPos);
    }
  }

  /** Local END TURN / yield. */
  private yieldTurn(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    if (this.walkAnim) return;
    this.performYield();
  }

  /** End the turn voluntarily — the one code path for local and remote yields. */
  private performYield(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    this.broadcastTurnInput({ playerId: player.id, action: 'skip', params: {} });
    logMessage(`${player.name} yields turn`);
    turnEnd();
    this.endTurnFor(player);   // 64.6 2b-ii: the end of the turn is the input itself
  }

  /** Local plant-flag button. */
  private plantFlagAtFeet(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    if (!canPlantFlag(player)) return;
    this.performPlantFlag(player.surfaceDir);
  }

  private performPlantFlag(surfaceDir: Vec3): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!canPlantFlag(player)) return;
    this.broadcastTurnInput({ playerId: player.id, action: 'plantFlag', params: { surfaceDir } });
    // Match: the stake (refused if the planet does not take it)
    const planted = this.turn(player, 'plantFlag', { surfaceDir }) as ReturnType<Match['plantStake']>;
    if (!planted.ok) { if (planted.reason) logMessage(planted.reason); return; }

    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
    const newFlag = this.gameState.plantedFlags.find(f => f.id === planted.flagId)!;
    const worldPos = getPlayerWorldPosition(updatedPlayer, this.gameState.planets);
    const normal = getPlayerSurfaceNormal(updatedPlayer);
    const pfm = createPlantedFlagMesh(newFlag, worldPos, normal, this.sceneObjs.scene, this.gameState.planets[newFlag.planetIndex].radius);
    this.sceneObjs.plantedFlagMeshes.set(newFlag.id, pfm);

    logMessage(`${player.name} plants a flag!`);

    updateMovementDisplay(updatedPlayer.movementRemaining);
    updateFlagButton(canPlantFlag(updatedPlayer));
    updateJumpButton(false, updatedPlayer.movementRemaining >= JUMP_MIN_COST, updatedPlayer.jumppackFuel);
  }

  /** Local remove-stake button. */
  private tryRemoveStake(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    const removable = getRemovableStakes(player, this.gameState.plantedFlags, this.gameState.planets, this.gameState.config.modeConfig.stakeRemoveCost);
    if (removable.length === 0) {
      logMessage('No enemy stakes in range to remove');
      return;
    }
    this.performRemoveStake(removable[0].id);
  }

  private performRemoveStake(stakeId: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!this.gameState.plantedFlags.some(f => f.id === stakeId)) return;
    this.broadcastTurnInput({ playerId: player.id, action: 'removeStake', params: { stakeId } });
    const removed = this.turn(player, 'removeStake', { stakeId }) as ReturnType<Match['removeStake']>;
    if (!removed.ok) { if (removed.reason) logMessage(removed.reason); return; }

    const pfm = this.sceneObjs.plantedFlagMeshes.get(stakeId);
    if (pfm) { disposePlantedFlagMesh(this.sceneObjs.scene, pfm); this.sceneObjs.plantedFlagMeshes.delete(stakeId); }

    logMessage(`${player.name} removes an enemy stake!`);

    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
    updateMovementDisplay(updatedPlayer.movementRemaining);
  }

  /** Local pickup (nearest carryable). */
  private tryPickupObject(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    const pickupable = getPickupableObjects(player, this.gameState.carryables, this.gameState.planets);
    if (pickupable.length === 0) {
      logMessage('Nothing to pick up nearby');
      return;
    }
    this.performPickup(pickupable[0].id);
  }

  private performPickup(objectId: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!this.gameState.carryables.some(c => c.id === objectId)) return;
    this.broadcastTurnInput({ playerId: player.id, action: 'pickup', params: { objectId } });
    // Match: the pickup, CTF flag status, and an immediate capture if picked up at home
    const picked = this.turn(player, 'pickup', { objectId }) as ReturnType<Match['pickup']>;
    if (!picked.ok) { if (picked.reason) logMessage(picked.reason); return; }
    logMessage(`${player.name} picks up ${picked.object.type}!`);

    this.syncCarryableMeshPositions();
    this.updateCarrierIndicators();
    this.updatePlayerMeshes();

    const updatedPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
    updateMovementDisplay(updatedPlayer.movementRemaining);
    updateTurnInfo(this.gameState);
    this.updateHomeWaypointMarker();
    if (picked.captured !== null) this.presentCapture(player.id, picked.captured);
  }

  /** CTF: recompute per-team flag status from the carryables (no-op without the ctf layer). */
  private refreshFlagStatus(): void {
    this.match.refreshFlagStatus();
  }

  private checkAndExecuteCapture(playerId: number): void {
    const captured = this.match.captureIfHome(playerId);
    if (captured !== null) this.presentCapture(playerId, captured);
  }

  /** CTF: a capture the Match executed — the core's mesh goes, the log, and the win check. */
  private presentCapture(playerId: number, objectId: number): void {
    const capturedMesh = this.carryableMeshes.get(objectId);
    if (capturedMesh) {
      disposeCarryableMesh(this.sceneObjs.scene, capturedMesh);
      this.carryableMeshes.delete(objectId);
    }
    this.syncCarryableMeshPositions();
    this.updateCarrierIndicators();
    const player = this.gameState.players.find(p => p.id === playerId);
    if (player) logMessage(`${player.name} captures the enemy nav core!`);
    if (isGameOver(this.gameState)) this.handleGameOver();
  }

  // ===========================================================================
  // CARRYABLE / COLLECTIBLE MESH SYNC
  // ===========================================================================

  /** Sync all carryable mesh positions and visibility based on current state. */
  private syncCarryableMeshPositions(): void {
    for (const carryable of this.gameState.carryables) {
      const mesh = this.carryableMeshes.get(carryable.id);
      if (!carryable.alive) {
        // Remove dead carryable meshes
        if (mesh) {
          disposeCarryableMesh(this.sceneObjs.scene, mesh);
          this.carryableMeshes.delete(carryable.id);
        }
        continue;
      }
      if (carryable.carriedBy !== null) {
        // Hide while carried
        if (mesh) mesh.group.visible = false;
      } else {
        // On ground — make visible and reposition
        if (mesh) {
          mesh.group.visible = true;
          const pos = getCarryableWorldPosition(carryable, this.gameState.planets);
          const normal = new THREE.Vector3(
            carryable.surfaceDir.x, carryable.surfaceDir.y, carryable.surfaceDir.z
          ).normalize();
          mesh.group.position.set(pos.x, pos.y, pos.z).addScaledVector(normal, 0.05);
          mesh.group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
        } else {
          // Create mesh if missing
          const pos = getCarryableWorldPosition(carryable, this.gameState.planets);
          const normal = new THREE.Vector3(
            carryable.surfaceDir.x, carryable.surfaceDir.y, carryable.surfaceDir.z
          ).normalize();
          const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
          const cm = createCarryableMesh(carryable, worldPos, normal, this.sceneObjs.scene);
          this.carryableMeshes.set(carryable.id, cm);
        }
      }
    }
  }

  /** Update carrier indicators on player meshes. */
  private updateCarrierIndicators(): void {
    // Remove old indicators
    for (const [playerId, ind] of this.carrierIndicators) {
      ind.geometry.dispose();
      (ind.material as THREE.Material).dispose();
      const pmesh = this.sceneObjs.playerMeshes.get(playerId);
      if (pmesh) pmesh.group.remove(ind);
    }
    this.carrierIndicators.clear();

    // Add new indicators for current carriers
    for (const carryable of this.gameState.carryables) {
      if (!carryable.alive || carryable.carriedBy === null) continue;
      const pmesh = this.sceneObjs.playerMeshes.get(carryable.carriedBy);
      if (!pmesh) continue;
      // Only one indicator per player (first carried object)
      if (this.carrierIndicators.has(carryable.carriedBy)) continue;
      const indicator = createCarrierIndicator(carryable.type);
      pmesh.group.add(indicator);
      this.carrierIndicators.set(carryable.carriedBy, indicator);
    }
  }

  /** Sync ground item meshes — create new, dispose dead. */
  private syncGroundItemMeshes(): void {
    for (const item of this.gameState.groundItems) {
      if (!item.alive) {
        const mesh = this.groundItemMeshes.get(item.id);
        if (mesh) {
          disposeGroundItemMesh(this.sceneObjs.scene, mesh);
          this.groundItemMeshes.delete(item.id);
        }
        continue;
      }
      if (!this.groundItemMeshes.has(item.id)) {
        const pos = getGroundItemWorldPosition(item, this.gameState.planets);
        const normal = new THREE.Vector3(
          item.surfaceDir.x, item.surfaceDir.y, item.surfaceDir.z
        ).normalize();
        const worldPos = new THREE.Vector3(pos.x, pos.y, pos.z);
        const gm = createGroundItemMesh(item, worldPos, normal, this.sceneObjs.scene);
        this.groundItemMeshes.set(item.id, gm);
      }
    }
  }

  // ===========================================================================
  // MODE-SPECIFIC VISUAL MARKERS
  // ===========================================================================

  /** Create a pulsing ring marker at the artifact site in excavation mode. */
  private createArtifactSiteMarker(): void {
    const ms = this.gameState.modeState.excavation;
    if (!ms) return;
    const planet = this.gameState.planets[ms.artifactPlanetIndex];
    if (!planet) return;

    const dir = ms.artifactSurfaceDir;
    const normal = new THREE.Vector3(dir.x, dir.y, dir.z).normalize();
    const surfaceRadius = planet.radius + 0.05;
    const worldPos = new THREE.Vector3(
      planet.position.x + normal.x * surfaceRadius,
      planet.position.y + normal.y * surfaceRadius,
      planet.position.z + normal.z * surfaceRadius
    );

    const group = new THREE.Group();

    // Ground ring
    const ringGeo = new THREE.TorusGeometry(0.15, 0.012, 8, 24);
    const ringMat = new THREE.MeshLambertMaterial({
      color: '#ffa030',
      emissive: new THREE.Color('#ffa030'),
      emissiveIntensity: 0.6,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = Math.PI / 2; // lay flat
    group.add(ring);

    // Vertical beam (thin glowing column)
    const beamGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.8, 6);
    const beamMat = new THREE.MeshLambertMaterial({
      color: '#ffc060',
      emissive: new THREE.Color('#ffc060'),
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.4,
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    beam.position.y = 0.4;
    group.add(beam);

    // Additive glow blob instead of a PointLight (a light here recompiled every lit shader when the marker appeared)
    const glowBlob = new THREE.Mesh(
      new THREE.SphereGeometry(0.35, 10, 8),
      new THREE.MeshBasicMaterial({ color: '#ffa030', transparent: true, opacity: 0.25, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    glowBlob.position.y = 0.2;
    group.add(glowBlob);

    group.position.copy(worldPos);
    const up = new THREE.Vector3(0, 1, 0);
    group.quaternion.setFromUnitVectors(up, normal);
    group.scale.setScalar(2);

    this.sceneObjs.scene.add(group);
    this.artifactSiteMarker = group;
  }

  /** Dispose artifact site marker. */
  private disposeArtifactSiteMarker(): void {
    if (this.artifactSiteMarker) {
      this.sceneObjs.scene.remove(this.artifactSiteMarker);
      disposeObjectTree(this.artifactSiteMarker);
      this.artifactSiteMarker = null;
    }
  }

  /** Create a pulsing blue ring marker at the signal source in Signal Trace mode. */
  private createSignalSourceMarker(): void {
    const ms = this.gameState.modeState.signalTrace;
    if (!ms) return;
    const planet = this.gameState.planets[ms.signalPlanetIndex];
    if (!planet) return;

    const dir = ms.signalSurfaceDir;
    const normal = new THREE.Vector3(dir.x, dir.y, dir.z).normalize();
    const surfaceRadius = planet.radius + 0.05;
    const worldPos = new THREE.Vector3(
      planet.position.x + normal.x * surfaceRadius,
      planet.position.y + normal.y * surfaceRadius,
      planet.position.z + normal.z * surfaceRadius
    );

    const group = new THREE.Group();

    // Ground ring (blue)
    const ringGeo = new THREE.TorusGeometry(0.15, 0.012, 8, 24);
    const ringMat = new THREE.MeshLambertMaterial({
      color: '#3080ff',
      emissive: new THREE.Color('#3080ff'),
      emissiveIntensity: 0.6,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = Math.PI / 2;
    group.add(ring);

    // Vertical beam (thin blue glowing column)
    const beamGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.8, 6);
    const beamMat = new THREE.MeshLambertMaterial({
      color: '#60a0ff',
      emissive: new THREE.Color('#60a0ff'),
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.4,
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    beam.position.y = 0.4;
    group.add(beam);

    // Additive glow blob instead of a PointLight (a light here recompiled every lit shader when the marker appeared)
    const glowBlob = new THREE.Mesh(
      new THREE.SphereGeometry(0.35, 10, 8),
      new THREE.MeshBasicMaterial({ color: '#3080ff', transparent: true, opacity: 0.25, blending: THREE.AdditiveBlending, depthWrite: false }),
    );
    glowBlob.position.y = 0.2;
    group.add(glowBlob);

    group.position.copy(worldPos);
    const up = new THREE.Vector3(0, 1, 0);
    group.quaternion.setFromUnitVectors(up, normal);
    group.scale.setScalar(2);

    this.sceneObjs.scene.add(group);
    this.signalSourceMarker = group;
  }

  /** Dispose signal source marker. */
  private disposeSignalSourceMarker(): void {
    if (this.signalSourceMarker) {
      this.sceneObjs.scene.remove(this.signalSourceMarker);
      disposeObjectTree(this.signalSourceMarker);
      this.signalSourceMarker = null;
    }
  }

  /** Create depot visuals for salvage run mode. */
  private createDepotMeshes(): void {
    const ms = this.gameState.modeState.salvage;
    if (!ms) return;

    for (const [teamIdxStr, planetIdx] of Object.entries(ms.depotPlanets)) {
      const teamIdx = Number(teamIdxStr);
      const planet = this.gameState.planets[planetIdx];
      if (!planet) continue;

      // Find a team player to determine starting surfaceDir
      const teamPlayer = this.gameState.players.find(p => p.teamIndex === teamIdx);
      const surfaceDir = teamPlayer ? teamPlayer.surfaceDir : { x: 0, y: 1, z: 0 };
      const normal = new THREE.Vector3(surfaceDir.x, surfaceDir.y, surfaceDir.z).normalize();
      const surfaceRadius = planet.radius + 0.05;
      const worldPos = new THREE.Vector3(
        planet.position.x + normal.x * surfaceRadius,
        planet.position.y + normal.y * surfaceRadius,
        planet.position.z + normal.z * surfaceRadius
      );

      const group = new THREE.Group();
      const model = buildTeamStructure(teamIdx, false);
      group.add(model);

      group.position.copy(worldPos);
      const up = new THREE.Vector3(0, 1, 0);
      group.quaternion.setFromUnitVectors(up, normal);
      group.scale.setScalar(surfacePropScale(planet.radius));

      this.sceneObjs.scene.add(group);
      this.depotMeshes.set(teamIdx, group);
    }
  }

  /** Dispose depot meshes. */
  private disposeDepotMeshes(): void {
    for (const [, group] of this.depotMeshes) {
      this.sceneObjs.scene.remove(group);
      disposeObjectTree(group);
    }
    this.depotMeshes.clear();
  }

  /** Show/hide a pulsing beacon on the player's home planet when carrying an enemy navCore (CTC). */
  private updateHomeWaypointMarker(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const carrying = this.gameState.carryables.find(c => c.alive && c.carriedBy === player.id);
    const shouldShow = carrying && carrying.type === 'navCore' &&
      this.gameState.config.modeConfig.mode === 'ctf' &&
      this.gameState.config.modeConfig.homeTeamPlanets;

    if (!shouldShow) {
      if (this.homeWaypointMarker) {
        this.homeWaypointMarker.visible = false;
      }
      return;
    }

    const homePlanetIdx = this.gameState.config.modeConfig.homeTeamPlanets![player.teamIndex];
    if (homePlanetIdx === undefined) return;
    const planet = this.gameState.planets[homePlanetIdx];
    if (!planet) return;

    if (!this.homeWaypointMarker) {
      const group = new THREE.Group();
      const ringGeo = new THREE.TorusGeometry(0.3, 0.015, 8, 32);
      const ringMat = new THREE.MeshBasicMaterial({
        color: '#40ff80',
        transparent: true,
        opacity: 0.7,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      group.add(ring);

      const beamGeo = new THREE.CylinderGeometry(0.008, 0.008, 1.0, 6);
      const beamMat = new THREE.MeshBasicMaterial({
        color: '#40ff80',
        transparent: true,
        opacity: 0.35,
      });
      const beam = new THREE.Mesh(beamGeo, beamMat);
      beam.position.y = 0.5;
      group.add(beam);

      this.sceneObjs.scene.add(group);
      this.homeWaypointMarker = group;
    }

    // Position at planet center, scaled to planet radius
    const pos = planet.position;
    this.homeWaypointMarker.position.set(pos.x, pos.y, pos.z);
    this.homeWaypointMarker.scale.setScalar(planet.radius * 1.5);
    this.homeWaypointMarker.visible = true;
  }

  private disposeHomeWaypointMarker(): void {
    if (this.homeWaypointMarker) {
      this.sceneObjs.scene.remove(this.homeWaypointMarker);
      disposeObjectTree(this.homeWaypointMarker);
      this.homeWaypointMarker = null;
    }
  }

  // ===========================================================================
  // OBJECTIVE ACTIONS (interact, collect, deliver, build drill)
  // ===========================================================================

  /** Local interact button. */
  private tryInteract(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    this.performInteract();
  }

  /**
   * Interact with a ruin — one code path for local, bot and remote.
   * Without `ruinId` the player must be within range of an interactable ruin.
   * Bots pass the ruin they mean: their planner works at planet level and does
   * not path to the exact spot, so the range check is skipped for them.
   */
  private performInteract(ruinId?: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    // Match: build the drill (Excavation), or start / continue / activate a ruin
    const done = this.turn(player, 'interact', ruinId === undefined ? {} : { ruinId }) as ReturnType<Match['interact']>;
    if (!done.ok) { if (done.reason) logMessage(done.reason); return; }
    this.broadcastTurnInput({ playerId: player.id, action: 'interact', params: {} });
    if (done.kind === 'drill') {
      const newDrill = this.gameState.modeState.excavation?.drillSites.find(d => d.id === done.drillId);
      if (newDrill) {
        const drillPos = getDrillSiteWorldPosition(newDrill, this.gameState.planets);
        const drillNormal = new THREE.Vector3(newDrill.surfaceDir.x, newDrill.surfaceDir.y, newDrill.surfaceDir.z).normalize();
        const drillWorldPos = new THREE.Vector3(drillPos.x, drillPos.y, drillPos.z);
        this.drillSiteMeshes.set(newDrill.id, createDrillSiteMesh(newDrill, newDrill.teamIndex, drillWorldPos, drillNormal, this.sceneObjs.scene));
      }
      logMessage(`${player.name} begins constructing a drill site!`);
    } else if (done.kind === 'activated') {
      for (const line of done.logs) logMessage(line);
      if (done.signalRevealed) {
        this.disposeSignalSourceMarker();
        this.createSignalSourceMarker();
      }
    } else if (this.shouldLogForPlayer(player.id)) {
      logMessage(done.kind === 'started'
        ? `${player.name} starts interacting with structure (${done.turns} turns)`
        : `${player.name} continues working... (${done.turnsLeft} turns left)`);
    }
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
    updateTurnInfo(this.gameState);
  }

  /** Local collect/pickup button: carryable, then ground items, then collectible node. */
  private tryCollectOrPickup(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    const pickupable = getPickupableObjects(player, this.gameState.carryables, this.gameState.planets);
    if (pickupable.length > 0) {
      this.performPickup(pickupable[0].id);
      return;
    }
    const pickup = checkGroundItemPickup(this.gameState, player.id);
    if (pickup.pickedUp.length > 0) {
      this.performCollect({});
      return;
    }
    const collectible = this.gameState.collectibles.find(c =>
      !c.collected && canCollect(player, c, this.gameState.planets)
    );
    if (!collectible) {
      logMessage('Nothing to pick up nearby');
      return;
    }
    this.performCollect({ collectibleId: collectible.id });
  }

  /** Collect ground items (no id) or a collectible node — one code path for local and remote. */
  private performCollect(params: CollectActionParams): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    // Match: heal items at the unit's feet, or a named collectible within the carry limit
    const got = this.turn(player, 'collect', params) as ReturnType<Match['collect']>;
    if (!got.ok) { if (got.reason) logMessage(got.reason); return; }
    if (got.kind === 'ground') {
      this.broadcastTurnInput({ playerId: player.id, action: 'collect', params: {} });
      this.syncGroundItemMeshes();
      for (const gi of got.pickedUp) {
        logMessage(`${player.name} picks up ${gi.type === 'healLarge' ? 'large healthpack' : 'small healthpack'}`);
      }
    } else {
      const collectible = got.collectible;
      this.broadcastTurnInput({ playerId: player.id, action: 'collect', params: { collectibleId: collectible.id } });
      const colMesh = this.collectibleMeshes.get(collectible.id);
      if (colMesh) {
        disposeCollectibleMesh(this.sceneObjs.scene, colMesh);
        this.collectibleMeshes.delete(collectible.id);
      }
      this.syncCarryableMeshPositions();
      this.updateCarrierIndicators();
      this.updatePlayerMeshes();
      logMessage(`${player.name} collects ${collectible.type}! (value: ${collectible.value})`);
    }
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
    updateTurnInfo(this.gameState);
  }

  /** Local drop button (whatever is carried). */
  private tryDropObject(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    const carrying = getPlayerCarrying(player.id, this.gameState.carryables);
    if (!carrying) {
      logMessage('Not carrying anything');
      return;
    }
    this.performDrop(carrying.id);
  }

  /** Drop a carried object — one code path for local (button + inventory) and remote. */
  private performDrop(carryableId: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const carrying = getPlayerCarrying(player.id, this.gameState.carryables);
    if (!carrying || carrying.id !== carryableId) return;
    this.broadcastTurnInput({ playerId: player.id, action: 'drop', params: { carryableId } });
    const dropped = this.turn(player, 'drop', { carryableId }) as ReturnType<Match['drop']>;
    if (!dropped.ok) return;
    logMessage(`${player.name} drops ${dropped.object.type}`);
    this.syncCarryableMeshPositions();
    this.updateCarrierIndicators();
    updateTurnInfo(this.gameState);
    this.updateHomeWaypointMarker();
    this.refreshInventoryPanel();
  }

  /** Local deliver button. */
  private tryDeliver(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;
    this.performDeliver();
  }

  /**
   * Deliver what the player carries (uplink fragment / salvage) — one code path
   * for local, bot and remote. Bots name the carryable and may deliver from
   * anywhere on the destination planet (their planner does not path to the antenna).
   */
  private performDeliver(carryableId?: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    // Match: a fragment uploaded at the antenna (Uplink), or salvage at the team depot
    const done = this.turn(player, 'deliver', carryableId === undefined ? {} : { carryableId }) as ReturnType<Match['deliver']>;
    if (!done.ok) {
      logMessage(done.reason);
    } else {
      this.broadcastTurnInput({ playerId: player.id, action: 'deliver', params: {} });
      const mesh = this.carryableMeshes.get(done.object.id);
      if (mesh) {
        disposeCarryableMesh(this.sceneObjs.scene, mesh);
        this.carryableMeshes.delete(done.object.id);
      }
      this.updateCarrierIndicators();
      logMessage(done.kind === 'fragment' ? `${player.name} uploads a data fragment!` : `${player.name} delivers salvage!`);
      if (done.kind === 'fragment' && isGameOver(this.gameState)) this.handleGameOver();
    }
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
    updateTurnInfo(this.gameState);
  }

  /** Start claiming a ruin on the player's planet. Bots only today — humans have no claim UI. */
  private performClaim(ruinId: number): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const claimed = this.turn(player, 'claim', { ruinId }) as ReturnType<Match['claim']>;
    if (!claimed.ok) return;
    if (this.shouldLogForPlayer(player.id)) logMessage(`${player.name} begins claiming ${claimed.ruin.ruinType}`);
    updateMovementDisplay(this.gameState.players[this.gameState.currentPlayerIndex].movementRemaining);
    this.syncRuinClaimMarkers();
  }

  /** Each claimed ruin shows the claiming team's signpost (half-raised while pending, lit when active, knocked over when broken). */
  private syncRuinClaimMarkers(): void {
    for (const ruin of this.gameState.ruins) {
      const rm = this.ruinMeshes.find(m => m.ruinId === ruin.id);
      if (rm) updateRuinClaimMarker(rm, ruin);
    }
  }

  // ===========================================================================
  // BOT HOST — what the bot turn runner may do to the game
  // ===========================================================================

  private makeBotHost(): BotHost {
    const game = this;
    return {
      get state() { return game.gameState; },
      get useWasm() { return game.gameWasm; },
      get worker() { return game.botWorker; },
      setState: next => { game.gameState = next; },
      buildFogContext: player => game.match.botContext(player),
      showThinking: player => game.showBotThinkingFor(player),
      hideThinking: () => { if (game.botThinking) hideBotThinking(game.botThinking); },
      log: (playerId, message) => { if (game.shouldLogForPlayer(playerId)) logMessage(message); },
      taunt: (player, text) => {
        if (!isFlavorEnabled()) return; // bot chatter is part of the flavor layer
        const team = TEAMS[player.teamIndex % TEAMS.length];
        pushNotification({ type: 'broadcast', message: text, sender: team.name, color: team.primary });
      },
      // X11: a bot's call-in, as the harness makes it: an order input from the unit, answered at apply
      callIn: (planetIndex, surfaceDir) => game.callInFire(game.gameState.players[game.gameState.currentPlayerIndex], planetIndex, surfaceDir),
      fireMission: (team, gunId, planetIndex, surfaceDir) => { const ok = game.match.seatFireMission(team, gunId, planetIndex, surfaceDir, 'human'); if (ok) { game.gameState = game.match.state; logMessage(`The commander orders fire from ${game.gameState.players.find(p => p.id === gunId)?.name ?? 'a gun'}`); } return ok; },
      mark: (team, mark) => { const n = game.match.seatMark(team, mark); if (n) game.gameState = game.match.state; return n > 0; },
      fire: params => {
        if (game.roundFlow && !WEAPONS[params.weaponKey].hitscan) {
          // Match: a prepaid order that flies in the round — nothing to wait for
          const bot = game.gameState.players[game.gameState.currentPlayerIndex];
          if (!game.turn(bot, 'queue', params).ok) return false;   // a brain's queue: the prepaid order (Match.queueBotShot)
          logMessage(`${bot.name} holds fire — ${WEAPONS[params.weaponKey].name} queued`);
          return true;
        }
        game.match.expectBotFlight();
        return game.performFire(params);
      },
      jump: aim => game.performJump(aim),
      move: dir => game.performMove(dir),
      build: (type, dir) => game.performBuild(type, dir),
      heal: key => game.performHeal(key),
      shop: params => game.performShop(params),
      melee: id => game.performMelee(id),
      plantFlag: dir => game.performPlantFlag(dir),
      removeStake: id => game.performRemoveStake(id),
      pickup: id => game.performPickup(id),
      interact: ruinId => game.performInteract(ruinId),
      collect: params => game.performCollect(params),
      deliver: id => game.performDeliver(id),
      claim: id => game.performClaim(id),
      mount: structureId => game.performMount(structureId),   // R2: the harness host has it; watch must equal auto-resolve
      waitUntil: pred => game.clock.waitUntil(pred),
      delay: ms => game.clock.delay(ms / 1000),
      waitForFlightResolution: () => game.match.waitForFlightResolution(),
      isWalking: () => game.walkAnim !== null,
      endTurn: () => game.endTurnFor(game.gameState.players[game.gameState.currentPlayerIndex]),   // 64.6 2b-ii: the brain's input
      get plannedRound() { return game.roundFlow; },
    };
  }

  /** Show the thinking sprite above a bot, unless fog hides the bot from the viewer. */
  private showBotThinkingFor(player: PlayerState): void {
    if (!this.botThinking) return;
    const viewer = this.gameState.players[this.viewerIndex];
    const visible = !this.fogCam || (viewer && player.teamIndex === viewer.teamIndex) || this.visibleEnemyIds.has(player.id);
    if (!visible) return;
    showBotThinking(this.botThinking, getPlayerWorldPosition(player, this.gameState.planets), player.surfaceDir);
  }

  /** The host's own pass (a network peer's match keeps this path, decisions 28/41; and its fallbacks). */
  private nextTurn(): void {
    if (this.turnAdvancing) return; // prevent double-advance from overlapping timeouts
    this.turnAdvancing = true;
    // 64.6 step 2a: the Match's boundary — advance the turn, open the next window — presented hop by hop
    this.followPass(this.match.passTurn(this.simPresenter));
  }

  /**
   * 64.6 step 2b-ii: the end of `player`'s turn is an input (`skip`, from its seat or its brain): the Match passes the
   * turn and this host walks the answer. The re-entry guard is the Match's (it refuses while a pass is under way).
   */
  private endTurnFor(player: PlayerState): void {
    const r = this.turn(player, 'skip', {}) as PlanResult<{ pass: TurnPass }>;
    if (r.ok) this.followPass(r.pass);
  }

  /**
   * 64.6 step 2b-ii: the turn policy's rule once a shot settled (Match.settleShot); the half-second beat the game
   * always left after a landing is this presenter's pacing — nothing steps while a unit aims, so the state waits as is.
   */
  private presentShotSettled(s: ShotSettled): void {
    switch (s.next) {
      case 'gameOver': this.handleGameOver(); return;
      case 'pass': this.followPass(s.pass); return;   // the hops were presented as the Match passed the turn
      case 'batch': this.clock.after(0.5, () => this.resolveBatch()); return;   // plan-then-resolve: the next queued order
      case 'bot': this.clock.after(0.5, () => { if (!this.match.wakeBotRunner()) this.endTurnFor(this.gameState.players[this.gameState.currentPlayerIndex]); }); return;   // nothing awaiting (should not happen): never stall
      case 'window': this.clock.after(0.5, () => this.resumeAimPhase()); return;
    }
  }

  /**
   * Local-only memory of a human's aim and weapon when their turn ends without a
   * shot, so the next turn starts where they left it. (The last shot's aim lives
   * in state; unfired changes used to be lost.)
   */
  private aimMemory = new Map<number, { azimuth: number; elevation: number; power: number; weaponIndex: number }>();

  private rememberAim(): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player || player.isBot || this.isRemoteTurn()) return;
    this.aimMemory.set(player.id, {
      azimuth: this.aimState.azimuth,
      elevation: this.aimState.elevation,
      power: this.aimState.power,
      weaponIndex: this.aimState.weaponIndex,
    });
  }

  /** Play out a turn resolution: log, animations, meshes, effects. No game logic. */
  private applyTurnOutcome(r: TurnResolution): void {
    for (const line of r.logs) logMessage(line);

    for (const pid of r.respawned) {
      const m = this.sceneObjs.playerMeshes.get(pid);
      if (m) resetAnimContext(m.animCtx);
      this.startRespawnDropCan(pid);
    }
    for (const pi of r.destroyedPlanets) this.playPlanetDestruction(pi);
    if (r.respawned.length > 0) {
      this.updatePlayerMeshes();
      updatePlayerBars(this.gameState);
    }

    if (r.artifactCreatedId != null) {
      const artifact = this.gameState.carryables.find(c => c.id === r.artifactCreatedId);
      if (artifact) {
        const pos = getCarryableWorldPosition(artifact, this.gameState.planets);
        const normal = new THREE.Vector3(artifact.surfaceDir.x, artifact.surfaceDir.y, artifact.surfaceDir.z).normalize();
        const cm = createCarryableMesh(artifact, new THREE.Vector3(pos.x, pos.y, pos.z), normal, this.sceneObjs.scene);
        this.carryableMeshes.set(artifact.id, cm);
      }
    }


    if (r.extractionCompleted) this.disposeSignalSourceMarker();
    if (r.signalRevealed) {
      this.disposeSignalSourceMarker();
      this.createSignalSourceMarker();
    }

    for (const e of r.erupted) {
      this.eruptionEffects.push(createEruptionEffect(this.sceneObjs.scene, e.position, e.surfaceDir));
      const hm = this.hazardMeshes.find(m => m.hazardId === e.hazardId);
      if (hm) {
        hm.animData.erupting = true;
        hm.animData.eruptionTimer = 1.5;
      }
    }
    for (let i = 0; i < r.eruptionDamages.length; i++) this.spikeCombatIntensity(0.3);
    for (const pid of r.launched) {
      const m = this.sceneObjs.playerMeshes.get(pid);
      if (m) m.animCtx.state = 'airborne_knockback';
    }
    if (r.erupted.length > 0) {
      updatePlayerBars(this.gameState);
      this.updatePlayerMeshes();
    }
  }

  /**
   * A planet was destroyed (Last Light nova): the state already marks it dead
   * and kills its occupants; here everything that stood on it leaves the
   * scene, with a flash where it was. Previously nothing visual happened.
   */
  private playPlanetDestruction(planetIndex: number): void {
    const planet = this.gameState.planets[planetIndex];
    if (!planet) return;
    const scene = this.sceneObjs.scene;

    const meshData = this.sceneObjs.planetMeshes.get(planet.id);
    if (meshData) {
      meshData.mesh.visible = false;
      if (meshData.glow) meshData.glow.visible = false;
      if (meshData.ring) meshData.ring.visible = false;
      if (meshData.clickSphere) meshData.clickSphere.visible = false;
      if (meshData.light) meshData.light.visible = false;
    }
    const onPlanet = <T extends { planetIndex: number }>(items: T[]) => items.filter(i => i.planetIndex === planetIndex);
    for (const h of onPlanet(this.gameState.hazards)) {
      const hm = this.hazardMeshes.find(m => m.hazardId === h.id);
      if (hm) hm.group.visible = false;
      this.hazardEmitterManager?.muteHazard(h.id);
    }
    for (const f of onPlanet(this.gameState.flora)) {
      const fm = this.floraMeshes.find(m => m.floraId === f.id);
      if (fm) fm.group.visible = false;
    }
    this.treeStands?.hidePlanet(planetIndex);
    this.rockMerge?.hidePlanet(planetIndex);
    for (const rk of onPlanet(this.gameState.rocks)) {
      const rm = this.rockMeshes.find(m => m.rockId === rk.id);
      if (rm) rm.group.visible = false;
    }
    for (const ru of onPlanet(this.gameState.ruins)) {
      const rm = this.ruinMeshes.find(m => m.ruinId === ru.id);
      if (rm) rm.group.visible = false;
    }
    for (const em of this.effectMeshes) if (em.planetIndex === planetIndex) em.group.visible = false;
    for (const s of onPlanet(this.gameState.structures)) {
      const sm = this.sceneObjs.structureMeshes.get(s.id);
      if (sm) sm.group.visible = sm.fogShown = false;
    }
    for (const fl of onPlanet(this.gameState.plantedFlags)) {
      const pfm = this.sceneObjs.plantedFlagMeshes.get(fl.id);
      if (pfm) pfm.group.visible = false;
    }
    for (const can of onPlanet(this.gameState.crashedDropCans)) {
      const cm = this.crashedDropCanMeshes.get(can.id);
      if (cm) cm.visible = false;
    }
    this.effects.push(createExplosion(scene, planet.position, planet.radius * 2.0, '#ffd080'));
    this.updatePlayerMeshes();
    updatePlayerBars(this.gameState);
  }

  // ===========================================================================
  // MOVEMENT MODE
  // ===========================================================================

  private toggleMoveMode(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    // Tether: cannot walk while tethered
    if (this.aimState.mode !== 'move' && player.tetheredTurns && player.tetheredTurns > 0) {
      logMessage("Can't move while tethered!");
      return;
    }

    // Toggle mode
    const newMode = this.aimState.mode === 'move' ? 'aim' : 'move';
    this.setControlMode(newMode);
  }

  private toggleJumpMode(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;
    if (this.isRemoteTurn()) return;

    // Tether: cannot jump while tethered
    if (player.tetheredTurns && player.tetheredTurns > 0) {
      logMessage("Can't jump while tethered!");
      return;
    }

    // Can only enter jump mode if we have any movement remaining
    if (this.aimState.mode !== 'jump' && player.movementRemaining < JUMP_MIN_COST) {
      logMessage(`Can't jump! (need at least ${JUMP_MIN_COST.toFixed(1)} movement)`);
      return;
    }

    // Can't jump while carrying an object
    if (this.aimState.mode !== 'jump' && !canCarrierJump(player.id, this.gameState.carryables, this.gameState.config.modeConfig)) {
      logMessage("Can't jump while carrying an object!");
      return;
    }

    // Toggle mode
    const newMode = this.aimState.mode === 'jump' ? 'aim' : 'jump';
    this.setControlMode(newMode);
  }

  private setControlMode(mode: 'aim' | 'move' | 'jump' | 'build'): void {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    this.aimState.mode = mode;

    // Update buttons
    updateMoveButton(mode === 'move');
    updateJumpButton(mode === 'jump', player.movementRemaining >= JUMP_MIN_COST, player.jumppackFuel);
    updateBuildButton(mode === 'build');

    // Fire button: JUMP in jump mode, END TURN after firing, FIRE otherwise
    const fireBtn = document.getElementById('fire-btn');
    if (mode === 'jump') {
      updateFireButtonText('JUMP');
      setFireButtonEnabled(true);
      if (fireBtn) fireBtn.classList.remove('yield-mode');
    } else if (this.hasFiredThisTurn) {
      updateFireButtonText('END TURN (Y)');
      setFireButtonEnabled(true);
      if (fireBtn) fireBtn.classList.add('yield-mode');
    } else {
      updateFireButtonText('FIRE');
      setFireButtonEnabled(true);
      if (fireBtn) fireBtn.classList.remove('yield-mode');
    }

    if (mode === 'build') {
      // Show placement radius ring (smaller than movement ring)
      this.updateBuildPlacementRing();
      // Hide aim indicator
      if (this.sceneObjs.aimIndicator) {
        hideAimIndicator(this.sceneObjs.aimIndicator);
      }
      // Hide preview line
      if (this.sceneObjs.previewLine) {
        removeFromScene(this.sceneObjs.scene, this.sceneObjs.previewLine);
        if (this.gravityField) setGravityFieldPreview(this.gravityField, null);
        this.sceneObjs.previewLine = null;
      }
      // Clear jump preview
      this.clearJumpPreview();
      this.jumpTargetPlanet = null;
    } else if (mode === 'move') {
      // Show movement ring
      this.updateMovementRing();
      // Hide aim indicator
      if (this.sceneObjs.aimIndicator) {
        hideAimIndicator(this.sceneObjs.aimIndicator);
      }
      // Hide preview line
      if (this.sceneObjs.previewLine) {
        removeFromScene(this.sceneObjs.scene, this.sceneObjs.previewLine);
        if (this.gravityField) setGravityFieldPreview(this.gravityField, null);
        this.sceneObjs.previewLine = null;
      }
      // Clear jump preview
      this.clearJumpPreview();
      this.jumpTargetPlanet = null;
    } else if (mode === 'jump') {
      // Hide movement ring
      if (this.sceneObjs.movementRing) {
        hideMovementRing(this.sceneObjs.movementRing);
      }
      // Hide weapon preview line (jump has its own trajectory)
      if (this.sceneObjs.previewLine) {
        removeFromScene(this.sceneObjs.scene, this.sceneObjs.previewLine);
        if (this.gravityField) setGravityFieldPreview(this.gravityField, null);
        this.sceneObjs.previewLine = null;
      }
      this.moveTargetDir = null;
      this.jumpTargetPlanet = null;
      // Show jump trajectory preview (this will also update aim indicator)
      this.jumpPreviewDirty = true;
    } else {
      // Aim mode
      // Hide movement ring
      if (this.sceneObjs.movementRing) {
        hideMovementRing(this.sceneObjs.movementRing);
      }
      // Clear jump preview
      this.clearJumpPreview();
      this.moveTargetDir = null;
      this.jumpTargetPlanet = null;
      // Show aim preview
      this.updatePreview();
    }
  }

  private updateBuildPlacementRing(): void {
    if (!this.sceneObjs.movementRing) return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const planet = this.gameState.planets[player.planetIndex];

    updateMovementRing(
      this.sceneObjs.movementRing,
      planet.position,
      player.surfaceDir,
      planet.radius,
      BUILD_PLACEMENT_RADIUS,
      planet.heightmap,
      planet.basePositions,
      this.buildTargetDir
    );
  }

  private updateMovementRing(): void {
    if (!this.sceneObjs.movementRing) return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    const planet = this.gameState.planets[player.planetIndex];

    // Effective surface range accounts for carrier movement penalty
    const penalty = getCarrierMovementPenalty(player.id, this.gameState.carryables, this.gameState.config.modeConfig);
    const effectiveRange = penalty > 0 ? player.movementRemaining * (1 - penalty) : player.movementRemaining;

    updateMovementRing(
      this.sceneObjs.movementRing,
      planet.position,
      player.surfaceDir,
      planet.radius,
      effectiveRange,
      planet.heightmap,
      planet.basePositions,
      this.moveTargetDir
    );
  }

  private handleMouseMove(e: MouseEvent): void {
    if (this.gameState.phase !== 'aiming') return;

    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;

    // Convert mouse to NDC
    this.mouseNDC.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouseNDC.y = -(e.clientY / window.innerHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.mouseNDC, this.sceneObjs.camera);

    // Waypoint mode: raycast to bot's planet
    if (this.waypointMode) {
      this.handleWaypointMouseMove();
      return;
    }

    if (this.aimState.mode === 'move') {
      this.handleMoveMouseMove(player);
    } else if (this.aimState.mode === 'build') {
      this.handleBuildMouseMove(player);
    }
    // Jump mode uses aim controls, no mouse handling needed
  }

  private handleMoveMouseMove(player: import('./types').PlayerState): void {
    // Raycast to find point on current planet
    const planet = this.gameState.planets[player.planetIndex];
    const meshData = this.sceneObjs.planetMeshes.get(planet.id);
    if (!meshData) return;

    const intersects = this.raycaster.intersectObject(meshData.mesh);
    if (intersects.length > 0) {
      const point = intersects[0].point;
      // Convert to direction from planet center
      const dir: Vec3 = {
        x: point.x - planet.position.x,
        y: point.y - planet.position.y,
        z: point.z - planet.position.z,
      };
      const len = Math.sqrt(dir.x ** 2 + dir.y ** 2 + dir.z ** 2);
      this.moveTargetDir = {
        x: dir.x / len,
        y: dir.y / len,
        z: dir.z / len,
      };
      this.updateMovementRing();

      // Show move cost label
      if (this.sceneObjs.movementRing) {
        const cost = arcDistance(player.surfaceDir, this.moveTargetDir, planet.radius);
        updateMovementCostLabel(this.sceneObjs.movementRing, cost, cost <= player.movementRemaining);
      }
    } else {
      this.moveTargetDir = null;
      this.updateMovementRing();
    }
  }

  private handleWaypointMouseMove(): void {
    if (!this.waypointMode) return;
    const planet = this.gameState.planets[this.waypointMode.planetIndex];
    const meshData = this.sceneObjs.planetMeshes.get(planet.id);
    if (!meshData) return;

    const intersects = this.raycaster.intersectObject(meshData.mesh);
    if (intersects.length > 0) {
      const point = intersects[0].point;
      const dir: Vec3 = {
        x: point.x - planet.position.x,
        y: point.y - planet.position.y,
        z: point.z - planet.position.z,
      };
      const len = Math.sqrt(dir.x ** 2 + dir.y ** 2 + dir.z ** 2);
      this.waypointTargetDir = { x: dir.x / len, y: dir.y / len, z: dir.z / len };

      // Show/update waypoint marker
      const markerPos = {
        x: planet.position.x + this.waypointTargetDir.x * (planet.radius + 0.3),
        y: planet.position.y + this.waypointTargetDir.y * (planet.radius + 0.3),
        z: planet.position.z + this.waypointTargetDir.z * (planet.radius + 0.3),
      };
      if (!this.waypointMarker) {
        const geo = new THREE.SphereGeometry(0.15, 8, 8);
        const mat = new THREE.MeshBasicMaterial({ color: 0xc8a84c, wireframe: true });
        this.waypointMarker = new THREE.Mesh(geo, mat);
        this.sceneObjs.scene.add(this.waypointMarker);
      }
      this.waypointMarker.position.set(markerPos.x, markerPos.y, markerPos.z);
      this.waypointMarker.visible = true;
    } else {
      this.waypointTargetDir = null;
      if (this.waypointMarker) this.waypointMarker.visible = false;
    }
  }

  private handleClick(e: MouseEvent): void {
    // Deploy phase: planet selection
    if (this.gameState.phase === 'deploying') {
      this.deploy.handleClick(e);
      return;
    }

    if (this.gameState.phase !== 'aiming') return;

    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot) return;

    // Waypoint mode: commit waypoint on left-click
    if (this.waypointMode) {
      if (this.waypointTargetDir) {
        this.commitWaypoint();
      }
      return;
    }

    if (this.aimState.mode === 'move') {
      this.handleMoveClick();
    } else if (this.aimState.mode === 'build') {
      this.handleBuildClick();
    }
    // Jump mode now uses fire button, not click
  }

  /** Local click on the movement ring. */
  private handleMoveClick(): void {
    if (!this.moveTargetDir) return;
    if (this.walkAnim) return; // already walking
    if (this.isRemoteTurn()) return;
    this.performMove(this.moveTargetDir);
  }

  /** Walk to a surface direction — the one code path for local, bot and remote moves. True when a walk started. */
  private performMove(targetDir: Vec3): boolean {
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (this.walkAnim) return false;
    if (this.roundFlow) return this.planMove(player, targetDir);

    const currentDir = player.surfaceDir;
    // Match: movement spent, facing, then what the unit walks into (returned core, heal items, capture)
    const moved = this.turn(player, 'move', { surfaceDir: targetDir }) as ReturnType<Match['move']>;
    if (moved.ok) {
      const { distance } = moved;
      // Start walk animation
      const duration = Math.max(0.15, distance / WALK_SPEED);
      this.walkAnim = {
        playerId: player.id,
        startDir: vecClone(currentDir),
        endDir: vecClone(targetDir),
        progress: 0,
        duration,
      };

      this.broadcastTurnInput({ playerId: player.id, action: 'move', params: { surfaceDir: vecClone(targetDir) } });

      const newPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
      updateMovementDisplay(newPlayer.movementRemaining);
      updateJumpButton(false, newPlayer.movementRemaining >= JUMP_MIN_COST, newPlayer.jumppackFuel);
      this.updateMovementRing();

      const visibleToViewer = this.shouldLogForPlayer(player.id);
      if (visibleToViewer) logMessage(`${player.name} moves (${distance.toFixed(1)} units)`);

      if (moved.pickedUp.length > 0) {
        this.syncGroundItemMeshes();
        for (const gi of moved.pickedUp) {
          if (visibleToViewer) logMessage(`${player.name} picks up ${gi.type === 'healLarge' ? 'large healthpack' : 'small healthpack'}`);
        }
      }
      this.syncCarryableMeshPositions();
      this.updateCarrierIndicators();
      if (moved.captured !== null) this.presentCapture(player.id, moved.captured);

      // Set waddle animation state + first footstep
      const playerMesh = this.sceneObjs.playerMeshes.get(player.id);
      if (playerMesh) {
        playerMesh.animCtx.state = 'waddle';
      }
      this.walkStepCount = 0;
      const walkStartPos = getPlayerWorldPosition(player, this.gameState.planets);
      playSpatial(walkStartPos, () => footstepDefault(0, 0));
      return true;
    }
    logMessage(moved.reason);
    return false;
  }

  private updateWalkAnimation(dt: number): void {
    if (!this.walkAnim) return;

    this.walkAnim.progress += dt / this.walkAnim.duration;

    // Check if the walking player is an enemy outside LOS in fog mode
    const walkerVisible = this.shouldLogForPlayer(this.walkAnim.playerId);

    if (this.walkAnim.progress >= 1) {
      // Walk complete — snap to final position
      this.walkAnim = null;
      this.updatePlayerMeshes();

      // Switch back to idle
      const player = this.gameState.players[this.gameState.currentPlayerIndex];
      const playerMesh = this.sceneObjs.playerMeshes.get(player.id);
      if (playerMesh) {
        playerMesh.animCtx.state = 'idle';
      }

      // Update fog (player moved, may reveal new vertices)
      this.updateFogForCurrentPlayer();

      // Re-check melee targets and heal after walking
      this.checkMeleeTargets();
      this.updateHealButtonState();

      // Stabilizers: the walk just spent movement, so the power ceiling and preview tighten
      if (stabilizersOn(this.gameState.config) && !player.isBot) this.onAimChanged();

      // Camera to final position (only if visible)
      if (walkerVisible) {
        const newPos = getPlayerWorldPosition(player, this.gameState.planets);
        setCameraTarget(this.cameraState, newPos);
      }
      return;
    }

    // Skip visual/audio for hidden enemies — animation progress still advances above
    if (!walkerVisible) return;

    // Trigger footsteps at waddle cadence (every 0.25s)
    const STEP_INTERVAL = 0.25;
    const elapsed = this.walkAnim.progress * this.walkAnim.duration;
    const expectedSteps = Math.floor(elapsed / STEP_INTERVAL);
    while (this.walkStepCount < expectedSteps) {
      this.walkStepCount++;
      const side = this.walkStepCount % 2;
      const walkPlayer = this.gameState.players.find(p => p.id === this.walkAnim!.playerId);
      if (walkPlayer) {
        const stepPos = getPlayerWorldPosition(walkPlayer, this.gameState.planets);
        playSpatial(stepPos, () => footstepDefault(0, side * 0.5 + Math.random() * 0.2));
      } else {
        footstepDefault(0, side * 0.5 + Math.random() * 0.2);
      }
    }

    // Interpolate position along planet surface via slerp
    const t = this.walkAnim.progress;
    // Ease in/out for natural feel
    const eased = t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const interpDir = vecSlerp(this.walkAnim.startDir, this.walkAnim.endDir, eased);
    const normalDir = vecNormalize(interpDir);

    // Find the player and planet for surface position calculation
    const playerIdx = this.gameState.players.findIndex(p => p.id === this.walkAnim!.playerId);
    if (playerIdx < 0) { this.walkAnim = null; return; }
    const player = this.gameState.players[playerIdx];
    const planet = this.gameState.planets[player.planetIndex];

    // Get surface height at interpolated direction
    const bp = planet.basePositions;
    const hm = planet.heightmap;
    let bestDot = -2, bestIdx = 0;
    for (let i = 0; i < hm.length; i++) {
      const vx = bp[i * 3], vy = bp[i * 3 + 1], vz = bp[i * 3 + 2];
      const d = vx * normalDir.x + vy * normalDir.y + vz * normalDir.z;
      if (d > bestDot) { bestDot = d; bestIdx = i; }
    }
    const surfaceRadius = planet.radius + hm[bestIdx];
    const worldPos: Vec3 = {
      x: planet.position.x + normalDir.x * surfaceRadius,
      y: planet.position.y + normalDir.y * surfaceRadius,
      z: planet.position.z + normalDir.z * surfaceRadius,
    };

    // Update mesh position
    const playerMesh = this.sceneObjs.playerMeshes.get(this.walkAnim.playerId);
    if (playerMesh) {
      updatePlayerMesh(player, playerMesh, worldPos, normalDir);
    }

    // Camera follows the walker unless the viewer pinned the view elsewhere
    if (!this.cameraState.manualPivot) lerpCameraTarget(this.cameraState, worldPos, 0.1, dt);
  }

  /** Jump with the current aim state (local UI and bots). */
  private executeJump(): void {
    if (this.isRemoteTurn()) return;
    this.performJump({ azimuth: this.aimState.azimuth, elevation: this.aimState.elevation, power: this.aimState.power });
  }

  /** Jump with explicit params — the one code path for local, bot and remote jumps. */
  private performJump(aim: JetpackParams): void {
    if (this.walkAnim) return; // can't jump while walking
    hideInGameShop();
    hideBuildMenu();
    const player = this.gameState.players[this.gameState.currentPlayerIndex];

    // Match: feasibility + launch vector (core/actions.ts); planned in the plan-together flows, launched otherwise
    const jumped = this.turn(player, 'jump', aim) as ReturnType<Match['jump']>;
    if (!jumped.ok) {
      logMessage(jumped.reason);
      return;
    }
    const { launch } = jumped;
    const { launchPos, launchVel, movementToUse, burnFuel } = launch;
    if (jumped.planned) { this.presentPlannedJump(player, aim, launch); return; }
    const playerPos = getPlayerWorldPosition(player, this.gameState.planets);
    const newPlayer = this.gameState.players[this.gameState.currentPlayerIndex];

    // Set jump flight animation
    const jumpMesh = this.sceneObjs.playerMeshes.get(player.id);
    if (jumpMesh) {
      jumpMesh.animCtx.state = 'airborne_jump';
      jumpMesh.animCtx.airborneSpeed = vecLength(launchVel);
    }
    playSpatial(playerPos, jumpLaunch);

    // Update visuals
    this.updatePlayerMeshes();
    updateMovementDisplay(newPlayer.movementRemaining);
    updateJumpButton(false, false);

    this.broadcastTurnInput({ playerId: player.id, action: 'jetpack', params: aim });

    // Log jump
    logMessage(`${player.name} launches!${burnFuel ? ' [FUEL]' : ''} (${movementToUse.toFixed(1)} movement spent)`);

    // Clear jump preview
    this.clearJumpPreview();

    // Reset to aim mode (but controls hidden)
    this.aimState.mode = 'aim';
    updateFireButtonText('FIRE');

    // Camera follows the jumping player — if the viewer can see them and has not pinned the view
    if (this.isUnitVisibleToViewer(player) && !this.cameraState.manualPivot) {
      this.jumpFollowPlayerId = player.id;
      setCameraTarget(this.cameraState, launchPos);
      setCameraZoom(this.cameraState, 45);
    }
  }

  // ===========================================================================
  // HELPERS
  // ===========================================================================

  /** Index of the player whose fog/LOS perspective we render from. */
  private get viewerIndex(): number {
    if (this.fogCam) {
      // In network mode, always view from local team's human player
      if (this.networkSync) {
        const localTeam = this.networkSync.localTeamIndex;
        const idx = this.gameState.players.findIndex(
          p => p.alive && !p.isBot && p.teamIndex === localTeam,
        );
        if (idx >= 0) return idx;
      }
      const idx = this.gameState.players.findIndex(p => p.alive && !p.isBot);
      if (idx >= 0) return idx;
    }
    return this.gameState.currentPlayerIndex;
  }

  private updatePlayerMeshes(): void {
    const viewerId = this.gameState.players[this.viewerIndex]?.id;

    for (const player of this.gameState.players) {
      const playerMesh = this.sceneObjs.playerMeshes.get(player.id);
      if (!playerMesh) continue;

      if (player.airborne && player.airbornePos) {
        // Airborne player - use airborne position, orient along velocity or default up
        const worldPos = player.airbornePos;
        let normal: Vec3 = { x: 0, y: 1, z: 0 };
        if (player.airborneVel) {
          const speed = vecLength(player.airborneVel);
          if (speed > 0.5) {
            // Orient feet toward nearest planet
            let closestPlanet = this.gameState.planets[0];
            let closestDist = Infinity;
            for (const p of this.gameState.planets) {
              const dist = vecLength(vecSub(worldPos, p.position));
              if (dist < closestDist) {
                closestDist = dist;
                closestPlanet = p;
              }
            }
            const towardPlanet = vecSub(closestPlanet.position, worldPos);
            const len = vecLength(towardPlanet);
            if (len > 0.1) {
              // Point feet toward planet (normal points away from planet)
              normal = { x: -towardPlanet.x / len, y: -towardPlanet.y / len, z: -towardPlanet.z / len };
            }
          }
        }
        updatePlayerMesh(player, playerMesh, worldPos, normal);
      } else {
        // Grounded player
        const worldPos = getPlayerWorldPosition(player, this.gameState.planets);
        const normal = getPlayerSurfaceNormal(player);
        updatePlayerMesh(player, playerMesh, worldPos, normal);
      }

      // Fog visibility: free cam/SISSI shows all, fog cam shows teammates + visible enemies
      if (player.id !== viewerId && player.alive) {
        const viewer = this.gameState.players[this.viewerIndex];
        const isTeammate = player.teamIndex === viewer.teamIndex;
        playerMesh.group.visible = !this.fogCam || isTeammate || this.visibleEnemyIds.has(player.id);
      }
      // Still riding the respawn drop can down
      if (this.respawnDropCans.has(player.id)) playerMesh.group.visible = false;
    }
  }

  /**
   * Compute the angular offset between the physics tangent basis (used by
   * computeLaunchDirection for azimuth=0) and the Three.js group quaternion's
   * local +Z axis. This offset converts a physics azimuth into a model rotation.y.
   */
  private getAzimuthOffset(player: { surfaceDir: Vec3 }, groupQ: THREE.Quaternion): number {
    const normal = vecNormalize(player.surfaceDir);

    // Same tangent basis as computeLaunchDirection (shared frame in math.ts)
    const { tx } = surfaceTangentFrame(normal);

    // Transform physics tx into model local space
    const invQ = groupQ.clone().invert();
    const localTx = new THREE.Vector3(tx.x, tx.y, tx.z).applyQuaternion(invQ);

    // tx is the azimuth=0 direction; get the angle from local +Z to localTx around Y
    return Math.atan2(localTx.x, localTx.z);
  }

  /**
   * Convert a world-space tangent direction to a model rotation.y value.
   */
  private worldDirToModelRotY(
    worldDir: Vec3,
    surfaceNormal: Vec3,
    groupQ: THREE.Quaternion
  ): number {
    // Project worldDir onto tangent plane
    const dotN = worldDir.x * surfaceNormal.x + worldDir.y * surfaceNormal.y + worldDir.z * surfaceNormal.z;
    const tangent = {
      x: worldDir.x - dotN * surfaceNormal.x,
      y: worldDir.y - dotN * surfaceNormal.y,
      z: worldDir.z - dotN * surfaceNormal.z,
    };
    // Transform to local space
    const invQ = groupQ.clone().invert();
    const localDir = new THREE.Vector3(tangent.x, tangent.y, tangent.z)
      .applyQuaternion(invQ)
      .normalize();
    return Math.atan2(localDir.x, localDir.z);
  }

  private updatePlayerAnimations(dt: number): void {
    const currentPlayer = this.gameState.players[this.gameState.currentPlayerIndex];

    for (const player of this.gameState.players) {
      const playerMesh = this.sceneObjs.playerMeshes.get(player.id);
      if (!playerMesh || !player.alive) continue;

      const ctx = playerMesh.animCtx;

      // Walking: face the movement direction
      if (this.walkAnim && this.walkAnim.playerId === player.id && ctx.state === 'waddle') {
        ctx.aimTracking = false;
        const walkDir = vecSub(this.walkAnim.endDir, this.walkAnim.startDir);
        if (vecLength(walkDir) > 1e-6) {
          const normal = vecNormalize(player.surfaceDir);
          playerMesh.model.group.rotation.y = this.worldDirToModelRotY(
            walkDir, normal, playerMesh.group.quaternion
          );
        }
      } else if (
        player.id === currentPlayer.id &&
        this.gameState.phase === 'aiming' &&
        !player.isBot &&
        this.aimState.mode === 'aim'
      ) {
        // Set aim tracking for the current human player during aim phase
        ctx.aimTracking = true;
        ctx.aimElevation = this.aimState.elevation;
        // Convert physics azimuth to model rotation using tangent basis offset
        const offset = this.getAzimuthOffset(player, playerMesh.group.quaternion);
        playerMesh.model.group.rotation.y = this.aimState.azimuth + offset;
      } else if (!player.airborne) {
        // All other grounded players: show launcher at their last aim direction
        ctx.aimTracking = true;
        ctx.aimElevation = player.lastAimElevation;
        const offset = this.getAzimuthOffset(player, playerMesh.group.quaternion);
        playerMesh.model.group.rotation.y = player.lastAimAzimuth + offset;
      } else {
        ctx.aimTracking = false;
        // Update airborne speed for flight animation intensity
        if (player.airborneVel) {
          ctx.airborneSpeed = vecLength(player.airborneVel);
        }
      }

      // Build mode: the current human carries the hammer instead of the launcher
      setHeldTool(playerMesh.model,
        player.id === currentPlayer.id && !player.isBot && this.aimState.mode === 'build' ? 'hammer' : 'launcher');

      updateAnimation(playerMesh.model, ctx, dt);
    }
  }

  private updateJumpPreview(): void {
    if (this.aimState.mode !== 'jump') {
      this.clearJumpPreview();
      return;
    }

    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (!player.alive) { this.clearJumpPreview(); return; }
    const playerPos = getPlayerWorldPosition(player, this.gameState.planets);

    // Same launch vector as the real jump (core/actions.ts) — the preview used
    // to have its own copy that left out the player's upgrade jumpBonus
    const { launchDir, launchVel, launchPos } = jumpLaunchVector(
      player, this.gameState.planets,
      { azimuth: this.aimState.azimuth, elevation: this.aimState.elevation, power: this.aimState.power },
      this.gameState.config.modeConfig,
    );

    // Simulate trajectory
    const trajResult = simulateJumpTrajectory(launchPos, launchVel, this.gameState.planets);

    // Clear old preview
    if (this.jumpPreviewLine) {
      removeFromScene(this.sceneObjs.scene, this.jumpPreviewLine);
      this.jumpPreviewLine = null;
    }

    // Create new preview line
    const points = trajResult.points;
    if (points.length > 1) {
      const geo = new THREE.BufferGeometry();
      const arr = new Float32Array(points.length * 3);
      for (let i = 0; i < points.length; i++) {
        arr[i * 3] = points[i].x;
        arr[i * 3 + 1] = points[i].y;
        arr[i * 3 + 2] = points[i].z;
      }
      geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));

      // Color based on landing: green = lands somewhere, red = lost in space
      const willLandSomewhere = trajResult.landingPlanetIndex !== null;
      const color = willLandSomewhere ? 0x88c4a0 : 0xc48888;

      const mat = new THREE.LineDashedMaterial({
        color,
        transparent: true,
        opacity: 0.6,
        dashSize: 0.8,
        gapSize: 0.4,
      });

      this.jumpPreviewLine = new THREE.Line(geo, mat);
      this.jumpPreviewLine.computeLineDistances();
      this.sceneObjs.scene.add(this.jumpPreviewLine);
    }

    // Update aim indicator for jump mode too
    if (this.sceneObjs.aimIndicator) {
      updateAimIndicator(
        this.sceneObjs.aimIndicator,
        playerPos,
        launchDir,
        this.aimState.power,
        '#88c4a0' // Green color for jump
      );
    }
  }

  private clearJumpPreview(): void {
    if (this.jumpPreviewLine) {
      removeFromScene(this.sceneObjs.scene, this.jumpPreviewLine);
      this.jumpPreviewLine = null;
    }
  }

  // ===========================================================================
  // FOG OF WAR
  // ===========================================================================

  /**
   * Update fog for the current player: recalculate LOS, update ghosts,
   * and apply fog colors to planet meshes.
   */
  private updateFogForCurrentPlayer(refresh = true): void {
    const playerIndex = this.gameState.currentPlayerIndex;
    const player = this.gameState.players[playerIndex];
    if (!player.alive) return;

    // Match: the current unit's line of sight and ghost markers (always, bots too) — unless the Match just did it
    if (refresh) this.match.refreshFog(playerIndex);
    const useDatalink = this.gameState.config.datalinkedLOS;

    // Check if any human on the viewer's team is spotted by enemies
    const vi = this.viewerIndex;
    const viewer = this.gameState.players[vi];
    const turnNumber = this.gameState.turnNumber;

    for (let pi = 0; pi < this.gameState.players.length; pi++) {
      const teammate = this.gameState.players[pi];
      if (!teammate.alive) continue;
      if (teammate.teamIndex !== viewer.teamIndex) continue;

      const spottingTeams = checkIfPlayerSpotted(teammate, this.gameState.players, this.gameState.planets, this.gameWasm);
      if (spottingTeams.length > 0) {
        // Every spotted teammate gets a marker (so you know who to move); only humans get the alert
        const lastNotified = this.spottedCooldowns.get(teammate.id) ?? -999;
        if (!teammate.isBot && turnNumber - lastNotified >= SPOTTED_NOTIFICATION_COOLDOWN) {
          logMessage(`${teammate.name} has been spotted by enemy forces!`);
          pushNotification({ type: 'broadcast', message: fx('Spotted: an enemy has line of sight to you', 'SENSOR ALERT: Position compromised'), sender: alertSender(), color: '#d4a040' });
          this.spottedCooldowns.set(teammate.id, turnNumber);
        }

        // Update exposure marker in the viewer's fog state (rendered from viewer's perspective)
        this.match.markExposure(vi, 'los', getPlayerWorldPosition(teammate, this.gameState.planets), EXPOSURE_MARKER_MAX_AGE);
      }
    }

    // Rendering uses the viewer's perspective (human player in fog cam)
    // 57.4: through the perception rule — a human guard sees what its alert level allows; an infiltrator sees under LOS
    this.visibleEnemyIds = new Set(
      useDatalink
        ? getTeamVisibleEnemies(viewer, this.gameState.players, this.gameState.planets, this.gameWasm, this.gameState)
        : getVisibleEnemies(viewer, this.gameState.players, this.gameState.planets, this.gameWasm, this.gameState)
    );
    this.updateChipVisibility();

    // Apply fog colors to planet meshes
    this.applyFogToScene();

    // Update ghost marker meshes (includes structure ghosts)
    this.updateGhostMeshes();

    // Update exposure marker meshes
    this.updateExposureMeshes();

    // Update player mesh visibility (hides enemies not in LOS)
    this.updatePlayerMeshes();

    // Update structure visibility (fog-gated)
    this.updateStructureVisibility();
    this.updateHazardVisibility();
    this.updateFloraVisibility();
    this.updateDropCanVisibility();

    // Update carryable/collectible/drill visibility (fog-gated)
    this.updateCarryableVisibility();
  }

  private viewerFogCache: {
    fog: FogState[]; players: PlayerState[]; vi: number; datalink: boolean; result: FogState | undefined;
  } | null = null;

  /**
   * The fog the viewer sees: their own, or the team merge under datalink.
   * Memoised on the fog/players arrays and viewer — a fog update used to
   * rebuild the merge (cloning every planet's arrays) up to seven times.
   * In-place reveals (revealScanAtPosition) reset the cache themselves.
   */
  private getViewerFog(): FogState | undefined {
    const vi = this.viewerIndex;
    const { fog, players } = this.gameState;
    const datalink = this.gameState.config.datalinkedLOS;
    const c = this.viewerFogCache;
    if (c && c.fog === fog && c.players === players && c.vi === vi && c.datalink === datalink) return c.result;
    const result = datalink ? getTeamFog(vi, fog, players) : fog[vi];
    this.viewerFogCache = { fog, players, vi, datalink, result };
    return result;
  }

  /** Apply the viewer's fog colors to every planet mesh, or to one planet only. */
  private applyFogToScene(onlyPlanetIndex?: number): void {
    if (this.devReveal) {
      this.restoreTrueColors();
      return;
    }

    const vi = this.viewerIndex;
    const fogState = this.getViewerFog();
    if (!fogState) return;

    for (let pi = 0; pi < this.gameState.planets.length; pi++) {
      if (onlyPlanetIndex !== undefined && pi !== onlyPlanetIndex) continue;
      const planet = this.gameState.planets[pi];
      const meshData = this.sceneObjs.planetMeshes.get(planet.id);
      if (!meshData) continue;

      applyFogColors(meshData, fogState.planets[pi], this.gameState.turnNumber);
    }
  }

  /**
   * Restore true colors to planet meshes (used when we need to snapshot
   * current true colors, e.g. after crater).
   */
  private restoreTrueColors(): void {
    for (const planet of this.gameState.planets) {
      const meshData = this.sceneObjs.planetMeshes.get(planet.id);
      const trueColors = this.truePlanetColors.get(planet.id);
      if (meshData && trueColors) {
        const geo = meshData.mesh.geometry as THREE.BufferGeometry | undefined;
        if (!geo) continue;
        const colorAttr = geo.attributes.color;
        if (!colorAttr) continue;
        (colorAttr.array as Float32Array).set(trueColors);
        colorAttr.needsUpdate = true;
        refreshSurfaceLookColours(meshData);   // 71.8
        refreshMountainStyleColours(meshData);   // 388
      }
    }
  }

  /** Does what this player learns show up on screen right now? (viewer, or a datalinked teammate) */
  private feedsViewerFog(playerIdx: number): boolean {
    const vi = this.viewerIndex;
    if (playerIdx === vi) return true;
    if (!this.gameState.config.datalinkedLOS) return false;
    const viewer = this.gameState.players[vi];
    const p = this.gameState.players[playerIdx];
    return !!viewer && !!p && p.teamIndex === viewer.teamIndex;
  }

  /**
   * A scanner (probe in flight, airborne player) looks around from `pos`:
   * terrain it faces, enemies and enemy structures it has a clear line to.
   * Anyone on the viewer's team it catches gets an exposure marker (and, for
   * humans, an alert).
   */
  private revealScanAtPosition(pos: Vec3, scannerId: number, source: 'probe' | 'flyover'): void {
    // Match: terrain, ghost markers and structure ghosts into the scanner's fog
    const scan = this.match.scanAt(pos, scannerId);
    if (scan) this.presentScan(scan, source);
  }

  /** The viewer-relative half of a scan: exposure markers and alerts when it found the viewer's units, and the screen. */
  private presentScan(scan: ScanResult, source: 'probe' | 'flyover'): void {
    const scannerIdx = this.gameState.players.findIndex(p => p.id === scan.scannerId);
    if (scannerIdx < 0) return;
    const scanner = this.gameState.players[scannerIdx];
    const { players, planets, turnNumber } = this.gameState;
    const { spotted, spottedStructures, terrainRevealed } = scan;
    const onScreen = this.feedsViewerFog(scannerIdx);
    this.viewerFogCache = null; // the scan mutated fog arrays in place

    if (spotted.length > 0) {
      // The viewer's team got scanned by an enemy
      const vi = this.viewerIndex;
      const viewerTeam = players[vi]?.teamIndex;
      if (viewerTeam !== undefined && scanner.teamIndex !== viewerTeam) {
        for (const spottedId of spotted) {
          const sp = players.find(p => p.id === spottedId);
          if (!sp || sp.teamIndex !== viewerTeam) continue;
          if (!sp.isBot) {
            const lastNotified = this.spottedCooldowns.get(sp.id) ?? -999;
            if (turnNumber - lastNotified >= SPOTTED_NOTIFICATION_COOLDOWN) {
              if (source === 'probe') {
                logMessage(`Enemy probe has scanned ${sp.name}'s position!`);
                pushNotification({ type: 'broadcast', message: fx('Spotted: an enemy probe scanned your position', 'PROBE ALERT: Enemy recon has painted your position'), sender: alertSender(), color: '#d4a040' });
              } else {
                logMessage(`Enemy flyover has eyes on ${sp.name}'s position!`);
                pushNotification({ type: 'broadcast', message: fx('Spotted: an airborne enemy can see you', 'SENSOR ALERT: Airborne contact has your position'), sender: alertSender(), color: '#d4a040' });
              }
              this.spottedCooldowns.set(sp.id, turnNumber);
            }
          }
          this.match.markExposure(vi, source === 'probe' ? 'probe' : 'los', getPlayerWorldPosition(sp, planets), EXPOSURE_MARKER_MAX_AGE);
        }
      }

      if (onScreen) {
        for (const id of spotted) this.visibleEnemyIds.add(id);
        this.updateChipVisibility();
        this.updateGhostMeshes();
        this.updatePlayerMeshes();
      }
    }

    if (spottedStructures.length > 0 && onScreen) {
      this.updateGhostMeshes();
      this.updateStructureVisibility();
      this.updateHazardVisibility();
      this.updateFloraVisibility();
      this.updateDropCanVisibility();
    }

    if (terrainRevealed && onScreen) {
      this.applyFogToScene();
      this.updateExposureMeshes();
    }
  }

  /**
   * Check if a projectile position is visible to the viewer's team (collective LOS).
   * Any alive team member having LOS makes it visible.
   * Excludes each viewer's own planet from blocking (player is on the surface).
   */
  private isProjectileVisibleToViewer(projPos: Vec3): boolean {
    const viewer = this.gameState.players[this.viewerIndex];
    if (!viewer) return true; // No viewer, show everything

    const planets = this.gameState.planets;

    // Any alive teammate with an unobstructed eye-to-projectile line (core/los.ts) sees it
    for (const p of this.gameState.players) {
      if (!p.alive || p.teamIndex !== viewer.teamIndex) continue;
      if (hasLineOfSight(losEyePoint(p, planets), projPos, planets)) return true;
    }
    return false;
  }

  /**
   * Create a line from an array of points and add it to the scene.
   */
  private createLineFromPoints(points: Vec3[], color: string, opacity: number): THREE.Line {
    const geo = new THREE.BufferGeometry();
    const arr = new Float32Array(points.length * 3);
    for (let i = 0; i < points.length; i++) {
      arr[i * 3] = points[i].x;
      arr[i * 3 + 1] = points[i].y;
      arr[i * 3 + 2] = points[i].z;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    const line = new THREE.Line(geo, mat);
    line.frustumCulled = false;
    this.sceneObjs.scene.add(line);

    // Attach direction chevrons
    const chevrons = this.createChevronsForPoints(points, color, opacity);
    line.userData.chevrons = chevrons;

    return line;
  }

  /**
   * Create small cone chevrons along a trail to indicate direction of travel.
   */
  private createChevronsForPoints(points: Vec3[], color: string, opacity: number): THREE.Group {
    const CHEVRON_SPACING = 30;
    const group = new THREE.Group();
    if (points.length < CHEVRON_SPACING * 2) return group;

    const coneGeo = new THREE.ConeGeometry(0.12, 0.25, 4);
    coneGeo.rotateX(Math.PI / 2); // Point cone tip along +Z

    for (let i = CHEVRON_SPACING; i < points.length - 1; i += CHEVRON_SPACING) {
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity });
      const cone = new THREE.Mesh(coneGeo, mat);
      cone.position.set(points[i].x, points[i].y, points[i].z);

      // Orient toward next point in the trail
      const next = Math.min(i + CHEVRON_SPACING, points.length - 1);
      cone.lookAt(points[next].x, points[next].y, points[next].z);

      group.add(cone);
    }

    this.sceneObjs.scene.add(group);
    return group;
  }

  /**
   * Remove a trail line and its associated chevrons from the scene.
   */
  private removeTrailLine(line: THREE.Line): void {
    const chevrons = line.userData.chevrons as THREE.Group | undefined;
    if (chevrons) {
      chevrons.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
      this.sceneObjs.scene.remove(chevrons);
    }
    removeFromScene(this.sceneObjs.scene, line);
  }

  /**
   * Persist the observed trail segments as static lines in the scene.
   * Called after each projectile finishes (impact or lost).
   */
  private persistCurrentTrail(): void {
    // Clean up live trail lines first
    this.clearLiveTrailLines();

    for (const segment of this.observedTrailSegments) {
      if (segment.length < 2) continue;
      const line = this.createLineFromPoints(segment, this.currentTrailColor, 0.25);
      line.userData.teamColor = this.currentTrailTeamColor;
      this.persistedTrails.push(line);
    }
    this.observedTrailSegments = [];
  }

  /**
   * Update live trail lines during flight (fog mode: one line per observed segment).
   */
  private updateLiveTrailLines(): void {
    // Keep one growable line (plus chevrons) per observed trail segment and
    // append only the new points each frame. The previous version rebuilt every
    // line, geometry, material and chevron from scratch every frame, producing
    // O(trail length) garbage per frame and in-flight hitching.
    const CHEVRON_SPACING = 30;
    const segments = this.observedTrailSegments;

    for (let si = 0; si < segments.length; si++) {
      const segment = segments[si];
      let lt = this.liveTrailLines[si];
      if (!lt) {
        const capacity = 2048;
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(capacity * 3), 3));
        geo.setDrawRange(0, 0);
        const mat = new THREE.LineBasicMaterial({ color: this.currentTrailColor, transparent: true, opacity: 0.4 });
        const line = new THREE.Line(geo, mat);
        line.frustumCulled = false;
        this.sceneObjs.scene.add(line);
        const chevrons = new THREE.Group();
        this.sceneObjs.scene.add(chevrons);
        const coneGeo = new THREE.ConeGeometry(0.12, 0.25, 4);
        coneGeo.rotateX(Math.PI / 2);
        const coneMat = new THREE.MeshBasicMaterial({ color: this.currentTrailColor, transparent: true, opacity: 0.4 });
        lt = { line, count: 0, capacity, chevrons, nextChevronIdx: CHEVRON_SPACING, coneGeo, coneMat };
        this.liveTrailLines[si] = lt;
      }
      if (segment.length <= lt.count) continue;

      // Grow the buffer if needed
      if (segment.length > lt.capacity) {
        let cap = lt.capacity;
        while (cap < segment.length) cap *= 2;
        const old = (lt.line.geometry.attributes.position as THREE.BufferAttribute).array as Float32Array;
        const grown = new Float32Array(cap * 3);
        grown.set(old);
        lt.line.geometry.setAttribute('position', new THREE.BufferAttribute(grown, 3));
        lt.capacity = cap;
      }

      const attr = lt.line.geometry.attributes.position as THREE.BufferAttribute;
      const arr = attr.array as Float32Array;
      for (let i = lt.count; i < segment.length; i++) {
        arr[i * 3] = segment[i].x;
        arr[i * 3 + 1] = segment[i].y;
        arr[i * 3 + 2] = segment[i].z;
      }
      attr.addUpdateRange(lt.count * 3, (segment.length - lt.count) * 3);
      attr.needsUpdate = true;
      lt.count = segment.length;
      lt.line.geometry.setDrawRange(0, lt.count);

      // Add direction chevrons as soon as their look-ahead point exists
      while (lt.nextChevronIdx + CHEVRON_SPACING <= lt.count - 1) {
        const i = lt.nextChevronIdx;
        const cone = new THREE.Mesh(lt.coneGeo, lt.coneMat);
        cone.position.set(segment[i].x, segment[i].y, segment[i].z);
        const next = segment[i + CHEVRON_SPACING];
        cone.lookAt(next.x, next.y, next.z);
        lt.chevrons.add(cone);
        lt.nextChevronIdx += CHEVRON_SPACING;
      }
    }
  }


  /**
   * Remove live trail lines from scene.
   */
  private clearLiveTrailLines(): void {
    for (const lt of this.liveTrailLines) {
      if (!lt) continue;
      this.sceneObjs.scene.remove(lt.chevrons);
      lt.coneGeo.dispose();
      lt.coneMat.dispose();
      removeFromScene(this.sceneObjs.scene, lt.line);
    }
    this.liveTrailLines = [];
  }


  /**
   * Promote current round trails into history (dimmed, team-colored).
   * Prune rounds beyond trailHistoryRounds.
   * Called at the start of the human player's turn.
   */
  private clearPersistedTrails(): void {
    const currentTurn = this.gameState.turnNumber;

    // Only prune and promote once per round (turnNumber change),
    // not once per human player turn within the same round
    if (currentTurn !== this.lastTrailPromotionTurn) {
      this.lastTrailPromotionTurn = currentTurn;

      // Prune oldest rounds beyond the limit
      while (this.historicalTrails.length >= this.trailHistoryRounds) {
        const old = this.historicalTrails.pop()!;
        for (const trail of old) this.removeTrailLine(trail);
      }

      // Dim existing historical rounds further (older = dimmer)
      for (let r = 0; r < this.historicalTrails.length; r++) {
        const age = r + 2; // +2 because index 0 is about to become age 1, shifting everything
        const opacity = Math.max(0.04, 0.12 / age);
        for (const trail of this.historicalTrails[r]) {
          const mat = trail.material as THREE.LineBasicMaterial;
          mat.opacity = opacity;
          mat.needsUpdate = true;
          const chevrons = trail.userData.chevrons as THREE.Group | undefined;
          if (chevrons) {
            chevrons.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                const m = child.material as THREE.MeshBasicMaterial;
                m.opacity = opacity;
                m.needsUpdate = true;
              }
            });
          }
        }
      }

      // Promote current round trails → newest historical layer (re-color to team, dim)
      if (this.persistedTrails.length > 0) {
        const newLayer: THREE.Line[] = [];
        for (const trail of this.persistedTrails) {
          const teamColor = trail.userData.teamColor || '#ffffff';
          const mat = trail.material as THREE.LineBasicMaterial;
          mat.color.set(teamColor);
          mat.opacity = 0.12;
          mat.needsUpdate = true;
          trail.visible = this.trailPersist;

          const chevrons = trail.userData.chevrons as THREE.Group | undefined;
          if (chevrons) {
            chevrons.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                const m = child.material as THREE.MeshBasicMaterial;
                m.color.set(teamColor);
                m.opacity = 0.12;
                m.needsUpdate = true;
              }
            });
            chevrons.visible = this.trailPersist;
          }

          newLayer.push(trail);
        }
        this.historicalTrails.unshift(newLayer);
        this.persistedTrails = [];
      }
    }

    this.clearLiveTrailLines();
  }

  /**
   * Sync ghost marker meshes (players + structures) to the viewer's fog.
   * Meshes whose marker is unchanged (same spot, same age) are kept; only
   * added, moved or aged markers are rebuilt. Rebuilding every marker and
   * its canvas label on each fog update — including every 10 steps of a
   * probe flight — was pure churn.
   */
  private updateGhostMeshes(): void {
    const scene = this.sceneObjs.scene;
    // SISSI mode: everything is visible directly, no markers
    const fogState = this.devReveal ? undefined : this.getViewerFog();
    const turn = this.gameState.turnNumber;
    const keyOf = (id: number, pos: Vec3, turnsSince: number) =>
      `${id}|${pos.x.toFixed(3)},${pos.y.toFixed(3)},${pos.z.toFixed(3)}|${turnsSince}`;

    // Player ghosts
    const wantGhosts = new Map<string, { position: Vec3; color: string; turnsSince: number; playerId: number }>();
    if (fogState) {
      for (const ghost of fogState.ghosts) {
        const enemy = this.gameState.players.find(p => p.id === ghost.playerId);
        if (!enemy) continue;
        const turnsSince = turn - ghost.turnSeen;
        wantGhosts.set(keyOf(ghost.playerId, ghost.position, turnsSince), {
          position: ghost.position, color: TEAMS[enemy.teamIndex % TEAMS.length].primary, turnsSince, playerId: ghost.playerId,
        });
      }
    }
    const keptGhosts: GhostMesh[] = [];
    const dropGhosts: GhostMesh[] = [];
    for (const gm of this.sceneObjs.ghostMeshes) {
      const key = gm.group.userData.fogKey as string | undefined;
      if (key !== undefined && wantGhosts.has(key)) { keptGhosts.push(gm); wantGhosts.delete(key); }
      else dropGhosts.push(gm);
    }
    disposeGhostMeshes(scene, dropGhosts);
    for (const [key, g] of wantGhosts) {
      const gm = createGhostMesh(scene, g.position, g.color, g.turnsSince, g.playerId);
      gm.group.userData.fogKey = key;
      keptGhosts.push(gm);
    }
    this.sceneObjs.ghostMeshes = keptGhosts;

    // Structure ghosts
    const wantStructs = new Map<string, { position: Vec3; color: string; turnsSince: number; structureId: number }>();
    if (fogState) {
      for (const sg of fogState.structureGhosts) {
        const owner = this.gameState.players.find(p => p.id === sg.ownerId);
        if (!owner) continue;
        const turnsSince = turn - sg.turnSeen;
        wantStructs.set(keyOf(sg.structureId, sg.position, turnsSince), {
          position: sg.position, color: TEAMS[owner.teamIndex % TEAMS.length].primary, turnsSince, structureId: sg.structureId,
        });
      }
    }
    const keptStructs: StructureGhostMesh[] = [];
    const dropStructs: StructureGhostMesh[] = [];
    for (const sm of this.sceneObjs.structureGhostMeshes) {
      const key = sm.group.userData.fogKey as string | undefined;
      if (key !== undefined && wantStructs.has(key)) { keptStructs.push(sm); wantStructs.delete(key); }
      else dropStructs.push(sm);
    }
    disposeStructureGhostMeshes(scene, dropStructs);
    for (const [key, g] of wantStructs) {
      const sm = createStructureGhostMesh(scene, g.position, g.color, g.turnsSince, g.structureId);
      sm.group.userData.fogKey = key;
      keptStructs.push(sm);
    }
    this.sceneObjs.structureGhostMeshes = keptStructs;
  }

  /**
   * Sync exposure marker meshes ("where enemies saw me") to the viewer's fog,
   * keeping unchanged markers.
   */
  private updateExposureMeshes(): void {
    const scene = this.sceneObjs.scene;
    const fogState = this.devReveal ? undefined : this.gameState.fog[this.viewerIndex];
    const turn = this.gameState.turnNumber;

    const want = new Map<string, { position: Vec3; turnsSince: number }>();
    if (fogState) {
      for (const e of fogState.exposures) {
        const turnsSince = turn - e.turnSeen;
        want.set(`${e.position.x.toFixed(3)},${e.position.y.toFixed(3)},${e.position.z.toFixed(3)}|${turnsSince}`, { position: e.position, turnsSince });
      }
    }
    const kept: ExposureMesh[] = [];
    const drop: ExposureMesh[] = [];
    for (const em of this.sceneObjs.exposureMeshes) {
      const key = em.group.userData.fogKey as string | undefined;
      if (key !== undefined && want.has(key)) { kept.push(em); want.delete(key); }
      else drop.push(em);
    }
    disposeExposureMeshes(scene, drop);
    for (const [key, e] of want) {
      const em = createExposureMesh(scene, e.position, e.turnsSince);
      em.group.userData.fogKey = key;
      kept.push(em);
    }
    this.sceneObjs.exposureMeshes = kept;
  }

  /**
   * Update enemy structure visibility based on fog state.
   * Own-team structures always visible, enemy structures hidden unless in LOS or ghost.
   */
  /**
   * A mesh for a structure that has none yet: built ones, and the ones a setup placed before the
   * match began (sector preparation, the harness's emplacements), which used to stay invisible.
   */
  /**
   * What a prop on planet `pi` needs from the match: a person's height and the planet's rock colour (decision 87),
   * and `ground`, the planet itself, so the prop is seated on the ground's own frame (rendering/groundSeat.ts).
   */
  private propOptsFor(pi: number): PropScaleOpts {
    const pl = this.gameState.planets[pi];
    const layers = PLANET_LAYERS[(pl?.palette?.name === 'earth' ? 'earth' : pl?.archetype) as keyof typeof PLANET_LAYERS];
    const ps = personSetOf(this.gameState);
    return { personHeight: PERSON_BODY_HEIGHT * ps.k, rockColor: layers?.rock, ground: pl, planets: this.gameState.planets, fine: ps, buildings: buildingsByFunction(this.gameState.config) ? 'function' : 'planet', modular: modularBuildingsOn(this.gameState.config), ...(this.gameState.config.finiteSupply ? { finiteSupply: true } : {}) };   // (63.14 step 2; 69.100 step 4)
  }

  private ensureStructureMesh(structure: StructureState): void {
    if (this.sceneObjs.structureMeshes.has(structure.id)) return;
    const owner = this.gameState.players.find(p => p.id === structure.ownerId);
    const worldPos = getStructureWorldPosition(structure, this.gameState.planets);
    const normal = getPlayerSurfaceNormal({ surfaceDir: structure.surfaceDir } as any);
    const personHeight = PERSON_BODY_HEIGHT * personSetOf(this.gameState).k;
    const mesh = createStructureMesh(structure, owner ? owner.teamIndex : null, worldPos, normal, this.sceneObjs.scene, this.gameState.planets[structure.planetIndex].radius, personHeight, this.propOptsFor(structure.planetIndex));
    this.sceneObjs.structureMeshes.set(structure.id, mesh);
  }

  private updateStructureVisibility(): void {
    // every structure gets its mesh the first frame it exists (cheap: a size check first)
    if (this.sceneObjs.structureMeshes.size < this.gameState.structures.length) {
      for (const s of this.gameState.structures) this.ensureStructureMesh(s);
    }
    if (!this.fogCam) {
      // Free/SISSI cam: all structures visible
      for (const [, structMesh] of this.sceneObjs.structureMeshes) {
        structMesh.group.visible = structMesh.fogShown = true;
      }
      return;
    }

    const viewer = this.gameState.players[this.viewerIndex];
    const vi = this.viewerIndex;
    const fogState = this.getViewerFog();

    for (const structure of this.gameState.structures) {
      const structMesh = this.sceneObjs.structureMeshes.get(structure.id);
      if (!structMesh) continue;

      const owner = this.gameState.players.find(p => p.id === structure.ownerId);
      const isOwnTeam = owner && owner.teamIndex === viewer.teamIndex;

      // (67.73 step 2: the fog's word kept apart — the far field shows the group only while its tier is also full)
      structMesh.group.visible = structMesh.fogShown = isOwnTeam ? true : this.isSurfaceRevealed(structure.planetIndex, structure.surfaceDir, fogState);
    }
  }

  /** Check if a surface position on a planet is revealed in the given fog state. */
  private isSurfaceRevealed(planetIndex: number, surfaceDir: Vec3, fogState: FogState | undefined): boolean {
    const planet = this.gameState.planets[planetIndex];
    if (!planet || !planet.alive) return false;
    const fogPlanet = fogState?.planets[planetIndex];
    if (!fogPlanet) return false;
    return fogPlanet.revealedVertices[nearestSurfaceVertex(planet, surfaceDir)];
  }

  private updateHazardVisibility(): void {
    if (!this.fogCam) {
      for (const hm of this.hazardMeshes) {
        const hazard = this.gameState.hazards.find(h => h.id === hm.hazardId);
        hm.group.visible = !hazard || hazard.alive;
      }
      return;
    }

    const vi = this.viewerIndex;
    const fogState = this.getViewerFog();

    for (const hm of this.hazardMeshes) {
      const hazard = this.gameState.hazards.find(h => h.id === hm.hazardId);
      if (!hazard || !hazard.alive) {
        hm.group.visible = false;
        continue;
      }
      hm.group.visible = this.isSurfaceRevealed(hazard.planetIndex, hazard.surfaceDir, fogState);
    }
  }

  private updateFloraVisibility(): void {
    // one lookup by id, not a scan per mesh (a battle's stands are hundreds of trees)
    const byId = new Map(this.gameState.flora.map(f => [f.id, f]));
    if (!this.fogCam) {
      for (const fm of this.floraMeshes) {
        const flora = byId.get(fm.floraId);
        fm.group.visible = !flora || flora.alive;
      }
      this.treeStands?.setHidden(() => false);   // 63.4 step 8: a felled tree is drawn felled (the stands follow the state)
      return;
    }

    const vi = this.viewerIndex;
    const fogState = this.getViewerFog();
    this.treeStands?.setHidden(f => { const live = byId.get(f.id) ?? f; return !this.isSurfaceRevealed(live.planetIndex, live.surfaceDir, fogState); });

    for (const fm of this.floraMeshes) {
      const flora = byId.get(fm.floraId);
      if (!flora || !flora.alive) {
        fm.group.visible = false;
        continue;
      }
      fm.group.visible = this.isSurfaceRevealed(flora.planetIndex, flora.surfaceDir, fogState);
    }
  }

  private updateDropCanVisibility(): void {
    if (!this.fogCam) {
      for (const [, mesh] of this.crashedDropCanMeshes) mesh.visible = true;
      return;
    }

    const viewer = this.gameState.players[this.viewerIndex];
    const vi = this.viewerIndex;
    const fogState = this.getViewerFog();

    for (const can of this.gameState.crashedDropCans) {
      const mesh = this.crashedDropCanMeshes.get(can.id);
      if (!mesh) continue;

      if (can.teamIndex === viewer.teamIndex) {
        mesh.visible = true;
      } else {
        mesh.visible = this.isSurfaceRevealed(can.planetIndex, can.surfaceDir, fogState);
      }
    }
  }

  private updateCarryableVisibility(): void {
    // During deploy phase, hide all mission items
    if (this.gameState.phase === 'deploying') {
      for (const [, cm] of this.carryableMeshes) cm.group.visible = false;
      for (const [, cm] of this.collectibleMeshes) cm.group.visible = false;
      for (const [, dm] of this.drillSiteMeshes) dm.group.visible = false;
      return;
    }

    if (!this.fogCam) {
      // Free/SISSI cam: all visible (unless carried)
      for (const [, cm] of this.carryableMeshes) {
        const carryable = this.gameState.carryables.find(c => c.id === cm.carryableId);
        cm.group.visible = !carryable || (carryable.alive && carryable.carriedBy === null);
      }
      for (const [, cm] of this.collectibleMeshes) {
        const collectible = this.gameState.collectibles.find(c => c.id === cm.collectibleId);
        cm.group.visible = !collectible || !collectible.collected;
      }
      for (const [, dm] of this.drillSiteMeshes) dm.group.visible = true;
      return;
    }

    const viewer = this.gameState.players[this.viewerIndex];
    const vi = this.viewerIndex;
    const fogState = this.getViewerFog();

    // Carryables: visible if own team OR surface position is revealed
    for (const [, cm] of this.carryableMeshes) {
      const carryable = this.gameState.carryables.find(c => c.id === cm.carryableId);
      if (!carryable || !carryable.alive || carryable.carriedBy !== null) {
        cm.group.visible = false;
        continue;
      }
      const isOwnTeam = carryable.teamIndex >= 0 && carryable.teamIndex === viewer.teamIndex;
      cm.group.visible = isOwnTeam || this.isSurfaceRevealed(carryable.planetIndex, carryable.surfaceDir, fogState);
    }

    // Collectibles: neutral items, visible if surface is revealed
    for (const [, cm] of this.collectibleMeshes) {
      const collectible = this.gameState.collectibles.find(c => c.id === cm.collectibleId);
      if (!collectible || collectible.collected) {
        cm.group.visible = false;
        continue;
      }
      cm.group.visible = this.isSurfaceRevealed(collectible.planetIndex, collectible.surfaceDir, fogState);
    }

    // Drill sites: visible if own team OR surface is revealed
    for (const [, dm] of this.drillSiteMeshes) {
      const ex = this.gameState.modeState.excavation;
      if (!ex) { dm.group.visible = false; continue; }
      const drill = ex.drillSites.find(d => d.id === dm.drillSiteId);
      if (!drill) { dm.group.visible = false; continue; }
      const isOwnTeam = drill.teamIndex === viewer.teamIndex;
      dm.group.visible = isOwnTeam || this.isSurfaceRevealed(drill.planetIndex, drill.surfaceDir, fogState);
    }
  }

  private presentAirborneStep(step: AirborneStep): void {
    const name = (id: number) => this.gameState.players.find(p => p.id === id)?.name ?? '?';
    const deathNotice = (id: number) => pushNotification({
      type: 'gcppa', message: matchMessage('death', { name: name(id) }), sender: matchSender(), color: '#c8a84c',
    });
    for (const e of step.events) {
      switch (e.kind) {
        case 'landed': {
          const planet = this.gameState.planets[e.planetIndex];
          logMessage(e.damage > 0
            ? `${name(e.playerId)} crash lands on ${planet.palette.name}! (${Math.round(e.damage)} dmg)`
            : `${name(e.playerId)} lands on ${planet.palette.name}`);
          if (e.died) {
            logMessage(`${name(e.playerId)} destroyed on impact!`);
            deathNotice(e.playerId);
            break;
          }
          // Landing animation by how the unit left the ground
          const landMesh = this.sceneObjs.playerMeshes.get(e.playerId);
          const landed = this.gameState.players.find(p => p.id === e.playerId);
          if (landMesh && landed) {
            landMesh.animCtx.state = landMesh.animCtx.state === 'airborne_knockback' ? 'landing_knockback' : 'landing';
            landMesh.animCtx.landingStart = 0;
            playSpatial(getPlayerWorldPosition(landed, this.gameState.planets), jumpLand);
            this.updateAmbientBiome();
          }
          break;
        }
        case 'diedInStar':
          logMessage(`${name(e.playerId)} consumed by ${this.gameState.planets[e.starIndex].palette.name}!`);
          deathNotice(e.playerId);
          break;
        case 'diedInWormhole':
          logMessage(`${name(e.playerId)} torn apart by wormhole instability!`);
          deathNotice(e.playerId);
          break;
        case 'warped':
          logMessage(`${name(e.playerId)} warps through rift!`);
          break;
        case 'lostInSpace':
          logMessage(`${name(e.playerId)} lost in space...`);
          deathNotice(e.playerId);
          break;
        case 'geyserBoost':
          logMessage(`${name(e.playerId)} boosted by geyser!`);
          this.effects.push(createExplosion(this.sceneObjs.scene, e.pos, 0.5, '#a0c8d0'));
          break;
        case 'killCredit':
          logMessage(`${name(e.killerId)} gets credit for eliminating ${name(e.victimId)}!`);
          break;
        case 'cargoRespawned':
          this.syncCarryableMeshPositions();
          logMessage(`${name(e.playerId)}'s cargo respawns at base!`);
          break;
      }
    }

    if (step.playersChanged) {
      updatePlayerBars(this.gameState);
      if (step.gameOver) {
        this.handleGameOver();
        this.jumpFollowPlayerId = null;
        return;
      }
      // If current player landed, update fog/LOS and preview to new position
      const currentPlayer = this.gameState.players[this.gameState.currentPlayerIndex];
      if (!currentPlayer.airborne && this.gameState.phase === 'aiming') {
        this.updateFogForCurrentPlayer(false);   // Match.stepAirborne already refreshed every landed unit's fog
        this.markPreviewDirty();
        this.checkMeleeTargets();
        this.updateHealButtonState();
      }
    }

    // Flyover scans the Match made this tick: their viewer-relative half
    for (const scan of step.flyovers) this.presentScan(scan, 'flyover');
  }

  /** Per-frame airborne visuals: meshes and the jump follow-cam. */
  private updateAirborneVisuals(dt: number): void {
    if (!hasAirbornePlayers(this.gameState.players) && this.jumpFollowPlayerId === null) return;

    // Update meshes for airborne players
    this.updatePlayerMeshes();

    // Follow cam for jumping player
    if (this.jumpFollowPlayerId !== null) {
      const jumper = this.gameState.players.find(p => p.id === this.jumpFollowPlayerId);
      if (jumper && jumper.airborne && jumper.airbornePos) {
        if (!this.cameraState.manualPivot) lerpCameraTarget(this.cameraState, jumper.airbornePos, 0.1, dt);
      } else {
        // Landed or dead — stop following, snap to landed position
        this.jumpFollowPlayerId = null;
        if (jumper && jumper.alive) {
          const pos = getPlayerWorldPosition(jumper, this.gameState.planets);
          setCameraTarget(this.cameraState, pos);
        }
      }
    }
  }

  private markPreviewDirty(): void {
    this.previewDirty = true;
  }

  private updatePreviewIfNeeded(dt: number): void {
    if (!this.previewDirty) return;

    // Throttle preview updates (~60fps with WASM, ~20fps on the JS fallback)
    this.previewThrottleTime += dt;
    if (this.previewThrottleTime < (this.gameWasm ? 0.016 : 0.05)) return;
    this.previewThrottleTime = 0;
    this.previewDirty = false;

    this.updatePreviewNow();
  }

  private updatePreviewNow(): void {
    if (this.gameState.phase !== 'aiming') return;
    const player = this.gameState.players[this.gameState.currentPlayerIndex];
    if (player.isBot || !player.alive) return;
    if (this.isRemoteTurn()) return; // Don't show preview during remote player's turn
    // Skip weapon preview in jump mode (jump has its own preview)
    if (this.aimState.mode === 'jump') return;
    // EMP: disable trajectory preview
    if (player.empTurnsRemaining && player.empTurnsRemaining > 0) return;

    // Simulate trajectory
    const weaponKey = getSelectedWeaponKey(this.aimState.weaponIndex);
    const params: FireParams = {
      azimuth: this.aimState.azimuth,
      elevation: this.aimState.elevation,
      power: this.aimState.power,
      weaponKey,
    };

    const weapon = WEAPONS[weaponKey];

    // === HITSCAN PREVIEW: straight line from launch to hit ===
    if (weapon.hitscan) {
      const surfaceNormal = vecNormalize(player.surfaceDir);
      const planet = this.gameState.planets[player.planetIndex];
      const surfaceRadius = getSurfaceRadius(planet, player.surfaceDir);
      const worldPos = vecAdd(planet.position, vecScale(surfaceNormal, surfaceRadius));
      const startPos = vecAdd(worldPos, vecScale(surfaceNormal, 1.2));
      const launchDir = computeLaunchDirection(surfaceNormal, params.azimuth, params.elevation);

      // Raycast to find end point (same logic as fireHitscan)
      const HITSCAN_MAX_RANGE = 100;
      let endDist = HITSCAN_MAX_RANGE;
      const terrainHit = raycastTerrain(startPos, launchDir, endDist, this.gameState.planets);
      if (terrainHit) endDist = terrainHit.distance;
      // Also check player hitboxes for preview
      const HITBOX_RADIUS = 0.6;
      for (const other of this.gameState.players) {
        if (other.id === player.id || !other.alive || other.airborne) continue;
        if (this.gameState.config.friendlyFire === 0 && other.teamIndex === player.teamIndex) continue;
        const otherPos = getPlayerWorldPosition(other, this.gameState.planets);
        const otherNormal = vecNormalize(other.surfaceDir);
        const hitboxCenter = vecAdd(otherPos, vecScale(otherNormal, 0.5));
        const oc = vecSub(startPos, hitboxCenter);
        const b = vecDot(oc, launchDir);
        const c = vecDot(oc, oc) - HITBOX_RADIUS * HITBOX_RADIUS;
        const disc = b * b - c;
        if (disc >= 0) {
          const t = -b - Math.sqrt(disc);
          if (t > 0 && t < endDist) endDist = t;
        }
      }

      const endPos = vecAdd(startPos, vecScale(launchDir, endDist));
      const hitscanPoints: Vec3[] = [startPos, endPos];
      if (this.gravityField) setGravityFieldPreview(this.gravityField, hitscanPoints, 2);
      const numPoints = 2;

      if (this.sceneObjs.previewLine) {
        const geo = this.sceneObjs.previewLine.geometry;
        const posAttr = geo.attributes.position;
        const posArr = posAttr.array as Float32Array;
        const colorAttr = geo.attributes.color;
        const colorArr = colorAttr.array as Float32Array;
        for (let i = 0; i < numPoints; i++) {
          posArr[i * 3] = hitscanPoints[i].x;
          posArr[i * 3 + 1] = hitscanPoints[i].y;
          posArr[i * 3 + 2] = hitscanPoints[i].z;
          colorArr[i * 3] = 0.85; colorArr[i * 3 + 1] = 0.63; colorArr[i * 3 + 2] = 0.63;
        }
        posAttr.needsUpdate = true;
        colorAttr.needsUpdate = true;
        geo.setDrawRange(0, numPoints);
        this.sceneObjs.previewLine.computeLineDistances();
      } else {
        const geo = new THREE.BufferGeometry();
        const posArr = new Float32Array(1000 * 3);
        const colorArr = new Float32Array(1000 * 3);
        for (let i = 0; i < numPoints; i++) {
          posArr[i * 3] = hitscanPoints[i].x;
          posArr[i * 3 + 1] = hitscanPoints[i].y;
          posArr[i * 3 + 2] = hitscanPoints[i].z;
          colorArr[i * 3] = 0.85; colorArr[i * 3 + 1] = 0.63; colorArr[i * 3 + 2] = 0.63;
        }
        geo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colorArr, 3));
        geo.setDrawRange(0, numPoints);
        const mat = new THREE.LineDashedMaterial({
          vertexColors: true, transparent: true, opacity: 0.5, dashSize: 0.6, gapSize: 0.3,
        });
        this.sceneObjs.previewLine = new THREE.Line(geo, mat);
        this.sceneObjs.previewLine.frustumCulled = false;
        this.sceneObjs.previewLine.computeLineDistances();
        this.sceneObjs.scene.add(this.sceneObjs.previewLine);
      }

      // Update aim indicator
      if (this.sceneObjs.aimIndicator) {
        const aimWorldPos = getPlayerWorldPosition(player, this.gameState.planets);
        const normal = getPlayerSurfaceNormal(player);
        const aimLaunchDir = computeLaunchDirection(normal, this.aimState.azimuth, this.aimState.elevation);
        updateAimIndicator(this.sceneObjs.aimIndicator, aimWorldPos, aimLaunchDir, this.aimState.power, weapon.color);
      }
      return;
    }

    const projectile = createProjectile(player, this.gameState.planets, params, this.gameState.config);
    // The preview IS the real flight: same simulation, same collision, same geysers/wormholes/hitboxes.
    const sim = this.simulateFullFlight(projectile);

    // How much of it this unit gets to see: its preview quality, or the whole
    // flight when the shot passes a spot it has already hit (core/unitModifiers.ts)
    const quality = (player.modifiers?.previewQuality ?? this.gameState.config.previewQuality ?? 1) * stabilityFactor(player, this.gameState.config);
    const pastImpacts = (this.gameState.shotRecords ?? [])
      .filter(r => r.shooterId === player.id && r.impactPoint && !r.submunition)
      .map(r => r.impactPoint!);
    const win = previewWindow(quality, sim.points, pastImpacts, PHYS_DT);
    // R14 (b): a solar flare takes the preview offline (the bots' search loses its dial-in alike: ai/bot/fireSearch.ts)
    const windowed = isTrajectoryPreviewDisabled(this.gameState) ? [] : previewPoints(sim.points, win, PHYS_DT);
    if (this.gravityField) setGravityFieldPreview(this.gravityField, windowed, Math.ceil(weapon.fuelDuration / PHYS_DT / win.stride));

    // Subsample to the line buffer capacity, keeping the last shown point.
    const maxCapacity = 1000;
    const stride = Math.max(1, Math.ceil(windowed.length / maxCapacity));
    const points: Vec3[] = [];
    for (let i = 0; i < windowed.length; i += stride) points.push(windowed[i]);
    const lastPt = windowed[windowed.length - 1];
    if (points[points.length - 1] !== lastPt) points.push(lastPt);
    const burnoutIndex = Math.ceil(weapon.fuelDuration / PHYS_DT / (stride * win.stride));
    // Dialed-in shots read cooler; a short window reads dimmer at its end
    const tint = win.dialedIn ? { r: 0.75, g: 1.0, b: 1.0 } : { r: 1.0, g: 0.9, b: 0.7 };

    // Preview line with vertex colors for burn/ballistic phases
    const numPoints = Math.min(points.length, maxCapacity);

    if (this.sceneObjs.previewLine) {
      const geo = this.sceneObjs.previewLine.geometry;
      const posAttr = geo.attributes.position;
      const posArr = posAttr.array as Float32Array;
      const colorAttr = geo.attributes.color;
      const colorArr = colorAttr.array as Float32Array;

      for (let i = 0; i < numPoints; i++) {
        posArr[i * 3] = points[i].x;
        posArr[i * 3 + 1] = points[i].y;
        posArr[i * 3 + 2] = points[i].z;

        // Bright during burn, dim after
        const brightness = i < burnoutIndex ? 1.0 : 0.4;
        colorArr[i * 3] = brightness * tint.r;
        colorArr[i * 3 + 1] = brightness * tint.g;
        colorArr[i * 3 + 2] = brightness * tint.b;
      }
      posAttr.needsUpdate = true;
      colorAttr.needsUpdate = true;
      geo.setDrawRange(0, numPoints);
      this.sceneObjs.previewLine.computeLineDistances();
    } else {
      const geo = new THREE.BufferGeometry();
      const posArr = new Float32Array(maxCapacity * 3);
      const colorArr = new Float32Array(maxCapacity * 3);

      for (let i = 0; i < numPoints; i++) {
        posArr[i * 3] = points[i].x;
        posArr[i * 3 + 1] = points[i].y;
        posArr[i * 3 + 2] = points[i].z;

        const brightness = i < burnoutIndex ? 1.0 : 0.4;
        colorArr[i * 3] = brightness * tint.r;
        colorArr[i * 3 + 1] = brightness * tint.g;
        colorArr[i * 3 + 2] = brightness * tint.b;
      }

      geo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(colorArr, 3));
      geo.setDrawRange(0, numPoints);

      const mat = new THREE.LineDashedMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.5,
        dashSize: 0.6,
        gapSize: 0.3,
      });

      this.sceneObjs.previewLine = new THREE.Line(geo, mat);
      this.sceneObjs.previewLine.frustumCulled = false;
      this.sceneObjs.previewLine.computeLineDistances();
      this.sceneObjs.scene.add(this.sceneObjs.previewLine);
    }

    // Update aim indicator
    if (this.sceneObjs.aimIndicator) {
      const worldPos = getPlayerWorldPosition(player, this.gameState.planets);
      const normal = getPlayerSurfaceNormal(player);
      const launchDir = computeLaunchDirection(normal, this.aimState.azimuth, this.aimState.elevation);
      updateAimIndicator(
        this.sceneObjs.aimIndicator,
        worldPos,
        launchDir,
        this.aimState.power,
        weapon.color
      );
    }
  }

  private updatePreview(): void {
    this.markPreviewDirty();
  }


  // ===========================================================================
  // GAME LOOP
  // ===========================================================================

  /** 67.30: the shooting stars — made per match (their showers seeded from it), their rate from the place, refreshed every couple of seconds. */
  private shootingStars: ShootingStars | null = null;
  private shootingSeed = '';
  private shootingRate = 0;
  private shootingRateAge = Infinity;
  private updateShootingStars(): void {
    const seed = this.gameState?.config?.seed;
    if (!seed) return;
    if (!this.shootingStars || this.shootingSeed !== seed) {
      this.shootingStars?.dispose();
      this.shootingStars = new ShootingStars(this.sceneObjs.scene, seed);
      this.shootingSeed = seed; this.shootingRateAge = Infinity;
    }
    this.shootingRateAge += this.lastFrameDt;
    if (this.shootingRateAge > 2) { this.shootingRate = skyCharacter(this.gameState); this.shootingRateAge = 0; }
    this.shootingStars.update(this.sceneObjs.camera, this.skyHaze.starsShown, this.skyHaze.localUp, this.shootingRate, this.lastFrameDt);
  }

  /**
   * 67.36: the windsocks (by every standing base and gun pit) and the pennants (on the standing masts) — one
   * InstancedMesh, on the action clock. Their mounts are gathered once a second from the structures and the ruins; each
   * prop reads the wind where it stands (`windAt`: the gust and the shelter there, calm with the switch off) four times a
   * second and swings on the GPU (rendering/windView.ts).
   */
  private windProps: WindProps | null = null;
  private windMountsAge = 0;
  private readonly windUp = new THREE.Vector3();
  private updateWindProps(dt: number): void {
    if (!this.actionHost) { this.disposeWindProps(); return; }
    const state = this.gameState;
    if (!this.windProps) { this.windProps = new WindProps(this.sceneObjs.scene, (PERSON_BODY_HEIGHT * personSetOf(state).k) / PERSON_UNITS); this.windMountsAge = Infinity; }
    this.windMountsAge += dt;
    if (this.windMountsAge >= 1) {
      this.windMountsAge = 0;
      this.windProps.sync(findWindMounts([...[...this.sceneObjs.structureMeshes.values()].map(m => m.group), ...this.ruinMeshes.map(r => r.group)]));
    }
    if (this.windProps.size === 0) return;
    const seconds = this.match.actionTick * PHYS_DT;
    this.windProps.update(dt, p => { const pl = state.planets[this.planetUnder(p)]; return this.windUp.set(p.x - pl.position.x, p.y - pl.position.y, p.z - pl.position.z).normalize(); },
      (p, up) => this.feltAt(p, this.planetUnder(p), up, seconds));
  }
  private disposeWindProps(): void { this.windProps?.dispose(); this.windProps = null; }
  /** The planet a point stands on (the nearest surface). */
  private planetUnder(p: { x: number; y: number; z: number }): number {
    let best = 0, gap = Infinity;
    this.gameState.planets.forEach((pl, i) => { if (pl.type !== 'planet' || pl.alive === false) return; const g = Math.hypot(p.x - pl.position.x, p.y - pl.position.y, p.z - pl.position.z) - pl.radius; if (g < gap) { gap = g; best = i; } });
    return best;
  }
  /** 67.36: the wind as the eye takes it at a point (windView windFelt over `windAt`): calm with the switch off. */
  private feltAt(p: { x: number; y: number; z: number }, pi: number, up: { x: number; y: number; z: number }, seconds: number): WindFelt | null {
    const pl = this.gameState.planets[pi];
    if (!pl || !this.gameState.config.wind) return null;
    const r = Math.hypot(p.x - pl.position.x, p.y - pl.position.y, p.z - pl.position.z);
    const f = windFelt(windAt(this.gameState, pi, up, seconds), up, (G * pl.mass) / (r * r));
    return f.strength > 0 ? f : null;
  }
  /** 67.36: the drift the wind gives a puff of smoke at a point (world units a second), or undefined in calm air. */
  private smokeDriftAt(p: { x: number; y: number; z: number }): THREE.Vector3 | undefined {
    if (!this.gameState.config.wind) return undefined;
    const pi = this.planetUnder(p), pl = this.gameState.planets[pi];
    const up = { x: p.x - pl.position.x, y: p.y - pl.position.y, z: p.z - pl.position.z }, l = Math.hypot(up.x, up.y, up.z) || 1;
    const f = this.feltAt(p, pi, { x: up.x / l, y: up.y / l, z: up.z / l }, this.actionHost ? this.match.actionTick * PHYS_DT : 0);
    return f ? smokeDrift(f, PERSON_BODY_HEIGHT * personSetOf(this.gameState).k) : undefined;
  }
  /** 67.36: each hazard's wisps drift in its own frame, the wind read where it stands twice a second. */
  private readonly wispDrifts = new Map<HazardMesh, { drift: THREE.Vector3 | null; next: number }>();
  private wispDrift(hm: HazardMesh, time: number): THREE.Vector3 | null {
    let c = this.wispDrifts.get(hm);
    if (!c) { c = { drift: null, next: 0 }; this.wispDrifts.set(hm, c); }
    if (time >= c.next) {
      c.next = time + 0.5;
      const at = hm.group.getWorldPosition(new THREE.Vector3()), w = this.smokeDriftAt(at);
      if (!w) c.drift = null;
      else {
        const q = hm.group.getWorldQuaternion(new THREE.Quaternion()).invert(), sc = hm.group.getWorldScale(new THREE.Vector3()).x || 1;
        c.drift = w.applyQuaternion(q).divideScalar(sc);   // world units a second → the hazard's own
      }
    }
    return c.drift;
  }
  /** Draw the frame — every frame, or (`?lean`, the browser smoke) every LEAN_RENDER_EVERY-th and on request. */
  private leanFrame = 0;
  /** 62.47: the wind where the camera is (core/wind.ts windHere), read once per drawn frame by the sound, the grass and the stands; calm off the air. */
  private windNow = { factor: 0, level: 0 };
  /** 62.52: the way the wind blows along the ground at the camera (world, unit) × the gust and the shelter there, or null (switch off, calm, off the air). */
  private windWorldAtViewer(): { x: number; y: number; z: number } | null {
    const pi = this.weatherView.planetIndex;
    if (pi < 0 || !this.weatherView.inAir || !this.gameState.config.wind) return null;
    const planet = this.gameState.planets[pi];
    const cam = this.sceneObjs.camera.position;
    const d = { x: cam.x - planet.position.x, y: cam.y - planet.position.y, z: cam.z - planet.position.z };
    const len = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z) || 1;
    const n = { x: d.x / len, y: d.y / len, z: d.z / len };
    const w = windAt(this.gameState, pi, n, this.actionHost ? this.match.actionTick * PHYS_DT : 0);
    if (!w) return null;
    const along = w.x * n.x + w.y * n.y + w.z * n.z;
    const t = { x: w.x - n.x * along, y: w.y - n.y * along, z: w.z - n.z * along };
    const tl = Math.sqrt(t.x * t.x + t.y * t.y + t.z * t.z);
    if (tl < 1e-12) return null;
    const k = this.windNow.factor;   // the gust × the shelter (read just before)
    return { x: (t.x / tl) * k, y: (t.y / tl) * k, z: (t.z / tl) * k };
  }

  /**
   * 67.38: the weather you hear coming — the planet's cells (core/windField.ts cellsAt, lane A's 62.54) round the listener
   * (the shoulder, or the commander's focus), as cues for the weather sound, four times a second. None with the weather
   * cells off, off the action clock or outside the air.
   */
  /** 62.54 step 7: the planet's cells as volumes, on the action clock — the planet under the camera (or the viewed unit's). */
  private seeWeatherCells(): void {
    if (!this.actionHost) { this.weatherCellsView.update(this.gameState, -1, 0, this.sceneObjs.camera, this.lastFrameDt); return; }
    let pi = this.weatherView.planetIndex;
    if (pi < 0) { const players = this.gameState.players; pi = (players.find(p => p.id === this.actionViewer?.id) ?? players.find(p => p.alive))?.planetIndex ?? -1; }
    this.weatherCellsView.update(this.gameState, pi, this.match.actionTick * PHYS_DT, this.sceneObjs.camera, this.lastFrameDt);
  }

  private aheadIn = 0;
  private hearWeatherAhead(dt: number): void {
    if ((this.aheadIn -= dt) > 0) return;
    this.aheadIn = 0.25;
    const cfg = this.gameState.config as typeof this.gameState.config & { weatherCells?: boolean };
    const pi = this.weatherView.planetIndex;
    if (!this.actionHost || !cfg.weatherCells || pi < 0 || !this.weatherView.inAir) { this.weatherSound?.ahead(null); return; }
    const planet = this.gameState.planets[pi], cam = this.sceneObjs.camera;
    const at = this.actionHost.commandFocus ?? { x: cam.position.x, y: cam.position.y, z: cam.position.z };
    const right = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld, 0).normalize();
    const seconds = this.match.actionTick * PHYS_DT;
    const cues = weatherCues(cellsAt(this.gameState, pi, weatherTime(this.gameState, seconds)), { x: at.x - planet.position.x, y: at.y - planet.position.y, z: at.z - planet.position.z }, { x: right.x, y: right.y, z: right.z });
    const person = PERSON_BODY_HEIGHT * personSetOf(this.gameState).k;
    this.weatherSound?.ahead(cues, (planet.radius / person) * 1.8);
  }

  /** 67.26: 1 over snow or ice under the listener on the action clock (the ground's material, core/groundMaterial.ts), else 0. */
  private hushAtViewer(): number {
    const pi = this.weatherView.planetIndex;
    if (!this.actionHost || pi < 0 || !this.weatherView.inAir) return 0;
    const planet = this.gameState.planets[pi], cam = this.sceneObjs.camera.position;
    const m = groundMaterialAt(planet, { x: cam.x - planet.position.x, y: cam.y - planet.position.y, z: cam.z - planet.position.z });
    return m === 'snow' || m === 'ice' ? 1 : 0;
  }

  /** 68.17: the topo overlay's uniforms — the viewed planet, the commander's strength, the corner map's own step (or the same rule over the planet's range). */
  private topoRange = new WeakMap<Float32Array, number>();
  private updateTopoOverlay(): void {
    const amount = this.actionHost?.topoAmount ?? 0;
    // the viewed unit's planet (the weather view tracks one only with weather on)
    const players = this.gameState.players;
    const u = players.find(p => p.id === this.actionViewer?.id) ?? players.find(p => p.alive);
    const planet = u ? this.gameState.planets[u.planetIndex] : undefined;
    if (!planet || amount <= 0.001) { setTopoOverlay(null, 0, 0); return; }
    let step = this.topoMap?.ready ? this.topoMap.contourStep : 0, range = this.topoMap?.ready ? this.topoMap.contourRange : 0;
    if (!(step > 0)) {
      let r = this.topoRange.get(planet.heightmap);
      if (r === undefined) { let lo = Infinity, hi = -Infinity; for (const h of planet.heightmap) { lo = Math.min(lo, h); hi = Math.max(hi, h); } r = hi - lo; this.topoRange.set(planet.heightmap, r); }
      range = r;
      step = contourInterval(range, PERSON_BODY_HEIGHT * personSetOf(this.gameState).k);
    }
    // 68.17's follow-up: finer than the map's step on flat ground (its index lines the map's lines), the Lab's dial over it
    setTopoOverlay(planet, amount, overlayStep(step, range, topoDensity()), this.lastFrameDt);   // (62.61: a change of step cross-fades)
  }

  private windAtViewer(): { factor: number; level: number } {
    const pi = this.weatherView.planetIndex;
    if (pi < 0 || !this.weatherView.inAir) return { factor: 0, level: 0 };
    const planet = this.gameState.planets[pi];
    const cam = this.sceneObjs.camera.position;
    return windHere(this.gameState, pi, { x: cam.x - planet.position.x, y: cam.y - planet.position.y, z: cam.z - planet.position.z }, this.actionHost ? this.match.actionTick * PHYS_DT : 0);
  }

  private renderFrame(): void {
    const g = globalThis as unknown as { __orbituaryRenderNow?: boolean };
    if (LEAN_RENDER_EVERY > 1 && !g.__orbituaryRenderNow && (this.leanFrame++ % LEAN_RENDER_EVERY) !== 0) return;
    g.__orbituaryRenderNow = false;
    const skyWeather = this.weatherView.update(this.gameState, this.sceneObjs.camera, this.actionHost ? this.match.actionTick * PHYS_DT : 0, this.lastFrameDt);
    this.skyHaze.update(this.sceneObjs.camera, this.gameState.planets, this.sceneObjs.planetMeshes, skyWeather);
    this.systemSky?.update(this.sceneObjs.camera, this.skyHaze, this.sceneObjs.renderer.domElement.height, this.gameState.planets, this.actionHost ? this.match.actionTick * PHYS_DT : 0);   // 74.1: after the dome, which it fades against; 74.2: the match's seconds turn the sky (the dome reads the turned sun next frame)
    this.auroras?.update(this.gameState.planets, this.skyHaze.sun, this.skyHaze.starsShown, skyWeather?.tintAmount ?? 0, performance.now() / 1000);   // 74.16: with the stars, under the weather's tint, on the night side
    this.updateShootingStars();   // 67.30: where the stars show
    this.weatherSound?.set(this.weatherView.inAir ? this.weatherView.kindNow ?? 'clear' : 'clear');   // heard only inside the air
    this.windNow = this.windAtViewer();
    this.weatherView.wind = this.windWorldAtViewer();   // 62.52: the rain slants and the flecks drift the way the wind blows (with the switch on)
    this.updateWindProps(this.lastFrameDt);              // 67.36: the windsocks and the pennants
    this.weatherSound?.gust(this.windNow.factor);   // 62.47: the gust and the shelter where the listener stands
    this.actionHost?.setWind(this.windNow.level);    // 68.14: a gale over the table masks the far fight's rumble
    this.weatherSound?.hush(this.hushAtViewer());   // 67.26: snow's hush — over snow or ice the weather's top end closes
    this.weatherSound?.far(this.actionHost?.commandListen ?? 0);   // 67.37: the commander's view hears the fight; the weather sits lower
    this.hearWeatherAhead(this.lastFrameDt);   // 67.38: the weather coming (the cells around the listener)
    this.updateTopoOverlay();   // 68.17: the contours on the ground in the commander's view
    this.seeWeatherCells();   // 62.54 step 7: the cells seen from outside
    // the grass in the wind (62.47: gusting, sheltered; 67.36: with the switch on, the field's way and level here), the puddles in the rain
    this.groundScatter?.reground(this.gameState.planets);   // 69.24: the pieces on ground a burst, a crater, a dig or a terrace changed, seated again (before weather's flush)
    this.treeStands?.bowlsChanged(this.gameState.planets);   // 69.24: and the tree chunks over a new bowl or pad (their tufts' lift takes them)
    this.groundScatter?.weather(this.weatherView.kindNow, this.lastFrameDt, this.windNow.factor || 1, { dir: this.weatherView.wind, level: this.windNow.level });
    if (this.groundScatter) this.treeStands?.windFrom(this.groundScatter.sway);   // 67.36: the trees in the same wind, its fronts bowing them in turn
    render(this.sceneObjs);
  }

  /**
   * I8: the tablet key (Q since 68.2b's key map; G before) opens the drone tablet for the unit the viewer drives — the Match holds the operator still
   * while a course is set (beginDroneTasking); launch hands the waypoints to Match.launchDrone, putting
   * it away cancels. Refused tasking (no drones, mounted …) opens it read-only with the reason.
   */
  /** I9: V — the feed of your latest drone in the air, then the team's next, then off (your soldier holds still meanwhile). */
  private toggleDroneFeed(): void {
    const host = this.actionHost;
    if (!host) return;
    this.droneFeed ??= new DroneFeed(this.sceneObjs.renderer.domElement, { cycle: () => this.toggleDroneFeed(), closed: () => this.actionHost?.setSuspended(false) });
    if (this.droneFeed.cycle(this.match.state, this.actionViewer.team ?? 0, host.controlledUnit()?.id ?? null)) host.setSuspended(true);
  }

  private toggleDroneTablet(): void {
    if (this.droneTablet?.isOpen) { this.droneTablet.cancel(); return; }
    const host = this.actionHost;
    const unit = host?.controlledUnit();
    if (!host || !unit) return;
    // 64.10: tasking, launch and cancel are inputs from the seat that holds the unit
    const seat = seatActor(unit.teamIndex);
    const begun = this.match.apply(this.match.stamp(seat, { kind: 'drone', unitId: unit.id, step: 'task' }));
    host.setSuspended(true);
    const done = () => { this.droneTablet = null; host.setSuspended(false); };
    this.droneTablet = new DroneTablet(this.match.state, unit.id, {
      blocked: begun.ok ? undefined : begun.reason,
      onLaunch: (waypoints, strike) => {
        const u = this.match.state.players.find(p => p.id === unit.id);
        if (u) this.match.apply(this.match.stamp(seat, { kind: 'drone', unitId: u.id, step: 'launch', route: waypoints, ...(strike ? { strike } : {}) }));
        done();
      },
      onCancel: () => {
        const u = this.match.state.players.find(p => p.id === unit.id);
        if (u && begun.ok) this.match.apply(this.match.stamp(seat, { kind: 'drone', unitId: u.id, step: 'cancel' }));
        done();
      },
    });
  }

  /** The planet nearest the camera (no star, wormhole or dead one), the camera's direction from its centre and its height over the ground; null: none. */
  private groundUnderCamera(): { index: number; planet: PlanetState; dir: Vec3; height: number } | null {
    const state = this.match.state, cam = this.sceneObjs.camera.position;
    let best = -1, bestGap = Infinity;
    state.planets.forEach((p, i) => {
      if (p.type === 'star' || p.type === 'wormhole' || p.alive === false) return;
      const gap = Math.hypot(cam.x - p.position.x, cam.y - p.position.y, cam.z - p.position.z) - p.radius;
      if (gap < bestGap) { bestGap = gap; best = i; }
    });
    if (best < 0) return null;
    const planet = state.planets[best];
    const dir = { x: cam.x - planet.position.x, y: cam.y - planet.position.y, z: cam.z - planet.position.z };
    return { index: best, planet, dir, height: Math.hypot(dir.x, dir.y, dir.z) - getSurfaceRadius(planet, dir) };
  }

  /** Keep the fine ground under the camera: the planet nearest the camera, the live colours of its mesh. */
  private updateFineGround(): void {
    const g = this.groundUnderCamera();
    if (!g) return;
    const { planet, dir, height } = g, ps = personSetOf(this.match.state);
    const md = this.sceneObjs.planetMeshes.get(planet.id);
    // (388: the style's colours where it draws — the bands where the slope's rock is drawn per pixel)
    const colors = md?.mstyle?.aCol ?? md?.mesh.geometry.getAttribute('color')?.array as Float32Array | undefined;
    // the owner's "Fade." (the commander view's terrain pop): the patch fades out with the camera's height over its ground
    this.fineGround!.update(planet, dir, ps, colors ?? null, this.nearField?.claim(NEAR_FIELD.claims.patch), fineHeightFade(height, PERSON_BODY_HEIGHT * ps.k));
  }

  /**
   * 67.61: the ground litter under the camera — made on the planet under it when the Lab's dial is on, its focus the drawn
   * ground below the camera; hidden above LITTER_HIDE_PEOPLE of height (the fine ground fades there too); remade on another
   * planet. Presentation only.
   */
  private updateLitter(): void {
    const scale = litterScale();
    if (scale <= 0) { if (this.litter) this.disposeLitter(); return; }
    const g = this.groundUnderCamera();
    if (!g) return;
    const { index: planetIndex, planet, dir, height } = g, person = PERSON_BODY_HEIGHT * personSetOf(this.match.state).k;
    if (this.litter && this.litter.planetIndex !== planetIndex) this.disposeLitter();
    if (!this.litter) {
      const fine = this.fineGround;
      const ground = planetLitterGround(() => this.match.state, planetIndex, fine ? (pl, d) => fine.drawnGround(pl, d, personSetOf(this.match.state)) : undefined);
      this.litter = { field: new LitterField(this.sceneObjs.scene, ground), planetIndex };
    }
    const f = this.litter.field, show = height < LITTER_HIDE_PEOPLE * person;
    f.scale = scale;
    for (const m of Object.values(f.meshes)) m.visible = show;
    if (!show) return;
    const l = Math.hypot(dir.x, dir.y, dir.z) || 1, r = this.fineGround ? this.fineGround.drawnGround(planet, dir, personSetOf(this.match.state)) : getSurfaceRadius(planet, dir);
    f.update({ x: planet.position.x + (dir.x / l) * r, y: planet.position.y + (dir.y / l) * r, z: planet.position.z + (dir.z / l) * r }, this.lastFrameDt);   // (69.76: its motion and grow-in)
  }
  private disposeLitter(): void { this.litter?.field.dispose(); this.litter = null; }

  /**
   * The impact effects' frame — dust, explosions and their fades, eruptions, shatters, ripples — stepped and disposed when
   * spent. Both clocks call it: the action clock's frame returned before it (64.11 on), so a gun's shell left its fireball
   * frozen over the crater it dug.
   */
  /**
   * The spatial-audio listener at the camera (after the camera has moved this frame). Both clocks call it: the action
   * clock's frame returned before it (59.3 on), so every `playSpatial` there — a shell's explosion — was heard from where
   * the turn camera last stood.
   */
  private followListener(): void {
    const P = this.profiler;
    P.begin('spatialAudio');
    const cam = this.sceneObjs.camera;
    const camFwd = cam.getWorldDirection(new THREE.Vector3());
    const camUp = new THREE.Vector3().copy(cam.up).applyQuaternion(cam.quaternion);
    updateListenerPosition(cam.position, camFwd, camUp);
    P.end('spatialAudio');
  }

  /**
   * The impact effects' shader programs, compiled once and held (the owner, Oct 12: "emplacement seems to lag up the game
   * quite a bit"). Measured in the page at a gun pit (human scale, master v96): every landing compiled two programs — the
   * explosion's transparent basic material and the impact dust's points — 580–940 ms of render in headless Chrome, and
   * released them once the last explosion and dust were disposed (~1.7 s and ~6 s on), so the next landing compiled them
   * again. One explosion, one dust puff and a muzzle flash are drawn in one real frame of the battle's scene (never culled,
   * a speck), then taken out and kept, never disposed: they hold the programs with the frame's own keys (renderer.compile
   * made programs under other keys — the frame's target and state differ — and the landing compiled its own anyway).
   */
  private impactWarm: { parts: THREE.Object3D[]; drawnAt: number; live: boolean } | null = null;
  private warmImpactPrograms(): void {
    if (this.impactWarm) return;
    const box = new THREE.Scene(), s = this.effectScale(), w = WEAPONS.standard;
    createExplosion(box, { x: 0, y: 0, z: 0 }, fireballRadius(w.blastRadius, s), w.color, s);
    createImpactDust(box, new THREE.Vector3(), new THREE.Vector3(0, 1, 0), s);
    // the action host's muzzle flash, and a shell in flight (opaque: the first shot compiled it)
    box.add(new THREE.Mesh(new THREE.SphereGeometry(1, 8, 6), new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0.9, depthWrite: false })));
    box.add(new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffb347 })));
    const parts = [...box.children];
    for (const o of parts) { o.frustumCulled = false; o.scale.setScalar(1e-6); o.traverse(c => { c.frustumCulled = false; }); this.sceneObjs.scene.add(o); }
    this.impactWarm = { parts, drawnAt: this.sceneObjs.renderer.info.render.frame, live: true };
  }
  /** 384: the battle's late looks compiled at the load (rendering/programWarm.ts); what it did, for the probes. */
  shaderWarm: ProgramWarm | null = null;
  private warmBattlePrograms(): void {
    const host = this.actionHost!.warmParts(), sky = new THREE.Scene();
    new ShootingStars(sky, 'warm');   // (made with the first frame, after this)
    // (74.21: the stands' trees, tufts and rocks, the far canopy, the ground scatter's per-cell and streamed kinds and the litter —
    // their programs linked here, not on the frame that first shows one: a 2.6–4.2 s wait on SwiftShader, a visible hitch on a GPU)
    // (and the bot's thinking mark: a mapped sprite drawn over a unit's head mid-match, made after the warm-up — its link waited on at its first draw)
    this.shaderWarm = warmPrograms(this.sceneObjs, [...host.parts, ...(this.layer0Views?.warmParts() ?? []), ...(this.farField?.warmParts() ?? []), ...sky.children,
      ...(this.treeStands?.warmParts() ?? []), ...(this.groundScatter?.warmParts() ?? []), litterWarmPart(), createBotThinkingSprite(new THREE.Scene()).sprite]);
    host.done();
  }
  /** After a frame: once the renderer has drawn one with the warm parts in it, they leave the scene (kept, not disposed). */
  private settleImpactWarm(): void {
    const w = this.impactWarm;
    if (!w?.live || this.sceneObjs.renderer.info.render.frame === w.drawnAt) return;
    for (const o of w.parts) this.sceneObjs.scene.remove(o);
    w.live = false;
  }

  private stepImpactEffects(dt: number): void {
    const P = this.profiler;
    // Update impact dust
    P.begin('dust');
    this.impactDustEffects = this.impactDustEffects.filter(d => {
      if (!updateImpactDust(d, dt)) {
        disposeImpactDust(this.sceneObjs.scene, d);
        return false;
      }
      return true;
    });
    P.end('dust');

    // Update effects
    P.begin('explosions');
    this.effects = this.effects.filter(e => {
      if (!updateExplosion(e, dt)) {
        disposeExplosion(this.sceneObjs.scene, e);
        return false;
      }
      return true;
    });
    this.impactFades = this.impactFades.filter(f => {
      if (!updateImpactFade(f, dt)) {
        disposeImpactFade(this.sceneObjs.scene, f);
        return false;
      }
      return true;
    });
    P.end('explosions');

    // Update eruption effects
    P.begin('eruptions');
    const planetsForEffects = this.gameState.planets.map(p => ({ position: p.position, mass: p.mass }));
    this.eruptionEffects = this.eruptionEffects.filter(e => {
      if (!updateEruptionEffect(e, dt, planetsForEffects)) {
        disposeEruptionEffect(this.sceneObjs.scene, e);
        return false;
      }
      return true;
    });
    P.end('eruptions');

    // Update shatter effects
    P.begin('shatters');
    this.shatterEffects = this.shatterEffects.filter(e => {
      if (!updateShatterEffect(e, dt, planetsForEffects)) {
        disposeShatterEffect(this.sceneObjs.scene, e);
        return false;
      }
      return true;
    });
    P.end('shatters');

    // Update ripple effects
    P.begin('ripples');
    this.rippleEffects = this.rippleEffects.filter(e => {
      if (!updateRippleEffect(e, dt)) {
        disposeRippleEffect(this.sceneObjs.scene, e);
        return false;
      }
      return true;
    });
    P.end('ripples');
  }

  private animate = (): void => {
    requestAnimationFrame(this.animate);
    const P = this.profiler;
    P.beginFrame();

    const now = performance.now() / 1000;
    if (this.lastTime === null) this.lastTime = now;
    const dt = Math.min(now - this.lastTime, FRAME_CLAMP_MS / 1000);   // (a longer frame slows the battle: the F3 overlay's CLOCK line counts them)
    this.lastTime = now;
    this.lastFrameDt = dt;

    // Phase 59.3: a real-time match runs its own frame (input → ticks → units, camera, HUD)
    if (this.actionHost) {
      P.begin('action');
      this.actionHost.frame(dt);
      this.followListener();   // the host moved the camera: the ear goes with it
      this.tickActionMusic(dt);   // 67.19: the fight drives the music
      P.setSim(this.actionHost.lastSim.ms, this.actionHost.lastSim.ticks, this.actionHost.lastSim.ticks * PHYS_DT);
      if (this.layer0Views) this.layer0Views.trackMarkersHidden = this.actionHost.tableShowing;   // 68.14: one enemy mark at the table
      this.layer0Views?.sync(this.match.state, dt, this.match.actionTick);
      this.topoMap?.update(this.match.state, this.actionViewer.team, this.actionHost.controlledUnit()?.id ?? this.actionViewer.id, this.match.actionTick, this.sectorSession?.historyNotes(3));   // X16: the place's record in the corner
      if (this.droneTablet?.isOpen) this.droneTablet.update(this.match.state, this.match.actionTick);
      if (this.droneFeed?.active) this.droneFeed.update(this.match.state, this.sceneObjs.camera, this.match.actionTick, dt);   // I9: the camera rides the drone
      // 62.61 step 2: the near field's ledger for this frame — the patch's rebuild claims first, the stands' build queue
      // next, the streamed cells the rest (after the stands, below)
      this.nearField?.beginFrame();
      if (this.fineGround) { const t0 = performance.now(); this.updateFineGround(); this.nearField?.spend('patch', performance.now() - t0); }
      this.updateLitter();   // 67.61: with or without the fine ground
      P.end('action');
      const t = performance.now() / 1000;
      for (const [, meshData] of this.sceneObjs.planetMeshes) {
        if (meshData.isStar) updateStarCorona(meshData, t, this.sceneObjs.camera);
        else if (meshData.clouds) updatePlanetClouds(meshData, dt, t);
      }
      updateFloraMeshes(this.floraMeshes, dt, t, this.windNow.level);
      P.begin('trees');
      this.treeStands?.update(this.sceneObjs.camera, dt, this.gameState.planets, this.gameState.flora, this.gameState.rocks, this.gameState);
      this.nearField?.update(this.sceneObjs.camera, dt, this.gameState.planets, PERSON_BODY_HEIGHT * personSetOf(this.gameState).k);   // the streamed cells, with what is left
      this.rockMerge?.follow(this.gameState.rocks, this.gameState.planets);
      P.end('trees');
      this.stepImpactEffects(dt);   // a shell's, a rocket's or a grenade's burst plays out and clears
      sweepStructureHeads(this.sceneObjs.structureMeshes.values(), dt);
      riseStructures(this.sceneObjs.structureMeshes.values(), dt);
      // the action clock places structures mid-match (a planted beacon) and destroys them by rocket bursts: draw both
      syncStructureMeshes(this.gameState.structures, this.sceneObjs.structureMeshes, i => this.ensureStructureMesh(this.gameState.structures[i]), s => structureFullHealth(this.gameState, s), this.gameState.tick);   // (69.61 step 6: a story HQ struck against its own hqHealth)
      this.farField?.update(this.gameState, this.sceneObjs.structureMeshes, this.actionHost?.parkedShipViews() ?? [], this.sceneObjs.camera, this.sceneObjs.renderer.domElement.height, this.skyHaze.sun, t);   // (67.73: after the sync, before the skirts read the shown)
      this.standSkirts?.sync(this.gameState.planets, personSetOf(this.gameState), id => this.sceneObjs.structureMeshes.get(id)?.group.visible ?? true);   // (209's follow-up: shown with its structure)
      lightWeb(this.gameState, this.sceneObjs.structureMeshes, t);   // 69.2: the web on the relays' lights (after the sync: a form made this frame takes it)
      stepStructureSmoke(this.sceneObjs.structureMeshes.values(), dt);   // 69.5e: an HQ's breaches and ruin smoking
      aimStructures(this.gameState.structures, this.sceneObjs.structureMeshes, dt);   // 69.9: the point-defence mounts slewing, firing, searching
      this.hitShapes?.update(this.match.state);
      P.begin('render');
      fitCameraDepth(this.sceneObjs.camera, this.gameState.planets);
      this.renderFrame();
      this.settleImpactWarm();
      P.end('render');
      P.setRenderInfo(this.sceneObjs.renderer.info);
      P.endFrame();
      return;
    }

    // Remote human actions wait on a queue until the game is idle for them
    this.pumpRemoteActions();

    // Handle aim input
    P.begin('aimInput');
    if (this.gameState.phase === 'aiming' && !this.activeDroneDelivery && this.respawnDropCans.size === 0) {
      const player = this.gameState.players[this.gameState.currentPlayerIndex];
      if (!player.isBot && player.alive && !isInGameHelpVisible() && !isInGameSettingsVisible()) {
        const changed = processAimInput(this.aimState, this.keyState, dt);
        if (changed) this.onAimChanged();
        this.updatePreviewIfNeeded(dt);

        // Update jump preview if in jump mode
        if (this.aimState.mode === 'jump' && this.jumpPreviewDirty) {
          this.updateJumpPreview();
          this.jumpPreviewDirty = false;
        }
      }
    }
    P.end('aimInput');

    // Fast-forward: hold . (period) to speed up physics during flight
    const ffAvailable = this.gameState.phase === 'firing' || !!this.roundPlay || hasAirbornePlayers(this.gameState.players);
    if (ffAvailable) {
      // Director pace for the current flight (1× / 4× / 16× by tier); holding . still adds ×8 on top
      const directed = (this.gameState.phase === 'firing' || this.roundPlay) && this.resolutionPace !== 'full' && this.shotPreview ? this.shotPreview.speed : 1;
      this.timeScale = Math.min(32, Math.max(directed, this.keyState['.'] ? 8 : 1, AUTOPLAY_TIME_SCALE));
    } else {
      this.timeScale = 1;
    }
    const ffIndicator = document.getElementById('ff-indicator');
    if (ffIndicator) {
      ffIndicator.classList.toggle('hidden', !ffAvailable);
      ffIndicator.classList.toggle('active', this.timeScale > 1);
      if (this.timeScale > 1) ffIndicator.textContent = this.shotPreview && this.resolutionPace !== 'full' && this.timeScale === this.shotPreview.speed ? `▶▶ ×${this.timeScale} · ${this.shotPreview.reason}` : `▶▶ ×${this.timeScale} (.)`;
    }

    // Projectile playback, airborne players and bomblet launches advance in ONE
    // fixed-step loop, so their interleaving is identical on every machine.
    P.begin('simulation');
    this.stepSimulation(dt * this.timeScale);
    this.updateProjectileVisuals(dt * this.timeScale);
    this.updateRoundVisuals(dt * this.timeScale);
    this.updateAirborneVisuals(dt * this.timeScale);
    // Turn-flow pauses and waits run on the same scaled clock, after the simulation
    // step so "landed" / "walk done" are seen the frame they happen.
    this.clock.tick(dt * this.timeScale);
    P.end('simulation');

    // Update star coronas, wormhole effects, and hazard animations
    const animTime = performance.now() / 1000;
    P.begin('starWormhole');
    for (const [, meshData] of this.sceneObjs.planetMeshes) {
      if (meshData.isStar) {
        updateStarCorona(meshData, animTime, this.sceneObjs.camera);
      } else if (meshData.isWormhole) {
        updateWormholeEffect(meshData, animTime, dt);
      } else if (meshData.clouds) {
        updatePlanetClouds(meshData, dt, animTime);
      }
    }
    P.end('starWormhole');

    P.begin('hazards');
    updateHazardMeshes(this.hazardMeshes, dt, animTime, hm => this.wispDrift(hm, animTime));   // 67.36: the wisps lean downwind
    P.end('hazards');

    P.begin('flags');
    updatePlantedFlags(this.sceneObjs.plantedFlagMeshes, animTime);
    P.end('flags');

    P.begin('flora');
    updateFloraMeshes(this.floraMeshes, dt, animTime, this.windNow.level);
    this.treeStands?.update(this.sceneObjs.camera, dt, this.gameState.planets, this.gameState.flora, this.gameState.rocks);
    this.rockMerge?.follow(this.gameState.rocks, this.gameState.planets);
    sweepStructureHeads(this.sceneObjs.structureMeshes.values(), dt);
    riseStructures(this.sceneObjs.structureMeshes.values(), dt);
    P.end('flora');

    P.begin('envEffects');
    updateEnvironmentalEffectMeshes(this.effectMeshes, dt, animTime);
    P.end('envEffects');

    P.begin('carryables');
    updateCarryableMeshes(this.carryableMeshes, dt, animTime);
    updateGroundItemMeshes(this.groundItemMeshes, dt, animTime);
    P.end('carryables');

    P.begin('collectibles');
    updateCollectibleMeshes(this.collectibleMeshes, dt, animTime);
    P.end('collectibles');

    // Drill sites animated per-drill
    P.begin('drills');
    if (this.gameState.modeState.excavation) {
      for (const drill of this.gameState.modeState.excavation.drillSites) {
        const dm = this.drillSiteMeshes.get(drill.id);
        if (dm) updateDrillSiteMesh(dm, drill, dt, animTime);
      }
    }
    P.end('drills');

    // Animate artifact site marker (pulsing ring)
    if (this.artifactSiteMarker) {
      const ring = this.artifactSiteMarker.children[0] as THREE.Mesh;
      if (ring) {
        const pulse = 0.8 + 0.2 * Math.sin(animTime * 3.0);
        ring.scale.setScalar(pulse);
      }
      const beam = this.artifactSiteMarker.children[1] as THREE.Mesh;
      if (beam && beam.material instanceof THREE.MeshLambertMaterial) {
        beam.material.opacity = 0.2 + 0.2 * Math.sin(animTime * 2.0);
      }
    }

    // Animate signal source marker (pulsing blue ring)
    if (this.signalSourceMarker) {
      const ring = this.signalSourceMarker.children[0] as THREE.Mesh;
      if (ring) {
        const pulse = 0.8 + 0.2 * Math.sin(animTime * 3.0);
        ring.scale.setScalar(pulse);
      }
      const beam = this.signalSourceMarker.children[1] as THREE.Mesh;
      if (beam && beam.material instanceof THREE.MeshLambertMaterial) {
        beam.material.opacity = 0.2 + 0.2 * Math.sin(animTime * 2.0);
      }
    }

    this.stepImpactEffects(dt);

    // Update drone delivery animation
    P.begin('drone');
    if (this.activeDroneDelivery) {
      const animTime = performance.now() / 1000;
      if (!updateDroneDelivery(this.activeDroneDelivery, dt, animTime)) {
        disposeDroneDelivery(this.sceneObjs.scene, this.activeDroneDelivery);
        this.activeDroneDelivery = null;
        // the delivery ends the buyer's turn (64.6 2b-ii: as an input — refused if the buyer already yielded)
        const buyer = this.gameState.players.find(p => p.id === this.droneBuyerId);
        if (buyer) this.endTurnFor(buyer);
      }
    }
    P.end('drone');

    // Update respawn drop can animations
    if (this.respawnDropCans.size > 0) {
      const animTime = performance.now() / 1000;
      for (const [pid, effect] of this.respawnDropCans) {
        if (effect.state !== 'done') {
          const prevState = effect.state;
          updateDropCanEffect(effect, dt, animTime);

          if (!effect.approachSoundPlayed && effect.rootGroup.visible && effect.state === 'approach') {
            effect.approachSoundPlayed = true;
            const pos = effect.rootGroup.position;
            playSpatial({ x: pos.x, y: pos.y, z: pos.z }, rocketLaunch, 4);
          }
          if (!effect.landedSoundPlayed && prevState === 'descend' && effect.state === 'landed') {
            effect.landedSoundPlayed = true;
            const pos = effect.rootGroup.position;
            playSpatial({ x: pos.x, y: pos.y, z: pos.z }, craterImpact, 3);
          }
        } else {
          // Landing complete — show player mesh, reset animation, store crashed scenery
          const meshData = this.sceneObjs.playerMeshes.get(pid);
          if (meshData) {
            meshData.group.visible = true;
            resetAnimContext(meshData.animCtx);
          }

          const sceneryId = pid + this.gameState.turnNumber * 1000;
          this.crashedDropCanMeshes.set(sceneryId, effect.rootGroup);
          disposeDropCanEffect(effect, true);  // keepCrashed
          this.respawnDropCans.delete(pid);
        }
      }
    }

    // Update deploy phase drop can animations + fan-out walks
    P.begin('deploy');
    if (this.gameState.phase === 'deploying') this.deploy.update(dt);
    P.end('deploy');

    // Update ferry pickup animation
    P.begin('ferry');
    if (this.gameState.phase === 'pickup' && this.ferryPickupEffects.length > 0) {
      this.updateFerryPickup(dt, animTime);
    }
    P.end('ferry');

    // Update walk interpolation
    P.begin('walk');
    this.updateWalkAnimation(dt);
    P.end('walk');

    // Update player animations
    P.begin('playerAnim');
    this.updatePlayerAnimations(dt);
    P.end('playerAnim');

    // Update audio conductor
    P.begin('audio');
    this.tickConductor(dt);
    P.end('audio');

    // Update camera
    P.begin('camera');
    updateCamera(this.sceneObjs.camera, this.cameraState, dt);
    // I9: the missile cam overrides the placed camera while a shot is ridden, and for a moment after it ends
    if (this.shotCam === 'ride' || this.missileCam?.active) {
      this.missileCam ??= new MissileCam(this.sceneObjs.renderer.domElement);
      const ride = this.rideThisFrame;
      this.missileCam.update(ride?.projectile ?? null, this.gameState.planets, ride?.impact ?? null, this.lastShotEnd, this.sceneObjs.camera, dt);
      if (!this.missileCam.active) this.lastShotEnd = null;
    }
    P.end('camera');

    // Update spatial audio listener to match camera (after camera update)
    this.followListener();
    const cam = this.sceneObjs.camera;

    // Fog markers keep a readable screen size at any zoom
    if (this.sceneObjs.ghostMeshes.length || this.sceneObjs.exposureMeshes.length || this.sceneObjs.structureGhostMeshes.length) {
      updateMarkerScreenScale(cam, [
        ...this.sceneObjs.ghostMeshes.map(g => g.group),
        ...this.sceneObjs.structureGhostMeshes.map(g => g.group),
        ...this.sceneObjs.exposureMeshes.map(g => g.group),
      ]);
    }

    this.updateThreatCueVisuals(cam);

    // Gravity field flow map (dev view)
    if (this.gravityField) {
      P.begin('gravityField');
      updateGravityField(this.gravityField, this.gameState.planets, dt);
      P.end('gravityField');
    }

    // Update minimap
    P.begin('minimap');
    if (this.minimapState) {
      updateMinimap(this.minimapState, this.gameState, this.cameraState.theta,
        this.visibleEnemyIds, this.viewerIndex, this.fogCam);
    }
    P.end('minimap');

    // Render
    P.begin('render');
    fitCameraDepth(this.sceneObjs.camera, this.gameState.planets);
    this.renderFrame();
    P.end('render');

    P.setRenderInfo(this.sceneObjs.renderer.info);
    P.endFrame();
  };
}

// =============================================================================
// START GAME
// =============================================================================

new Game();
